import { BaseEntity, EntitySaveOptions, EntityDeleteOptions, CompositeKey, ValidationResult, ValidationErrorInfo, ValidationErrorType, Metadata, ProviderType, DatabaseProviderBase } from "@memberjunction/core";
import { RegisterClass } from "@memberjunction/global";
import { z } from "zod";

export const loadModule = () => {
  // no-op, only used to ensure this file is a valid module and to allow easy loading
}

     
 
/**
 * zod schema definition for the entity Addresses
 */
export const ORCIDAddressSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ORCIDRecordID: z.string().describe(`
        * * Field Name: ORCIDRecordID
        * * Display Name: ORCID Record ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Records (vwRecords.ID)`),
    PutCode: z.number().describe(`
        * * Field Name: PutCode
        * * Display Name: Put Code
        * * SQL Data Type: bigint`),
    Country: z.string().nullable().describe(`
        * * Field Name: Country
        * * Display Name: Country
        * * SQL Data Type: nvarchar(10)`),
    DisplayIndex: z.number().nullable().describe(`
        * * Field Name: DisplayIndex
        * * Display Name: Display Index
        * * SQL Data Type: int`),
    Visibility: z.string().nullable().describe(`
        * * Field Name: Visibility
        * * Display Name: Visibility
        * * SQL Data Type: nvarchar(20)`),
    SourceName: z.string().nullable().describe(`
        * * Field Name: SourceName
        * * Display Name: Source Name
        * * SQL Data Type: nvarchar(255)`),
    SourceOrcid: z.string().nullable().describe(`
        * * Field Name: SourceOrcid
        * * Display Name: Source Orcid
        * * SQL Data Type: nvarchar(19)`),
    OrcidCreatedDate: z.date().nullable().describe(`
        * * Field Name: OrcidCreatedDate
        * * Display Name: Orcid Created Date
        * * SQL Data Type: datetimeoffset`),
    OrcidLastModifiedDate: z.date().nullable().describe(`
        * * Field Name: OrcidLastModifiedDate
        * * Display Name: Orcid Last Modified Date
        * * SQL Data Type: datetimeoffset`),
    RawJSON: z.string().nullable().describe(`
        * * Field Name: RawJSON
        * * Display Name: Raw JSON
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncStatus: z.string().describe(`
        * * Field Name: __mj_integration_SyncStatus
        * * Display Name: Mj Integration Sync Status
        * * SQL Data Type: nvarchar(50)
        * * Default Value: Active`),
    __mj_integration_LastSyncedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedAt
        * * Display Name: Mj Integration Last Synced At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastSyncedSnapshot: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedSnapshot
        * * Display Name: Mj Integration Last Synced Snapshot
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncMessage: z.string().nullable().describe(`
        * * Field Name: __mj_integration_SyncMessage
        * * Display Name: Mj Integration Sync Message
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ContentHash: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ContentHash
        * * Display Name: Mj Integration Content Hash
        * * SQL Data Type: nvarchar(64)`),
    __mj_integration_CustomOverflow: z.string().nullable().describe(`
        * * Field Name: __mj_integration_CustomOverflow
        * * Display Name: Mj Integration Custom Overflow
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ExternalVersion: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ExternalVersion
        * * Display Name: Mj Integration External Version
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastSeenModifiedValue: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSeenModifiedValue
        * * Display Name: Mj Integration Last Seen Modified Value
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastReconciledAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastReconciledAt
        * * Display Name: Mj Integration Last Reconciled At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastWriterDirection: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastWriterDirection
        * * Display Name: Mj Integration Last Writer Direction
        * * SQL Data Type: nvarchar(10)`),
    __mj_integration_IsTombstoned: z.boolean().describe(`
        * * Field Name: __mj_integration_IsTombstoned
        * * Display Name: Mj Integration Is Tombstoned
        * * SQL Data Type: bit
        * * Default Value: 0`),
    __mj_integration_DeletedDetectedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_DeletedDetectedAt
        * * Display Name: Mj Integration Deleted Detected At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type ORCIDAddressEntityType = z.infer<typeof ORCIDAddressSchema>;

/**
 * zod schema definition for the entity Agu Persons
 */
export const CommonAguPersonSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    AGUPersonID: z.string().describe(`
        * * Field Name: AGUPersonID
        * * Display Name: AGU Person ID
        * * SQL Data Type: uniqueidentifier`),
    FirstName: z.string().nullable().describe(`
        * * Field Name: FirstName
        * * Display Name: First Name
        * * SQL Data Type: nvarchar(200)`),
    LastName: z.string().nullable().describe(`
        * * Field Name: LastName
        * * Display Name: Last Name
        * * SQL Data Type: nvarchar(200)`),
    Email: z.string().nullable().describe(`
        * * Field Name: Email
        * * Display Name: Email
        * * SQL Data Type: nvarchar(255)`),
    OrcID: z.string().nullable().describe(`
        * * Field Name: OrcID
        * * Display Name: Orc ID
        * * SQL Data Type: nvarchar(50)`),
    LinkedInUrl: z.string().nullable().describe(`
        * * Field Name: LinkedInUrl
        * * Display Name: Linked In Url
        * * SQL Data Type: nvarchar(500)`),
    UserID: z.string().nullable().describe(`
        * * Field Name: UserID
        * * Display Name: User ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Users (vwUsers.ID)
        * * Description: MJ login for this person. NULL until the person signs in (member provisioning) or the seed matched an existing user by email. Unique: one login per person. Deliberately NOT __mj.User.LinkedRecordType/LinkedEntityID, which staff accounts already use for Employee links.`),
    IdentityProviderSubject: z.string().nullable().describe(`
        * * Field Name: IdentityProviderSubject
        * * Display Name: Identity Provider Subject
        * * SQL Data Type: nvarchar(200)`),
    Source: z.union([z.literal('Both'), z.literal('Contributor'), z.literal('MAPS'), z.literal('Roster'), z.literal('SSO')]).describe(`
        * * Field Name: Source
        * * Display Name: Source
        * * SQL Data Type: nvarchar(20)
    * * Value List Type: List
    * * Possible Values 
    *   * Both
    *   * Contributor
    *   * MAPS
    *   * Roster
    *   * SSO
        * * Description: Which seed population supplied the row: MAPS (member feed only), Contributor (AGU content corpus only), Both.`),
    SeededAt: z.date().describe(`
        * * Field Name: SeededAt
        * * Display Name: Seeded At
        * * SQL Data Type: datetimeoffset
        * * Default Value: sysdatetimeoffset()`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    User: z.string().nullable().describe(`
        * * Field Name: User
        * * Display Name: User
        * * SQL Data Type: nvarchar(100)`),
});

export type CommonAguPersonEntityType = z.infer<typeof CommonAguPersonSchema>;

/**
 * zod schema definition for the entity Application Users
 */
export const FeedbackPlatformApplicationUserSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    UserID: z.string().describe(`
        * * Field Name: UserID
        * * Display Name: User ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Users (vwUsers.ID)
        * * Description: Reference to the main User record in MemberJunction (unique).`),
    PasswordHash: z.string().describe(`
        * * Field Name: PasswordHash
        * * Display Name: Password Hash
        * * SQL Data Type: nvarchar(64)
        * * Description: MD5 hash of the user password.`),
    IsActive: z.boolean().describe(`
        * * Field Name: IsActive
        * * Display Name: Is Active
        * * SQL Data Type: bit
        * * Default Value: 1
        * * Description: Whether the application user account is active.`),
    LastLoginDate: z.date().nullable().describe(`
        * * Field Name: LastLoginDate
        * * Display Name: Last Login Date
        * * SQL Data Type: datetimeoffset
        * * Description: Last successful login timestamp.`),
    PasswordResetToken: z.string().nullable().describe(`
        * * Field Name: PasswordResetToken
        * * Display Name: Password Reset Token
        * * SQL Data Type: nvarchar(255)
        * * Description: Token used for password reset functionality.`),
    PasswordResetExpiry: z.date().nullable().describe(`
        * * Field Name: PasswordResetExpiry
        * * Display Name: Password Reset Expiry
        * * SQL Data Type: datetimeoffset
        * * Description: Expiry date/time for password reset token.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    User: z.string().describe(`
        * * Field Name: User
        * * Display Name: User
        * * SQL Data Type: nvarchar(100)`),
});

export type FeedbackPlatformApplicationUserEntityType = z.infer<typeof FeedbackPlatformApplicationUserSchema>;

/**
 * zod schema definition for the entity Awards
 */
export const CoreSignalAwardSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ProfileID: z.string().describe(`
        * * Field Name: ProfileID
        * * Display Name: Profile ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Profiles (vwProfiles.ID)`),
    OrderInProfile: z.number().describe(`
        * * Field Name: OrderInProfile
        * * Display Name: Order In Profile
        * * SQL Data Type: int`),
    Title: z.string().nullable().describe(`
        * * Field Name: Title
        * * Display Name: Title
        * * SQL Data Type: nvarchar(1000)`),
    Issuer: z.string().nullable().describe(`
        * * Field Name: Issuer
        * * Display Name: Issuer
        * * SQL Data Type: nvarchar(500)`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(MAX)`),
    DateYear: z.number().nullable().describe(`
        * * Field Name: DateYear
        * * Display Name: Date Year
        * * SQL Data Type: smallint`),
    DateMonth: z.number().nullable().describe(`
        * * Field Name: DateMonth
        * * Display Name: Date Month
        * * SQL Data Type: tinyint`),
    __mj_integration_SyncStatus: z.string().describe(`
        * * Field Name: __mj_integration_SyncStatus
        * * Display Name: Mj Integration Sync Status
        * * SQL Data Type: nvarchar(50)
        * * Default Value: Active`),
    __mj_integration_LastSyncedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedAt
        * * Display Name: Mj Integration Last Synced At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastSyncedSnapshot: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedSnapshot
        * * Display Name: Mj Integration Last Synced Snapshot
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncMessage: z.string().nullable().describe(`
        * * Field Name: __mj_integration_SyncMessage
        * * Display Name: Mj Integration Sync Message
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ContentHash: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ContentHash
        * * Display Name: Mj Integration Content Hash
        * * SQL Data Type: nvarchar(64)`),
    __mj_integration_CustomOverflow: z.string().nullable().describe(`
        * * Field Name: __mj_integration_CustomOverflow
        * * Display Name: Mj Integration Custom Overflow
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ExternalVersion: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ExternalVersion
        * * Display Name: Mj Integration External Version
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastSeenModifiedValue: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSeenModifiedValue
        * * Display Name: Mj Integration Last Seen Modified Value
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastReconciledAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastReconciledAt
        * * Display Name: Mj Integration Last Reconciled At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastWriterDirection: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastWriterDirection
        * * Display Name: Mj Integration Last Writer Direction
        * * SQL Data Type: nvarchar(10)`),
    __mj_integration_IsTombstoned: z.boolean().describe(`
        * * Field Name: __mj_integration_IsTombstoned
        * * Display Name: Mj Integration Is Tombstoned
        * * SQL Data Type: bit
        * * Default Value: 0`),
    __mj_integration_DeletedDetectedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_DeletedDetectedAt
        * * Display Name: Mj Integration Deleted Detected At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type CoreSignalAwardEntityType = z.infer<typeof CoreSignalAwardSchema>;

/**
 * zod schema definition for the entity Certifications
 */
export const CoreSignalCertificationSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ProfileID: z.string().describe(`
        * * Field Name: ProfileID
        * * Display Name: Profile ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Profiles (vwProfiles.ID)`),
    OrderInProfile: z.number().describe(`
        * * Field Name: OrderInProfile
        * * Display Name: Order In Profile
        * * SQL Data Type: int`),
    Title: z.string().nullable().describe(`
        * * Field Name: Title
        * * Display Name: Title
        * * SQL Data Type: nvarchar(1000)`),
    Issuer: z.string().nullable().describe(`
        * * Field Name: Issuer
        * * Display Name: Issuer
        * * SQL Data Type: nvarchar(500)`),
    IssuerUrl: z.string().nullable().describe(`
        * * Field Name: IssuerUrl
        * * Display Name: Issuer Url
        * * SQL Data Type: nvarchar(1000)`),
    CertificateUrl: z.string().nullable().describe(`
        * * Field Name: CertificateUrl
        * * Display Name: Certificate Url
        * * SQL Data Type: nvarchar(2000)`),
    CredentialID: z.string().nullable().describe(`
        * * Field Name: CredentialID
        * * Display Name: Credential ID
        * * SQL Data Type: nvarchar(255)`),
    DateFromYear: z.number().nullable().describe(`
        * * Field Name: DateFromYear
        * * Display Name: Date From Year
        * * SQL Data Type: smallint`),
    DateFromMonth: z.number().nullable().describe(`
        * * Field Name: DateFromMonth
        * * Display Name: Date From Month
        * * SQL Data Type: tinyint`),
    DateToYear: z.number().nullable().describe(`
        * * Field Name: DateToYear
        * * Display Name: Date To Year
        * * SQL Data Type: smallint`),
    DateToMonth: z.number().nullable().describe(`
        * * Field Name: DateToMonth
        * * Display Name: Date To Month
        * * SQL Data Type: tinyint`),
    __mj_integration_SyncStatus: z.string().describe(`
        * * Field Name: __mj_integration_SyncStatus
        * * Display Name: Mj Integration Sync Status
        * * SQL Data Type: nvarchar(50)
        * * Default Value: Active`),
    __mj_integration_LastSyncedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedAt
        * * Display Name: Mj Integration Last Synced At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastSyncedSnapshot: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedSnapshot
        * * Display Name: Mj Integration Last Synced Snapshot
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncMessage: z.string().nullable().describe(`
        * * Field Name: __mj_integration_SyncMessage
        * * Display Name: Mj Integration Sync Message
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ContentHash: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ContentHash
        * * Display Name: Mj Integration Content Hash
        * * SQL Data Type: nvarchar(64)`),
    __mj_integration_CustomOverflow: z.string().nullable().describe(`
        * * Field Name: __mj_integration_CustomOverflow
        * * Display Name: Mj Integration Custom Overflow
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ExternalVersion: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ExternalVersion
        * * Display Name: Mj Integration External Version
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastSeenModifiedValue: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSeenModifiedValue
        * * Display Name: Mj Integration Last Seen Modified Value
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastReconciledAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastReconciledAt
        * * Display Name: Mj Integration Last Reconciled At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastWriterDirection: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastWriterDirection
        * * Display Name: Mj Integration Last Writer Direction
        * * SQL Data Type: nvarchar(10)`),
    __mj_integration_IsTombstoned: z.boolean().describe(`
        * * Field Name: __mj_integration_IsTombstoned
        * * Display Name: Mj Integration Is Tombstoned
        * * SQL Data Type: bit
        * * Default Value: 0`),
    __mj_integration_DeletedDetectedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_DeletedDetectedAt
        * * Display Name: Mj Integration Deleted Detected At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type CoreSignalCertificationEntityType = z.infer<typeof CoreSignalCertificationSchema>;

/**
 * zod schema definition for the entity Committee Members
 */
export const FeedbackPlatformCommitteeMemberSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    SeasonID: z.string().describe(`
        * * Field Name: SeasonID
        * * Display Name: Season ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Seasons (vwSeasons.ID)`),
    SectionID: z.string().describe(`
        * * Field Name: SectionID
        * * Display Name: Section ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Sections (vwSections.ID)`),
    UserID: z.string().describe(`
        * * Field Name: UserID
        * * Display Name: User ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Users (vwUsers.ID)`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Season: z.string().describe(`
        * * Field Name: Season
        * * Display Name: Season
        * * SQL Data Type: nvarchar(200)`),
    Section: z.string().describe(`
        * * Field Name: Section
        * * Display Name: Section
        * * SQL Data Type: nvarchar(200)`),
    User: z.string().describe(`
        * * Field Name: User
        * * Display Name: User
        * * SQL Data Type: nvarchar(100)`),
});

export type FeedbackPlatformCommitteeMemberEntityType = z.infer<typeof FeedbackPlatformCommitteeMemberSchema>;

/**
 * zod schema definition for the entity Committee Selection Items
 */
export const FeedbackPlatformCommitteeSelectionItemSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    CommitteeSelectionID: z.string().describe(`
        * * Field Name: CommitteeSelectionID
        * * Display Name: Committee Selection ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Committee Selections (vwCommitteeSelections.ID)`),
    PresentationID: z.string().describe(`
        * * Field Name: PresentationID
        * * Display Name: Presentation ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Presentations (vwPresentations.ID)`),
    Slot: z.number().describe(`
        * * Field Name: Slot
        * * Display Name: Slot
        * * SQL Data Type: tinyint`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Presentation: z.string().describe(`
        * * Field Name: Presentation
        * * Display Name: Presentation
        * * SQL Data Type: nvarchar(500)`),
});

export type FeedbackPlatformCommitteeSelectionItemEntityType = z.infer<typeof FeedbackPlatformCommitteeSelectionItemSchema>;

/**
 * zod schema definition for the entity Committee Selections
 */
export const FeedbackPlatformCommitteeSelectionSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    SeasonID: z.string().describe(`
        * * Field Name: SeasonID
        * * Display Name: Season ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Seasons (vwSeasons.ID)`),
    SectionID: z.string().describe(`
        * * Field Name: SectionID
        * * Display Name: Section ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Sections (vwSections.ID)`),
    Status: z.union([z.literal('Draft'), z.literal('Submitted')]).describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: nvarchar(20)
        * * Default Value: Draft
    * * Value List Type: List
    * * Possible Values 
    *   * Draft
    *   * Submitted`),
    SubmittedAt: z.date().nullable().describe(`
        * * Field Name: SubmittedAt
        * * Display Name: Submitted At
        * * SQL Data Type: datetimeoffset`),
    SubmittedByUserID: z.string().nullable().describe(`
        * * Field Name: SubmittedByUserID
        * * Display Name: Submitted By User ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Users (vwUsers.ID)`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Season: z.string().describe(`
        * * Field Name: Season
        * * Display Name: Season
        * * SQL Data Type: nvarchar(200)`),
    Section: z.string().describe(`
        * * Field Name: Section
        * * Display Name: Section
        * * SQL Data Type: nvarchar(200)`),
    SubmittedByUser: z.string().nullable().describe(`
        * * Field Name: SubmittedByUser
        * * Display Name: Submitted By User
        * * SQL Data Type: nvarchar(100)`),
});

export type FeedbackPlatformCommitteeSelectionEntityType = z.infer<typeof FeedbackPlatformCommitteeSelectionSchema>;

/**
 * zod schema definition for the entity Conference Registrations
 */
export const MAPSConferenceRegistrationSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    PersonID: z.string().describe(`
        * * Field Name: PersonID
        * * Display Name: Person ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: People (vwPeople.ID)
        * * Description: References the person who registered for the meeting.`),
    Year: z.number().describe(`
        * * Field Name: Year
        * * Display Name: Year
        * * SQL Data Type: int
        * * Description: Year of the AGU annual meeting (e.g., 2023, 2024, 2025).`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(150)
        * * Description: Company or organization affiliation reported at registration time.`),
    Title: z.string().nullable().describe(`
        * * Field Name: Title
        * * Display Name: Title
        * * SQL Data Type: nvarchar(150)
        * * Description: Job title reported at registration time.`),
    City: z.string().nullable().describe(`
        * * Field Name: City
        * * Display Name: City
        * * SQL Data Type: nvarchar(75)
        * * Description: City of affiliation reported at registration time.`),
    State: z.string().nullable().describe(`
        * * Field Name: State
        * * Display Name: State
        * * SQL Data Type: nvarchar(75)
        * * Description: State or province of affiliation reported at registration time.`),
    Country: z.string().nullable().describe(`
        * * Field Name: Country
        * * Display Name: Country
        * * SQL Data Type: nvarchar(75)
        * * Description: Country of affiliation reported at registration time. Per source convention, blank when the country is the United States.`),
    JobFunction: z.string().nullable().describe(`
        * * Field Name: JobFunction
        * * Display Name: Job Function
        * * SQL Data Type: nvarchar(50)
        * * Description: Self-reported job function captured at registration. Only populated for the 2025 meeting; uses a value set distinct from MAPS.Person.JobFunction.`),
    CareerStage: z.union([z.literal('Early Career'), z.literal('Experienced'), z.literal('Mid-Career'), z.literal('Retired'), z.literal('Student'), z.literal('Unknown')]).nullable().describe(`
        * * Field Name: CareerStage
        * * Display Name: Career Stage
        * * SQL Data Type: nvarchar(20)
    * * Value List Type: List
    * * Possible Values 
    *   * Early Career
    *   * Experienced
    *   * Mid-Career
    *   * Retired
    *   * Student
    *   * Unknown
        * * Description: Self-reported career stage captured at registration. Only populated for the 2024 and 2025 meetings.`),
    InstitutionType: z.string().nullable().describe(`
        * * Field Name: InstitutionType
        * * Display Name: Institution Type
        * * SQL Data Type: nvarchar(75)
        * * Description: Type of employing institution reported at registration. Includes some categories no longer offered on the current form (e.g., Military, Self Employed, Unemployed, Retired).`),
    PrimarySection: z.string().nullable().describe(`
        * * Field Name: PrimarySection
        * * Display Name: Primary Section
        * * SQL Data Type: nvarchar(100)
        * * Description: Primary AGU scientific section the registrant affiliates with (e.g., Atmospheric Sciences, Hydrology, Seismology). May be blank.`),
    FederalGovernment: z.union([z.literal('No'), z.literal('Unknown'), z.literal('Yes')]).nullable().describe(`
        * * Field Name: FederalGovernment
        * * Display Name: Federal Government
        * * SQL Data Type: nvarchar(10)
    * * Value List Type: List
    * * Possible Values 
    *   * No
    *   * Unknown
    *   * Yes
        * * Description: Whether the registrant identified as a US federal government employee. Yes / No / Unknown.`),
    FirstTimeAttendee: z.union([z.literal('No'), z.literal('Unknown'), z.literal('Yes')]).nullable().describe(`
        * * Field Name: FirstTimeAttendee
        * * Display Name: First Time Attendee
        * * SQL Data Type: nvarchar(10)
    * * Value List Type: List
    * * Possible Values 
    *   * No
    *   * Unknown
    *   * Yes
        * * Description: Whether the registrant identified as a first time attendee of the AGU annual meeting. Yes / No / Unknown.`),
    EarlyCareerScientist: z.union([z.literal('No'), z.literal('Unknown'), z.literal('Yes')]).nullable().describe(`
        * * Field Name: EarlyCareerScientist
        * * Display Name: Early Career Scientist
        * * SQL Data Type: nvarchar(10)
    * * Value List Type: List
    * * Possible Values 
    *   * No
    *   * Unknown
    *   * Yes
        * * Description: Whether the registrant self-identified as an early career scientist. Yes / No / Unknown.`),
    K12Educator: z.union([z.literal('No'), z.literal('Unknown'), z.literal('Yes')]).nullable().describe(`
        * * Field Name: K12Educator
        * * Display Name: K12 Educator
        * * SQL Data Type: nvarchar(10)
    * * Value List Type: List
    * * Possible Values 
    *   * No
    *   * Unknown
    *   * Yes
        * * Description: Whether the registrant self-identified as a K-12 educator. Yes / No / Unknown.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type MAPSConferenceRegistrationEntityType = z.infer<typeof MAPSConferenceRegistrationSchema>;

/**
 * zod schema definition for the entity Contact Message Events
 */
export const MAPSContactMessageEventSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ContactMessageID: z.string().describe(`
        * * Field Name: ContactMessageID
        * * Display Name: Contact Message ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Contact Messages (vwContactMessages.ID)
        * * Description: Parent MAPS.ContactMessage — resolved from the event's custom_args.contactMessageId echo.`),
    EventType: z.union([z.literal('bounce'), z.literal('click'), z.literal('deferred'), z.literal('delivered'), z.literal('dropped'), z.literal('open'), z.literal('processed'), z.literal('spamreport')]).describe(`
        * * Field Name: EventType
        * * Display Name: Event Type
        * * SQL Data Type: nvarchar(20)
    * * Value List Type: List
    * * Possible Values 
    *   * bounce
    *   * click
    *   * deferred
    *   * delivered
    *   * dropped
    *   * open
    *   * processed
    *   * spamreport
        * * Description: SendGrid's own event vocabulary, stored verbatim: processed, deferred, delivered, open, click, bounce, dropped, spamreport. processed/deferred are audit-only (no rollup change).`),
    OccurredAt: z.date().describe(`
        * * Field Name: OccurredAt
        * * Display Name: Occurred At
        * * SQL Data Type: datetimeoffset
        * * Description: When the event happened at SendGrid (the event's unix timestamp), distinct from __mj_CreatedAt (when the webhook delivered it here).`),
    SGEventID: z.string().nullable().describe(`
        * * Field Name: SGEventID
        * * Display Name: SG Event ID
        * * SQL Data Type: nvarchar(128)
        * * Description: SendGrid sg_event_id — globally unique per event; the exact-idempotency key for webhook retries (filtered unique index). Nullable defensively: an event missing the field is still recorded, just without dedup.`),
    Url: z.string().nullable().describe(`
        * * Field Name: Url
        * * Display Name: Url
        * * SQL Data Type: nvarchar(2048)
        * * Description: Click events: the URL that was clicked. NULL for other event types.`),
    Reason: z.string().nullable().describe(`
        * * Field Name: Reason
        * * Display Name: Reason
        * * SQL Data Type: nvarchar(1000)
        * * Description: Bounce/dropped/spam detail (the event's reason field). NULL for other event types.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    ContactMessage: z.string().nullable().describe(`
        * * Field Name: ContactMessage
        * * Display Name: Contact Message
        * * SQL Data Type: nvarchar(310)`),
});

export type MAPSContactMessageEventEntityType = z.infer<typeof MAPSContactMessageEventSchema>;

/**
 * zod schema definition for the entity Contact Messages
 */
export const MAPSContactMessageSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    CompanyID: z.string().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
        * * Description: Owning company. Authoritative attribution/scoping key for the partner-account model (V031).`),
    PartnerTokenID: z.string().nullable().describe(`
        * * Field Name: PartnerTokenID
        * * Display Name: Partner Token ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Partner Tokens (vwPartnerTokens.ID)
        * * Description: Legacy owning partner token. Dual-written while the standalone cookie path lives; dropped in PR B. Ownership scoping uses CompanyID.`),
    SearchLogID: z.string().nullable().describe(`
        * * Field Name: SearchLogID
        * * Display Name: Search Log ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Search Logs (vwSearchLogs.ID)
        * * Description: Originating search (MAPS.SearchLog). The owned search snapshot is both the ownership check and the trusted source of the recipient email. Nullable so contact history outlives search-log retention.`),
    BatchID: z.string().describe(`
        * * Field Name: BatchID
        * * Display Name: Batch ID
        * * SQL Data Type: uniqueidentifier
        * * Description: Groups the recipients of one mapsContactSend mutation call (one modal send). The history drawer groups rows on it.`),
    CandidateID: z.string().describe(`
        * * Field Name: CandidateID
        * * Display Name: Candidate ID
        * * SQL Data Type: nvarchar(200)
        * * Description: Engine contributor id (= MAPSCandidate.candidateId; matches Recommendation Contributor.CustomerID sizing). Not a FK — the corpus and this ledger have independent lifecycles.`),
    RecipientName: z.string().nullable().describe(`
        * * Field Name: RecipientName
        * * Display Name: Recipient Name
        * * SQL Data Type: nvarchar(310)
        * * Description: Candidate display name at send time (first + last from the snapshot).`),
    RecipientEmail: z.string().describe(`
        * * Field Name: RecipientEmail
        * * Display Name: Recipient Email
        * * SQL Data Type: nvarchar(255)
        * * Description: The candidate's email, resolved server-side from the owned search snapshot — never client-supplied. In redirect/safety-net modes this remains the INTENDED recipient; RedirectedTo records where the mail actually went.`),
    ReplyTo: z.string().nullable().describe(`
        * * Field Name: ReplyTo
        * * Display Name: Reply To
        * * SQL Data Type: nvarchar(255)
        * * Description: Reply-To header: the requesting partner user's email, so candidate replies go straight to the recruiter. NULL when the session carries no user email.`),
    Subject: z.string().describe(`
        * * Field Name: Subject
        * * Display Name: Subject
        * * SQL Data Type: nvarchar(500)
        * * Description: Message subject as composed (before any redirect-mode test stamp).`),
    Body: z.string().describe(`
        * * Field Name: Body
        * * Display Name: Body
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Message body as composed (plain text; before any redirect-mode banner).`),
    DeliveryMode: z.union([z.literal('dryrun'), z.literal('live'), z.literal('redirect'), z.literal('sandbox')]).describe(`
        * * Field Name: DeliveryMode
        * * Display Name: Delivery Mode
        * * SQL Data Type: nvarchar(10)
    * * Value List Type: List
    * * Possible Values 
    *   * dryrun
    *   * live
    *   * redirect
    *   * sandbox
        * * Description: Delivery-safety mode the send ran under: dryrun (no external call), sandbox (SendGrid validates, no delivery), redirect (delivered to the test/catch inbox only), live (real member delivery; requires the two-key unlock). Partner-facing "contacted" signals count only live and redirect rows.`),
    RedirectedTo: z.string().nullable().describe(`
        * * Field Name: RedirectedTo
        * * Display Name: Redirected To
        * * SQL Data Type: nvarchar(255)
        * * Description: Where the mail actually went when the safety net rewrote the recipient (redirect mode, or any non-live mode falling to the hard-coded catch address). NULL for live sends.`),
    Status: z.union([z.literal('Bounced'), z.literal('Clicked'), z.literal('Delivered'), z.literal('Dropped'), z.literal('Failed'), z.literal('Opened'), z.literal('Pending'), z.literal('Sent'), z.literal('Spam')]).describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: nvarchar(12)
        * * Default Value: Pending
    * * Value List Type: List
    * * Possible Values 
    *   * Bounced
    *   * Clicked
    *   * Delivered
    *   * Dropped
    *   * Failed
    *   * Opened
    *   * Pending
    *   * Sent
    *   * Spam
        * * Description: Rollup of send outcome + newest webhook engagement. Monotonic (Pending<Sent<Delivered<Opened<Clicked; Bounced/Dropped/Spam/Failed terminal) — webhook replays can never regress it.`),
    SGMessageID: z.string().nullable().describe(`
        * * Field Name: SGMessageID
        * * Display Name: SG Message ID
        * * SQL Data Type: nvarchar(128)
        * * Description: SendGrid x-message-id captured from the send response. Support/dashboard lookup convenience only — webhook correlation uses custom_args.contactMessageId (= ID).`),
    SentAt: z.date().nullable().describe(`
        * * Field Name: SentAt
        * * Display Name: Sent At
        * * SQL Data Type: datetimeoffset
        * * Description: When the send left this system (engine call returned success). NULL for rows that never sent (Failed before transport).`),
    DeliveredAt: z.date().nullable().describe(`
        * * Field Name: DeliveredAt
        * * Display Name: Delivered At
        * * SQL Data Type: datetimeoffset
        * * Description: First delivered event timestamp (set-if-null on webhook replay).`),
    OpenedAt: z.date().nullable().describe(`
        * * Field Name: OpenedAt
        * * Display Name: Opened At
        * * SQL Data Type: datetimeoffset
        * * Description: First open event timestamp (set-if-null on webhook replay).`),
    OpenCount: z.number().describe(`
        * * Field Name: OpenCount
        * * Display Name: Open Count
        * * SQL Data Type: int
        * * Default Value: 0
        * * Description: Count of unique open events (deduped on sg_event_id — exact, not approximate).`),
    ClickedAt: z.date().nullable().describe(`
        * * Field Name: ClickedAt
        * * Display Name: Clicked At
        * * SQL Data Type: datetimeoffset
        * * Description: First click event timestamp (set-if-null on webhook replay).`),
    ClickCount: z.number().describe(`
        * * Field Name: ClickCount
        * * Display Name: Click Count
        * * SQL Data Type: int
        * * Default Value: 0
        * * Description: Count of unique click events (deduped on sg_event_id — exact, not approximate).`),
    BouncedAt: z.date().nullable().describe(`
        * * Field Name: BouncedAt
        * * Display Name: Bounced At
        * * SQL Data Type: datetimeoffset
        * * Description: Timestamp of the bounce event that made Status terminal.`),
    BounceReason: z.string().nullable().describe(`
        * * Field Name: BounceReason
        * * Display Name: Bounce Reason
        * * SQL Data Type: nvarchar(500)
        * * Description: Bounce/dropped/spam detail from the event that made Status terminal.`),
    ErrorMessage: z.string().nullable().describe(`
        * * Field Name: ErrorMessage
        * * Display Name: Error Message
        * * SQL Data Type: nvarchar(1000)
        * * Description: Send-path failure detail (Status=Failed): engine/provider error, or the safety reason when the recipient was blocked before transport.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    InviteID: z.string().nullable().describe(`
        * * Field Name: InviteID
        * * Display Name: Invite ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Magic Link Invites (vwMagicLinkInvites.ID)
        * * Description: Magic-link invite the sending session was minted from (partner key sessions). Exact per-send provenance: rotation mints a new invite, so this splits across rotations — rotation-stable sender grouping uses ReplyTo. NULL on pre-V041 rows, staff sends, and cookie sessions.`),
    UserID: z.string().nullable().describe(`
        * * Field Name: UserID
        * * Display Name: User ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Users (vwUsers.ID)
        * * Description: Real MJ user for staff/named-user sends. Partner recruiters have no per-recruiter MJ user (shared anonymous link principal), so at most one of InviteID/UserID is set. NULL on pre-V041 rows and cookie sessions.`),
    Company: z.string().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
    User: z.string().nullable().describe(`
        * * Field Name: User
        * * Display Name: User
        * * SQL Data Type: nvarchar(100)`),
});

export type MAPSContactMessageEntityType = z.infer<typeof MAPSContactMessageSchema>;

/**
 * zod schema definition for the entity Contents
 */
export const RecommendationContentSchema = z.object({
    ID: z.number().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: int
        * * Description: Unique identifier for the content item.`),
    SourceID: z.number().nullable().describe(`
        * * Field Name: SourceID
        * * Display Name: Source ID
        * * SQL Data Type: int
        * * Description: Identifier for the source system or dataset.`),
    Source: z.string().nullable().describe(`
        * * Field Name: Source
        * * Display Name: Source
        * * SQL Data Type: nvarchar(100)
        * * Description: Name of the content source (e.g., NSF, NASA, NIH).`),
    ContentID: z.string().nullable().describe(`
        * * Field Name: ContentID
        * * Display Name: Content ID
        * * SQL Data Type: nvarchar(100)
        * * Description: External identifier from the source system.`),
    ContentType: z.string().nullable().describe(`
        * * Field Name: ContentType
        * * Display Name: Content Type
        * * SQL Data Type: nvarchar(100)
        * * Description: Type of content (e.g., Grant, Article, Report).`),
    Title: z.string().nullable().describe(`
        * * Field Name: Title
        * * Display Name: Title
        * * SQL Data Type: nvarchar(500)
        * * Description: Title of the content item.`),
    Text: z.string().nullable().describe(`
        * * Field Name: Text
        * * Display Name: Text
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Full text content or description.`),
    DOI: z.string().nullable().describe(`
        * * Field Name: DOI
        * * Display Name: DOI
        * * SQL Data Type: nvarchar(100)
        * * Description: Digital Object Identifier if applicable.`),
    URL: z.string().nullable().describe(`
        * * Field Name: URL
        * * Display Name: URL
        * * SQL Data Type: nvarchar(MAX)
        * * Description: URL to the original content source.`),
    Date: z.date().nullable().describe(`
        * * Field Name: Date
        * * Display Name: Date
        * * SQL Data Type: date
        * * Description: Publication or release date of the content.`),
    EmbeddingID: z.string().nullable().describe(`
        * * Field Name: EmbeddingID
        * * Display Name: Embedding ID
        * * SQL Data Type: nvarchar(50)
        * * Description: Identifier for the associated vector embedding.`),
    UpdateVector: z.boolean().describe(`
        * * Field Name: UpdateVector
        * * Display Name: Update Vector
        * * SQL Data Type: bit
        * * Description: Flag indicating if the vector embedding needs to be updated.`),
    isError: z.boolean().describe(`
        * * Field Name: isError
        * * Display Name: is Error
        * * SQL Data Type: bit
        * * Default Value: 0
        * * Description: Flag indicating if there was an error processing this content.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()
        * * Description: Timestamp when the record was created.`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()
        * * Description: Timestamp when the record was last updated.`),
    Metadata: z.string().nullable().describe(`
        * * Field Name: Metadata
        * * Display Name: Metadata
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Additional metadata in JSON format.`),
    DisplayTitle: z.string().nullable().describe(`
        * * Field Name: DisplayTitle
        * * Display Name: Display Title
        * * SQL Data Type: nvarchar(400)`),
    DisplayTitleVersion: z.string().nullable().describe(`
        * * Field Name: DisplayTitleVersion
        * * Display Name: Display Title Version
        * * SQL Data Type: nvarchar(16)`),
});

export type RecommendationContentEntityType = z.infer<typeof RecommendationContentSchema>;

/**
 * zod schema definition for the entity Contributor Contents
 */
export const RecommendationContributorContentSchema = z.object({
    ID: z.number().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: int`),
    SourceID: z.number().nullable().describe(`
        * * Field Name: SourceID
        * * Display Name: Source ID
        * * SQL Data Type: int`),
    Source: z.string().nullable().describe(`
        * * Field Name: Source
        * * Display Name: Source
        * * SQL Data Type: varchar(15)`),
    ContributorID: z.number().describe(`
        * * Field Name: ContributorID
        * * Display Name: Contributor ID
        * * SQL Data Type: int
        * * Related Entity/Foreign Key: Contributors (vwContributors.ID)`),
    ContentID: z.number().nullable().describe(`
        * * Field Name: ContentID
        * * Display Name: Content ID
        * * SQL Data Type: int
        * * Related Entity/Foreign Key: Contents (vwContents.ID)`),
    DOI: z.string().nullable().describe(`
        * * Field Name: DOI
        * * Display Name: Doi
        * * SQL Data Type: nvarchar(100)`),
    Role1: z.string().nullable().describe(`
        * * Field Name: Role1
        * * Display Name: Role 1
        * * SQL Data Type: nvarchar(100)`),
    Role2: z.string().nullable().describe(`
        * * Field Name: Role2
        * * Display Name: Role 2
        * * SQL Data Type: nvarchar(100)`),
    CorrespondingAuthor: z.number().nullable().describe(`
        * * Field Name: CorrespondingAuthor
        * * Display Name: Corresponding Author
        * * SQL Data Type: int`),
    Affiliation: z.string().nullable().describe(`
        * * Field Name: Affiliation
        * * Display Name: Affiliation
        * * SQL Data Type: nvarchar(500)`),
    Order: z.number().nullable().describe(`
        * * Field Name: Order
        * * Display Name: Order
        * * SQL Data Type: int`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type RecommendationContributorContentEntityType = z.infer<typeof RecommendationContributorContentSchema>;

/**
 * zod schema definition for the entity Contributors
 */
export const RecommendationContributorSchema = z.object({
    ID: z.number().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: int`),
    CustomerID: z.string().describe(`
        * * Field Name: CustomerID
        * * Display Name: Customer ID
        * * SQL Data Type: nvarchar(100)`),
    Name: z.string().nullable().describe(`
        * * Field Name: Name
        * * Display Name: Name
        * * SQL Data Type: nvarchar(100)`),
    Organization: z.string().nullable().describe(`
        * * Field Name: Organization
        * * Display Name: Organization
        * * SQL Data Type: nvarchar(500)`),
    JobTitle: z.string().nullable().describe(`
        * * Field Name: JobTitle
        * * Display Name: Job Title
        * * SQL Data Type: nvarchar(500)`),
    Email: z.string().nullable().describe(`
        * * Field Name: Email
        * * Display Name: Email
        * * SQL Data Type: nvarchar(255)`),
    DoNotDisplay: z.boolean().describe(`
        * * Field Name: DoNotDisplay
        * * Display Name: Do Not Display
        * * SQL Data Type: bit
        * * Default Value: 0`),
    EmbeddingID: z.string().nullable().describe(`
        * * Field Name: EmbeddingID
        * * Display Name: Embedding ID
        * * SQL Data Type: nvarchar(50)`),
    UpdateVector: z.boolean().describe(`
        * * Field Name: UpdateVector
        * * Display Name: Update Vector
        * * SQL Data Type: bit
        * * Default Value: 1`),
    isError: z.boolean().describe(`
        * * Field Name: isError
        * * Display Name: is Error
        * * SQL Data Type: bit
        * * Default Value: 0`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    FirstName: z.string().nullable().describe(`
        * * Field Name: FirstName
        * * Display Name: First Name
        * * SQL Data Type: nvarchar(200)`),
    LastName: z.string().nullable().describe(`
        * * Field Name: LastName
        * * Display Name: Last Name
        * * SQL Data Type: nvarchar(200)`),
    AGUPersonID: z.string().nullable().describe(`
        * * Field Name: AGUPersonID
        * * Display Name: AGU Person ID
        * * SQL Data Type: uniqueidentifier`),
    MergedIntoContributorID: z.number().nullable().describe(`
        * * Field Name: MergedIntoContributorID
        * * Display Name: Merged Into Contributor ID
        * * SQL Data Type: int
        * * Related Entity/Foreign Key: Contributors (vwContributors.ID)`),
    RepresentationHashes: z.string().nullable().describe(`
        * * Field Name: RepresentationHashes
        * * Display Name: Representation Hashes
        * * SQL Data Type: nvarchar(MAX)`),
    RepresentationsBuiltAt: z.date().nullable().describe(`
        * * Field Name: RepresentationsBuiltAt
        * * Display Name: Representations Built At
        * * SQL Data Type: datetimeoffset`),
    MergedIntoContributor: z.string().nullable().describe(`
        * * Field Name: MergedIntoContributor
        * * Display Name: Merged Into Contributor
        * * SQL Data Type: nvarchar(100)`),
    RootMergedIntoContributorID: z.number().nullable().describe(`
        * * Field Name: RootMergedIntoContributorID
        * * Display Name: Root Merged Into Contributor ID
        * * SQL Data Type: int`),
});

export type RecommendationContributorEntityType = z.infer<typeof RecommendationContributorSchema>;

/**
 * zod schema definition for the entity Conversation Messages
 */
export const FeedbackPlatformConversationMessageSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ConversationID: z.string().describe(`
        * * Field Name: ConversationID
        * * Display Name: Conversation ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Conversations__FeedbackPlatform (vwConversations__FeedbackPlatform.ID)`),
    SenderUserID: z.string().describe(`
        * * Field Name: SenderUserID
        * * Display Name: Sender User ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Users (vwUsers.ID)`),
    MessageContent: z.string().describe(`
        * * Field Name: MessageContent
        * * Display Name: Message Content
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Content of the message.`),
    SentDate: z.date().describe(`
        * * Field Name: SentDate
        * * Display Name: Sent Date
        * * SQL Data Type: datetimeoffset
        * * Default Value: sysdatetimeoffset()
        * * Description: When the message was sent.`),
    ReadDate: z.date().nullable().describe(`
        * * Field Name: ReadDate
        * * Display Name: Read Date
        * * SQL Data Type: datetimeoffset
        * * Description: When the message was marked as read.`),
    Status: z.union([z.literal('Deleted'), z.literal('Delivered'), z.literal('Failed'), z.literal('Read'), z.literal('Sent')]).describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: nvarchar(20)
        * * Default Value: Sent
    * * Value List Type: List
    * * Possible Values 
    *   * Deleted
    *   * Delivered
    *   * Failed
    *   * Read
    *   * Sent
        * * Description: Current status of the message (Sent, Delivered, Read, Deleted, Failed).`),
    MessageType: z.union([z.literal('Message'), z.literal('Question'), z.literal('Response'), z.literal('SystemNotification')]).describe(`
        * * Field Name: MessageType
        * * Display Name: Message Type
        * * SQL Data Type: nvarchar(50)
        * * Default Value: Message
    * * Value List Type: List
    * * Possible Values 
    *   * Message
    *   * Question
    *   * Response
    *   * SystemNotification
        * * Description: Type of message (regular message, question, response, or system notification).`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    ThreadPosition: z.number().nullable().describe(`
        * * Field Name: ThreadPosition
        * * Display Name: Thread Position
        * * SQL Data Type: tinyint`),
    SenderUser: z.string().describe(`
        * * Field Name: SenderUser
        * * Display Name: Sender User
        * * SQL Data Type: nvarchar(100)`),
});

export type FeedbackPlatformConversationMessageEntityType = z.infer<typeof FeedbackPlatformConversationMessageSchema>;

/**
 * zod schema definition for the entity Conversations__FeedbackPlatform
 */
export const FeedbackPlatformConversation__FeedbackPlatformSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ReviewID: z.string().describe(`
        * * Field Name: ReviewID
        * * Display Name: Review ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Reviews (vwReviews.ID)`),
    Subject: z.string().nullable().describe(`
        * * Field Name: Subject
        * * Display Name: Subject
        * * SQL Data Type: nvarchar(255)
        * * Description: Subject line or topic of the conversation.`),
    Status: z.union([z.literal('Active'), z.literal('Archived'), z.literal('Closed')]).describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: nvarchar(20)
        * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Archived
    *   * Closed
        * * Description: Current status of the conversation thread.`),
    StartedDate: z.date().describe(`
        * * Field Name: StartedDate
        * * Display Name: Started Date
        * * SQL Data Type: datetimeoffset
        * * Default Value: sysdatetimeoffset()
        * * Description: When the conversation was started.`),
    LastMessageDate: z.date().nullable().describe(`
        * * Field Name: LastMessageDate
        * * Display Name: Last Message Date
        * * SQL Data Type: datetimeoffset
        * * Description: Timestamp of the most recent message in this conversation.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type FeedbackPlatformConversation__FeedbackPlatformEntityType = z.infer<typeof FeedbackPlatformConversation__FeedbackPlatformSchema>;

/**
 * zod schema definition for the entity Courses
 */
export const CoreSignalCourseSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ProfileID: z.string().describe(`
        * * Field Name: ProfileID
        * * Display Name: Profile ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Profiles (vwProfiles.ID)`),
    OrderInProfile: z.number().describe(`
        * * Field Name: OrderInProfile
        * * Display Name: Order In Profile
        * * SQL Data Type: int`),
    Title: z.string().nullable().describe(`
        * * Field Name: Title
        * * Display Name: Title
        * * SQL Data Type: nvarchar(1000)`),
    Organizer: z.string().nullable().describe(`
        * * Field Name: Organizer
        * * Display Name: Organizer
        * * SQL Data Type: nvarchar(500)`),
    __mj_integration_SyncStatus: z.string().describe(`
        * * Field Name: __mj_integration_SyncStatus
        * * Display Name: Mj Integration Sync Status
        * * SQL Data Type: nvarchar(50)
        * * Default Value: Active`),
    __mj_integration_LastSyncedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedAt
        * * Display Name: Mj Integration Last Synced At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastSyncedSnapshot: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedSnapshot
        * * Display Name: Mj Integration Last Synced Snapshot
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncMessage: z.string().nullable().describe(`
        * * Field Name: __mj_integration_SyncMessage
        * * Display Name: Mj Integration Sync Message
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ContentHash: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ContentHash
        * * Display Name: Mj Integration Content Hash
        * * SQL Data Type: nvarchar(64)`),
    __mj_integration_CustomOverflow: z.string().nullable().describe(`
        * * Field Name: __mj_integration_CustomOverflow
        * * Display Name: Mj Integration Custom Overflow
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ExternalVersion: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ExternalVersion
        * * Display Name: Mj Integration External Version
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastSeenModifiedValue: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSeenModifiedValue
        * * Display Name: Mj Integration Last Seen Modified Value
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastReconciledAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastReconciledAt
        * * Display Name: Mj Integration Last Reconciled At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastWriterDirection: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastWriterDirection
        * * Display Name: Mj Integration Last Writer Direction
        * * SQL Data Type: nvarchar(10)`),
    __mj_integration_IsTombstoned: z.boolean().describe(`
        * * Field Name: __mj_integration_IsTombstoned
        * * Display Name: Mj Integration Is Tombstoned
        * * SQL Data Type: bit
        * * Default Value: 0`),
    __mj_integration_DeletedDetectedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_DeletedDetectedAt
        * * Display Name: Mj Integration Deleted Detected At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type CoreSignalCourseEntityType = z.infer<typeof CoreSignalCourseSchema>;

/**
 * zod schema definition for the entity Cv Parses
 */
export const ResearchCvParseSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    MemberDocumentID: z.string().describe(`
        * * Field Name: MemberDocumentID
        * * Display Name: Member Document ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Member Documents (vwMemberDocuments.ID)
        * * Description: The CV document that was submitted.`),
    ProviderName: z.string().describe(`
        * * Field Name: ProviderName
        * * Display Name: Provider Name
        * * SQL Data Type: nvarchar(50)
        * * Description: Parsing provider, for example Julius.`),
    ProviderRef: z.string().nullable().describe(`
        * * Field Name: ProviderRef
        * * Display Name: Provider Ref
        * * SQL Data Type: nvarchar(200)
        * * Description: Provider-side identifier for the submitted CV.`),
    State: z.union([z.literal('done'), z.literal('failed'), z.literal('processing'), z.literal('submitting'), z.literal('unsupported_format')]).describe(`
        * * Field Name: State
        * * Display Name: State
        * * SQL Data Type: nvarchar(30)
    * * Value List Type: List
    * * Possible Values 
    *   * done
    *   * failed
    *   * processing
    *   * submitting
    *   * unsupported_format
        * * Description: submitting, processing, done, failed or unsupported_format.`),
    AttemptCount: z.number().describe(`
        * * Field Name: AttemptCount
        * * Display Name: Attempt Count
        * * SQL Data Type: int
        * * Default Value: 0
        * * Description: Number of submission attempts made.`),
    ProgressCompleted: z.number().nullable().describe(`
        * * Field Name: ProgressCompleted
        * * Display Name: Progress Completed
        * * SQL Data Type: int
        * * Description: Provider-reported units completed while processing.`),
    ProgressTotal: z.number().nullable().describe(`
        * * Field Name: ProgressTotal
        * * Display Name: Progress Total
        * * SQL Data Type: int
        * * Description: Provider-reported total units while processing.`),
    ErrorCode: z.string().nullable().describe(`
        * * Field Name: ErrorCode
        * * Display Name: Error Code
        * * SQL Data Type: nvarchar(50)
        * * Description: Short error code when State is failed; never contains CV content.`),
    SubmittedAt: z.date().nullable().describe(`
        * * Field Name: SubmittedAt
        * * Display Name: Submitted At
        * * SQL Data Type: datetimeoffset
        * * Description: When the CV was sent to the provider.`),
    FinishedAt: z.date().nullable().describe(`
        * * Field Name: FinishedAt
        * * Display Name: Finished At
        * * SQL Data Type: datetimeoffset
        * * Description: When the provider finished or the parse failed.`),
    ProviderExpiresAt: z.date().nullable().describe(`
        * * Field Name: ProviderExpiresAt
        * * Display Name: Provider Expires At
        * * SQL Data Type: datetimeoffset
        * * Description: When the provider will forget the submitted CV.`),
    ProviderPurgedAt: z.date().nullable().describe(`
        * * Field Name: ProviderPurgedAt
        * * Display Name: Provider Purged At
        * * SQL Data Type: datetimeoffset
        * * Description: When the provider-side copy was observed gone (for example a 404 after expiry).`),
    SegmentsJson: z.string().nullable().describe(`
        * * Field Name: SegmentsJson
        * * Display Name: Segments Json
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Normalized CV segments (id, text, page locations) as JSON.`),
    MatchesJson: z.string().nullable().describe(`
        * * Field Name: MatchesJson
        * * Display Name: Matches Json
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Cached provider job matches as JSON.`),
    MatchesAreSample: z.boolean().nullable().describe(`
        * * Field Name: MatchesAreSample
        * * Display Name: Matches Are Sample
        * * SQL Data Type: bit
        * * Description: True when the cached matches are a sample rather than the full result.`),
    MatchesFetchedAt: z.date().nullable().describe(`
        * * Field Name: MatchesFetchedAt
        * * Display Name: Matches Fetched At
        * * SQL Data Type: datetimeoffset
        * * Description: When MatchesJson was last fetched from the provider.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    NarrativesJson: z.string().nullable().describe(`
        * * Field Name: NarrativesJson
        * * Display Name: Narratives Json
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Career Explorer narrative sections generated for this CV parse, stored as JSON.`),
    MemberDocument: z.string().describe(`
        * * Field Name: MemberDocument
        * * Display Name: Member Document
        * * SQL Data Type: nvarchar(255)`),
});

export type ResearchCvParseEntityType = z.infer<typeof ResearchCvParseSchema>;

/**
 * zod schema definition for the entity Cv Tags
 */
export const ResearchCvTagSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    CvParseID: z.string().describe(`
        * * Field Name: CvParseID
        * * Display Name: Cv Parse ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Cv Parses (vwCvParses.ID)
        * * Description: The parse this tag belongs to.`),
    ProviderTagID: z.string().describe(`
        * * Field Name: ProviderTagID
        * * Display Name: Provider Tag ID
        * * SQL Data Type: nvarchar(50)
        * * Description: Provider tag identifier.`),
    Name: z.string().nullable().describe(`
        * * Field Name: Name
        * * Display Name: Name
        * * SQL Data Type: nvarchar(300)
        * * Description: Tag display name.`),
    Type: z.string().describe(`
        * * Field Name: Type
        * * Display Name: Type
        * * SQL Data Type: nvarchar(50)
        * * Description: Provider tag type.`),
    Subtype: z.string().describe(`
        * * Field Name: Subtype
        * * Display Name: Subtype
        * * SQL Data Type: nvarchar(100)
        * * Description: Provider tag subtype.`),
    ConfidenceLevel: z.string().describe(`
        * * Field Name: ConfidenceLevel
        * * Display Name: Confidence Level
        * * SQL Data Type: nvarchar(20)
        * * Description: Provider confidence level for the tag.`),
    SegmentRef: z.string().describe(`
        * * Field Name: SegmentRef
        * * Display Name: Segment Ref
        * * SQL Data Type: nvarchar(50)
        * * Description: Segment of the CV the tag was found in.`),
    ChainRef: z.string().describe(`
        * * Field Name: ChainRef
        * * Display Name: Chain Ref
        * * SQL Data Type: nvarchar(50)
        * * Description: Provider chain identifier grouping related tags.`),
    ChainPosition: z.number().describe(`
        * * Field Name: ChainPosition
        * * Display Name: Chain Position
        * * SQL Data Type: int
        * * Description: Position of the tag within its chain.`),
    ReviewStatus: z.union([z.literal('approved'), z.literal('declined'), z.literal('unreviewed')]).describe(`
        * * Field Name: ReviewStatus
        * * Display Name: Review Status
        * * SQL Data Type: nvarchar(20)
        * * Default Value: unreviewed
    * * Value List Type: List
    * * Possible Values 
    *   * approved
    *   * declined
    *   * unreviewed
        * * Description: Member review: unreviewed, approved or declined.`),
    ReviewedAt: z.date().nullable().describe(`
        * * Field Name: ReviewedAt
        * * Display Name: Reviewed At
        * * SQL Data Type: datetimeoffset
        * * Description: When the member last reviewed the tag.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    CvParse: z.string().describe(`
        * * Field Name: CvParse
        * * Display Name: Cv Parse
        * * SQL Data Type: nvarchar(50)`),
});

export type ResearchCvTagEntityType = z.infer<typeof ResearchCvTagSchema>;

/**
 * zod schema definition for the entity Distinctions
 */
export const ORCIDDistinctionSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ORCIDRecordID: z.string().describe(`
        * * Field Name: ORCIDRecordID
        * * Display Name: ORCID Record ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Records (vwRecords.ID)`),
    PutCode: z.number().describe(`
        * * Field Name: PutCode
        * * Display Name: Put Code
        * * SQL Data Type: bigint`),
    DepartmentName: z.string().nullable().describe(`
        * * Field Name: DepartmentName
        * * Display Name: Department Name
        * * SQL Data Type: nvarchar(500)`),
    RoleTitle: z.string().nullable().describe(`
        * * Field Name: RoleTitle
        * * Display Name: Role Title
        * * SQL Data Type: nvarchar(500)`),
    StartYear: z.number().nullable().describe(`
        * * Field Name: StartYear
        * * Display Name: Start Year
        * * SQL Data Type: smallint`),
    StartMonth: z.number().nullable().describe(`
        * * Field Name: StartMonth
        * * Display Name: Start Month
        * * SQL Data Type: tinyint`),
    StartDay: z.number().nullable().describe(`
        * * Field Name: StartDay
        * * Display Name: Start Day
        * * SQL Data Type: tinyint`),
    EndYear: z.number().nullable().describe(`
        * * Field Name: EndYear
        * * Display Name: End Year
        * * SQL Data Type: smallint`),
    EndMonth: z.number().nullable().describe(`
        * * Field Name: EndMonth
        * * Display Name: End Month
        * * SQL Data Type: tinyint`),
    EndDay: z.number().nullable().describe(`
        * * Field Name: EndDay
        * * Display Name: End Day
        * * SQL Data Type: tinyint`),
    OrgName: z.string().nullable().describe(`
        * * Field Name: OrgName
        * * Display Name: Org Name
        * * SQL Data Type: nvarchar(500)`),
    OrgCity: z.string().nullable().describe(`
        * * Field Name: OrgCity
        * * Display Name: Org City
        * * SQL Data Type: nvarchar(255)`),
    OrgRegion: z.string().nullable().describe(`
        * * Field Name: OrgRegion
        * * Display Name: Org Region
        * * SQL Data Type: nvarchar(255)`),
    OrgCountry: z.string().nullable().describe(`
        * * Field Name: OrgCountry
        * * Display Name: Org Country
        * * SQL Data Type: nvarchar(10)`),
    OrgDisambiguatedID: z.string().nullable().describe(`
        * * Field Name: OrgDisambiguatedID
        * * Display Name: Org Disambiguated ID
        * * SQL Data Type: nvarchar(255)`),
    OrgDisambiguationSource: z.string().nullable().describe(`
        * * Field Name: OrgDisambiguationSource
        * * Display Name: Org Disambiguation Source
        * * SQL Data Type: nvarchar(50)`),
    Url: z.string().nullable().describe(`
        * * Field Name: Url
        * * Display Name: Url
        * * SQL Data Type: nvarchar(2000)`),
    Visibility: z.string().nullable().describe(`
        * * Field Name: Visibility
        * * Display Name: Visibility
        * * SQL Data Type: nvarchar(20)`),
    SourceName: z.string().nullable().describe(`
        * * Field Name: SourceName
        * * Display Name: Source Name
        * * SQL Data Type: nvarchar(255)`),
    SourceOrcid: z.string().nullable().describe(`
        * * Field Name: SourceOrcid
        * * Display Name: Source Orcid
        * * SQL Data Type: nvarchar(19)`),
    OrcidCreatedDate: z.date().nullable().describe(`
        * * Field Name: OrcidCreatedDate
        * * Display Name: Orcid Created Date
        * * SQL Data Type: datetimeoffset`),
    OrcidLastModifiedDate: z.date().nullable().describe(`
        * * Field Name: OrcidLastModifiedDate
        * * Display Name: Orcid Last Modified Date
        * * SQL Data Type: datetimeoffset`),
    RawJSON: z.string().nullable().describe(`
        * * Field Name: RawJSON
        * * Display Name: Raw JSON
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncStatus: z.string().describe(`
        * * Field Name: __mj_integration_SyncStatus
        * * Display Name: Mj Integration Sync Status
        * * SQL Data Type: nvarchar(50)
        * * Default Value: Active`),
    __mj_integration_LastSyncedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedAt
        * * Display Name: Mj Integration Last Synced At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastSyncedSnapshot: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedSnapshot
        * * Display Name: Mj Integration Last Synced Snapshot
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncMessage: z.string().nullable().describe(`
        * * Field Name: __mj_integration_SyncMessage
        * * Display Name: Mj Integration Sync Message
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ContentHash: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ContentHash
        * * Display Name: Mj Integration Content Hash
        * * SQL Data Type: nvarchar(64)`),
    __mj_integration_CustomOverflow: z.string().nullable().describe(`
        * * Field Name: __mj_integration_CustomOverflow
        * * Display Name: Mj Integration Custom Overflow
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ExternalVersion: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ExternalVersion
        * * Display Name: Mj Integration External Version
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastSeenModifiedValue: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSeenModifiedValue
        * * Display Name: Mj Integration Last Seen Modified Value
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastReconciledAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastReconciledAt
        * * Display Name: Mj Integration Last Reconciled At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastWriterDirection: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastWriterDirection
        * * Display Name: Mj Integration Last Writer Direction
        * * SQL Data Type: nvarchar(10)`),
    __mj_integration_IsTombstoned: z.boolean().describe(`
        * * Field Name: __mj_integration_IsTombstoned
        * * Display Name: Mj Integration Is Tombstoned
        * * SQL Data Type: bit
        * * Default Value: 0`),
    __mj_integration_DeletedDetectedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_DeletedDetectedAt
        * * Display Name: Mj Integration Deleted Detected At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type ORCIDDistinctionEntityType = z.infer<typeof ORCIDDistinctionSchema>;

/**
 * zod schema definition for the entity Educations
 */
export const ORCIDEducationSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ORCIDRecordID: z.string().describe(`
        * * Field Name: ORCIDRecordID
        * * Display Name: ORCID Record ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Records (vwRecords.ID)`),
    PutCode: z.number().describe(`
        * * Field Name: PutCode
        * * Display Name: Put Code
        * * SQL Data Type: bigint`),
    DepartmentName: z.string().nullable().describe(`
        * * Field Name: DepartmentName
        * * Display Name: Department Name
        * * SQL Data Type: nvarchar(500)`),
    RoleTitle: z.string().nullable().describe(`
        * * Field Name: RoleTitle
        * * Display Name: Role Title
        * * SQL Data Type: nvarchar(500)`),
    StartYear: z.number().nullable().describe(`
        * * Field Name: StartYear
        * * Display Name: Start Year
        * * SQL Data Type: smallint`),
    StartMonth: z.number().nullable().describe(`
        * * Field Name: StartMonth
        * * Display Name: Start Month
        * * SQL Data Type: tinyint`),
    StartDay: z.number().nullable().describe(`
        * * Field Name: StartDay
        * * Display Name: Start Day
        * * SQL Data Type: tinyint`),
    EndYear: z.number().nullable().describe(`
        * * Field Name: EndYear
        * * Display Name: End Year
        * * SQL Data Type: smallint`),
    EndMonth: z.number().nullable().describe(`
        * * Field Name: EndMonth
        * * Display Name: End Month
        * * SQL Data Type: tinyint`),
    EndDay: z.number().nullable().describe(`
        * * Field Name: EndDay
        * * Display Name: End Day
        * * SQL Data Type: tinyint`),
    OrgName: z.string().nullable().describe(`
        * * Field Name: OrgName
        * * Display Name: Org Name
        * * SQL Data Type: nvarchar(500)`),
    OrgCity: z.string().nullable().describe(`
        * * Field Name: OrgCity
        * * Display Name: Org City
        * * SQL Data Type: nvarchar(255)`),
    OrgRegion: z.string().nullable().describe(`
        * * Field Name: OrgRegion
        * * Display Name: Org Region
        * * SQL Data Type: nvarchar(255)`),
    OrgCountry: z.string().nullable().describe(`
        * * Field Name: OrgCountry
        * * Display Name: Org Country
        * * SQL Data Type: nvarchar(10)`),
    OrgDisambiguatedID: z.string().nullable().describe(`
        * * Field Name: OrgDisambiguatedID
        * * Display Name: Org Disambiguated ID
        * * SQL Data Type: nvarchar(255)`),
    OrgDisambiguationSource: z.string().nullable().describe(`
        * * Field Name: OrgDisambiguationSource
        * * Display Name: Org Disambiguation Source
        * * SQL Data Type: nvarchar(50)`),
    Url: z.string().nullable().describe(`
        * * Field Name: Url
        * * Display Name: Url
        * * SQL Data Type: nvarchar(2000)`),
    Visibility: z.string().nullable().describe(`
        * * Field Name: Visibility
        * * Display Name: Visibility
        * * SQL Data Type: nvarchar(20)`),
    SourceName: z.string().nullable().describe(`
        * * Field Name: SourceName
        * * Display Name: Source Name
        * * SQL Data Type: nvarchar(255)`),
    SourceOrcid: z.string().nullable().describe(`
        * * Field Name: SourceOrcid
        * * Display Name: Source Orcid
        * * SQL Data Type: nvarchar(19)`),
    OrcidCreatedDate: z.date().nullable().describe(`
        * * Field Name: OrcidCreatedDate
        * * Display Name: Orcid Created Date
        * * SQL Data Type: datetimeoffset`),
    OrcidLastModifiedDate: z.date().nullable().describe(`
        * * Field Name: OrcidLastModifiedDate
        * * Display Name: Orcid Last Modified Date
        * * SQL Data Type: datetimeoffset`),
    RawJSON: z.string().nullable().describe(`
        * * Field Name: RawJSON
        * * Display Name: Raw JSON
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncStatus: z.string().describe(`
        * * Field Name: __mj_integration_SyncStatus
        * * Display Name: Mj Integration Sync Status
        * * SQL Data Type: nvarchar(50)
        * * Default Value: Active`),
    __mj_integration_LastSyncedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedAt
        * * Display Name: Mj Integration Last Synced At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastSyncedSnapshot: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedSnapshot
        * * Display Name: Mj Integration Last Synced Snapshot
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncMessage: z.string().nullable().describe(`
        * * Field Name: __mj_integration_SyncMessage
        * * Display Name: Mj Integration Sync Message
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ContentHash: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ContentHash
        * * Display Name: Mj Integration Content Hash
        * * SQL Data Type: nvarchar(64)`),
    __mj_integration_CustomOverflow: z.string().nullable().describe(`
        * * Field Name: __mj_integration_CustomOverflow
        * * Display Name: Mj Integration Custom Overflow
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ExternalVersion: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ExternalVersion
        * * Display Name: Mj Integration External Version
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastSeenModifiedValue: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSeenModifiedValue
        * * Display Name: Mj Integration Last Seen Modified Value
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastReconciledAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastReconciledAt
        * * Display Name: Mj Integration Last Reconciled At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastWriterDirection: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastWriterDirection
        * * Display Name: Mj Integration Last Writer Direction
        * * SQL Data Type: nvarchar(10)`),
    __mj_integration_IsTombstoned: z.boolean().describe(`
        * * Field Name: __mj_integration_IsTombstoned
        * * Display Name: Mj Integration Is Tombstoned
        * * SQL Data Type: bit
        * * Default Value: 0`),
    __mj_integration_DeletedDetectedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_DeletedDetectedAt
        * * Display Name: Mj Integration Deleted Detected At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type ORCIDEducationEntityType = z.infer<typeof ORCIDEducationSchema>;

/**
 * zod schema definition for the entity Educations__CoreSignal
 */
export const CoreSignalEducation__CoreSignalSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ProfileID: z.string().describe(`
        * * Field Name: ProfileID
        * * Display Name: Profile ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Profiles (vwProfiles.ID)`),
    OrderInProfile: z.number().describe(`
        * * Field Name: OrderInProfile
        * * Display Name: Order In Profile
        * * SQL Data Type: int`),
    Degree: z.string().nullable().describe(`
        * * Field Name: Degree
        * * Display Name: Degree
        * * SQL Data Type: nvarchar(1000)`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(MAX)`),
    ActivitiesAndSocieties: z.string().nullable().describe(`
        * * Field Name: ActivitiesAndSocieties
        * * Display Name: Activities And Societies
        * * SQL Data Type: nvarchar(MAX)`),
    InstitutionCoreSignalID: z.number().nullable().describe(`
        * * Field Name: InstitutionCoreSignalID
        * * Display Name: Institution Core Signal ID
        * * SQL Data Type: bigint`),
    InstitutionName: z.string().nullable().describe(`
        * * Field Name: InstitutionName
        * * Display Name: Institution Name
        * * SQL Data Type: nvarchar(500)`),
    InstitutionUrl: z.string().nullable().describe(`
        * * Field Name: InstitutionUrl
        * * Display Name: Institution Url
        * * SQL Data Type: nvarchar(1000)`),
    InstitutionWebsite: z.string().nullable().describe(`
        * * Field Name: InstitutionWebsite
        * * Display Name: Institution Website
        * * SQL Data Type: nvarchar(1000)`),
    InstitutionCity: z.string().nullable().describe(`
        * * Field Name: InstitutionCity
        * * Display Name: Institution City
        * * SQL Data Type: nvarchar(255)`),
    InstitutionState: z.string().nullable().describe(`
        * * Field Name: InstitutionState
        * * Display Name: Institution State
        * * SQL Data Type: nvarchar(255)`),
    InstitutionCountryISO2: z.string().nullable().describe(`
        * * Field Name: InstitutionCountryISO2
        * * Display Name: Institution Country ISO2
        * * SQL Data Type: nvarchar(10)`),
    DateFromYear: z.number().nullable().describe(`
        * * Field Name: DateFromYear
        * * Display Name: Date From Year
        * * SQL Data Type: smallint`),
    DateToYear: z.number().nullable().describe(`
        * * Field Name: DateToYear
        * * Display Name: Date To Year
        * * SQL Data Type: smallint`),
    __mj_integration_SyncStatus: z.string().describe(`
        * * Field Name: __mj_integration_SyncStatus
        * * Display Name: Mj Integration Sync Status
        * * SQL Data Type: nvarchar(50)
        * * Default Value: Active`),
    __mj_integration_LastSyncedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedAt
        * * Display Name: Mj Integration Last Synced At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastSyncedSnapshot: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedSnapshot
        * * Display Name: Mj Integration Last Synced Snapshot
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncMessage: z.string().nullable().describe(`
        * * Field Name: __mj_integration_SyncMessage
        * * Display Name: Mj Integration Sync Message
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ContentHash: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ContentHash
        * * Display Name: Mj Integration Content Hash
        * * SQL Data Type: nvarchar(64)`),
    __mj_integration_CustomOverflow: z.string().nullable().describe(`
        * * Field Name: __mj_integration_CustomOverflow
        * * Display Name: Mj Integration Custom Overflow
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ExternalVersion: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ExternalVersion
        * * Display Name: Mj Integration External Version
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastSeenModifiedValue: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSeenModifiedValue
        * * Display Name: Mj Integration Last Seen Modified Value
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastReconciledAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastReconciledAt
        * * Display Name: Mj Integration Last Reconciled At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastWriterDirection: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastWriterDirection
        * * Display Name: Mj Integration Last Writer Direction
        * * SQL Data Type: nvarchar(10)`),
    __mj_integration_IsTombstoned: z.boolean().describe(`
        * * Field Name: __mj_integration_IsTombstoned
        * * Display Name: Mj Integration Is Tombstoned
        * * SQL Data Type: bit
        * * Default Value: 0`),
    __mj_integration_DeletedDetectedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_DeletedDetectedAt
        * * Display Name: Mj Integration Deleted Detected At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type CoreSignalEducation__CoreSignalEntityType = z.infer<typeof CoreSignalEducation__CoreSignalSchema>;

/**
 * zod schema definition for the entity Emails
 */
export const ORCIDEmailSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ORCIDRecordID: z.string().describe(`
        * * Field Name: ORCIDRecordID
        * * Display Name: ORCID Record ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Records (vwRecords.ID)`),
    PutCode: z.number().nullable().describe(`
        * * Field Name: PutCode
        * * Display Name: Put Code
        * * SQL Data Type: bigint`),
    Email: z.string().nullable().describe(`
        * * Field Name: Email
        * * Display Name: Email
        * * SQL Data Type: nvarchar(255)`),
    Verified: z.boolean().nullable().describe(`
        * * Field Name: Verified
        * * Display Name: Verified
        * * SQL Data Type: bit`),
    IsPrimary: z.boolean().nullable().describe(`
        * * Field Name: IsPrimary
        * * Display Name: Is Primary
        * * SQL Data Type: bit`),
    DisplayIndex: z.number().nullable().describe(`
        * * Field Name: DisplayIndex
        * * Display Name: Display Index
        * * SQL Data Type: int`),
    Visibility: z.string().nullable().describe(`
        * * Field Name: Visibility
        * * Display Name: Visibility
        * * SQL Data Type: nvarchar(20)`),
    SourceName: z.string().nullable().describe(`
        * * Field Name: SourceName
        * * Display Name: Source Name
        * * SQL Data Type: nvarchar(255)`),
    SourceOrcid: z.string().nullable().describe(`
        * * Field Name: SourceOrcid
        * * Display Name: Source Orcid
        * * SQL Data Type: nvarchar(19)`),
    OrcidCreatedDate: z.date().nullable().describe(`
        * * Field Name: OrcidCreatedDate
        * * Display Name: Orcid Created Date
        * * SQL Data Type: datetimeoffset`),
    OrcidLastModifiedDate: z.date().nullable().describe(`
        * * Field Name: OrcidLastModifiedDate
        * * Display Name: Orcid Last Modified Date
        * * SQL Data Type: datetimeoffset`),
    RawJSON: z.string().nullable().describe(`
        * * Field Name: RawJSON
        * * Display Name: Raw JSON
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncStatus: z.string().describe(`
        * * Field Name: __mj_integration_SyncStatus
        * * Display Name: Mj Integration Sync Status
        * * SQL Data Type: nvarchar(50)
        * * Default Value: Active`),
    __mj_integration_LastSyncedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedAt
        * * Display Name: Mj Integration Last Synced At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastSyncedSnapshot: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedSnapshot
        * * Display Name: Mj Integration Last Synced Snapshot
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncMessage: z.string().nullable().describe(`
        * * Field Name: __mj_integration_SyncMessage
        * * Display Name: Mj Integration Sync Message
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ContentHash: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ContentHash
        * * Display Name: Mj Integration Content Hash
        * * SQL Data Type: nvarchar(64)`),
    __mj_integration_CustomOverflow: z.string().nullable().describe(`
        * * Field Name: __mj_integration_CustomOverflow
        * * Display Name: Mj Integration Custom Overflow
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ExternalVersion: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ExternalVersion
        * * Display Name: Mj Integration External Version
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastSeenModifiedValue: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSeenModifiedValue
        * * Display Name: Mj Integration Last Seen Modified Value
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastReconciledAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastReconciledAt
        * * Display Name: Mj Integration Last Reconciled At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastWriterDirection: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastWriterDirection
        * * Display Name: Mj Integration Last Writer Direction
        * * SQL Data Type: nvarchar(10)`),
    __mj_integration_IsTombstoned: z.boolean().describe(`
        * * Field Name: __mj_integration_IsTombstoned
        * * Display Name: Mj Integration Is Tombstoned
        * * SQL Data Type: bit
        * * Default Value: 0`),
    __mj_integration_DeletedDetectedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_DeletedDetectedAt
        * * Display Name: Mj Integration Deleted Detected At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type ORCIDEmailEntityType = z.infer<typeof ORCIDEmailSchema>;

/**
 * zod schema definition for the entity Employments
 */
export const ORCIDEmploymentSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ORCIDRecordID: z.string().describe(`
        * * Field Name: ORCIDRecordID
        * * Display Name: ORCID Record ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Records (vwRecords.ID)`),
    PutCode: z.number().describe(`
        * * Field Name: PutCode
        * * Display Name: Put Code
        * * SQL Data Type: bigint`),
    DepartmentName: z.string().nullable().describe(`
        * * Field Name: DepartmentName
        * * Display Name: Department Name
        * * SQL Data Type: nvarchar(500)`),
    RoleTitle: z.string().nullable().describe(`
        * * Field Name: RoleTitle
        * * Display Name: Role Title
        * * SQL Data Type: nvarchar(500)`),
    StartYear: z.number().nullable().describe(`
        * * Field Name: StartYear
        * * Display Name: Start Year
        * * SQL Data Type: smallint`),
    StartMonth: z.number().nullable().describe(`
        * * Field Name: StartMonth
        * * Display Name: Start Month
        * * SQL Data Type: tinyint`),
    StartDay: z.number().nullable().describe(`
        * * Field Name: StartDay
        * * Display Name: Start Day
        * * SQL Data Type: tinyint`),
    EndYear: z.number().nullable().describe(`
        * * Field Name: EndYear
        * * Display Name: End Year
        * * SQL Data Type: smallint`),
    EndMonth: z.number().nullable().describe(`
        * * Field Name: EndMonth
        * * Display Name: End Month
        * * SQL Data Type: tinyint`),
    EndDay: z.number().nullable().describe(`
        * * Field Name: EndDay
        * * Display Name: End Day
        * * SQL Data Type: tinyint`),
    OrgName: z.string().nullable().describe(`
        * * Field Name: OrgName
        * * Display Name: Org Name
        * * SQL Data Type: nvarchar(500)`),
    OrgCity: z.string().nullable().describe(`
        * * Field Name: OrgCity
        * * Display Name: Org City
        * * SQL Data Type: nvarchar(255)`),
    OrgRegion: z.string().nullable().describe(`
        * * Field Name: OrgRegion
        * * Display Name: Org Region
        * * SQL Data Type: nvarchar(255)`),
    OrgCountry: z.string().nullable().describe(`
        * * Field Name: OrgCountry
        * * Display Name: Org Country
        * * SQL Data Type: nvarchar(10)`),
    OrgDisambiguatedID: z.string().nullable().describe(`
        * * Field Name: OrgDisambiguatedID
        * * Display Name: Org Disambiguated ID
        * * SQL Data Type: nvarchar(255)`),
    OrgDisambiguationSource: z.string().nullable().describe(`
        * * Field Name: OrgDisambiguationSource
        * * Display Name: Org Disambiguation Source
        * * SQL Data Type: nvarchar(50)`),
    Url: z.string().nullable().describe(`
        * * Field Name: Url
        * * Display Name: Url
        * * SQL Data Type: nvarchar(2000)`),
    Visibility: z.string().nullable().describe(`
        * * Field Name: Visibility
        * * Display Name: Visibility
        * * SQL Data Type: nvarchar(20)`),
    SourceName: z.string().nullable().describe(`
        * * Field Name: SourceName
        * * Display Name: Source Name
        * * SQL Data Type: nvarchar(255)`),
    SourceOrcid: z.string().nullable().describe(`
        * * Field Name: SourceOrcid
        * * Display Name: Source Orcid
        * * SQL Data Type: nvarchar(19)`),
    OrcidCreatedDate: z.date().nullable().describe(`
        * * Field Name: OrcidCreatedDate
        * * Display Name: Orcid Created Date
        * * SQL Data Type: datetimeoffset`),
    OrcidLastModifiedDate: z.date().nullable().describe(`
        * * Field Name: OrcidLastModifiedDate
        * * Display Name: Orcid Last Modified Date
        * * SQL Data Type: datetimeoffset`),
    RawJSON: z.string().nullable().describe(`
        * * Field Name: RawJSON
        * * Display Name: Raw JSON
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncStatus: z.string().describe(`
        * * Field Name: __mj_integration_SyncStatus
        * * Display Name: Mj Integration Sync Status
        * * SQL Data Type: nvarchar(50)
        * * Default Value: Active`),
    __mj_integration_LastSyncedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedAt
        * * Display Name: Mj Integration Last Synced At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastSyncedSnapshot: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedSnapshot
        * * Display Name: Mj Integration Last Synced Snapshot
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncMessage: z.string().nullable().describe(`
        * * Field Name: __mj_integration_SyncMessage
        * * Display Name: Mj Integration Sync Message
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ContentHash: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ContentHash
        * * Display Name: Mj Integration Content Hash
        * * SQL Data Type: nvarchar(64)`),
    __mj_integration_CustomOverflow: z.string().nullable().describe(`
        * * Field Name: __mj_integration_CustomOverflow
        * * Display Name: Mj Integration Custom Overflow
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ExternalVersion: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ExternalVersion
        * * Display Name: Mj Integration External Version
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastSeenModifiedValue: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSeenModifiedValue
        * * Display Name: Mj Integration Last Seen Modified Value
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastReconciledAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastReconciledAt
        * * Display Name: Mj Integration Last Reconciled At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastWriterDirection: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastWriterDirection
        * * Display Name: Mj Integration Last Writer Direction
        * * SQL Data Type: nvarchar(10)`),
    __mj_integration_IsTombstoned: z.boolean().describe(`
        * * Field Name: __mj_integration_IsTombstoned
        * * Display Name: Mj Integration Is Tombstoned
        * * SQL Data Type: bit
        * * Default Value: 0`),
    __mj_integration_DeletedDetectedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_DeletedDetectedAt
        * * Display Name: Mj Integration Deleted Detected At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type ORCIDEmploymentEntityType = z.infer<typeof ORCIDEmploymentSchema>;

/**
 * zod schema definition for the entity Experiences
 */
export const CoreSignalExperienceSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ProfileID: z.string().describe(`
        * * Field Name: ProfileID
        * * Display Name: Profile ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Profiles (vwProfiles.ID)`),
    OrderInProfile: z.number().describe(`
        * * Field Name: OrderInProfile
        * * Display Name: Order In Profile
        * * SQL Data Type: int
        * * Description: Position within the feed's experience array; with ProfileID it is the sync natural key (CoreSignal items carry no source-assigned id). Order shifts between deliveries are absorbed by content-hash + tombstoning.`),
    PositionTitle: z.string().nullable().describe(`
        * * Field Name: PositionTitle
        * * Display Name: Position Title
        * * SQL Data Type: nvarchar(500)`),
    Department: z.string().nullable().describe(`
        * * Field Name: Department
        * * Display Name: Department
        * * SQL Data Type: nvarchar(255)`),
    ManagementLevel: z.string().nullable().describe(`
        * * Field Name: ManagementLevel
        * * Display Name: Management Level
        * * SQL Data Type: nvarchar(255)`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(MAX)`),
    Location: z.string().nullable().describe(`
        * * Field Name: Location
        * * Display Name: Location
        * * SQL Data Type: nvarchar(500)`),
    DateFromYear: z.number().nullable().describe(`
        * * Field Name: DateFromYear
        * * Display Name: Date From Year
        * * SQL Data Type: smallint`),
    DateFromMonth: z.number().nullable().describe(`
        * * Field Name: DateFromMonth
        * * Display Name: Date From Month
        * * SQL Data Type: tinyint`),
    DateToYear: z.number().nullable().describe(`
        * * Field Name: DateToYear
        * * Display Name: Date To Year
        * * SQL Data Type: smallint`),
    DateToMonth: z.number().nullable().describe(`
        * * Field Name: DateToMonth
        * * Display Name: Date To Month
        * * SQL Data Type: tinyint`),
    DurationMonths: z.number().nullable().describe(`
        * * Field Name: DurationMonths
        * * Display Name: Duration Months
        * * SQL Data Type: int`),
    IsActive: z.boolean().describe(`
        * * Field Name: IsActive
        * * Display Name: Is Active
        * * SQL Data Type: bit
        * * Default Value: 0
        * * Description: Feed active_experience: the profile claims this position is current. Qualify with StaleCurrentSuspect + Profile.SourceCheckedAt before presenting as "Current".`),
    StaleCurrentSuspect: z.boolean().describe(`
        * * Field Name: StaleCurrentSuspect
        * * Display Name: Stale Current Suspect
        * * SQL Data Type: bit
        * * Default Value: 0
        * * Description: 1 = IsActive but the matched person's ORCID employment at a matching org ended 3+ years ago — the abandoned-LinkedIn-profile signature (measured on 14% of shared spells). Set by the "Run CoreSignal Ingest" action's stale-flag pass; consumers must caveat these.`),
    CompanyCoreSignalID: z.number().nullable().describe(`
        * * Field Name: CompanyCoreSignalID
        * * Display Name: Company Core Signal ID
        * * SQL Data Type: bigint`),
    CompanyName: z.string().nullable().describe(`
        * * Field Name: CompanyName
        * * Display Name: Company Name
        * * SQL Data Type: nvarchar(500)`),
    CompanyType: z.string().nullable().describe(`
        * * Field Name: CompanyType
        * * Display Name: Company Type
        * * SQL Data Type: nvarchar(255)`),
    CompanyIndustry: z.string().nullable().describe(`
        * * Field Name: CompanyIndustry
        * * Display Name: Company Industry
        * * SQL Data Type: nvarchar(255)`),
    CompanySizeRange: z.string().nullable().describe(`
        * * Field Name: CompanySizeRange
        * * Display Name: Company Size Range
        * * SQL Data Type: nvarchar(50)`),
    CompanyEmployeesCount: z.number().nullable().describe(`
        * * Field Name: CompanyEmployeesCount
        * * Display Name: Company Employees Count
        * * SQL Data Type: int`),
    CompanyWebsite: z.string().nullable().describe(`
        * * Field Name: CompanyWebsite
        * * Display Name: Company Website
        * * SQL Data Type: nvarchar(1000)`),
    CompanyLinkedInUrl: z.string().nullable().describe(`
        * * Field Name: CompanyLinkedInUrl
        * * Display Name: Company Linked In Url
        * * SQL Data Type: nvarchar(1000)`),
    CompanyHQCity: z.string().nullable().describe(`
        * * Field Name: CompanyHQCity
        * * Display Name: Company HQ City
        * * SQL Data Type: nvarchar(255)`),
    CompanyHQState: z.string().nullable().describe(`
        * * Field Name: CompanyHQState
        * * Display Name: Company HQ State
        * * SQL Data Type: nvarchar(255)`),
    CompanyHQCountry: z.string().nullable().describe(`
        * * Field Name: CompanyHQCountry
        * * Display Name: Company HQ Country
        * * SQL Data Type: nvarchar(255)`),
    CompanyHQCountryISO2: z.string().nullable().describe(`
        * * Field Name: CompanyHQCountryISO2
        * * Display Name: Company HQ Country ISO2
        * * SQL Data Type: nvarchar(10)`),
    __mj_integration_SyncStatus: z.string().describe(`
        * * Field Name: __mj_integration_SyncStatus
        * * Display Name: Mj Integration Sync Status
        * * SQL Data Type: nvarchar(50)
        * * Default Value: Active`),
    __mj_integration_LastSyncedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedAt
        * * Display Name: Mj Integration Last Synced At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastSyncedSnapshot: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedSnapshot
        * * Display Name: Mj Integration Last Synced Snapshot
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncMessage: z.string().nullable().describe(`
        * * Field Name: __mj_integration_SyncMessage
        * * Display Name: Mj Integration Sync Message
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ContentHash: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ContentHash
        * * Display Name: Mj Integration Content Hash
        * * SQL Data Type: nvarchar(64)`),
    __mj_integration_CustomOverflow: z.string().nullable().describe(`
        * * Field Name: __mj_integration_CustomOverflow
        * * Display Name: Mj Integration Custom Overflow
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ExternalVersion: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ExternalVersion
        * * Display Name: Mj Integration External Version
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastSeenModifiedValue: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSeenModifiedValue
        * * Display Name: Mj Integration Last Seen Modified Value
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastReconciledAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastReconciledAt
        * * Display Name: Mj Integration Last Reconciled At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastWriterDirection: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastWriterDirection
        * * Display Name: Mj Integration Last Writer Direction
        * * SQL Data Type: nvarchar(10)`),
    __mj_integration_IsTombstoned: z.boolean().describe(`
        * * Field Name: __mj_integration_IsTombstoned
        * * Display Name: Mj Integration Is Tombstoned
        * * SQL Data Type: bit
        * * Default Value: 0`),
    __mj_integration_DeletedDetectedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_DeletedDetectedAt
        * * Display Name: Mj Integration Deleted Detected At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type CoreSignalExperienceEntityType = z.infer<typeof CoreSignalExperienceSchema>;

/**
 * zod schema definition for the entity Export Logs
 */
export const MAPSExportLogSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    PartnerTokenID: z.string().nullable().describe(`
        * * Field Name: PartnerTokenID
        * * Display Name: Partner Token ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Partner Tokens (vwPartnerTokens.ID)
        * * Description: Legacy owning partner token. NULL for company-resolved sessions; dual-written until PR B. Ownership scoping uses CompanyID.`),
    SearchLogID: z.string().nullable().describe(`
        * * Field Name: SearchLogID
        * * Display Name: Search Log ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Search Logs (vwSearchLogs.ID)
        * * Description: Search this export was sourced from. Nullable to permit out-of-band exports later.`),
    Format: z.union([z.literal('csv'), z.literal('xlsx')]).describe(`
        * * Field Name: Format
        * * Display Name: Format
        * * SQL Data Type: nvarchar(8)
    * * Value List Type: List
    * * Possible Values 
    *   * csv
    *   * xlsx
        * * Description: Export file format: xlsx or csv.`),
    SelectedCount: z.number().describe(`
        * * Field Name: SelectedCount
        * * Display Name: Selected Count
        * * SQL Data Type: int
        * * Description: Number of candidates included in this export. Server enforces a 30-row cap.`),
    IncludesSummaries: z.boolean().describe(`
        * * Field Name: IncludesSummaries
        * * Display Name: Includes Summaries
        * * SQL Data Type: bit
        * * Description: 1 if the export includes the generated Why Suggested / Things to Look Into text; 0 otherwise.`),
    Timestamp: z.date().describe(`
        * * Field Name: Timestamp
        * * Display Name: Timestamp
        * * SQL Data Type: datetimeoffset
        * * Default Value: sysdatetimeoffset()`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
        * * Description: Owning company. Authoritative attribution/scoping key for the partner-account model; NOT NULL is enforced at the app layer in PR A and at the DB in PR B.`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type MAPSExportLogEntityType = z.infer<typeof MAPSExportLogSchema>;

/**
 * zod schema definition for the entity External Identifiers
 */
export const ORCIDExternalIdentifierSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ORCIDRecordID: z.string().describe(`
        * * Field Name: ORCIDRecordID
        * * Display Name: ORCID Record ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Records (vwRecords.ID)`),
    PutCode: z.number().describe(`
        * * Field Name: PutCode
        * * Display Name: Put Code
        * * SQL Data Type: bigint`),
    ExternalIDType: z.string().nullable().describe(`
        * * Field Name: ExternalIDType
        * * Display Name: External ID Type
        * * SQL Data Type: nvarchar(255)`),
    ExternalIDValue: z.string().nullable().describe(`
        * * Field Name: ExternalIDValue
        * * Display Name: External ID Value
        * * SQL Data Type: nvarchar(500)`),
    ExternalIDUrl: z.string().nullable().describe(`
        * * Field Name: ExternalIDUrl
        * * Display Name: External ID Url
        * * SQL Data Type: nvarchar(2000)`),
    ExternalIDRelationship: z.string().nullable().describe(`
        * * Field Name: ExternalIDRelationship
        * * Display Name: External ID Relationship
        * * SQL Data Type: nvarchar(20)`),
    DisplayIndex: z.number().nullable().describe(`
        * * Field Name: DisplayIndex
        * * Display Name: Display Index
        * * SQL Data Type: int`),
    Visibility: z.string().nullable().describe(`
        * * Field Name: Visibility
        * * Display Name: Visibility
        * * SQL Data Type: nvarchar(20)`),
    SourceName: z.string().nullable().describe(`
        * * Field Name: SourceName
        * * Display Name: Source Name
        * * SQL Data Type: nvarchar(255)`),
    SourceOrcid: z.string().nullable().describe(`
        * * Field Name: SourceOrcid
        * * Display Name: Source Orcid
        * * SQL Data Type: nvarchar(19)`),
    OrcidCreatedDate: z.date().nullable().describe(`
        * * Field Name: OrcidCreatedDate
        * * Display Name: Orcid Created Date
        * * SQL Data Type: datetimeoffset`),
    OrcidLastModifiedDate: z.date().nullable().describe(`
        * * Field Name: OrcidLastModifiedDate
        * * Display Name: Orcid Last Modified Date
        * * SQL Data Type: datetimeoffset`),
    RawJSON: z.string().nullable().describe(`
        * * Field Name: RawJSON
        * * Display Name: Raw JSON
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncStatus: z.string().describe(`
        * * Field Name: __mj_integration_SyncStatus
        * * Display Name: Mj Integration Sync Status
        * * SQL Data Type: nvarchar(50)
        * * Default Value: Active`),
    __mj_integration_LastSyncedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedAt
        * * Display Name: Mj Integration Last Synced At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastSyncedSnapshot: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedSnapshot
        * * Display Name: Mj Integration Last Synced Snapshot
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncMessage: z.string().nullable().describe(`
        * * Field Name: __mj_integration_SyncMessage
        * * Display Name: Mj Integration Sync Message
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ContentHash: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ContentHash
        * * Display Name: Mj Integration Content Hash
        * * SQL Data Type: nvarchar(64)`),
    __mj_integration_CustomOverflow: z.string().nullable().describe(`
        * * Field Name: __mj_integration_CustomOverflow
        * * Display Name: Mj Integration Custom Overflow
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ExternalVersion: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ExternalVersion
        * * Display Name: Mj Integration External Version
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastSeenModifiedValue: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSeenModifiedValue
        * * Display Name: Mj Integration Last Seen Modified Value
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastReconciledAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastReconciledAt
        * * Display Name: Mj Integration Last Reconciled At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastWriterDirection: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastWriterDirection
        * * Display Name: Mj Integration Last Writer Direction
        * * SQL Data Type: nvarchar(10)`),
    __mj_integration_IsTombstoned: z.boolean().describe(`
        * * Field Name: __mj_integration_IsTombstoned
        * * Display Name: Mj Integration Is Tombstoned
        * * SQL Data Type: bit
        * * Default Value: 0`),
    __mj_integration_DeletedDetectedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_DeletedDetectedAt
        * * Display Name: Mj Integration Deleted Detected At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type ORCIDExternalIdentifierEntityType = z.infer<typeof ORCIDExternalIdentifierSchema>;

/**
 * zod schema definition for the entity Flyway _schema _histories
 */
export const dboflyway_schema_historySchema = z.object({
    installed_rank: z.number().describe(`
        * * Field Name: installed_rank
        * * Display Name: installed _rank
        * * SQL Data Type: int`),
    version: z.string().nullable().describe(`
        * * Field Name: version
        * * Display Name: version
        * * SQL Data Type: nvarchar(50)`),
    description: z.string().nullable().describe(`
        * * Field Name: description
        * * Display Name: description
        * * SQL Data Type: nvarchar(200)`),
    type: z.string().describe(`
        * * Field Name: type
        * * Display Name: type
        * * SQL Data Type: nvarchar(20)`),
    script: z.string().describe(`
        * * Field Name: script
        * * Display Name: script
        * * SQL Data Type: nvarchar(1000)`),
    checksum: z.number().nullable().describe(`
        * * Field Name: checksum
        * * Display Name: checksum
        * * SQL Data Type: int`),
    installed_by: z.string().describe(`
        * * Field Name: installed_by
        * * Display Name: installed _by
        * * SQL Data Type: nvarchar(100)`),
    installed_on: z.date().describe(`
        * * Field Name: installed_on
        * * Display Name: installed _on
        * * SQL Data Type: datetime
        * * Default Value: getdate()`),
    execution_time: z.number().describe(`
        * * Field Name: execution_time
        * * Display Name: execution _time
        * * SQL Data Type: int`),
    success: z.boolean().describe(`
        * * Field Name: success
        * * Display Name: success
        * * SQL Data Type: bit`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type dboflyway_schema_historyEntityType = z.infer<typeof dboflyway_schema_historySchema>;

/**
 * zod schema definition for the entity Funding External IDs
 */
export const ORCIDFundingExternalIDSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    FundingID: z.string().describe(`
        * * Field Name: FundingID
        * * Display Name: Funding ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Fundings (vwFundings.ID)`),
    ExternalIDType: z.string().nullable().describe(`
        * * Field Name: ExternalIDType
        * * Display Name: External ID Type
        * * SQL Data Type: nvarchar(100)`),
    ExternalIDValue: z.string().nullable().describe(`
        * * Field Name: ExternalIDValue
        * * Display Name: External ID Value
        * * SQL Data Type: nvarchar(500)`),
    ExternalIDUrl: z.string().nullable().describe(`
        * * Field Name: ExternalIDUrl
        * * Display Name: External ID Url
        * * SQL Data Type: nvarchar(2000)`),
    ExternalIDRelationship: z.string().nullable().describe(`
        * * Field Name: ExternalIDRelationship
        * * Display Name: External ID Relationship
        * * SQL Data Type: nvarchar(20)`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type ORCIDFundingExternalIDEntityType = z.infer<typeof ORCIDFundingExternalIDSchema>;

/**
 * zod schema definition for the entity Fundings
 */
export const ORCIDFundingSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ORCIDRecordID: z.string().describe(`
        * * Field Name: ORCIDRecordID
        * * Display Name: ORCID Record ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Records (vwRecords.ID)`),
    PutCode: z.number().describe(`
        * * Field Name: PutCode
        * * Display Name: Put Code
        * * SQL Data Type: bigint`),
    Title: z.string().nullable().describe(`
        * * Field Name: Title
        * * Display Name: Title
        * * SQL Data Type: nvarchar(2000)`),
    TranslatedTitle: z.string().nullable().describe(`
        * * Field Name: TranslatedTitle
        * * Display Name: Translated Title
        * * SQL Data Type: nvarchar(2000)`),
    FundingType: z.string().nullable().describe(`
        * * Field Name: FundingType
        * * Display Name: Funding Type
        * * SQL Data Type: nvarchar(100)`),
    OrganizationDefinedType: z.string().nullable().describe(`
        * * Field Name: OrganizationDefinedType
        * * Display Name: Organization Defined Type
        * * SQL Data Type: nvarchar(500)`),
    AmountValue: z.string().nullable().describe(`
        * * Field Name: AmountValue
        * * Display Name: Amount Value
        * * SQL Data Type: nvarchar(100)`),
    AmountCurrency: z.string().nullable().describe(`
        * * Field Name: AmountCurrency
        * * Display Name: Amount Currency
        * * SQL Data Type: nvarchar(10)`),
    StartYear: z.number().nullable().describe(`
        * * Field Name: StartYear
        * * Display Name: Start Year
        * * SQL Data Type: smallint`),
    StartMonth: z.number().nullable().describe(`
        * * Field Name: StartMonth
        * * Display Name: Start Month
        * * SQL Data Type: tinyint`),
    StartDay: z.number().nullable().describe(`
        * * Field Name: StartDay
        * * Display Name: Start Day
        * * SQL Data Type: tinyint`),
    EndYear: z.number().nullable().describe(`
        * * Field Name: EndYear
        * * Display Name: End Year
        * * SQL Data Type: smallint`),
    EndMonth: z.number().nullable().describe(`
        * * Field Name: EndMonth
        * * Display Name: End Month
        * * SQL Data Type: tinyint`),
    EndDay: z.number().nullable().describe(`
        * * Field Name: EndDay
        * * Display Name: End Day
        * * SQL Data Type: tinyint`),
    OrgName: z.string().nullable().describe(`
        * * Field Name: OrgName
        * * Display Name: Org Name
        * * SQL Data Type: nvarchar(500)`),
    OrgCity: z.string().nullable().describe(`
        * * Field Name: OrgCity
        * * Display Name: Org City
        * * SQL Data Type: nvarchar(255)`),
    OrgRegion: z.string().nullable().describe(`
        * * Field Name: OrgRegion
        * * Display Name: Org Region
        * * SQL Data Type: nvarchar(255)`),
    OrgCountry: z.string().nullable().describe(`
        * * Field Name: OrgCountry
        * * Display Name: Org Country
        * * SQL Data Type: nvarchar(10)`),
    OrgDisambiguatedID: z.string().nullable().describe(`
        * * Field Name: OrgDisambiguatedID
        * * Display Name: Org Disambiguated ID
        * * SQL Data Type: nvarchar(255)`),
    OrgDisambiguationSource: z.string().nullable().describe(`
        * * Field Name: OrgDisambiguationSource
        * * Display Name: Org Disambiguation Source
        * * SQL Data Type: nvarchar(50)`),
    Url: z.string().nullable().describe(`
        * * Field Name: Url
        * * Display Name: Url
        * * SQL Data Type: nvarchar(2000)`),
    Visibility: z.string().nullable().describe(`
        * * Field Name: Visibility
        * * Display Name: Visibility
        * * SQL Data Type: nvarchar(20)`),
    SourceName: z.string().nullable().describe(`
        * * Field Name: SourceName
        * * Display Name: Source Name
        * * SQL Data Type: nvarchar(255)`),
    SourceOrcid: z.string().nullable().describe(`
        * * Field Name: SourceOrcid
        * * Display Name: Source Orcid
        * * SQL Data Type: nvarchar(19)`),
    OrcidCreatedDate: z.date().nullable().describe(`
        * * Field Name: OrcidCreatedDate
        * * Display Name: Orcid Created Date
        * * SQL Data Type: datetimeoffset`),
    OrcidLastModifiedDate: z.date().nullable().describe(`
        * * Field Name: OrcidLastModifiedDate
        * * Display Name: Orcid Last Modified Date
        * * SQL Data Type: datetimeoffset`),
    RawJSON: z.string().nullable().describe(`
        * * Field Name: RawJSON
        * * Display Name: Raw JSON
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncStatus: z.string().describe(`
        * * Field Name: __mj_integration_SyncStatus
        * * Display Name: Mj Integration Sync Status
        * * SQL Data Type: nvarchar(50)
        * * Default Value: Active`),
    __mj_integration_LastSyncedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedAt
        * * Display Name: Mj Integration Last Synced At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastSyncedSnapshot: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedSnapshot
        * * Display Name: Mj Integration Last Synced Snapshot
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncMessage: z.string().nullable().describe(`
        * * Field Name: __mj_integration_SyncMessage
        * * Display Name: Mj Integration Sync Message
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ContentHash: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ContentHash
        * * Display Name: Mj Integration Content Hash
        * * SQL Data Type: nvarchar(64)`),
    __mj_integration_CustomOverflow: z.string().nullable().describe(`
        * * Field Name: __mj_integration_CustomOverflow
        * * Display Name: Mj Integration Custom Overflow
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ExternalVersion: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ExternalVersion
        * * Display Name: Mj Integration External Version
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastSeenModifiedValue: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSeenModifiedValue
        * * Display Name: Mj Integration Last Seen Modified Value
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastReconciledAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastReconciledAt
        * * Display Name: Mj Integration Last Reconciled At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastWriterDirection: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastWriterDirection
        * * Display Name: Mj Integration Last Writer Direction
        * * SQL Data Type: nvarchar(10)`),
    __mj_integration_IsTombstoned: z.boolean().describe(`
        * * Field Name: __mj_integration_IsTombstoned
        * * Display Name: Mj Integration Is Tombstoned
        * * SQL Data Type: bit
        * * Default Value: 0`),
    __mj_integration_DeletedDetectedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_DeletedDetectedAt
        * * Display Name: Mj Integration Deleted Detected At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    OwnerRole: z.string().nullable().describe(`
        * * Field Name: OwnerRole
        * * Display Name: Owner Role
        * * SQL Data Type: nvarchar(50)
        * * Description: The record owner's contributor-role on this grant from the funding detail endpoint (lead=PI, co-lead, supported-by, other-contribution); NULL when ORCID exposes no role. The "PI experience" signal.`),
    ShortDescription: z.string().nullable().describe(`
        * * Field Name: ShortDescription
        * * Display Name: Short Description
        * * SQL Data Type: nvarchar(MAX)
        * * Description: The grant abstract (short-description) from the funding detail endpoint; NULL when absent.`),
    DetailFetchedAt: z.date().nullable().describe(`
        * * Field Name: DetailFetchedAt
        * * Display Name: Detail Fetched At
        * * SQL Data Type: datetimeoffset
        * * Description: Ledger: when the funding detail endpoint was last successfully fetched for this row. Drives the enrichment worklist (re-fetch when NULL, on error, or when OrcidLastModifiedDate is newer).`),
    DetailFetchStatus: z.string().nullable().describe(`
        * * Field Name: DetailFetchStatus
        * * Display Name: Detail Fetch Status
        * * SQL Data Type: nvarchar(20)
        * * Description: Ledger: outcome of the last detail fetch — ok | notfound (404, deleted put-code) | forbidden (403, private funding) | error (transient, retried).`),
});

export type ORCIDFundingEntityType = z.infer<typeof ORCIDFundingSchema>;

/**
 * zod schema definition for the entity Ingest Batches
 */
export const JobsIngestBatchSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    IngestBatchID: z.number().describe(`
        * * Field Name: IngestBatchID
        * * Display Name: Ingest Batch ID
        * * SQL Data Type: int
        * * Description: Compact INT identity used as the reference target for Jobs.JobPosting.IngestBatchID (the GUID ID exists for MJ/CodeGen conformance).`),
    DumpType: z.union([z.literal('daily'), z.literal('initial'), z.literal('reconcile')]).describe(`
        * * Field Name: DumpType
        * * Display Name: Dump Type
        * * SQL Data Type: nvarchar(10)
    * * Value List Type: List
    * * Possible Values 
    *   * daily
    *   * initial
    *   * reconcile
        * * Description: daily = one feed dump date; initial = the one-time seed/backfill; reconcile = the weekly SQL↔Pinecone drift sweep (counts land in the Vectors* columns).`),
    DumpDate: z.date().describe(`
        * * Field Name: DumpDate
        * * Display Name: Dump Date
        * * SQL Data Type: date`),
    BlobPrefix: z.string().describe(`
        * * Field Name: BlobPrefix
        * * Display Name: Blob Prefix
        * * SQL Data Type: nvarchar(500)`),
    ManifestRowCount: z.number().nullable().describe(`
        * * Field Name: ManifestRowCount
        * * Display Name: Manifest Row Count
        * * SQL Data Type: bigint`),
    ParsedRowCount: z.number().nullable().describe(`
        * * Field Name: ParsedRowCount
        * * Display Name: Parsed Row Count
        * * SQL Data Type: bigint`),
    DiffNew: z.number().nullable().describe(`
        * * Field Name: DiffNew
        * * Display Name: Diff New
        * * SQL Data Type: bigint`),
    DiffUpdated: z.number().nullable().describe(`
        * * Field Name: DiffUpdated
        * * Display Name: Diff Updated
        * * SQL Data Type: bigint`),
    DiffExpired: z.number().nullable().describe(`
        * * Field Name: DiffExpired
        * * Display Name: Diff Expired
        * * SQL Data Type: bigint`),
    DiffSkipped: z.number().nullable().describe(`
        * * Field Name: DiffSkipped
        * * Display Name: Diff Skipped
        * * SQL Data Type: bigint`),
    GateKept: z.number().nullable().describe(`
        * * Field Name: GateKept
        * * Display Name: Gate Kept
        * * SQL Data Type: bigint`),
    GateRejected: z.number().nullable().describe(`
        * * Field Name: GateRejected
        * * Display Name: Gate Rejected
        * * SQL Data Type: bigint`),
    VectorsUpserted: z.number().nullable().describe(`
        * * Field Name: VectorsUpserted
        * * Display Name: Vectors Upserted
        * * SQL Data Type: bigint`),
    VectorsMetadataUpdated: z.number().nullable().describe(`
        * * Field Name: VectorsMetadataUpdated
        * * Display Name: Vectors Metadata Updated
        * * SQL Data Type: bigint`),
    VectorsDeleted: z.number().nullable().describe(`
        * * Field Name: VectorsDeleted
        * * Display Name: Vectors Deleted
        * * SQL Data Type: bigint`),
    Stage: z.union([z.literal('complete'), z.literal('diffing'), z.literal('failed'), z.literal('gating'), z.literal('parsing'), z.literal('vectorsync'), z.literal('writing')]).describe(`
        * * Field Name: Stage
        * * Display Name: Stage
        * * SQL Data Type: nvarchar(20)
    * * Value List Type: List
    * * Possible Values 
    *   * complete
    *   * diffing
    *   * failed
    *   * gating
    *   * parsing
    *   * vectorsync
    *   * writing`),
    ErrorDetail: z.string().nullable().describe(`
        * * Field Name: ErrorDetail
        * * Display Name: Error Detail
        * * SQL Data Type: nvarchar(MAX)`),
    StartedAt: z.date().describe(`
        * * Field Name: StartedAt
        * * Display Name: Started At
        * * SQL Data Type: datetimeoffset
        * * Default Value: sysdatetimeoffset()`),
    CompletedAt: z.date().nullable().describe(`
        * * Field Name: CompletedAt
        * * Display Name: Completed At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type JobsIngestBatchEntityType = z.infer<typeof JobsIngestBatchSchema>;

/**
 * zod schema definition for the entity Ingestion Runs
 */
export const adminIngestionRunSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    IngestionTypeID: z.string().nullable().describe(`
        * * Field Name: IngestionTypeID
        * * Display Name: Ingestion Type ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Ingestion Types (vwIngestionTypes.ID)
        * * Description: The admin.IngestionType this file was processed as.`),
    SourceFilePath: z.string().describe(`
        * * Field Name: SourceFilePath
        * * Display Name: Source File Path
        * * SQL Data Type: nvarchar(800)
        * * Description: Original file path/URL as dropped (SharePoint or blob).`),
    FinalFilePath: z.string().nullable().describe(`
        * * Field Name: FinalFilePath
        * * Display Name: Final File Path
        * * SQL Data Type: nvarchar(800)
        * * Description: Post-processing destination (e.g., archive path). NULL until archived.`),
    FileSizeBytes: z.number().nullable().describe(`
        * * Field Name: FileSizeBytes
        * * Display Name: File Size Bytes
        * * SQL Data Type: bigint
        * * Description: Size of the source file in bytes. Audit only.`),
    FileHashSha256: z.string().nullable().describe(`
        * * Field Name: FileHashSha256
        * * Display Name: File Hash Sha 256
        * * SQL Data Type: char(64)
        * * Description: SHA-256 of the file bytes. Audit only; not used for dedup.`),
    Status: z.string().describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: nvarchar(50)
        * * Default Value: Pending
        * * Description: Pending | Loading | Succeeded | Failed.`),
    RowCountsJson: z.string().nullable().describe(`
        * * Field Name: RowCountsJson
        * * Display Name: Row Counts Json
        * * SQL Data Type: nvarchar(MAX)
        * * Description: JSON counts returned by the upsert SP (inserted/updated and the various skipped-row tallies).`),
    ErrorMessage: z.string().nullable().describe(`
        * * Field Name: ErrorMessage
        * * Display Name: Error Message
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Failure detail, set by admin.spIngestionMarkFailed on the ADF failure path.`),
    StartedAt: z.date().nullable().describe(`
        * * Field Name: StartedAt
        * * Display Name: Started At
        * * SQL Data Type: datetimeoffset
        * * Description: When the upsert SP began loading this run.`),
    CompletedAt: z.date().nullable().describe(`
        * * Field Name: CompletedAt
        * * Display Name: Completed At
        * * SQL Data Type: datetimeoffset
        * * Description: When the run reached a terminal state (Succeeded/Failed).`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type adminIngestionRunEntityType = z.infer<typeof adminIngestionRunSchema>;

/**
 * zod schema definition for the entity Ingestion Types
 */
export const adminIngestionTypeSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    TypeName: z.string().describe(`
        * * Field Name: TypeName
        * * Display Name: Type Name
        * * SQL Data Type: nvarchar(100)
        * * Description: Unique name of the ingestion type (e.g., "TA Tool Individuals", "Maritz").`),
    DesignatedFolder: z.string().describe(`
        * * Field Name: DesignatedFolder
        * * Display Name: Designated Folder
        * * SQL Data Type: nvarchar(200)
        * * Description: SharePoint folder this type is dropped into. The folder is the type signal (no content-based routing).`),
    TargetSchema: z.string().describe(`
        * * Field Name: TargetSchema
        * * Display Name: Target Schema
        * * SQL Data Type: nvarchar(50)
        * * Default Value: MAPS
        * * Description: Schema of the primary target table. Always MAPS for this deployment.`),
    TargetTable: z.string().describe(`
        * * Field Name: TargetTable
        * * Display Name: Target Table
        * * SQL Data Type: nvarchar(100)
        * * Description: Primary MAPS target table (e.g., Person, ConferenceRegistration). Informational; the upsert SP may write child tables too.`),
    StagingTable: z.string().describe(`
        * * Field Name: StagingTable
        * * Display Name: Staging Table
        * * SQL Data Type: nvarchar(200)
        * * Description: Fully-qualified staging table the ADF Copy lands raw rows into (e.g., staging.TAToolIndividual).`),
    UpsertStoredProcedure: z.string().describe(`
        * * Field Name: UpsertStoredProcedure
        * * Display Name: Upsert Stored Procedure
        * * SQL Data Type: nvarchar(200)
        * * Description: Stored procedure that transforms staging rows into the MAPS target(s) (e.g., admin.spIngestTAToolIndividuals).`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(500)
        * * Description: Human-readable description of the type and its target mapping.`),
    IsActive: z.boolean().describe(`
        * * Field Name: IsActive
        * * Display Name: Is Active
        * * SQL Data Type: bit
        * * Default Value: 1
        * * Description: 0 disables the type (pipeline ignores its drops). 1 = active.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type adminIngestionTypeEntityType = z.infer<typeof adminIngestionTypeSchema>;

/**
 * zod schema definition for the entity Invited Positions
 */
export const ORCIDInvitedPositionSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ORCIDRecordID: z.string().describe(`
        * * Field Name: ORCIDRecordID
        * * Display Name: ORCID Record ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Records (vwRecords.ID)`),
    PutCode: z.number().describe(`
        * * Field Name: PutCode
        * * Display Name: Put Code
        * * SQL Data Type: bigint`),
    DepartmentName: z.string().nullable().describe(`
        * * Field Name: DepartmentName
        * * Display Name: Department Name
        * * SQL Data Type: nvarchar(500)`),
    RoleTitle: z.string().nullable().describe(`
        * * Field Name: RoleTitle
        * * Display Name: Role Title
        * * SQL Data Type: nvarchar(500)`),
    StartYear: z.number().nullable().describe(`
        * * Field Name: StartYear
        * * Display Name: Start Year
        * * SQL Data Type: smallint`),
    StartMonth: z.number().nullable().describe(`
        * * Field Name: StartMonth
        * * Display Name: Start Month
        * * SQL Data Type: tinyint`),
    StartDay: z.number().nullable().describe(`
        * * Field Name: StartDay
        * * Display Name: Start Day
        * * SQL Data Type: tinyint`),
    EndYear: z.number().nullable().describe(`
        * * Field Name: EndYear
        * * Display Name: End Year
        * * SQL Data Type: smallint`),
    EndMonth: z.number().nullable().describe(`
        * * Field Name: EndMonth
        * * Display Name: End Month
        * * SQL Data Type: tinyint`),
    EndDay: z.number().nullable().describe(`
        * * Field Name: EndDay
        * * Display Name: End Day
        * * SQL Data Type: tinyint`),
    OrgName: z.string().nullable().describe(`
        * * Field Name: OrgName
        * * Display Name: Org Name
        * * SQL Data Type: nvarchar(500)`),
    OrgCity: z.string().nullable().describe(`
        * * Field Name: OrgCity
        * * Display Name: Org City
        * * SQL Data Type: nvarchar(255)`),
    OrgRegion: z.string().nullable().describe(`
        * * Field Name: OrgRegion
        * * Display Name: Org Region
        * * SQL Data Type: nvarchar(255)`),
    OrgCountry: z.string().nullable().describe(`
        * * Field Name: OrgCountry
        * * Display Name: Org Country
        * * SQL Data Type: nvarchar(10)`),
    OrgDisambiguatedID: z.string().nullable().describe(`
        * * Field Name: OrgDisambiguatedID
        * * Display Name: Org Disambiguated ID
        * * SQL Data Type: nvarchar(255)`),
    OrgDisambiguationSource: z.string().nullable().describe(`
        * * Field Name: OrgDisambiguationSource
        * * Display Name: Org Disambiguation Source
        * * SQL Data Type: nvarchar(50)`),
    Url: z.string().nullable().describe(`
        * * Field Name: Url
        * * Display Name: Url
        * * SQL Data Type: nvarchar(2000)`),
    Visibility: z.string().nullable().describe(`
        * * Field Name: Visibility
        * * Display Name: Visibility
        * * SQL Data Type: nvarchar(20)`),
    SourceName: z.string().nullable().describe(`
        * * Field Name: SourceName
        * * Display Name: Source Name
        * * SQL Data Type: nvarchar(255)`),
    SourceOrcid: z.string().nullable().describe(`
        * * Field Name: SourceOrcid
        * * Display Name: Source Orcid
        * * SQL Data Type: nvarchar(19)`),
    OrcidCreatedDate: z.date().nullable().describe(`
        * * Field Name: OrcidCreatedDate
        * * Display Name: Orcid Created Date
        * * SQL Data Type: datetimeoffset`),
    OrcidLastModifiedDate: z.date().nullable().describe(`
        * * Field Name: OrcidLastModifiedDate
        * * Display Name: Orcid Last Modified Date
        * * SQL Data Type: datetimeoffset`),
    RawJSON: z.string().nullable().describe(`
        * * Field Name: RawJSON
        * * Display Name: Raw JSON
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncStatus: z.string().describe(`
        * * Field Name: __mj_integration_SyncStatus
        * * Display Name: Mj Integration Sync Status
        * * SQL Data Type: nvarchar(50)
        * * Default Value: Active`),
    __mj_integration_LastSyncedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedAt
        * * Display Name: Mj Integration Last Synced At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastSyncedSnapshot: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedSnapshot
        * * Display Name: Mj Integration Last Synced Snapshot
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncMessage: z.string().nullable().describe(`
        * * Field Name: __mj_integration_SyncMessage
        * * Display Name: Mj Integration Sync Message
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ContentHash: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ContentHash
        * * Display Name: Mj Integration Content Hash
        * * SQL Data Type: nvarchar(64)`),
    __mj_integration_CustomOverflow: z.string().nullable().describe(`
        * * Field Name: __mj_integration_CustomOverflow
        * * Display Name: Mj Integration Custom Overflow
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ExternalVersion: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ExternalVersion
        * * Display Name: Mj Integration External Version
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastSeenModifiedValue: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSeenModifiedValue
        * * Display Name: Mj Integration Last Seen Modified Value
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastReconciledAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastReconciledAt
        * * Display Name: Mj Integration Last Reconciled At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastWriterDirection: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastWriterDirection
        * * Display Name: Mj Integration Last Writer Direction
        * * SQL Data Type: nvarchar(10)`),
    __mj_integration_IsTombstoned: z.boolean().describe(`
        * * Field Name: __mj_integration_IsTombstoned
        * * Display Name: Mj Integration Is Tombstoned
        * * SQL Data Type: bit
        * * Default Value: 0`),
    __mj_integration_DeletedDetectedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_DeletedDetectedAt
        * * Display Name: Mj Integration Deleted Detected At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type ORCIDInvitedPositionEntityType = z.infer<typeof ORCIDInvitedPositionSchema>;

/**
 * zod schema definition for the entity Job Posting Sources
 */
export const JobsJobPostingSourceSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    JobPostingID: z.string().describe(`
        * * Field Name: JobPostingID
        * * Display Name: Job Posting ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Job Postings (vwJobPostings.ID)`),
    Source: z.string().describe(`
        * * Field Name: Source
        * * Display Name: Source
        * * SQL Data Type: nvarchar(50)`),
    SourceID: z.string().describe(`
        * * Field Name: SourceID
        * * Display Name: Source ID
        * * SQL Data Type: nvarchar(200)`),
    Url: z.string().nullable().describe(`
        * * Field Name: Url
        * * Display Name: Url
        * * SQL Data Type: nvarchar(2000)`),
    SourceStatus: z.string().nullable().describe(`
        * * Field Name: SourceStatus
        * * Display Name: Source Status
        * * SQL Data Type: nvarchar(20)`),
    SourceUpdatedAt: z.date().nullable().describe(`
        * * Field Name: SourceUpdatedAt
        * * Display Name: Source Updated At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    JobPosting: z.string().describe(`
        * * Field Name: JobPosting
        * * Display Name: Job Posting
        * * SQL Data Type: nvarchar(500)`),
});

export type JobsJobPostingSourceEntityType = z.infer<typeof JobsJobPostingSourceSchema>;

/**
 * zod schema definition for the entity Job Postings
 */
export const JobsJobPostingSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    JobID: z.number().describe(`
        * * Field Name: JobID
        * * Display Name: Job ID
        * * SQL Data Type: bigint
        * * Description: Coresignal job_id — the stable cross-board natural key. Also the Pinecone vector ID (as a string).`),
    Title: z.string().describe(`
        * * Field Name: Title
        * * Display Name: Title
        * * SQL Data Type: nvarchar(500)`),
    Description: z.string().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(MAX)`),
    CountryISO2: z.string().nullable().describe(`
        * * Field Name: CountryISO2
        * * Display Name: Country ISO2
        * * SQL Data Type: nvarchar(10)`),
    CountryName: z.string().nullable().describe(`
        * * Field Name: CountryName
        * * Display Name: Country Name
        * * SQL Data Type: nvarchar(100)`),
    StateName: z.string().nullable().describe(`
        * * Field Name: StateName
        * * Display Name: State Name
        * * SQL Data Type: nvarchar(200)`),
    City: z.string().nullable().describe(`
        * * Field Name: City
        * * Display Name: City
        * * SQL Data Type: nvarchar(200)`),
    Latitude: z.number().nullable().describe(`
        * * Field Name: Latitude
        * * Display Name: Latitude
        * * SQL Data Type: decimal(9, 6)`),
    Longitude: z.number().nullable().describe(`
        * * Field Name: Longitude
        * * Display Name: Longitude
        * * SQL Data Type: decimal(9, 6)`),
    Seniority: z.string().nullable().describe(`
        * * Field Name: Seniority
        * * Display Name: Seniority
        * * SQL Data Type: nvarchar(100)`),
    ManagementLevel: z.string().nullable().describe(`
        * * Field Name: ManagementLevel
        * * Display Name: Management Level
        * * SQL Data Type: nvarchar(100)`),
    EmploymentType: z.string().nullable().describe(`
        * * Field Name: EmploymentType
        * * Display Name: Employment Type
        * * SQL Data Type: nvarchar(100)`),
    AcceptsRemote: z.boolean().nullable().describe(`
        * * Field Name: AcceptsRemote
        * * Display Name: Accepts Remote
        * * SQL Data Type: bit`),
    DatePosted: z.date().nullable().describe(`
        * * Field Name: DatePosted
        * * Display Name: Date Posted
        * * SQL Data Type: datetimeoffset`),
    DatePostedUnix: z.number().nullable().describe(`
        * * Field Name: DatePostedUnix
        * * Display Name: Date Posted Unix
        * * SQL Data Type: bigint
        * * Description: DatePosted as unix epoch seconds, written by the worker from the same source value. Exists because Pinecone metadata range filters ($gte/$lte) compare numbers, not ISO strings — this is the value mirrored into vector metadata as posted_ts.`),
    ValidThrough: z.date().nullable().describe(`
        * * Field Name: ValidThrough
        * * Display Name: Valid Through
        * * SQL Data Type: datetimeoffset`),
    SourceStatus: z.number().describe(`
        * * Field Name: SourceStatus
        * * Display Name: Source Status
        * * SQL Data Type: tinyint
        * * Description: Feed status: 1 = at least one source board active, 2 = all inactive, 3 = deleted (verified empirically against job_sources[].status). Steady state holds only 1 — status 2/3 rows are deleted by the nightly merge.`),
    SourceCreatedAt: z.date().nullable().describe(`
        * * Field Name: SourceCreatedAt
        * * Display Name: Source Created At
        * * SQL Data Type: datetimeoffset`),
    SourceUpdatedAt: z.date().describe(`
        * * Field Name: SourceUpdatedAt
        * * Display Name: Source Updated At
        * * SQL Data Type: datetimeoffset
        * * Description: Feed updated_at (UTC). Recency tiebreak for the worker's diff; also the stale-sweep signal — a healthy feed touches every live posting periodically.`),
    CompanyName: z.string().nullable().describe(`
        * * Field Name: CompanyName
        * * Display Name: Company Name
        * * SQL Data Type: nvarchar(400)`),
    CompanyDomain: z.string().nullable().describe(`
        * * Field Name: CompanyDomain
        * * Display Name: Company Domain
        * * SQL Data Type: nvarchar(200)`),
    CompanyIndustry: z.string().nullable().describe(`
        * * Field Name: CompanyIndustry
        * * Display Name: Company Industry
        * * SQL Data Type: nvarchar(200)`),
    CompanySizeRange: z.string().nullable().describe(`
        * * Field Name: CompanySizeRange
        * * Display Name: Company Size Range
        * * SQL Data Type: nvarchar(50)`),
    CompanyEmployeesCount: z.number().nullable().describe(`
        * * Field Name: CompanyEmployeesCount
        * * Display Name: Company Employees Count
        * * SQL Data Type: int`),
    SalaryMinAnnual: z.number().nullable().describe(`
        * * Field Name: SalaryMinAnnual
        * * Display Name: Salary Min Annual
        * * SQL Data Type: decimal(14, 2)
        * * Description: Annualized salary lower bound in the original currency, derived by the worker from SalaryRaw (prefer non-estimated sources; HOUR×2080 / DAY×260 / WEEK×52 / MONTH×12; mistyped periods rejected). Only USD rows are mirrored into Pinecone sal_min/sal_max metadata in v1.`),
    SalaryMaxAnnual: z.number().nullable().describe(`
        * * Field Name: SalaryMaxAnnual
        * * Display Name: Salary Max Annual
        * * SQL Data Type: decimal(14, 2)`),
    SalaryCurrency: z.string().nullable().describe(`
        * * Field Name: SalaryCurrency
        * * Display Name: Salary Currency
        * * SQL Data Type: nvarchar(10)`),
    SalaryIsEstimated: z.boolean().nullable().describe(`
        * * Field Name: SalaryIsEstimated
        * * Display Name: Salary Is Estimated
        * * SQL Data Type: bit`),
    SalaryRaw: z.string().nullable().describe(`
        * * Field Name: SalaryRaw
        * * Display Name: Salary Raw
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Original salary JSON array from the feed, verbatim — the one raw fragment kept (normalization is lossy; this drives re-derivation).`),
    GateTier: z.union([z.literal('core'), z.literal('professional')]).describe(`
        * * Field Name: GateTier
        * * Display Name: Gate Tier
        * * SQL Data Type: nvarchar(20)
    * * Value List Type: List
    * * Possible Values 
    *   * core
    *   * professional
        * * Description: Which rubric tier kept this posting: core (requires specialized earth/space-science knowledge) or professional (degree-level role serving an earth/space system). Stored so the client keep rule (jobs.gateKeepTiers) can narrow/widen without re-classifying — rubric v4, plans/jobs/relevance-rubric.md.`),
    GateSection: z.string().nullable().describe(`
        * * Field Name: GateSection
        * * Display Name: Gate Section
        * * SQL Data Type: nvarchar(100)
        * * Description: AGU discipline-section tag emitted by the same gate stage-2 call that assigned GateTier (e.g. "Hydrology", "Space Physics and Aeronomy"). Nullable — present when the classifier names one. Future search facet.`),
    ExternalUrl: z.string().nullable().describe(`
        * * Field Name: ExternalUrl
        * * Display Name: External Url
        * * SQL Data Type: nvarchar(2000)`),
    IngestBatchID: z.number().describe(`
        * * Field Name: IngestBatchID
        * * Display Name: Ingest Batch ID
        * * SQL Data Type: int
        * * Description: Jobs.IngestBatch.IngestBatchID (compact INT identity) of the batch that last wrote this row. Deliberately not a declared FK: the nightly bulk MERGE must not pay per-row constraint checks, and the ledger row is guaranteed to exist by the worker's pass ordering.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    CompanyLogoUrl: z.string().nullable().describe(`
        * * Field Name: CompanyLogoUrl
        * * Display Name: Company Logo Url
        * * SQL Data Type: nvarchar(1000)`),
    CompanyType: z.string().nullable().describe(`
        * * Field Name: CompanyType
        * * Display Name: Company Type
        * * SQL Data Type: nvarchar(100)`),
    CompanyDescription: z.string().nullable().describe(`
        * * Field Name: CompanyDescription
        * * Display Name: Company Description
        * * SQL Data Type: nvarchar(4000)`),
    Benefits: z.string().nullable().describe(`
        * * Field Name: Benefits
        * * Display Name: Benefits
        * * SQL Data Type: nvarchar(MAX)`),
    SalaryPeriod: z.string().nullable().describe(`
        * * Field Name: SalaryPeriod
        * * Display Name: Salary Period
        * * SQL Data Type: nvarchar(10)`),
});

export type JobsJobPostingEntityType = z.infer<typeof JobsJobPostingSchema>;

/**
 * zod schema definition for the entity Keywords
 */
export const ORCIDKeywordSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ORCIDRecordID: z.string().describe(`
        * * Field Name: ORCIDRecordID
        * * Display Name: ORCID Record ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Records (vwRecords.ID)`),
    PutCode: z.number().describe(`
        * * Field Name: PutCode
        * * Display Name: Put Code
        * * SQL Data Type: bigint`),
    Content: z.string().nullable().describe(`
        * * Field Name: Content
        * * Display Name: Content
        * * SQL Data Type: nvarchar(500)`),
    DisplayIndex: z.number().nullable().describe(`
        * * Field Name: DisplayIndex
        * * Display Name: Display Index
        * * SQL Data Type: int`),
    Visibility: z.string().nullable().describe(`
        * * Field Name: Visibility
        * * Display Name: Visibility
        * * SQL Data Type: nvarchar(20)`),
    SourceName: z.string().nullable().describe(`
        * * Field Name: SourceName
        * * Display Name: Source Name
        * * SQL Data Type: nvarchar(255)`),
    SourceOrcid: z.string().nullable().describe(`
        * * Field Name: SourceOrcid
        * * Display Name: Source Orcid
        * * SQL Data Type: nvarchar(19)`),
    OrcidCreatedDate: z.date().nullable().describe(`
        * * Field Name: OrcidCreatedDate
        * * Display Name: Orcid Created Date
        * * SQL Data Type: datetimeoffset`),
    OrcidLastModifiedDate: z.date().nullable().describe(`
        * * Field Name: OrcidLastModifiedDate
        * * Display Name: Orcid Last Modified Date
        * * SQL Data Type: datetimeoffset`),
    RawJSON: z.string().nullable().describe(`
        * * Field Name: RawJSON
        * * Display Name: Raw JSON
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncStatus: z.string().describe(`
        * * Field Name: __mj_integration_SyncStatus
        * * Display Name: Mj Integration Sync Status
        * * SQL Data Type: nvarchar(50)
        * * Default Value: Active`),
    __mj_integration_LastSyncedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedAt
        * * Display Name: Mj Integration Last Synced At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastSyncedSnapshot: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedSnapshot
        * * Display Name: Mj Integration Last Synced Snapshot
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncMessage: z.string().nullable().describe(`
        * * Field Name: __mj_integration_SyncMessage
        * * Display Name: Mj Integration Sync Message
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ContentHash: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ContentHash
        * * Display Name: Mj Integration Content Hash
        * * SQL Data Type: nvarchar(64)`),
    __mj_integration_CustomOverflow: z.string().nullable().describe(`
        * * Field Name: __mj_integration_CustomOverflow
        * * Display Name: Mj Integration Custom Overflow
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ExternalVersion: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ExternalVersion
        * * Display Name: Mj Integration External Version
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastSeenModifiedValue: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSeenModifiedValue
        * * Display Name: Mj Integration Last Seen Modified Value
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastReconciledAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastReconciledAt
        * * Display Name: Mj Integration Last Reconciled At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastWriterDirection: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastWriterDirection
        * * Display Name: Mj Integration Last Writer Direction
        * * SQL Data Type: nvarchar(10)`),
    __mj_integration_IsTombstoned: z.boolean().describe(`
        * * Field Name: __mj_integration_IsTombstoned
        * * Display Name: Mj Integration Is Tombstoned
        * * SQL Data Type: bit
        * * Default Value: 0`),
    __mj_integration_DeletedDetectedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_DeletedDetectedAt
        * * Display Name: Mj Integration Deleted Detected At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type ORCIDKeywordEntityType = z.infer<typeof ORCIDKeywordSchema>;

/**
 * zod schema definition for the entity Languages
 */
export const CoreSignalLanguageSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ProfileID: z.string().describe(`
        * * Field Name: ProfileID
        * * Display Name: Profile ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Profiles (vwProfiles.ID)`),
    OrderInProfile: z.number().describe(`
        * * Field Name: OrderInProfile
        * * Display Name: Order In Profile
        * * SQL Data Type: int`),
    Language: z.string().nullable().describe(`
        * * Field Name: Language
        * * Display Name: Language
        * * SQL Data Type: nvarchar(255)`),
    Proficiency: z.string().nullable().describe(`
        * * Field Name: Proficiency
        * * Display Name: Proficiency
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_SyncStatus: z.string().describe(`
        * * Field Name: __mj_integration_SyncStatus
        * * Display Name: Mj Integration Sync Status
        * * SQL Data Type: nvarchar(50)
        * * Default Value: Active`),
    __mj_integration_LastSyncedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedAt
        * * Display Name: Mj Integration Last Synced At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastSyncedSnapshot: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedSnapshot
        * * Display Name: Mj Integration Last Synced Snapshot
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncMessage: z.string().nullable().describe(`
        * * Field Name: __mj_integration_SyncMessage
        * * Display Name: Mj Integration Sync Message
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ContentHash: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ContentHash
        * * Display Name: Mj Integration Content Hash
        * * SQL Data Type: nvarchar(64)`),
    __mj_integration_CustomOverflow: z.string().nullable().describe(`
        * * Field Name: __mj_integration_CustomOverflow
        * * Display Name: Mj Integration Custom Overflow
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ExternalVersion: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ExternalVersion
        * * Display Name: Mj Integration External Version
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastSeenModifiedValue: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSeenModifiedValue
        * * Display Name: Mj Integration Last Seen Modified Value
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastReconciledAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastReconciledAt
        * * Display Name: Mj Integration Last Reconciled At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastWriterDirection: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastWriterDirection
        * * Display Name: Mj Integration Last Writer Direction
        * * SQL Data Type: nvarchar(10)`),
    __mj_integration_IsTombstoned: z.boolean().describe(`
        * * Field Name: __mj_integration_IsTombstoned
        * * Display Name: Mj Integration Is Tombstoned
        * * SQL Data Type: bit
        * * Default Value: 0`),
    __mj_integration_DeletedDetectedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_DeletedDetectedAt
        * * Display Name: Mj Integration Deleted Detected At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type CoreSignalLanguageEntityType = z.infer<typeof CoreSignalLanguageSchema>;

/**
 * zod schema definition for the entity Member Documents
 */
export const ResearchMemberDocumentSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    UserID: z.string().nullable().describe(`
        * * Field Name: UserID
        * * Display Name: User ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Users (vwUsers.ID)`),
    Purpose: z.union([z.literal('cv'), z.literal('search')]).describe(`
        * * Field Name: Purpose
        * * Display Name: Purpose
        * * SQL Data Type: nvarchar(20)
    * * Value List Type: List
    * * Possible Values 
    *   * cv
    *   * search`),
    BlobPath: z.string().describe(`
        * * Field Name: BlobPath
        * * Display Name: Blob Path
        * * SQL Data Type: nvarchar(500)`),
    OriginalFileName: z.string().describe(`
        * * Field Name: OriginalFileName
        * * Display Name: Original File Name
        * * SQL Data Type: nvarchar(255)`),
    ContentType: z.string().describe(`
        * * Field Name: ContentType
        * * Display Name: Content Type
        * * SQL Data Type: nvarchar(100)`),
    SizeBytes: z.number().describe(`
        * * Field Name: SizeBytes
        * * Display Name: Size Bytes
        * * SQL Data Type: bigint`),
    ExtractedText: z.string().nullable().describe(`
        * * Field Name: ExtractedText
        * * Display Name: Extracted Text
        * * SQL Data Type: nvarchar(MAX)`),
    ExtractStatus: z.union([z.literal('extract_failed'), z.literal('extracted'), z.literal('pending')]).describe(`
        * * Field Name: ExtractStatus
        * * Display Name: Extract Status
        * * SQL Data Type: nvarchar(20)
        * * Default Value: pending
    * * Value List Type: List
    * * Possible Values 
    *   * extract_failed
    *   * extracted
    *   * pending`),
    ExtractError: z.string().nullable().describe(`
        * * Field Name: ExtractError
        * * Display Name: Extract Error
        * * SQL Data Type: nvarchar(500)`),
    UploadedAt: z.date().describe(`
        * * Field Name: UploadedAt
        * * Display Name: Uploaded At
        * * SQL Data Type: datetimeoffset
        * * Default Value: sysdatetimeoffset()`),
    ExtractedAt: z.date().nullable().describe(`
        * * Field Name: ExtractedAt
        * * Display Name: Extracted At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    AguPersonGUID: z.string().nullable().describe(`
        * * Field Name: AguPersonGUID
        * * Display Name: Agu Person GUID
        * * SQL Data Type: uniqueidentifier
        * * Description: Owner as Common.AguPerson GUID. Set for external-drop CVs, whose owner has no MJ user; null owner UserID is allowed when this is set.`),
    Source: z.union([z.literal('app'), z.literal('external_drop')]).describe(`
        * * Field Name: Source
        * * Display Name: Source
        * * SQL Data Type: nvarchar(20)
        * * Default Value: app
    * * Value List Type: List
    * * Possible Values 
    *   * app
    *   * external_drop
        * * Description: Where the document came from: app (uploaded in the portal) or external_drop (found in the person documents container under <AguPersonGUID>/CV/).`),
    StorageAccountID: z.string().nullable().describe(`
        * * Field Name: StorageAccountID
        * * Display Name: Storage Account ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: File Storage Accounts (vwFileStorageAccounts.ID)
        * * Description: File storage account holding the blob at BlobPath.`),
    DetectedFileType: z.union([z.literal('doc'), z.literal('docx'), z.literal('other'), z.literal('pdf'), z.literal('txt')]).nullable().describe(`
        * * Field Name: DetectedFileType
        * * Display Name: Detected File Type
        * * SQL Data Type: nvarchar(10)
    * * Value List Type: List
    * * Possible Values 
    *   * doc
    *   * docx
    *   * other
    *   * pdf
    *   * txt
        * * Description: File type detected from the blob content: pdf, docx, doc, txt or other.`),
    ExternalETag: z.string().nullable().describe(`
        * * Field Name: ExternalETag
        * * Display Name: External E Tag
        * * SQL Data Type: nvarchar(100)
        * * Description: Blob ETag last seen for an external-drop document; a change means the blob was overwritten in place.`),
    DeletedAt: z.date().nullable().describe(`
        * * Field Name: DeletedAt
        * * Display Name: Deleted At
        * * SQL Data Type: datetimeoffset
        * * Description: Member tombstone: set when the member removes the document; the external blob itself is never deleted.`),
    SourceMissingAt: z.date().nullable().describe(`
        * * Field Name: SourceMissingAt
        * * Display Name: Source Missing At
        * * SQL Data Type: datetimeoffset
        * * Description: Set when an external-drop blob vanished from the drop container.`),
    ConsentedAt: z.date().nullable().describe(`
        * * Field Name: ConsentedAt
        * * Display Name: Consented At
        * * SQL Data Type: datetimeoffset
        * * Description: When the member consented to Career Explorer processing of this document; NULL when no consent was recorded.`),
    User: z.string().nullable().describe(`
        * * Field Name: User
        * * Display Name: User
        * * SQL Data Type: nvarchar(100)`),
    StorageAccount: z.string().nullable().describe(`
        * * Field Name: StorageAccount
        * * Display Name: Storage Account
        * * SQL Data Type: nvarchar(200)`),
});

export type ResearchMemberDocumentEntityType = z.infer<typeof ResearchMemberDocumentSchema>;

/**
 * zod schema definition for the entity Member Item States
 */
export const ResearchMemberItemStateSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    UserID: z.string().describe(`
        * * Field Name: UserID
        * * Display Name: User ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Users (vwUsers.ID)`),
    ItemKind: z.union([z.literal('grant'), z.literal('job'), z.literal('person')]).describe(`
        * * Field Name: ItemKind
        * * Display Name: Item Kind
        * * SQL Data Type: nvarchar(20)
    * * Value List Type: List
    * * Possible Values 
    *   * grant
    *   * job
    *   * person`),
    ItemKey: z.string().describe(`
        * * Field Name: ItemKey
        * * Display Name: Item Key
        * * SQL Data Type: nvarchar(100)`),
    IsSaved_: z.boolean().describe(`
        * * Field Name: IsSaved
        * * Display Name: Is Saved
        * * SQL Data Type: bit
        * * Default Value: 0`),
    IsPinned: z.boolean().describe(`
        * * Field Name: IsPinned
        * * Display Name: Is Pinned
        * * SQL Data Type: bit
        * * Default Value: 0`),
    IsDismissed: z.boolean().describe(`
        * * Field Name: IsDismissed
        * * Display Name: Is Dismissed
        * * SQL Data Type: bit
        * * Default Value: 0`),
    SavedAt: z.date().nullable().describe(`
        * * Field Name: SavedAt
        * * Display Name: Saved At
        * * SQL Data Type: datetimeoffset`),
    PinnedAt: z.date().nullable().describe(`
        * * Field Name: PinnedAt
        * * Display Name: Pinned At
        * * SQL Data Type: datetimeoffset`),
    DismissedAt: z.date().nullable().describe(`
        * * Field Name: DismissedAt
        * * Display Name: Dismissed At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    User: z.string().describe(`
        * * Field Name: User
        * * Display Name: User
        * * SQL Data Type: nvarchar(100)`),
});

export type ResearchMemberItemStateEntityType = z.infer<typeof ResearchMemberItemStateSchema>;

/**
 * zod schema definition for the entity Member Organizations
 */
export const CoreSignalMemberOrganizationSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ProfileID: z.string().describe(`
        * * Field Name: ProfileID
        * * Display Name: Profile ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Profiles (vwProfiles.ID)`),
    OrderInProfile: z.number().describe(`
        * * Field Name: OrderInProfile
        * * Display Name: Order In Profile
        * * SQL Data Type: int`),
    OrganizationName: z.string().nullable().describe(`
        * * Field Name: OrganizationName
        * * Display Name: Organization Name
        * * SQL Data Type: nvarchar(500)`),
    Position: z.string().nullable().describe(`
        * * Field Name: Position
        * * Display Name: Position
        * * SQL Data Type: nvarchar(500)`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(MAX)`),
    DateFromYear: z.number().nullable().describe(`
        * * Field Name: DateFromYear
        * * Display Name: Date From Year
        * * SQL Data Type: smallint`),
    DateFromMonth: z.number().nullable().describe(`
        * * Field Name: DateFromMonth
        * * Display Name: Date From Month
        * * SQL Data Type: tinyint`),
    DateToYear: z.number().nullable().describe(`
        * * Field Name: DateToYear
        * * Display Name: Date To Year
        * * SQL Data Type: smallint`),
    DateToMonth: z.number().nullable().describe(`
        * * Field Name: DateToMonth
        * * Display Name: Date To Month
        * * SQL Data Type: tinyint`),
    __mj_integration_SyncStatus: z.string().describe(`
        * * Field Name: __mj_integration_SyncStatus
        * * Display Name: Mj Integration Sync Status
        * * SQL Data Type: nvarchar(50)
        * * Default Value: Active`),
    __mj_integration_LastSyncedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedAt
        * * Display Name: Mj Integration Last Synced At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastSyncedSnapshot: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedSnapshot
        * * Display Name: Mj Integration Last Synced Snapshot
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncMessage: z.string().nullable().describe(`
        * * Field Name: __mj_integration_SyncMessage
        * * Display Name: Mj Integration Sync Message
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ContentHash: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ContentHash
        * * Display Name: Mj Integration Content Hash
        * * SQL Data Type: nvarchar(64)`),
    __mj_integration_CustomOverflow: z.string().nullable().describe(`
        * * Field Name: __mj_integration_CustomOverflow
        * * Display Name: Mj Integration Custom Overflow
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ExternalVersion: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ExternalVersion
        * * Display Name: Mj Integration External Version
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastSeenModifiedValue: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSeenModifiedValue
        * * Display Name: Mj Integration Last Seen Modified Value
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastReconciledAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastReconciledAt
        * * Display Name: Mj Integration Last Reconciled At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastWriterDirection: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastWriterDirection
        * * Display Name: Mj Integration Last Writer Direction
        * * SQL Data Type: nvarchar(10)`),
    __mj_integration_IsTombstoned: z.boolean().describe(`
        * * Field Name: __mj_integration_IsTombstoned
        * * Display Name: Mj Integration Is Tombstoned
        * * SQL Data Type: bit
        * * Default Value: 0`),
    __mj_integration_DeletedDetectedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_DeletedDetectedAt
        * * Display Name: Mj Integration Deleted Detected At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type CoreSignalMemberOrganizationEntityType = z.infer<typeof CoreSignalMemberOrganizationSchema>;

/**
 * zod schema definition for the entity Member Profiles
 */
export const ResearchMemberProfileSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    AguPersonID: z.string().describe(`
        * * Field Name: AguPersonID
        * * Display Name: Agu Person ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Agu Persons (vwAguPersons.ID)`),
    ResearchAreas: z.string().nullable().describe(`
        * * Field Name: ResearchAreas
        * * Display Name: Research Areas
        * * SQL Data Type: nvarchar(MAX)`),
    ResearchAreasInferredAt: z.date().nullable().describe(`
        * * Field Name: ResearchAreasInferredAt
        * * Display Name: Research Areas Inferred At
        * * SQL Data Type: datetimeoffset`),
    LastVisitAt: z.date().nullable().describe(`
        * * Field Name: LastVisitAt
        * * Display Name: Last Visit At
        * * SQL Data Type: datetimeoffset`),
    PreviousVisitAt: z.date().nullable().describe(`
        * * Field Name: PreviousVisitAt
        * * Display Name: Previous Visit At
        * * SQL Data Type: datetimeoffset
        * * Description: Start of the visit before the current one. "New since your last visit" counts recommendations first seen after this instant.`),
    VisitCount: z.number().describe(`
        * * Field Name: VisitCount
        * * Display Name: Visit Count
        * * SQL Data Type: int
        * * Default Value: 0`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type ResearchMemberProfileEntityType = z.infer<typeof ResearchMemberProfileSchema>;

/**
 * zod schema definition for the entity Member Recommendations
 */
export const ResearchMemberRecommendationSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    UserID: z.string().describe(`
        * * Field Name: UserID
        * * Display Name: User ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Users (vwUsers.ID)`),
    ItemKind: z.union([z.literal('grant'), z.literal('job'), z.literal('person')]).describe(`
        * * Field Name: ItemKind
        * * Display Name: Item Kind
        * * SQL Data Type: nvarchar(20)
    * * Value List Type: List
    * * Possible Values 
    *   * grant
    *   * job
    *   * person`),
    ItemKey: z.string().describe(`
        * * Field Name: ItemKey
        * * Display Name: Item Key
        * * SQL Data Type: nvarchar(100)`),
    Score: z.number().describe(`
        * * Field Name: Score
        * * Display Name: Score
        * * SQL Data Type: decimal(6, 5)`),
    Reason: z.string().nullable().describe(`
        * * Field Name: Reason
        * * Display Name: Reason
        * * SQL Data Type: nvarchar(500)`),
    FirstSeenAt: z.date().describe(`
        * * Field Name: FirstSeenAt
        * * Display Name: First Seen At
        * * SQL Data Type: datetimeoffset
        * * Default Value: sysdatetimeoffset()`),
    LastSeenAt: z.date().describe(`
        * * Field Name: LastSeenAt
        * * Display Name: Last Seen At
        * * SQL Data Type: datetimeoffset
        * * Default Value: sysdatetimeoffset()`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    User: z.string().describe(`
        * * Field Name: User
        * * Display Name: User
        * * SQL Data Type: nvarchar(100)`),
});

export type ResearchMemberRecommendationEntityType = z.infer<typeof ResearchMemberRecommendationSchema>;

/**
 * zod schema definition for the entity Memberships
 */
export const ORCIDMembershipSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ORCIDRecordID: z.string().describe(`
        * * Field Name: ORCIDRecordID
        * * Display Name: ORCID Record ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Records (vwRecords.ID)`),
    PutCode: z.number().describe(`
        * * Field Name: PutCode
        * * Display Name: Put Code
        * * SQL Data Type: bigint`),
    DepartmentName: z.string().nullable().describe(`
        * * Field Name: DepartmentName
        * * Display Name: Department Name
        * * SQL Data Type: nvarchar(500)`),
    RoleTitle: z.string().nullable().describe(`
        * * Field Name: RoleTitle
        * * Display Name: Role Title
        * * SQL Data Type: nvarchar(500)`),
    StartYear: z.number().nullable().describe(`
        * * Field Name: StartYear
        * * Display Name: Start Year
        * * SQL Data Type: smallint`),
    StartMonth: z.number().nullable().describe(`
        * * Field Name: StartMonth
        * * Display Name: Start Month
        * * SQL Data Type: tinyint`),
    StartDay: z.number().nullable().describe(`
        * * Field Name: StartDay
        * * Display Name: Start Day
        * * SQL Data Type: tinyint`),
    EndYear: z.number().nullable().describe(`
        * * Field Name: EndYear
        * * Display Name: End Year
        * * SQL Data Type: smallint`),
    EndMonth: z.number().nullable().describe(`
        * * Field Name: EndMonth
        * * Display Name: End Month
        * * SQL Data Type: tinyint`),
    EndDay: z.number().nullable().describe(`
        * * Field Name: EndDay
        * * Display Name: End Day
        * * SQL Data Type: tinyint`),
    OrgName: z.string().nullable().describe(`
        * * Field Name: OrgName
        * * Display Name: Org Name
        * * SQL Data Type: nvarchar(500)`),
    OrgCity: z.string().nullable().describe(`
        * * Field Name: OrgCity
        * * Display Name: Org City
        * * SQL Data Type: nvarchar(255)`),
    OrgRegion: z.string().nullable().describe(`
        * * Field Name: OrgRegion
        * * Display Name: Org Region
        * * SQL Data Type: nvarchar(255)`),
    OrgCountry: z.string().nullable().describe(`
        * * Field Name: OrgCountry
        * * Display Name: Org Country
        * * SQL Data Type: nvarchar(10)`),
    OrgDisambiguatedID: z.string().nullable().describe(`
        * * Field Name: OrgDisambiguatedID
        * * Display Name: Org Disambiguated ID
        * * SQL Data Type: nvarchar(255)`),
    OrgDisambiguationSource: z.string().nullable().describe(`
        * * Field Name: OrgDisambiguationSource
        * * Display Name: Org Disambiguation Source
        * * SQL Data Type: nvarchar(50)`),
    Url: z.string().nullable().describe(`
        * * Field Name: Url
        * * Display Name: Url
        * * SQL Data Type: nvarchar(2000)`),
    Visibility: z.string().nullable().describe(`
        * * Field Name: Visibility
        * * Display Name: Visibility
        * * SQL Data Type: nvarchar(20)`),
    SourceName: z.string().nullable().describe(`
        * * Field Name: SourceName
        * * Display Name: Source Name
        * * SQL Data Type: nvarchar(255)`),
    SourceOrcid: z.string().nullable().describe(`
        * * Field Name: SourceOrcid
        * * Display Name: Source Orcid
        * * SQL Data Type: nvarchar(19)`),
    OrcidCreatedDate: z.date().nullable().describe(`
        * * Field Name: OrcidCreatedDate
        * * Display Name: Orcid Created Date
        * * SQL Data Type: datetimeoffset`),
    OrcidLastModifiedDate: z.date().nullable().describe(`
        * * Field Name: OrcidLastModifiedDate
        * * Display Name: Orcid Last Modified Date
        * * SQL Data Type: datetimeoffset`),
    RawJSON: z.string().nullable().describe(`
        * * Field Name: RawJSON
        * * Display Name: Raw JSON
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncStatus: z.string().describe(`
        * * Field Name: __mj_integration_SyncStatus
        * * Display Name: Mj Integration Sync Status
        * * SQL Data Type: nvarchar(50)
        * * Default Value: Active`),
    __mj_integration_LastSyncedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedAt
        * * Display Name: Mj Integration Last Synced At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastSyncedSnapshot: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedSnapshot
        * * Display Name: Mj Integration Last Synced Snapshot
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncMessage: z.string().nullable().describe(`
        * * Field Name: __mj_integration_SyncMessage
        * * Display Name: Mj Integration Sync Message
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ContentHash: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ContentHash
        * * Display Name: Mj Integration Content Hash
        * * SQL Data Type: nvarchar(64)`),
    __mj_integration_CustomOverflow: z.string().nullable().describe(`
        * * Field Name: __mj_integration_CustomOverflow
        * * Display Name: Mj Integration Custom Overflow
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ExternalVersion: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ExternalVersion
        * * Display Name: Mj Integration External Version
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastSeenModifiedValue: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSeenModifiedValue
        * * Display Name: Mj Integration Last Seen Modified Value
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastReconciledAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastReconciledAt
        * * Display Name: Mj Integration Last Reconciled At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastWriterDirection: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastWriterDirection
        * * Display Name: Mj Integration Last Writer Direction
        * * SQL Data Type: nvarchar(10)`),
    __mj_integration_IsTombstoned: z.boolean().describe(`
        * * Field Name: __mj_integration_IsTombstoned
        * * Display Name: Mj Integration Is Tombstoned
        * * SQL Data Type: bit
        * * Default Value: 0`),
    __mj_integration_DeletedDetectedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_DeletedDetectedAt
        * * Display Name: Mj Integration Deleted Detected At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type ORCIDMembershipEntityType = z.infer<typeof ORCIDMembershipSchema>;

/**
 * zod schema definition for the entity Nomination Responses
 */
export const FeedbackPlatformNominationResponseSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    NominationID: z.string().describe(`
        * * Field Name: NominationID
        * * Display Name: Nomination ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Nominations (vwNominations.ID)`),
    ReviewQuestionID: z.string().describe(`
        * * Field Name: ReviewQuestionID
        * * Display Name: Review Question ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Review Questions (vwReviewQuestions.ID)`),
    NumericValue: z.number().nullable().describe(`
        * * Field Name: NumericValue
        * * Display Name: Numeric Value
        * * SQL Data Type: int`),
    TextValue: z.string().nullable().describe(`
        * * Field Name: TextValue
        * * Display Name: Text Value
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type FeedbackPlatformNominationResponseEntityType = z.infer<typeof FeedbackPlatformNominationResponseSchema>;

/**
 * zod schema definition for the entity Nominations
 */
export const FeedbackPlatformNominationSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    SeasonID: z.string().describe(`
        * * Field Name: SeasonID
        * * Display Name: Season ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Seasons (vwSeasons.ID)`),
    PresentationID: z.string().describe(`
        * * Field Name: PresentationID
        * * Display Name: Presentation ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Presentations (vwPresentations.ID)`),
    NominatorUserID: z.string().describe(`
        * * Field Name: NominatorUserID
        * * Display Name: Nominator User ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Users (vwUsers.ID)`),
    Status: z.union([z.literal('Draft'), z.literal('Submitted')]).describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: nvarchar(20)
        * * Default Value: Draft
    * * Value List Type: List
    * * Possible Values 
    *   * Draft
    *   * Submitted`),
    NoConflictAttested: z.boolean().describe(`
        * * Field Name: NoConflictAttested
        * * Display Name: No Conflict Attested
        * * SQL Data Type: bit
        * * Default Value: 0`),
    AdditionalReasoning: z.string().nullable().describe(`
        * * Field Name: AdditionalReasoning
        * * Display Name: Additional Reasoning
        * * SQL Data Type: nvarchar(MAX)`),
    SubmittedAt: z.date().nullable().describe(`
        * * Field Name: SubmittedAt
        * * Display Name: Submitted At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Season: z.string().describe(`
        * * Field Name: Season
        * * Display Name: Season
        * * SQL Data Type: nvarchar(200)`),
    Presentation: z.string().describe(`
        * * Field Name: Presentation
        * * Display Name: Presentation
        * * SQL Data Type: nvarchar(500)`),
    NominatorUser: z.string().describe(`
        * * Field Name: NominatorUser
        * * Display Name: Nominator User
        * * SQL Data Type: nvarchar(100)`),
});

export type FeedbackPlatformNominationEntityType = z.infer<typeof FeedbackPlatformNominationSchema>;

/**
 * zod schema definition for the entity Notification Types
 */
export const FeedbackPlatformNotificationTypeSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    Name: z.string().describe(`
        * * Field Name: Name
        * * Display Name: Name
        * * SQL Data Type: nvarchar(100)
        * * Description: Name of the notification type (e.g., "ReviewAssigned", "ReviewCompleted").`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Description of when this notification is sent.`),
    DefaultEnabled: z.boolean().describe(`
        * * Field Name: DefaultEnabled
        * * Display Name: Default Enabled
        * * SQL Data Type: bit
        * * Default Value: 1
        * * Description: Whether this notification type is enabled by default for new users.`),
    Status: z.union([z.literal('Active'), z.literal('Canceled'), z.literal('Pending')]).describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: nvarchar(20)
        * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Canceled
    *   * Pending
        * * Description: Current status of the notification type.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type FeedbackPlatformNotificationTypeEntityType = z.infer<typeof FeedbackPlatformNotificationTypeSchema>;

/**
 * zod schema definition for the entity Notifications
 */
export const FeedbackPlatformNotificationSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    RecipientUserID: z.string().describe(`
        * * Field Name: RecipientUserID
        * * Display Name: Recipient User ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Users (vwUsers.ID)`),
    NotificationTypeID: z.string().describe(`
        * * Field Name: NotificationTypeID
        * * Display Name: Notification Type ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Notification Types (vwNotificationTypes.ID)`),
    Title: z.string().describe(`
        * * Field Name: Title
        * * Display Name: Title
        * * SQL Data Type: nvarchar(255)
        * * Description: Title or subject of the notification.`),
    Message: z.string().describe(`
        * * Field Name: Message
        * * Display Name: Message
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Content of the notification message.`),
    RelatedEntityType: z.union([z.literal('Conversation'), z.literal('Presentation'), z.literal('Review'), z.literal('ReviewAssignment')]).nullable().describe(`
        * * Field Name: RelatedEntityType
        * * Display Name: Related Entity Type
        * * SQL Data Type: nvarchar(50)
    * * Value List Type: List
    * * Possible Values 
    *   * Conversation
    *   * Presentation
    *   * Review
    *   * ReviewAssignment
        * * Description: Type of entity this notification relates to.`),
    RelatedEntityID: z.string().nullable().describe(`
        * * Field Name: RelatedEntityID
        * * Display Name: Related Entity ID
        * * SQL Data Type: uniqueidentifier
        * * Description: ID of the related entity.`),
    SentDate: z.date().nullable().describe(`
        * * Field Name: SentDate
        * * Display Name: Sent Date
        * * SQL Data Type: datetimeoffset
        * * Description: When the notification was sent to the user.`),
    ReadDate: z.date().nullable().describe(`
        * * Field Name: ReadDate
        * * Display Name: Read Date
        * * SQL Data Type: datetimeoffset
        * * Description: When the user read/viewed the notification.`),
    Status: z.union([z.literal('Canceled'), z.literal('Failed'), z.literal('Pending'), z.literal('Read'), z.literal('Sent')]).describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: nvarchar(20)
        * * Default Value: Pending
    * * Value List Type: List
    * * Possible Values 
    *   * Canceled
    *   * Failed
    *   * Pending
    *   * Read
    *   * Sent
        * * Description: Current delivery status of the notification.`),
    DeliveryMethod: z.union([z.literal('Both'), z.literal('Email'), z.literal('InApp')]).nullable().describe(`
        * * Field Name: DeliveryMethod
        * * Display Name: Delivery Method
        * * SQL Data Type: nvarchar(20)
    * * Value List Type: List
    * * Possible Values 
    *   * Both
    *   * Email
    *   * InApp
        * * Description: Method used to deliver the notification.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    RecipientUser: z.string().describe(`
        * * Field Name: RecipientUser
        * * Display Name: Recipient User
        * * SQL Data Type: nvarchar(100)`),
    NotificationType: z.string().describe(`
        * * Field Name: NotificationType
        * * Display Name: Notification Type
        * * SQL Data Type: nvarchar(100)`),
});

export type FeedbackPlatformNotificationEntityType = z.infer<typeof FeedbackPlatformNotificationSchema>;

/**
 * zod schema definition for the entity Other Names
 */
export const ORCIDOtherNameSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ORCIDRecordID: z.string().describe(`
        * * Field Name: ORCIDRecordID
        * * Display Name: ORCID Record ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Records (vwRecords.ID)`),
    PutCode: z.number().describe(`
        * * Field Name: PutCode
        * * Display Name: Put Code
        * * SQL Data Type: bigint`),
    Content: z.string().nullable().describe(`
        * * Field Name: Content
        * * Display Name: Content
        * * SQL Data Type: nvarchar(500)`),
    DisplayIndex: z.number().nullable().describe(`
        * * Field Name: DisplayIndex
        * * Display Name: Display Index
        * * SQL Data Type: int`),
    Visibility: z.string().nullable().describe(`
        * * Field Name: Visibility
        * * Display Name: Visibility
        * * SQL Data Type: nvarchar(20)`),
    SourceName: z.string().nullable().describe(`
        * * Field Name: SourceName
        * * Display Name: Source Name
        * * SQL Data Type: nvarchar(255)`),
    SourceOrcid: z.string().nullable().describe(`
        * * Field Name: SourceOrcid
        * * Display Name: Source Orcid
        * * SQL Data Type: nvarchar(19)`),
    OrcidCreatedDate: z.date().nullable().describe(`
        * * Field Name: OrcidCreatedDate
        * * Display Name: Orcid Created Date
        * * SQL Data Type: datetimeoffset`),
    OrcidLastModifiedDate: z.date().nullable().describe(`
        * * Field Name: OrcidLastModifiedDate
        * * Display Name: Orcid Last Modified Date
        * * SQL Data Type: datetimeoffset`),
    RawJSON: z.string().nullable().describe(`
        * * Field Name: RawJSON
        * * Display Name: Raw JSON
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncStatus: z.string().describe(`
        * * Field Name: __mj_integration_SyncStatus
        * * Display Name: Mj Integration Sync Status
        * * SQL Data Type: nvarchar(50)
        * * Default Value: Active`),
    __mj_integration_LastSyncedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedAt
        * * Display Name: Mj Integration Last Synced At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastSyncedSnapshot: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedSnapshot
        * * Display Name: Mj Integration Last Synced Snapshot
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncMessage: z.string().nullable().describe(`
        * * Field Name: __mj_integration_SyncMessage
        * * Display Name: Mj Integration Sync Message
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ContentHash: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ContentHash
        * * Display Name: Mj Integration Content Hash
        * * SQL Data Type: nvarchar(64)`),
    __mj_integration_CustomOverflow: z.string().nullable().describe(`
        * * Field Name: __mj_integration_CustomOverflow
        * * Display Name: Mj Integration Custom Overflow
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ExternalVersion: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ExternalVersion
        * * Display Name: Mj Integration External Version
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastSeenModifiedValue: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSeenModifiedValue
        * * Display Name: Mj Integration Last Seen Modified Value
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastReconciledAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastReconciledAt
        * * Display Name: Mj Integration Last Reconciled At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastWriterDirection: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastWriterDirection
        * * Display Name: Mj Integration Last Writer Direction
        * * SQL Data Type: nvarchar(10)`),
    __mj_integration_IsTombstoned: z.boolean().describe(`
        * * Field Name: __mj_integration_IsTombstoned
        * * Display Name: Mj Integration Is Tombstoned
        * * SQL Data Type: bit
        * * Default Value: 0`),
    __mj_integration_DeletedDetectedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_DeletedDetectedAt
        * * Display Name: Mj Integration Deleted Detected At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type ORCIDOtherNameEntityType = z.infer<typeof ORCIDOtherNameSchema>;

/**
 * zod schema definition for the entity Participants
 */
export const FeedbackPlatformParticipantSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    SeasonID: z.string().describe(`
        * * Field Name: SeasonID
        * * Display Name: Season ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Seasons (vwSeasons.ID)`),
    UserID: z.string().describe(`
        * * Field Name: UserID
        * * Display Name: User ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Users (vwUsers.ID)`),
    Institution: z.string().nullable().describe(`
        * * Field Name: Institution
        * * Display Name: Institution
        * * SQL Data Type: nvarchar(255)`),
    CareerStage: z.union([z.literal('Early career professional'), z.literal('Master’s student'), z.literal('PhD student'), z.literal('Postdoctoral researcher'), z.literal('Undergraduate')]).nullable().describe(`
        * * Field Name: CareerStage
        * * Display Name: Career Stage
        * * SQL Data Type: nvarchar(40)
    * * Value List Type: List
    * * Possible Values 
    *   * Early career professional
    *   * Master’s student
    *   * PhD student
    *   * Postdoctoral researcher
    *   * Undergraduate`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Season: z.string().describe(`
        * * Field Name: Season
        * * Display Name: Season
        * * SQL Data Type: nvarchar(200)`),
    User: z.string().describe(`
        * * Field Name: User
        * * Display Name: User
        * * SQL Data Type: nvarchar(100)`),
});

export type FeedbackPlatformParticipantEntityType = z.infer<typeof FeedbackPlatformParticipantSchema>;

/**
 * zod schema definition for the entity Partner Token Users
 */
export const MAPSPartnerTokenUserSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    PartnerTokenID: z.string().describe(`
        * * Field Name: PartnerTokenID
        * * Display Name: Partner Token ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Partner Tokens (vwPartnerTokens.ID)
        * * Description: Foreign key to MAPS.PartnerToken. The partner-scoping key (tokenId) the resolved MJ user's MAPS session is bound to; its CompanyID supplies the session companyId.`),
    Email: z.string().describe(`
        * * Field Name: Email
        * * Display Name: Email
        * * SQL Data Type: nvarchar(80)
        * * Description: Magic-link recipient / staff email, stored lowercased. Unique. The server lowercases the inbound MJ email before lookup so matching is case-insensitive.`),
    UserID: z.string().nullable().describe(`
        * * Field Name: UserID
        * * Display Name: User ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Users (vwUsers.ID)
        * * Description: Optional FK to __mj.User. Nullable so an admin can pre-provision the mapping from just the invited email before the user record exists; populated once the invitee has signed in.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    User: z.string().nullable().describe(`
        * * Field Name: User
        * * Display Name: User
        * * SQL Data Type: nvarchar(100)`),
});

export type MAPSPartnerTokenUserEntityType = z.infer<typeof MAPSPartnerTokenUserSchema>;

/**
 * zod schema definition for the entity Partner Tokens
 */
export const MAPSPartnerTokenSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    CompanyID: z.string().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
        * * Description: Foreign key to __mj.Company. Single source of truth for partner display name and metadata.`),
    TokenHash: z.string().describe(`
        * * Field Name: TokenHash
        * * Display Name: Token Hash
        * * SQL Data Type: char(64)
        * * Description: sha256 hex of the plaintext magic key. Lookups hash the inbound key and compare.`),
    TokenLabel: z.string().nullable().describe(`
        * * Field Name: TokenLabel
        * * Display Name: Token Label
        * * SQL Data Type: nvarchar(40)
        * * Description: Optional human-readable label (e.g., "acme-q3-2026") for ops/support identification.`),
    IsAdmin: z.boolean().describe(`
        * * Field Name: IsAdmin
        * * Display Name: Is Admin
        * * SQL Data Type: bit
        * * Default Value: 0
        * * Description: 1 = AGU-staff admin token (gates /admin/dashboard via requireMapsAdmin). 0 = standard partner token.`),
    IssuedAt: z.date().describe(`
        * * Field Name: IssuedAt
        * * Display Name: Issued At
        * * SQL Data Type: datetimeoffset
        * * Default Value: sysdatetimeoffset()
        * * Description: When the token was issued.`),
    ExpiresAt: z.date().nullable().describe(`
        * * Field Name: ExpiresAt
        * * Display Name: Expires At
        * * SQL Data Type: datetimeoffset
        * * Description: Optional expiry. NULL means the token never expires; resolvers also check RevokedAt independently.`),
    RevokedAt: z.date().nullable().describe(`
        * * Field Name: RevokedAt
        * * Display Name: Revoked At
        * * SQL Data Type: datetimeoffset
        * * Description: Set when the token has been revoked. Resolved sessions are rejected immediately when this is non-NULL.`),
    LastUsedAt: z.date().nullable().describe(`
        * * Field Name: LastUsedAt
        * * Display Name: Last Used At
        * * SQL Data Type: datetimeoffset
        * * Description: Last time mapsAccessValidate accepted this token. Touched on every successful magic-link redemption.`),
    SoftSearchCapPerMonth: z.number().nullable().describe(`
        * * Field Name: SoftSearchCapPerMonth
        * * Display Name: Soft Search Cap Per Month
        * * SQL Data Type: int
        * * Description: Soft per-month search cap. NULL = uncapped. Exceeding the cap logs a warning but does not block. Ignored for admin tokens.`),
    Notes: z.string().nullable().describe(`
        * * Field Name: Notes
        * * Display Name: Notes
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Free-form operational notes (e.g., issuance context, support handoff). Not surfaced to partners.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Company: z.string().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type MAPSPartnerTokenEntityType = z.infer<typeof MAPSPartnerTokenSchema>;

/**
 * zod schema definition for the entity Patents
 */
export const CoreSignalPatentSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ProfileID: z.string().describe(`
        * * Field Name: ProfileID
        * * Display Name: Profile ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Profiles (vwProfiles.ID)`),
    OrderInProfile: z.number().describe(`
        * * Field Name: OrderInProfile
        * * Display Name: Order In Profile
        * * SQL Data Type: int`),
    Title: z.string().nullable().describe(`
        * * Field Name: Title
        * * Display Name: Title
        * * SQL Data Type: nvarchar(1000)`),
    PatentNumber: z.string().nullable().describe(`
        * * Field Name: PatentNumber
        * * Display Name: Patent Number
        * * SQL Data Type: nvarchar(255)`),
    Status: z.string().nullable().describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: nvarchar(100)`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(MAX)`),
    PatentUrl: z.string().nullable().describe(`
        * * Field Name: PatentUrl
        * * Display Name: Patent Url
        * * SQL Data Type: nvarchar(2000)`),
    DateYear: z.number().nullable().describe(`
        * * Field Name: DateYear
        * * Display Name: Date Year
        * * SQL Data Type: smallint`),
    DateMonth: z.number().nullable().describe(`
        * * Field Name: DateMonth
        * * Display Name: Date Month
        * * SQL Data Type: tinyint`),
    __mj_integration_SyncStatus: z.string().describe(`
        * * Field Name: __mj_integration_SyncStatus
        * * Display Name: Mj Integration Sync Status
        * * SQL Data Type: nvarchar(50)
        * * Default Value: Active`),
    __mj_integration_LastSyncedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedAt
        * * Display Name: Mj Integration Last Synced At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastSyncedSnapshot: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedSnapshot
        * * Display Name: Mj Integration Last Synced Snapshot
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncMessage: z.string().nullable().describe(`
        * * Field Name: __mj_integration_SyncMessage
        * * Display Name: Mj Integration Sync Message
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ContentHash: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ContentHash
        * * Display Name: Mj Integration Content Hash
        * * SQL Data Type: nvarchar(64)`),
    __mj_integration_CustomOverflow: z.string().nullable().describe(`
        * * Field Name: __mj_integration_CustomOverflow
        * * Display Name: Mj Integration Custom Overflow
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ExternalVersion: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ExternalVersion
        * * Display Name: Mj Integration External Version
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastSeenModifiedValue: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSeenModifiedValue
        * * Display Name: Mj Integration Last Seen Modified Value
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastReconciledAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastReconciledAt
        * * Display Name: Mj Integration Last Reconciled At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastWriterDirection: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastWriterDirection
        * * Display Name: Mj Integration Last Writer Direction
        * * SQL Data Type: nvarchar(10)`),
    __mj_integration_IsTombstoned: z.boolean().describe(`
        * * Field Name: __mj_integration_IsTombstoned
        * * Display Name: Mj Integration Is Tombstoned
        * * SQL Data Type: bit
        * * Default Value: 0`),
    __mj_integration_DeletedDetectedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_DeletedDetectedAt
        * * Display Name: Mj Integration Deleted Detected At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type CoreSignalPatentEntityType = z.infer<typeof CoreSignalPatentSchema>;

/**
 * zod schema definition for the entity Peer Reviews
 */
export const ORCIDPeerReviewSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ORCIDRecordID: z.string().describe(`
        * * Field Name: ORCIDRecordID
        * * Display Name: ORCID Record ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Records (vwRecords.ID)`),
    PutCode: z.number().describe(`
        * * Field Name: PutCode
        * * Display Name: Put Code
        * * SQL Data Type: bigint`),
    ReviewerRole: z.string().nullable().describe(`
        * * Field Name: ReviewerRole
        * * Display Name: Reviewer Role
        * * SQL Data Type: nvarchar(50)`),
    ReviewType: z.string().nullable().describe(`
        * * Field Name: ReviewType
        * * Display Name: Review Type
        * * SQL Data Type: nvarchar(50)`),
    ReviewGroupID: z.string().nullable().describe(`
        * * Field Name: ReviewGroupID
        * * Display Name: Review Group ID
        * * SQL Data Type: nvarchar(255)`),
    ReviewUrl: z.string().nullable().describe(`
        * * Field Name: ReviewUrl
        * * Display Name: Review Url
        * * SQL Data Type: nvarchar(2000)`),
    CompletionYear: z.number().nullable().describe(`
        * * Field Name: CompletionYear
        * * Display Name: Completion Year
        * * SQL Data Type: smallint`),
    CompletionMonth: z.number().nullable().describe(`
        * * Field Name: CompletionMonth
        * * Display Name: Completion Month
        * * SQL Data Type: tinyint`),
    CompletionDay: z.number().nullable().describe(`
        * * Field Name: CompletionDay
        * * Display Name: Completion Day
        * * SQL Data Type: tinyint`),
    ConveningOrgName: z.string().nullable().describe(`
        * * Field Name: ConveningOrgName
        * * Display Name: Convening Org Name
        * * SQL Data Type: nvarchar(500)`),
    ConveningOrgCity: z.string().nullable().describe(`
        * * Field Name: ConveningOrgCity
        * * Display Name: Convening Org City
        * * SQL Data Type: nvarchar(255)`),
    ConveningOrgRegion: z.string().nullable().describe(`
        * * Field Name: ConveningOrgRegion
        * * Display Name: Convening Org Region
        * * SQL Data Type: nvarchar(255)`),
    ConveningOrgCountry: z.string().nullable().describe(`
        * * Field Name: ConveningOrgCountry
        * * Display Name: Convening Org Country
        * * SQL Data Type: nvarchar(10)`),
    ConveningOrgDisambiguatedID: z.string().nullable().describe(`
        * * Field Name: ConveningOrgDisambiguatedID
        * * Display Name: Convening Org Disambiguated ID
        * * SQL Data Type: nvarchar(255)`),
    ConveningOrgDisambiguationSource: z.string().nullable().describe(`
        * * Field Name: ConveningOrgDisambiguationSource
        * * Display Name: Convening Org Disambiguation Source
        * * SQL Data Type: nvarchar(50)`),
    SubjectType: z.string().nullable().describe(`
        * * Field Name: SubjectType
        * * Display Name: Subject Type
        * * SQL Data Type: nvarchar(100)`),
    SubjectName: z.string().nullable().describe(`
        * * Field Name: SubjectName
        * * Display Name: Subject Name
        * * SQL Data Type: nvarchar(2000)`),
    SubjectContainerName: z.string().nullable().describe(`
        * * Field Name: SubjectContainerName
        * * Display Name: Subject Container Name
        * * SQL Data Type: nvarchar(1000)`),
    SubjectUrl: z.string().nullable().describe(`
        * * Field Name: SubjectUrl
        * * Display Name: Subject Url
        * * SQL Data Type: nvarchar(2000)`),
    SubjectExternalIDType: z.string().nullable().describe(`
        * * Field Name: SubjectExternalIDType
        * * Display Name: Subject External ID Type
        * * SQL Data Type: nvarchar(100)`),
    SubjectExternalIDValue: z.string().nullable().describe(`
        * * Field Name: SubjectExternalIDValue
        * * Display Name: Subject External ID Value
        * * SQL Data Type: nvarchar(500)`),
    Visibility: z.string().nullable().describe(`
        * * Field Name: Visibility
        * * Display Name: Visibility
        * * SQL Data Type: nvarchar(20)`),
    SourceName: z.string().nullable().describe(`
        * * Field Name: SourceName
        * * Display Name: Source Name
        * * SQL Data Type: nvarchar(255)`),
    SourceOrcid: z.string().nullable().describe(`
        * * Field Name: SourceOrcid
        * * Display Name: Source Orcid
        * * SQL Data Type: nvarchar(19)`),
    OrcidCreatedDate: z.date().nullable().describe(`
        * * Field Name: OrcidCreatedDate
        * * Display Name: Orcid Created Date
        * * SQL Data Type: datetimeoffset`),
    OrcidLastModifiedDate: z.date().nullable().describe(`
        * * Field Name: OrcidLastModifiedDate
        * * Display Name: Orcid Last Modified Date
        * * SQL Data Type: datetimeoffset`),
    RawJSON: z.string().nullable().describe(`
        * * Field Name: RawJSON
        * * Display Name: Raw JSON
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncStatus: z.string().describe(`
        * * Field Name: __mj_integration_SyncStatus
        * * Display Name: Mj Integration Sync Status
        * * SQL Data Type: nvarchar(50)
        * * Default Value: Active`),
    __mj_integration_LastSyncedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedAt
        * * Display Name: Mj Integration Last Synced At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastSyncedSnapshot: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedSnapshot
        * * Display Name: Mj Integration Last Synced Snapshot
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncMessage: z.string().nullable().describe(`
        * * Field Name: __mj_integration_SyncMessage
        * * Display Name: Mj Integration Sync Message
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ContentHash: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ContentHash
        * * Display Name: Mj Integration Content Hash
        * * SQL Data Type: nvarchar(64)`),
    __mj_integration_CustomOverflow: z.string().nullable().describe(`
        * * Field Name: __mj_integration_CustomOverflow
        * * Display Name: Mj Integration Custom Overflow
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ExternalVersion: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ExternalVersion
        * * Display Name: Mj Integration External Version
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastSeenModifiedValue: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSeenModifiedValue
        * * Display Name: Mj Integration Last Seen Modified Value
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastReconciledAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastReconciledAt
        * * Display Name: Mj Integration Last Reconciled At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastWriterDirection: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastWriterDirection
        * * Display Name: Mj Integration Last Writer Direction
        * * SQL Data Type: nvarchar(10)`),
    __mj_integration_IsTombstoned: z.boolean().describe(`
        * * Field Name: __mj_integration_IsTombstoned
        * * Display Name: Mj Integration Is Tombstoned
        * * SQL Data Type: bit
        * * Default Value: 0`),
    __mj_integration_DeletedDetectedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_DeletedDetectedAt
        * * Display Name: Mj Integration Deleted Detected At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type ORCIDPeerReviewEntityType = z.infer<typeof ORCIDPeerReviewSchema>;

/**
 * zod schema definition for the entity People
 */
export const MAPSPersonSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    AGUPersonID: z.string().describe(`
        * * Field Name: AGUPersonID
        * * Display Name: AGU Person ID
        * * SQL Data Type: uniqueidentifier
        * * Description: AGU-assigned identifier for the person. This is the same ID used by the AGU personalization algorithm and is the join key back to AGU source systems.`),
    FirstName: z.string().describe(`
        * * Field Name: FirstName
        * * Display Name: First Name
        * * SQL Data Type: nvarchar(75)
        * * Description: First name of the person.`),
    LastName: z.string().describe(`
        * * Field Name: LastName
        * * Display Name: Last Name
        * * SQL Data Type: nvarchar(75)
        * * Description: Last name of the person.`),
    Bio: z.string().nullable().describe(`
        * * Field Name: Bio
        * * Display Name: Bio
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Self-provided biography text. May be empty.`),
    JobSector: z.string().nullable().describe(`
        * * Field Name: JobSector
        * * Display Name: Job Sector
        * * SQL Data Type: nvarchar(75)
        * * Description: Self-reported job sector (e.g., Corporate, Non-Profit, University/College/Higher Education). May be stale - collected at signup and rarely refreshed.`),
    JobFunction: z.string().nullable().describe(`
        * * Field Name: JobFunction
        * * Display Name: Job Function
        * * SQL Data Type: nvarchar(50)
        * * Description: Self-reported job function (e.g., Applied Research, Teaching, Management). May be stale - collected at signup and rarely refreshed.`),
    Email: z.string().describe(`
        * * Field Name: Email
        * * Display Name: Email
        * * SQL Data Type: nvarchar(80)
        * * Description: Validated email address. A valid email is one of the inclusion criteria for the Talent Acquisition tool.`),
    CurrentCareerStage: z.union([z.literal('Early Career'), z.literal('Experienced'), z.literal('Mid-Career'), z.literal('Retired'), z.literal('Student')]).nullable().describe(`
        * * Field Name: CurrentCareerStage
        * * Display Name: Current Career Stage
        * * SQL Data Type: nvarchar(20)
    * * Value List Type: List
    * * Possible Values 
    *   * Early Career
    *   * Experienced
    *   * Mid-Career
    *   * Retired
    *   * Student
        * * Description: Current career stage. Calculated from the highest-degree date when available, otherwise self-reported.`),
    ReviewCountSince2021: z.number().describe(`
        * * Field Name: ReviewCountSince2021
        * * Display Name: Review Count Since 2021
        * * SQL Data Type: int
        * * Default Value: 0
        * * Description: Number of AGU journal reviews completed by this person since 2021.`),
    LastContentYear: z.number().nullable().describe(`
        * * Field Name: LastContentYear
        * * Display Name: Last Content Year
        * * SQL Data Type: int
        * * Description: Year of the most recent AGU content authored or contributed to by this person. Intended as a recency proxy for internal use only - not to be used as a feature in the talent acquisition algorithm.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    LinkedInUrl: z.string().nullable().describe(`
        * * Field Name: LinkedInUrl
        * * Display Name: Linked In Url
        * * SQL Data Type: nvarchar(255)
        * * Description: AGU member's LinkedIn profile URL (e.g., https://www.linkedin.com/in/...). NULL when not provided in the personalization feed. Per AGU guidance (6-8 client meeting), surfaced as a small icon when present; absence is not flagged on the card.`),
    OrcID: z.string().nullable().describe(`
        * * Field Name: OrcID
        * * Display Name: Orc ID
        * * SQL Data Type: nvarchar(50)
        * * Description: AGU member's ORCID iD (e.g., 0000-0002-1825-0097, or a full https://orcid.org/... URL). NULL when not provided in the personalization feed. Surfaced as a small icon when present; absence is not flagged on the card.`),
});

export type MAPSPersonEntityType = z.infer<typeof MAPSPersonSchema>;

/**
 * zod schema definition for the entity Person Honor Receiveds
 */
export const MAPSPersonHonorReceivedSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    PersonID: z.string().describe(`
        * * Field Name: PersonID
        * * Display Name: Person ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: People (vwPeople.ID)
        * * Description: References the person who received the honor.`),
    HonorType: z.string().describe(`
        * * Field Name: HonorType
        * * Display Name: Honor Type
        * * SQL Data Type: nvarchar(100)
        * * Description: Category of honor (e.g., Fellow, Medal, Award).`),
    HonorName: z.string().describe(`
        * * Field Name: HonorName
        * * Display Name: Honor Name
        * * SQL Data Type: nvarchar(255)
        * * Description: Specific name of the honor.`),
    Year: z.number().nullable().describe(`
        * * Field Name: Year
        * * Display Name: Year
        * * SQL Data Type: int
        * * Description: Year the honor was received.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type MAPSPersonHonorReceivedEntityType = z.infer<typeof MAPSPersonHonorReceivedSchema>;

/**
 * zod schema definition for the entity Person Jobs
 */
export const MAPSPersonJobSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    PersonID: z.string().describe(`
        * * Field Name: PersonID
        * * Display Name: Person ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: People (vwPeople.ID)
        * * Description: References the person this job belongs to.`),
    Organization: z.string().describe(`
        * * Field Name: Organization
        * * Display Name: Organization
        * * SQL Data Type: nvarchar(150)
        * * Description: Name of the employing organization.`),
    JobTitle: z.string().nullable().describe(`
        * * Field Name: JobTitle
        * * Display Name: Job Title
        * * SQL Data Type: nvarchar(150)
        * * Description: Job title held at the organization. May be missing in source data.`),
    StartDate: z.date().nullable().describe(`
        * * Field Name: StartDate
        * * Display Name: Start Date
        * * SQL Data Type: date
        * * Description: Start date of the job. NULL when source data reported "Unknown".`),
    EndDate: z.date().nullable().describe(`
        * * Field Name: EndDate
        * * Display Name: End Date
        * * SQL Data Type: date
        * * Description: End date of the job. NULL for current jobs (Status = Current) or when otherwise unknown.`),
    Status: z.union([z.literal('Current'), z.literal('Past'), z.literal('Unknown')]).describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: nvarchar(20)
        * * Default Value: Past
    * * Value List Type: List
    * * Possible Values 
    *   * Current
    *   * Past
    *   * Unknown
        * * Description: Status of this job: Current (still held, source said "Present"), Past (ended), or Unknown.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type MAPSPersonJobEntityType = z.infer<typeof MAPSPersonJobSchema>;

/**
 * zod schema definition for the entity Person Qualification Facts
 */
export const MAPSPersonQualificationFactsSchema = z.object({
    AGUPersonID: z.string().describe(`
        * * Field Name: AGUPersonID
        * * Display Name: AGU Person ID
        * * SQL Data Type: uniqueidentifier`),
    DegreeLevel: z.number().nullable().describe(`
        * * Field Name: DegreeLevel
        * * Display Name: Degree Level
        * * SQL Data Type: tinyint`),
    MaxDegreeYear: z.number().nullable().describe(`
        * * Field Name: MaxDegreeYear
        * * Display Name: Max Degree Year
        * * SQL Data Type: int`),
    YearsExperience: z.number().nullable().describe(`
        * * Field Name: YearsExperience
        * * Display Name: Years Experience
        * * SQL Data Type: int`),
    HasFunding: z.boolean().describe(`
        * * Field Name: HasFunding
        * * Display Name: Has Funding
        * * SQL Data Type: bit
        * * Default Value: 0`),
    HasPIFunding: z.boolean().describe(`
        * * Field Name: HasPIFunding
        * * Display Name: Has PI Funding
        * * SQL Data Type: bit
        * * Default Value: 0`),
    HasGrantFunding: z.boolean().describe(`
        * * Field Name: HasGrantFunding
        * * Display Name: Has Grant Funding
        * * SQL Data Type: bit
        * * Default Value: 0`),
    HasActiveFunding: z.boolean().describe(`
        * * Field Name: HasActiveFunding
        * * Display Name: Has Active Funding
        * * SQL Data Type: bit
        * * Default Value: 0`),
    DistinctionCount: z.number().describe(`
        * * Field Name: DistinctionCount
        * * Display Name: Distinction Count
        * * SQL Data Type: int
        * * Default Value: 0`),
    RefreshedAt: z.date().describe(`
        * * Field Name: RefreshedAt
        * * Display Name: Refreshed At
        * * SQL Data Type: datetime2
        * * Default Value: sysutcdatetime()`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type MAPSPersonQualificationFactsEntityType = z.infer<typeof MAPSPersonQualificationFactsSchema>;

/**
 * zod schema definition for the entity Person Volunteer Activities
 */
export const MAPSPersonVolunteerActivitySchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    PersonID: z.string().describe(`
        * * Field Name: PersonID
        * * Display Name: Person ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: People (vwPeople.ID)
        * * Description: References the person this volunteer activity belongs to.`),
    RoleDescription: z.string().describe(`
        * * Field Name: RoleDescription
        * * Display Name: Role Description
        * * SQL Data Type: nvarchar(255)
        * * Description: Description of the volunteer role (committee name or journal/editor role).`),
    YearStart: z.number().nullable().describe(`
        * * Field Name: YearStart
        * * Display Name: Year Start
        * * SQL Data Type: int
        * * Description: Year the volunteer service began. NULL when source data was missing (source sentinel "0" mapped to NULL on ingest).`),
    YearEnd: z.number().nullable().describe(`
        * * Field Name: YearEnd
        * * Display Name: Year End
        * * SQL Data Type: int
        * * Description: Year the volunteer service ended. NULL when source data was missing or service is ongoing.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type MAPSPersonVolunteerActivityEntityType = z.infer<typeof MAPSPersonVolunteerActivitySchema>;

/**
 * zod schema definition for the entity Presentation Categories
 */
export const FeedbackPlatformPresentationCategorySchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    Name: z.string().describe(`
        * * Field Name: Name
        * * Display Name: Name
        * * SQL Data Type: nvarchar(255)
        * * Description: Name of the presentation category.`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Detailed description of the category.`),
    Status: z.union([z.literal('Active'), z.literal('Canceled'), z.literal('Pending')]).describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: nvarchar(20)
        * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Canceled
    *   * Pending
        * * Description: Current status of the category.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type FeedbackPlatformPresentationCategoryEntityType = z.infer<typeof FeedbackPlatformPresentationCategorySchema>;

/**
 * zod schema definition for the entity Presentations
 */
export const FeedbackPlatformPresentationSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    Title: z.string().describe(`
        * * Field Name: Title
        * * Display Name: Title
        * * SQL Data Type: nvarchar(500)
        * * Description: Title of the presentation.`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Detailed description of the presentation content.`),
    Abstract: z.string().nullable().describe(`
        * * Field Name: Abstract
        * * Display Name: Abstract
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Academic abstract of the presentation.`),
    PresenterUserID: z.string().describe(`
        * * Field Name: PresenterUserID
        * * Display Name: Presenter User ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Users (vwUsers.ID)`),
    PresentationCategoryID: z.string().describe(`
        * * Field Name: PresentationCategoryID
        * * Display Name: Presentation Category ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Presentation Categories (vwPresentationCategories.ID)`),
    SubmissionDate: z.date().nullable().describe(`
        * * Field Name: SubmissionDate
        * * Display Name: Submission Date
        * * SQL Data Type: datetimeoffset
        * * Description: When the presentation was submitted for review.`),
    ConferenceDate: z.date().nullable().describe(`
        * * Field Name: ConferenceDate
        * * Display Name: Conference Date
        * * SQL Data Type: datetimeoffset
        * * Description: Scheduled conference presentation date.`),
    FileURL: z.string().nullable().describe(`
        * * Field Name: FileURL
        * * Display Name: File URL
        * * SQL Data Type: nvarchar(1024)
        * * Description: URL to the presentation file (PDF, PPTX, DOCX, etc.).`),
    FileName: z.string().nullable().describe(`
        * * Field Name: FileName
        * * Display Name: File Name
        * * SQL Data Type: nvarchar(255)
        * * Description: Original filename of the uploaded presentation.`),
    FileSize: z.number().nullable().describe(`
        * * Field Name: FileSize
        * * Display Name: File Size
        * * SQL Data Type: bigint
        * * Description: Size of the presentation file in bytes.`),
    FileMimeType: z.string().nullable().describe(`
        * * Field Name: FileMimeType
        * * Display Name: File Mime Type
        * * SQL Data Type: nvarchar(100)
        * * Description: MIME type of the presentation file.`),
    Status: z.union([z.literal('Archived'), z.literal('Canceled'), z.literal('Draft'), z.literal('ReviewCompleted'), z.literal('Submitted'), z.literal('UnderReview')]).describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: nvarchar(20)
        * * Default Value: Draft
    * * Value List Type: List
    * * Possible Values 
    *   * Archived
    *   * Canceled
    *   * Draft
    *   * ReviewCompleted
    *   * Submitted
    *   * UnderReview
        * * Description: Current status of the presentation in the review workflow.`),
    ReviewDeadline: z.date().nullable().describe(`
        * * Field Name: ReviewDeadline
        * * Display Name: Review Deadline
        * * SQL Data Type: datetimeoffset
        * * Description: Deadline for reviewers to complete their reviews.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    SeasonID: z.string().nullable().describe(`
        * * Field Name: SeasonID
        * * Display Name: Season ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Seasons (vwSeasons.ID)`),
    SectionID: z.string().nullable().describe(`
        * * Field Name: SectionID
        * * Display Name: Section ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Sections (vwSections.ID)`),
    SessionID: z.string().nullable().describe(`
        * * Field Name: SessionID
        * * Display Name: Session ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Sessions (vwSessions.ID)`),
    PresentationCode: z.string().nullable().describe(`
        * * Field Name: PresentationCode
        * * Display Name: Presentation Code
        * * SQL Data Type: nvarchar(20)`),
    Format: z.union([z.literal('Oral'), z.literal('Poster')]).nullable().describe(`
        * * Field Name: Format
        * * Display Name: Format
        * * SQL Data Type: nvarchar(10)
    * * Value List Type: List
    * * Possible Values 
    *   * Oral
    *   * Poster`),
    PresenterUser: z.string().describe(`
        * * Field Name: PresenterUser
        * * Display Name: Presenter User
        * * SQL Data Type: nvarchar(100)`),
    PresentationCategory: z.string().describe(`
        * * Field Name: PresentationCategory
        * * Display Name: Presentation Category
        * * SQL Data Type: nvarchar(255)`),
    Season: z.string().nullable().describe(`
        * * Field Name: Season
        * * Display Name: Season
        * * SQL Data Type: nvarchar(200)`),
    Section: z.string().nullable().describe(`
        * * Field Name: Section
        * * Display Name: Section
        * * SQL Data Type: nvarchar(200)`),
    Session: z.string().nullable().describe(`
        * * Field Name: Session
        * * Display Name: Session
        * * SQL Data Type: nvarchar(500)`),
});

export type FeedbackPlatformPresentationEntityType = z.infer<typeof FeedbackPlatformPresentationSchema>;

/**
 * zod schema definition for the entity Profile Matches
 */
export const CoreSignalProfileMatchSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ProfileID: z.string().describe(`
        * * Field Name: ProfileID
        * * Display Name: Profile ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Profiles (vwProfiles.ID)`),
    AGUPersonID: z.string().describe(`
        * * Field Name: AGUPersonID
        * * Display Name: AGU Person ID
        * * SQL Data Type: uniqueidentifier
        * * Description: The delivery CSV's Customer ID (= MAPS.Person.AGUPersonID). Kept even when no MAPS.Person row exists yet (MatchStatus=PersonNotFound; 800 in the initial delivery) so later ingests can resolve it.`),
    PersonID: z.string().nullable().describe(`
        * * Field Name: PersonID
        * * Display Name: Person ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: People (vwPeople.ID)`),
    MatchCardinality: z.number().describe(`
        * * Field Name: MatchCardinality
        * * Display Name: Match Cardinality
        * * SQL Data Type: int
        * * Default Value: 1
        * * Description: Profiles matched to the same person in the delivery (1 = clean 1:1). High cardinality = name-collision shotgun from the name+org reverse match.`),
    MatchStatus: z.union([z.literal('Ambiguous'), z.literal('Confirmed'), z.literal('Duplicate'), z.literal('Pending'), z.literal('PersonNotFound'), z.literal('Rejected'), z.literal('Withdrawn')]).describe(`
        * * Field Name: MatchStatus
        * * Display Name: Match Status
        * * SQL Data Type: nvarchar(20)
        * * Default Value: Pending
    * * Value List Type: List
    * * Possible Values 
    *   * Ambiguous
    *   * Confirmed
    *   * Duplicate
    *   * Pending
    *   * PersonNotFound
    *   * Rejected
    *   * Withdrawn
        * * Description: Pending (awaiting adjudication) | Confirmed (this profile IS the person) | Duplicate (same human, non-primary duplicate profile) | Rejected (different human) | Ambiguous (cannot decide — excluded from consumption but retained) | PersonNotFound (AGUPersonID unresolvable) | Withdrawn (pair absent from a later re-delivery).`),
    IsPrimary: z.boolean().describe(`
        * * Field Name: IsPrimary
        * * Display Name: Is Primary
        * * SQL Data Type: bit
        * * Default Value: 0
        * * Description: The single profile consumers read for this person (filtered unique index enforces one per person). Only Confirmed rows may be primary (CK).`),
    LinkedInVerified: z.boolean().describe(`
        * * Field Name: LinkedInVerified
        * * Display Name: Linked In Verified
        * * SQL Data Type: bit
        * * Default Value: 0`),
    NameExact: z.boolean().describe(`
        * * Field Name: NameExact
        * * Display Name: Name Exact
        * * SQL Data Type: bit
        * * Default Value: 0`),
    ResearchSignal: z.boolean().describe(`
        * * Field Name: ResearchSignal
        * * Display Name: Research Signal
        * * SQL Data Type: bit
        * * Default Value: 0`),
    OrgOverlap: z.boolean().describe(`
        * * Field Name: OrgOverlap
        * * Display Name: Org Overlap
        * * SQL Data Type: bit
        * * Default Value: 0
        * * Description: Person's known org appears in the profile's experience/education. Recorded for audit but CIRCULAR (the reverse match conditioned on it) — measured true for 100% of multi-set profiles; never use it to pick a winner.`),
    TitleRankConflict: z.boolean().describe(`
        * * Field Name: TitleRankConflict
        * * Display Name: Title Rank Conflict
        * * SQL Data Type: bit
        * * Default Value: 0
        * * Description: Red flag: profile shows a Student/Junior-level title at the same org + overlapping years where ORCID shows Professor-level (or vice versa) — the wrong-person signature measured on 4.3% of rankable 1:1 pairs.`),
    DisjointSpells: z.boolean().describe(`
        * * Field Name: DisjointSpells
        * * Display Name: Disjoint Spells
        * * SQL Data Type: bit
        * * Default Value: 0`),
    AllNewEmployers: z.boolean().describe(`
        * * Field Name: AllNewEmployers
        * * Display Name: All New Employers
        * * SQL Data Type: bit
        * * Default Value: 0`),
    ConfidenceScore: z.number().nullable().describe(`
        * * Field Name: ConfidenceScore
        * * Display Name: Confidence Score
        * * SQL Data Type: decimal(4, 3)`),
    AdjudicationMethod: z.union([z.literal('Deterministic'), z.literal('LLM'), z.literal('Manual')]).nullable().describe(`
        * * Field Name: AdjudicationMethod
        * * Display Name: Adjudication Method
        * * SQL Data Type: nvarchar(20)
    * * Value List Type: List
    * * Possible Values 
    *   * Deterministic
    *   * LLM
    *   * Manual`),
    AdjudicationModel: z.string().nullable().describe(`
        * * Field Name: AdjudicationModel
        * * Display Name: Adjudication Model
        * * SQL Data Type: nvarchar(100)`),
    AdjudicationNotes: z.string().nullable().describe(`
        * * Field Name: AdjudicationNotes
        * * Display Name: Adjudication Notes
        * * SQL Data Type: nvarchar(MAX)`),
    AdjudicatedAt: z.date().nullable().describe(`
        * * Field Name: AdjudicatedAt
        * * Display Name: Adjudicated At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type CoreSignalProfileMatchEntityType = z.infer<typeof CoreSignalProfileMatchSchema>;

/**
 * zod schema definition for the entity Profiles
 */
export const CoreSignalProfileSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    CoreSignalID: z.number().describe(`
        * * Field Name: CoreSignalID
        * * Display Name: Core Signal ID
        * * SQL Data Type: bigint
        * * Description: CoreSignal profile id (feed "id") — the stable external key for sync.`),
    FullName: z.string().nullable().describe(`
        * * Field Name: FullName
        * * Display Name: Full Name
        * * SQL Data Type: nvarchar(500)`),
    FirstName: z.string().nullable().describe(`
        * * Field Name: FirstName
        * * Display Name: First Name
        * * SQL Data Type: nvarchar(255)`),
    LastName: z.string().nullable().describe(`
        * * Field Name: LastName
        * * Display Name: Last Name
        * * SQL Data Type: nvarchar(255)`),
    Headline: z.string().nullable().describe(`
        * * Field Name: Headline
        * * Display Name: Headline
        * * SQL Data Type: nvarchar(1000)`),
    Summary: z.string().nullable().describe(`
        * * Field Name: Summary
        * * Display Name: Summary
        * * SQL Data Type: nvarchar(MAX)`),
    PictureUrl: z.string().nullable().describe(`
        * * Field Name: PictureUrl
        * * Display Name: Picture Url
        * * SQL Data Type: nvarchar(2000)`),
    LinkedInUrl: z.string().nullable().describe(`
        * * Field Name: LinkedInUrl
        * * Display Name: Linked In Url
        * * SQL Data Type: nvarchar(1000)`),
    LinkedInShorthand: z.string().nullable().describe(`
        * * Field Name: LinkedInShorthand
        * * Display Name: Linked In Shorthand
        * * SQL Data Type: nvarchar(255)
        * * Description: Canonical LinkedIn slug (feed linkedin_canonical_shorthand_name). Equality with MAPS.Person.LinkedInUrl's slug is the strongest match-verification signal.`),
    WebsiteUrl: z.string().nullable().describe(`
        * * Field Name: WebsiteUrl
        * * Display Name: Website Url
        * * SQL Data Type: nvarchar(2000)`),
    LocationCity: z.string().nullable().describe(`
        * * Field Name: LocationCity
        * * Display Name: Location City
        * * SQL Data Type: nvarchar(255)`),
    LocationState: z.string().nullable().describe(`
        * * Field Name: LocationState
        * * Display Name: Location State
        * * SQL Data Type: nvarchar(255)`),
    LocationCountry: z.string().nullable().describe(`
        * * Field Name: LocationCountry
        * * Display Name: Location Country
        * * SQL Data Type: nvarchar(255)`),
    LocationCountryISO2: z.string().nullable().describe(`
        * * Field Name: LocationCountryISO2
        * * Display Name: Location Country ISO2
        * * SQL Data Type: nvarchar(10)`),
    LocationFull: z.string().nullable().describe(`
        * * Field Name: LocationFull
        * * Display Name: Location Full
        * * SQL Data Type: nvarchar(500)`),
    IsDeleted: z.boolean().describe(`
        * * Field Name: IsDeleted
        * * Display Name: Is Deleted
        * * SQL Data Type: bit
        * * Default Value: 0
        * * Description: Feed is_deleted: CoreSignal's own duplicate/removed marker (1,833 in the initial delivery). Deleted profiles are never adjudicated primary and never surface in views.`),
    IsWorking: z.boolean().nullable().describe(`
        * * Field Name: IsWorking
        * * Display Name: Is Working
        * * SQL Data Type: bit`),
    ActiveExperienceTitle: z.string().nullable().describe(`
        * * Field Name: ActiveExperienceTitle
        * * Display Name: Active Experience Title
        * * SQL Data Type: nvarchar(500)`),
    ActiveExperienceDepartment: z.string().nullable().describe(`
        * * Field Name: ActiveExperienceDepartment
        * * Display Name: Active Experience Department
        * * SQL Data Type: nvarchar(255)`),
    ActiveExperienceManagementLevel: z.string().nullable().describe(`
        * * Field Name: ActiveExperienceManagementLevel
        * * Display Name: Active Experience Management Level
        * * SQL Data Type: nvarchar(255)`),
    IsDecisionMaker: z.boolean().nullable().describe(`
        * * Field Name: IsDecisionMaker
        * * Display Name: Is Decision Maker
        * * SQL Data Type: bit`),
    TotalExperienceMonths: z.number().nullable().describe(`
        * * Field Name: TotalExperienceMonths
        * * Display Name: Total Experience Months
        * * SQL Data Type: int`),
    ProfileScore: z.number().nullable().describe(`
        * * Field Name: ProfileScore
        * * Display Name: Profile Score
        * * SQL Data Type: decimal(4, 3)
        * * Description: CoreSignal profile-COMPLETENESS score (0-1). NOT match confidence — nearly all profiles score 0.9+; do not use it to rank match candidates.`),
    ConnectionsCount: z.number().nullable().describe(`
        * * Field Name: ConnectionsCount
        * * Display Name: Connections Count
        * * SQL Data Type: int`),
    FollowersCount: z.number().nullable().describe(`
        * * Field Name: FollowersCount
        * * Display Name: Followers Count
        * * SQL Data Type: int`),
    SourceCreatedAt: z.date().nullable().describe(`
        * * Field Name: SourceCreatedAt
        * * Display Name: Source Created At
        * * SQL Data Type: datetimeoffset`),
    SourceUpdatedAt: z.date().nullable().describe(`
        * * Field Name: SourceUpdatedAt
        * * Display Name: Source Updated At
        * * SQL Data Type: datetimeoffset`),
    SourceCheckedAt: z.date().nullable().describe(`
        * * Field Name: SourceCheckedAt
        * * Display Name: Source Checked At
        * * SQL Data Type: datetimeoffset
        * * Description: Feed checked_at — when CoreSignal last verified the source profile. The freshness qualifier for IsWorking/active-experience claims (stale "present" positions on abandoned LinkedIn profiles are a measured 14% hazard).`),
    SourceChangedAt: z.date().nullable().describe(`
        * * Field Name: SourceChangedAt
        * * Display Name: Source Changed At
        * * SQL Data Type: datetimeoffset`),
    RawJSON: z.string().nullable().describe(`
        * * Field Name: RawJSON
        * * Display Name: Raw JSON
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Full profile JSON preserved verbatim (includes non-normalized collections: activity, recommendations, interests).`),
    __mj_integration_SyncStatus: z.string().describe(`
        * * Field Name: __mj_integration_SyncStatus
        * * Display Name: Mj Integration Sync Status
        * * SQL Data Type: nvarchar(50)
        * * Default Value: Active`),
    __mj_integration_LastSyncedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedAt
        * * Display Name: Mj Integration Last Synced At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastSyncedSnapshot: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedSnapshot
        * * Display Name: Mj Integration Last Synced Snapshot
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncMessage: z.string().nullable().describe(`
        * * Field Name: __mj_integration_SyncMessage
        * * Display Name: Mj Integration Sync Message
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ContentHash: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ContentHash
        * * Display Name: Mj Integration Content Hash
        * * SQL Data Type: nvarchar(64)`),
    __mj_integration_CustomOverflow: z.string().nullable().describe(`
        * * Field Name: __mj_integration_CustomOverflow
        * * Display Name: Mj Integration Custom Overflow
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ExternalVersion: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ExternalVersion
        * * Display Name: Mj Integration External Version
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastSeenModifiedValue: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSeenModifiedValue
        * * Display Name: Mj Integration Last Seen Modified Value
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastReconciledAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastReconciledAt
        * * Display Name: Mj Integration Last Reconciled At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastWriterDirection: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastWriterDirection
        * * Display Name: Mj Integration Last Writer Direction
        * * SQL Data Type: nvarchar(10)`),
    __mj_integration_IsTombstoned: z.boolean().describe(`
        * * Field Name: __mj_integration_IsTombstoned
        * * Display Name: Mj Integration Is Tombstoned
        * * SQL Data Type: bit
        * * Default Value: 0`),
    __mj_integration_DeletedDetectedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_DeletedDetectedAt
        * * Display Name: Mj Integration Deleted Detected At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type CoreSignalProfileEntityType = z.infer<typeof CoreSignalProfileSchema>;

/**
 * zod schema definition for the entity Projects
 */
export const CoreSignalProjectSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ProfileID: z.string().describe(`
        * * Field Name: ProfileID
        * * Display Name: Profile ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Profiles (vwProfiles.ID)`),
    OrderInProfile: z.number().describe(`
        * * Field Name: OrderInProfile
        * * Display Name: Order In Profile
        * * SQL Data Type: int`),
    Name: z.string().nullable().describe(`
        * * Field Name: Name
        * * Display Name: Name
        * * SQL Data Type: nvarchar(1000)`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(MAX)`),
    ProjectUrl: z.string().nullable().describe(`
        * * Field Name: ProjectUrl
        * * Display Name: Project Url
        * * SQL Data Type: nvarchar(2000)`),
    DateFromYear: z.number().nullable().describe(`
        * * Field Name: DateFromYear
        * * Display Name: Date From Year
        * * SQL Data Type: smallint`),
    DateFromMonth: z.number().nullable().describe(`
        * * Field Name: DateFromMonth
        * * Display Name: Date From Month
        * * SQL Data Type: tinyint`),
    DateToYear: z.number().nullable().describe(`
        * * Field Name: DateToYear
        * * Display Name: Date To Year
        * * SQL Data Type: smallint`),
    DateToMonth: z.number().nullable().describe(`
        * * Field Name: DateToMonth
        * * Display Name: Date To Month
        * * SQL Data Type: tinyint`),
    __mj_integration_SyncStatus: z.string().describe(`
        * * Field Name: __mj_integration_SyncStatus
        * * Display Name: Mj Integration Sync Status
        * * SQL Data Type: nvarchar(50)
        * * Default Value: Active`),
    __mj_integration_LastSyncedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedAt
        * * Display Name: Mj Integration Last Synced At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastSyncedSnapshot: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedSnapshot
        * * Display Name: Mj Integration Last Synced Snapshot
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncMessage: z.string().nullable().describe(`
        * * Field Name: __mj_integration_SyncMessage
        * * Display Name: Mj Integration Sync Message
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ContentHash: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ContentHash
        * * Display Name: Mj Integration Content Hash
        * * SQL Data Type: nvarchar(64)`),
    __mj_integration_CustomOverflow: z.string().nullable().describe(`
        * * Field Name: __mj_integration_CustomOverflow
        * * Display Name: Mj Integration Custom Overflow
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ExternalVersion: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ExternalVersion
        * * Display Name: Mj Integration External Version
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastSeenModifiedValue: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSeenModifiedValue
        * * Display Name: Mj Integration Last Seen Modified Value
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastReconciledAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastReconciledAt
        * * Display Name: Mj Integration Last Reconciled At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastWriterDirection: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastWriterDirection
        * * Display Name: Mj Integration Last Writer Direction
        * * SQL Data Type: nvarchar(10)`),
    __mj_integration_IsTombstoned: z.boolean().describe(`
        * * Field Name: __mj_integration_IsTombstoned
        * * Display Name: Mj Integration Is Tombstoned
        * * SQL Data Type: bit
        * * Default Value: 0`),
    __mj_integration_DeletedDetectedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_DeletedDetectedAt
        * * Display Name: Mj Integration Deleted Detected At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type CoreSignalProjectEntityType = z.infer<typeof CoreSignalProjectSchema>;

/**
 * zod schema definition for the entity Publications
 */
export const CoreSignalPublicationSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ProfileID: z.string().describe(`
        * * Field Name: ProfileID
        * * Display Name: Profile ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Profiles (vwProfiles.ID)`),
    OrderInProfile: z.number().describe(`
        * * Field Name: OrderInProfile
        * * Display Name: Order In Profile
        * * SQL Data Type: int`),
    Title: z.string().nullable().describe(`
        * * Field Name: Title
        * * Display Name: Title
        * * SQL Data Type: nvarchar(2000)`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(MAX)`),
    PublicationUrl: z.string().nullable().describe(`
        * * Field Name: PublicationUrl
        * * Display Name: Publication Url
        * * SQL Data Type: nvarchar(2000)`),
    PublisherNames: z.string().nullable().describe(`
        * * Field Name: PublisherNames
        * * Display Name: Publisher Names
        * * SQL Data Type: nvarchar(1000)`),
    DateYear: z.number().nullable().describe(`
        * * Field Name: DateYear
        * * Display Name: Date Year
        * * SQL Data Type: smallint`),
    DateMonth: z.number().nullable().describe(`
        * * Field Name: DateMonth
        * * Display Name: Date Month
        * * SQL Data Type: tinyint`),
    __mj_integration_SyncStatus: z.string().describe(`
        * * Field Name: __mj_integration_SyncStatus
        * * Display Name: Mj Integration Sync Status
        * * SQL Data Type: nvarchar(50)
        * * Default Value: Active`),
    __mj_integration_LastSyncedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedAt
        * * Display Name: Mj Integration Last Synced At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastSyncedSnapshot: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedSnapshot
        * * Display Name: Mj Integration Last Synced Snapshot
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncMessage: z.string().nullable().describe(`
        * * Field Name: __mj_integration_SyncMessage
        * * Display Name: Mj Integration Sync Message
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ContentHash: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ContentHash
        * * Display Name: Mj Integration Content Hash
        * * SQL Data Type: nvarchar(64)`),
    __mj_integration_CustomOverflow: z.string().nullable().describe(`
        * * Field Name: __mj_integration_CustomOverflow
        * * Display Name: Mj Integration Custom Overflow
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ExternalVersion: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ExternalVersion
        * * Display Name: Mj Integration External Version
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastSeenModifiedValue: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSeenModifiedValue
        * * Display Name: Mj Integration Last Seen Modified Value
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastReconciledAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastReconciledAt
        * * Display Name: Mj Integration Last Reconciled At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastWriterDirection: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastWriterDirection
        * * Display Name: Mj Integration Last Writer Direction
        * * SQL Data Type: nvarchar(10)`),
    __mj_integration_IsTombstoned: z.boolean().describe(`
        * * Field Name: __mj_integration_IsTombstoned
        * * Display Name: Mj Integration Is Tombstoned
        * * SQL Data Type: bit
        * * Default Value: 0`),
    __mj_integration_DeletedDetectedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_DeletedDetectedAt
        * * Display Name: Mj Integration Deleted Detected At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type CoreSignalPublicationEntityType = z.infer<typeof CoreSignalPublicationSchema>;

/**
 * zod schema definition for the entity Qualifications
 */
export const ORCIDQualificationSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ORCIDRecordID: z.string().describe(`
        * * Field Name: ORCIDRecordID
        * * Display Name: ORCID Record ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Records (vwRecords.ID)`),
    PutCode: z.number().describe(`
        * * Field Name: PutCode
        * * Display Name: Put Code
        * * SQL Data Type: bigint`),
    DepartmentName: z.string().nullable().describe(`
        * * Field Name: DepartmentName
        * * Display Name: Department Name
        * * SQL Data Type: nvarchar(500)`),
    RoleTitle: z.string().nullable().describe(`
        * * Field Name: RoleTitle
        * * Display Name: Role Title
        * * SQL Data Type: nvarchar(500)`),
    StartYear: z.number().nullable().describe(`
        * * Field Name: StartYear
        * * Display Name: Start Year
        * * SQL Data Type: smallint`),
    StartMonth: z.number().nullable().describe(`
        * * Field Name: StartMonth
        * * Display Name: Start Month
        * * SQL Data Type: tinyint`),
    StartDay: z.number().nullable().describe(`
        * * Field Name: StartDay
        * * Display Name: Start Day
        * * SQL Data Type: tinyint`),
    EndYear: z.number().nullable().describe(`
        * * Field Name: EndYear
        * * Display Name: End Year
        * * SQL Data Type: smallint`),
    EndMonth: z.number().nullable().describe(`
        * * Field Name: EndMonth
        * * Display Name: End Month
        * * SQL Data Type: tinyint`),
    EndDay: z.number().nullable().describe(`
        * * Field Name: EndDay
        * * Display Name: End Day
        * * SQL Data Type: tinyint`),
    OrgName: z.string().nullable().describe(`
        * * Field Name: OrgName
        * * Display Name: Org Name
        * * SQL Data Type: nvarchar(500)`),
    OrgCity: z.string().nullable().describe(`
        * * Field Name: OrgCity
        * * Display Name: Org City
        * * SQL Data Type: nvarchar(255)`),
    OrgRegion: z.string().nullable().describe(`
        * * Field Name: OrgRegion
        * * Display Name: Org Region
        * * SQL Data Type: nvarchar(255)`),
    OrgCountry: z.string().nullable().describe(`
        * * Field Name: OrgCountry
        * * Display Name: Org Country
        * * SQL Data Type: nvarchar(10)`),
    OrgDisambiguatedID: z.string().nullable().describe(`
        * * Field Name: OrgDisambiguatedID
        * * Display Name: Org Disambiguated ID
        * * SQL Data Type: nvarchar(255)`),
    OrgDisambiguationSource: z.string().nullable().describe(`
        * * Field Name: OrgDisambiguationSource
        * * Display Name: Org Disambiguation Source
        * * SQL Data Type: nvarchar(50)`),
    Url: z.string().nullable().describe(`
        * * Field Name: Url
        * * Display Name: Url
        * * SQL Data Type: nvarchar(2000)`),
    Visibility: z.string().nullable().describe(`
        * * Field Name: Visibility
        * * Display Name: Visibility
        * * SQL Data Type: nvarchar(20)`),
    SourceName: z.string().nullable().describe(`
        * * Field Name: SourceName
        * * Display Name: Source Name
        * * SQL Data Type: nvarchar(255)`),
    SourceOrcid: z.string().nullable().describe(`
        * * Field Name: SourceOrcid
        * * Display Name: Source Orcid
        * * SQL Data Type: nvarchar(19)`),
    OrcidCreatedDate: z.date().nullable().describe(`
        * * Field Name: OrcidCreatedDate
        * * Display Name: Orcid Created Date
        * * SQL Data Type: datetimeoffset`),
    OrcidLastModifiedDate: z.date().nullable().describe(`
        * * Field Name: OrcidLastModifiedDate
        * * Display Name: Orcid Last Modified Date
        * * SQL Data Type: datetimeoffset`),
    RawJSON: z.string().nullable().describe(`
        * * Field Name: RawJSON
        * * Display Name: Raw JSON
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncStatus: z.string().describe(`
        * * Field Name: __mj_integration_SyncStatus
        * * Display Name: Mj Integration Sync Status
        * * SQL Data Type: nvarchar(50)
        * * Default Value: Active`),
    __mj_integration_LastSyncedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedAt
        * * Display Name: Mj Integration Last Synced At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastSyncedSnapshot: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedSnapshot
        * * Display Name: Mj Integration Last Synced Snapshot
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncMessage: z.string().nullable().describe(`
        * * Field Name: __mj_integration_SyncMessage
        * * Display Name: Mj Integration Sync Message
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ContentHash: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ContentHash
        * * Display Name: Mj Integration Content Hash
        * * SQL Data Type: nvarchar(64)`),
    __mj_integration_CustomOverflow: z.string().nullable().describe(`
        * * Field Name: __mj_integration_CustomOverflow
        * * Display Name: Mj Integration Custom Overflow
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ExternalVersion: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ExternalVersion
        * * Display Name: Mj Integration External Version
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastSeenModifiedValue: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSeenModifiedValue
        * * Display Name: Mj Integration Last Seen Modified Value
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastReconciledAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastReconciledAt
        * * Display Name: Mj Integration Last Reconciled At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastWriterDirection: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastWriterDirection
        * * Display Name: Mj Integration Last Writer Direction
        * * SQL Data Type: nvarchar(10)`),
    __mj_integration_IsTombstoned: z.boolean().describe(`
        * * Field Name: __mj_integration_IsTombstoned
        * * Display Name: Mj Integration Is Tombstoned
        * * SQL Data Type: bit
        * * Default Value: 0`),
    __mj_integration_DeletedDetectedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_DeletedDetectedAt
        * * Display Name: Mj Integration Deleted Detected At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type ORCIDQualificationEntityType = z.infer<typeof ORCIDQualificationSchema>;

/**
 * zod schema definition for the entity Record Links
 */
export const ORCIDRecordLinkSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ORCIDRecordID: z.string().describe(`
        * * Field Name: ORCIDRecordID
        * * Display Name: ORCID Record ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Records (vwRecords.ID)`),
    EntityID: z.string().describe(`
        * * Field Name: EntityID
        * * Display Name: Entity ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Entities (vwEntities.ID)`),
    RecordID: z.string().describe(`
        * * Field Name: RecordID
        * * Display Name: Record ID
        * * SQL Data Type: nvarchar(450)`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    RecordGUID: z.string().nullable().describe(`
        * * Field Name: RecordGUID
        * * Display Name: Record GUID
        * * SQL Data Type: uniqueidentifier`),
    Entity: z.string().describe(`
        * * Field Name: Entity
        * * Display Name: Entity
        * * SQL Data Type: nvarchar(255)`),
});

export type ORCIDRecordLinkEntityType = z.infer<typeof ORCIDRecordLinkSchema>;

/**
 * zod schema definition for the entity Records
 */
export const ORCIDRecordSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    OrcID: z.string().describe(`
        * * Field Name: OrcID
        * * Display Name: Orc ID
        * * SQL Data Type: nvarchar(19)
        * * Description: Canonical ORCID iD (e.g., 0000-0002-1825-0097). Globally unique external key.`),
    GivenNames: z.string().nullable().describe(`
        * * Field Name: GivenNames
        * * Display Name: Given Names
        * * SQL Data Type: nvarchar(255)`),
    FamilyName: z.string().nullable().describe(`
        * * Field Name: FamilyName
        * * Display Name: Family Name
        * * SQL Data Type: nvarchar(255)`),
    CreditName: z.string().nullable().describe(`
        * * Field Name: CreditName
        * * Display Name: Credit Name
        * * SQL Data Type: nvarchar(500)`),
    Biography: z.string().nullable().describe(`
        * * Field Name: Biography
        * * Display Name: Biography
        * * SQL Data Type: nvarchar(MAX)`),
    OrcidCreatedDate: z.date().nullable().describe(`
        * * Field Name: OrcidCreatedDate
        * * Display Name: Orcid Created Date
        * * SQL Data Type: datetimeoffset`),
    OrcidLastModifiedDate: z.date().nullable().describe(`
        * * Field Name: OrcidLastModifiedDate
        * * Display Name: Orcid Last Modified Date
        * * SQL Data Type: datetimeoffset`),
    RawJSON: z.string().nullable().describe(`
        * * Field Name: RawJSON
        * * Display Name: Raw JSON
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Full /record JSON payload, preserved verbatim.`),
    __mj_integration_SyncStatus: z.string().describe(`
        * * Field Name: __mj_integration_SyncStatus
        * * Display Name: Mj Integration Sync Status
        * * SQL Data Type: nvarchar(50)
        * * Default Value: Active`),
    __mj_integration_LastSyncedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedAt
        * * Display Name: Mj Integration Last Synced At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastSyncedSnapshot: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedSnapshot
        * * Display Name: Mj Integration Last Synced Snapshot
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncMessage: z.string().nullable().describe(`
        * * Field Name: __mj_integration_SyncMessage
        * * Display Name: Mj Integration Sync Message
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ContentHash: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ContentHash
        * * Display Name: Mj Integration Content Hash
        * * SQL Data Type: nvarchar(64)`),
    __mj_integration_CustomOverflow: z.string().nullable().describe(`
        * * Field Name: __mj_integration_CustomOverflow
        * * Display Name: Mj Integration Custom Overflow
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ExternalVersion: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ExternalVersion
        * * Display Name: Mj Integration External Version
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastSeenModifiedValue: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSeenModifiedValue
        * * Display Name: Mj Integration Last Seen Modified Value
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastReconciledAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastReconciledAt
        * * Display Name: Mj Integration Last Reconciled At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastWriterDirection: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastWriterDirection
        * * Display Name: Mj Integration Last Writer Direction
        * * SQL Data Type: nvarchar(10)`),
    __mj_integration_IsTombstoned: z.boolean().describe(`
        * * Field Name: __mj_integration_IsTombstoned
        * * Display Name: Mj Integration Is Tombstoned
        * * SQL Data Type: bit
        * * Default Value: 0`),
    __mj_integration_DeletedDetectedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_DeletedDetectedAt
        * * Display Name: Mj Integration Deleted Detected At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type ORCIDRecordEntityType = z.infer<typeof ORCIDRecordSchema>;

/**
 * zod schema definition for the entity Research Resources
 */
export const ORCIDResearchResourceSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ORCIDRecordID: z.string().describe(`
        * * Field Name: ORCIDRecordID
        * * Display Name: ORCID Record ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Records (vwRecords.ID)`),
    PutCode: z.number().describe(`
        * * Field Name: PutCode
        * * Display Name: Put Code
        * * SQL Data Type: bigint`),
    Title: z.string().nullable().describe(`
        * * Field Name: Title
        * * Display Name: Title
        * * SQL Data Type: nvarchar(2000)`),
    StartYear: z.number().nullable().describe(`
        * * Field Name: StartYear
        * * Display Name: Start Year
        * * SQL Data Type: smallint`),
    StartMonth: z.number().nullable().describe(`
        * * Field Name: StartMonth
        * * Display Name: Start Month
        * * SQL Data Type: tinyint`),
    StartDay: z.number().nullable().describe(`
        * * Field Name: StartDay
        * * Display Name: Start Day
        * * SQL Data Type: tinyint`),
    EndYear: z.number().nullable().describe(`
        * * Field Name: EndYear
        * * Display Name: End Year
        * * SQL Data Type: smallint`),
    EndMonth: z.number().nullable().describe(`
        * * Field Name: EndMonth
        * * Display Name: End Month
        * * SQL Data Type: tinyint`),
    EndDay: z.number().nullable().describe(`
        * * Field Name: EndDay
        * * Display Name: End Day
        * * SQL Data Type: tinyint`),
    Url: z.string().nullable().describe(`
        * * Field Name: Url
        * * Display Name: Url
        * * SQL Data Type: nvarchar(2000)`),
    Visibility: z.string().nullable().describe(`
        * * Field Name: Visibility
        * * Display Name: Visibility
        * * SQL Data Type: nvarchar(20)`),
    SourceName: z.string().nullable().describe(`
        * * Field Name: SourceName
        * * Display Name: Source Name
        * * SQL Data Type: nvarchar(255)`),
    SourceOrcid: z.string().nullable().describe(`
        * * Field Name: SourceOrcid
        * * Display Name: Source Orcid
        * * SQL Data Type: nvarchar(19)`),
    OrcidCreatedDate: z.date().nullable().describe(`
        * * Field Name: OrcidCreatedDate
        * * Display Name: Orcid Created Date
        * * SQL Data Type: datetimeoffset`),
    OrcidLastModifiedDate: z.date().nullable().describe(`
        * * Field Name: OrcidLastModifiedDate
        * * Display Name: Orcid Last Modified Date
        * * SQL Data Type: datetimeoffset`),
    RawJSON: z.string().nullable().describe(`
        * * Field Name: RawJSON
        * * Display Name: Raw JSON
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncStatus: z.string().describe(`
        * * Field Name: __mj_integration_SyncStatus
        * * Display Name: Mj Integration Sync Status
        * * SQL Data Type: nvarchar(50)
        * * Default Value: Active`),
    __mj_integration_LastSyncedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedAt
        * * Display Name: Mj Integration Last Synced At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastSyncedSnapshot: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedSnapshot
        * * Display Name: Mj Integration Last Synced Snapshot
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncMessage: z.string().nullable().describe(`
        * * Field Name: __mj_integration_SyncMessage
        * * Display Name: Mj Integration Sync Message
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ContentHash: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ContentHash
        * * Display Name: Mj Integration Content Hash
        * * SQL Data Type: nvarchar(64)`),
    __mj_integration_CustomOverflow: z.string().nullable().describe(`
        * * Field Name: __mj_integration_CustomOverflow
        * * Display Name: Mj Integration Custom Overflow
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ExternalVersion: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ExternalVersion
        * * Display Name: Mj Integration External Version
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastSeenModifiedValue: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSeenModifiedValue
        * * Display Name: Mj Integration Last Seen Modified Value
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastReconciledAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastReconciledAt
        * * Display Name: Mj Integration Last Reconciled At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastWriterDirection: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastWriterDirection
        * * Display Name: Mj Integration Last Writer Direction
        * * SQL Data Type: nvarchar(10)`),
    __mj_integration_IsTombstoned: z.boolean().describe(`
        * * Field Name: __mj_integration_IsTombstoned
        * * Display Name: Mj Integration Is Tombstoned
        * * SQL Data Type: bit
        * * Default Value: 0`),
    __mj_integration_DeletedDetectedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_DeletedDetectedAt
        * * Display Name: Mj Integration Deleted Detected At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type ORCIDResearchResourceEntityType = z.infer<typeof ORCIDResearchResourceSchema>;

/**
 * zod schema definition for the entity Researcher Urls
 */
export const ORCIDResearcherUrlSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ORCIDRecordID: z.string().describe(`
        * * Field Name: ORCIDRecordID
        * * Display Name: ORCID Record ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Records (vwRecords.ID)`),
    PutCode: z.number().describe(`
        * * Field Name: PutCode
        * * Display Name: Put Code
        * * SQL Data Type: bigint`),
    UrlName: z.string().nullable().describe(`
        * * Field Name: UrlName
        * * Display Name: Url Name
        * * SQL Data Type: nvarchar(355)`),
    Url: z.string().nullable().describe(`
        * * Field Name: Url
        * * Display Name: Url
        * * SQL Data Type: nvarchar(2000)`),
    DisplayIndex: z.number().nullable().describe(`
        * * Field Name: DisplayIndex
        * * Display Name: Display Index
        * * SQL Data Type: int`),
    Visibility: z.string().nullable().describe(`
        * * Field Name: Visibility
        * * Display Name: Visibility
        * * SQL Data Type: nvarchar(20)`),
    SourceName: z.string().nullable().describe(`
        * * Field Name: SourceName
        * * Display Name: Source Name
        * * SQL Data Type: nvarchar(255)`),
    SourceOrcid: z.string().nullable().describe(`
        * * Field Name: SourceOrcid
        * * Display Name: Source Orcid
        * * SQL Data Type: nvarchar(19)`),
    OrcidCreatedDate: z.date().nullable().describe(`
        * * Field Name: OrcidCreatedDate
        * * Display Name: Orcid Created Date
        * * SQL Data Type: datetimeoffset`),
    OrcidLastModifiedDate: z.date().nullable().describe(`
        * * Field Name: OrcidLastModifiedDate
        * * Display Name: Orcid Last Modified Date
        * * SQL Data Type: datetimeoffset`),
    RawJSON: z.string().nullable().describe(`
        * * Field Name: RawJSON
        * * Display Name: Raw JSON
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncStatus: z.string().describe(`
        * * Field Name: __mj_integration_SyncStatus
        * * Display Name: Mj Integration Sync Status
        * * SQL Data Type: nvarchar(50)
        * * Default Value: Active`),
    __mj_integration_LastSyncedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedAt
        * * Display Name: Mj Integration Last Synced At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastSyncedSnapshot: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedSnapshot
        * * Display Name: Mj Integration Last Synced Snapshot
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncMessage: z.string().nullable().describe(`
        * * Field Name: __mj_integration_SyncMessage
        * * Display Name: Mj Integration Sync Message
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ContentHash: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ContentHash
        * * Display Name: Mj Integration Content Hash
        * * SQL Data Type: nvarchar(64)`),
    __mj_integration_CustomOverflow: z.string().nullable().describe(`
        * * Field Name: __mj_integration_CustomOverflow
        * * Display Name: Mj Integration Custom Overflow
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ExternalVersion: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ExternalVersion
        * * Display Name: Mj Integration External Version
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastSeenModifiedValue: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSeenModifiedValue
        * * Display Name: Mj Integration Last Seen Modified Value
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastReconciledAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastReconciledAt
        * * Display Name: Mj Integration Last Reconciled At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastWriterDirection: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastWriterDirection
        * * Display Name: Mj Integration Last Writer Direction
        * * SQL Data Type: nvarchar(10)`),
    __mj_integration_IsTombstoned: z.boolean().describe(`
        * * Field Name: __mj_integration_IsTombstoned
        * * Display Name: Mj Integration Is Tombstoned
        * * SQL Data Type: bit
        * * Default Value: 0`),
    __mj_integration_DeletedDetectedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_DeletedDetectedAt
        * * Display Name: Mj Integration Deleted Detected At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type ORCIDResearcherUrlEntityType = z.infer<typeof ORCIDResearcherUrlSchema>;

/**
 * zod schema definition for the entity Review Assignments
 */
export const FeedbackPlatformReviewAssignmentSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    PresentationID: z.string().describe(`
        * * Field Name: PresentationID
        * * Display Name: Presentation ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Presentations (vwPresentations.ID)`),
    ReviewerUserID: z.string().describe(`
        * * Field Name: ReviewerUserID
        * * Display Name: Reviewer User ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Users (vwUsers.ID)`),
    AssignedDate: z.date().describe(`
        * * Field Name: AssignedDate
        * * Display Name: Assigned Date
        * * SQL Data Type: datetimeoffset
        * * Default Value: sysdatetimeoffset()
        * * Description: When the reviewer was assigned to this presentation.`),
    Status: z.union([z.literal('Assigned'), z.literal('Canceled'), z.literal('Completed'), z.literal('Declined'), z.literal('InProgress')]).describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: nvarchar(20)
        * * Default Value: Assigned
    * * Value List Type: List
    * * Possible Values 
    *   * Assigned
    *   * Canceled
    *   * Completed
    *   * Declined
    *   * InProgress
        * * Description: Current status of the review assignment.`),
    NotificationSent: z.boolean().describe(`
        * * Field Name: NotificationSent
        * * Display Name: Notification Sent
        * * SQL Data Type: bit
        * * Default Value: 0
        * * Description: Whether assignment notification has been sent to reviewer.`),
    ReminderCount: z.number().describe(`
        * * Field Name: ReminderCount
        * * Display Name: Reminder Count
        * * SQL Data Type: int
        * * Default Value: 0
        * * Description: Number of reminder emails sent to the reviewer.`),
    LastReminderDate: z.date().nullable().describe(`
        * * Field Name: LastReminderDate
        * * Display Name: Last Reminder Date
        * * SQL Data Type: datetimeoffset
        * * Description: Date of the last reminder email sent.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Presentation: z.string().describe(`
        * * Field Name: Presentation
        * * Display Name: Presentation
        * * SQL Data Type: nvarchar(500)`),
    ReviewerUser: z.string().describe(`
        * * Field Name: ReviewerUser
        * * Display Name: Reviewer User
        * * SQL Data Type: nvarchar(100)`),
});

export type FeedbackPlatformReviewAssignmentEntityType = z.infer<typeof FeedbackPlatformReviewAssignmentSchema>;

/**
 * zod schema definition for the entity Review Question Rubrics
 */
export const FeedbackPlatformReviewQuestionRubricSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ReviewQuestionID: z.string().describe(`
        * * Field Name: ReviewQuestionID
        * * Display Name: Review Question ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Review Questions (vwReviewQuestions.ID)
        * * Description: References the review question this rubric applies to.`),
    RatingValue: z.number().describe(`
        * * Field Name: RatingValue
        * * Display Name: Rating Value
        * * SQL Data Type: int
        * * Description: Numeric value for this rubric level (1-10).`),
    RatingLabel: z.string().describe(`
        * * Field Name: RatingLabel
        * * Display Name: Rating Label
        * * SQL Data Type: nvarchar(50)
        * * Description: Display label for this rubric level (e.g., "Not evident", "Emerging", "Proficient", "Exemplary").`),
    Criteria: z.string().nullable().describe(`
        * * Field Name: Criteria
        * * Display Name: Criteria
        * * SQL Data Type: nvarchar(MAX)
        * * Description: JSON array of criteria/bullet points for this rubric level.`),
    Sequence: z.number().describe(`
        * * Field Name: Sequence
        * * Display Name: Sequence
        * * SQL Data Type: int
        * * Description: Display order for this rubric level within the question.`),
    Status: z.union([z.literal('Active'), z.literal('Inactive')]).describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: nvarchar(20)
        * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Inactive
        * * Description: Current status of this rubric level.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type FeedbackPlatformReviewQuestionRubricEntityType = z.infer<typeof FeedbackPlatformReviewQuestionRubricSchema>;

/**
 * zod schema definition for the entity Review Question Types
 */
export const FeedbackPlatformReviewQuestionTypeSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    Name: z.string().describe(`
        * * Field Name: Name
        * * Display Name: Name
        * * SQL Data Type: nvarchar(255)
        * * Description: Name of the review question input type (e.g., "1-5 Scale", "Recommendation Level", "Free Text").`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Detailed description of the input type and its validation rules.`),
    InputType: z.union([z.literal('NumericRating'), z.literal('SelectionList'), z.literal('TextArea'), z.literal('TextComment')]).describe(`
        * * Field Name: InputType
        * * Display Name: Input Type
        * * SQL Data Type: nvarchar(50)
        * * Default Value: NumericRating
    * * Value List Type: List
    * * Possible Values 
    *   * NumericRating
    *   * SelectionList
    *   * TextArea
    *   * TextComment
        * * Description: Type of input control (NumericRating, SelectionList, TextComment, TextArea).`),
    MinValue: z.number().nullable().describe(`
        * * Field Name: MinValue
        * * Display Name: Min Value
        * * SQL Data Type: int
        * * Description: Minimum value for numeric ratings (applies to NumericRating type only).`),
    MaxValue: z.number().nullable().describe(`
        * * Field Name: MaxValue
        * * Display Name: Max Value
        * * SQL Data Type: int
        * * Description: Maximum value for numeric ratings (applies to NumericRating type only).`),
    AllowedValues: z.string().nullable().describe(`
        * * Field Name: AllowedValues
        * * Display Name: Allowed Values
        * * SQL Data Type: nvarchar(MAX)
        * * Description: JSON array of allowed values for SelectionList type (e.g., ["Excellent", "Good", "Fair", "Poor"]).`),
    Status: z.union([z.literal('Active'), z.literal('Canceled'), z.literal('Pending')]).describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: nvarchar(20)
        * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Canceled
    *   * Pending
        * * Description: Current status of the review question type definition.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type FeedbackPlatformReviewQuestionTypeEntityType = z.infer<typeof FeedbackPlatformReviewQuestionTypeSchema>;

/**
 * zod schema definition for the entity Review Questions
 */
export const FeedbackPlatformReviewQuestionSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    QuestionText: z.string().describe(`
        * * Field Name: QuestionText
        * * Display Name: Question Text
        * * SQL Data Type: nvarchar(MAX)
        * * Description: The actual question or prompt text shown to reviewers.`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Additional context or instructions for this question.`),
    ReviewQuestionTypeID: z.string().describe(`
        * * Field Name: ReviewQuestionTypeID
        * * Display Name: Review Question Type ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Review Question Types (vwReviewQuestionTypes.ID)
        * * Description: References the input type and validation rules for this question.`),
    Sequence: z.number().describe(`
        * * Field Name: Sequence
        * * Display Name: Sequence
        * * SQL Data Type: int
        * * Description: Order in which this question appears in review forms.`),
    IsRequired: z.boolean().describe(`
        * * Field Name: IsRequired
        * * Display Name: Is Required
        * * SQL Data Type: bit
        * * Default Value: 1
        * * Description: Whether answering this question is required to complete the review.`),
    PresentationCategoryID: z.string().nullable().describe(`
        * * Field Name: PresentationCategoryID
        * * Display Name: Presentation Category ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Presentation Categories (vwPresentationCategories.ID)
        * * Description: Optional: restricts this question to specific presentation categories.`),
    Status: z.union([z.literal('Active'), z.literal('Canceled'), z.literal('Pending')]).describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: nvarchar(20)
        * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Canceled
    *   * Pending
        * * Description: Current status of the review question.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    SeasonID: z.string().nullable().describe(`
        * * Field Name: SeasonID
        * * Display Name: Season ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Seasons (vwSeasons.ID)`),
    FormType: z.union([z.literal('Nomination'), z.literal('Review')]).nullable().describe(`
        * * Field Name: FormType
        * * Display Name: Form Type
        * * SQL Data Type: nvarchar(20)
    * * Value List Type: List
    * * Possible Values 
    *   * Nomination
    *   * Review`),
    ReviewQuestionType: z.string().describe(`
        * * Field Name: ReviewQuestionType
        * * Display Name: Review Question Type
        * * SQL Data Type: nvarchar(255)`),
    PresentationCategory: z.string().nullable().describe(`
        * * Field Name: PresentationCategory
        * * Display Name: Presentation Category
        * * SQL Data Type: nvarchar(255)`),
    Season: z.string().nullable().describe(`
        * * Field Name: Season
        * * Display Name: Season
        * * SQL Data Type: nvarchar(200)`),
});

export type FeedbackPlatformReviewQuestionEntityType = z.infer<typeof FeedbackPlatformReviewQuestionSchema>;

/**
 * zod schema definition for the entity Review Responses
 */
export const FeedbackPlatformReviewResponseSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ReviewID: z.string().describe(`
        * * Field Name: ReviewID
        * * Display Name: Review ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Reviews (vwReviews.ID)`),
    ReviewQuestionID: z.string().describe(`
        * * Field Name: ReviewQuestionID
        * * Display Name: Review Question ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Review Questions (vwReviewQuestions.ID)`),
    NumericValue: z.number().nullable().describe(`
        * * Field Name: NumericValue
        * * Display Name: Numeric Value
        * * SQL Data Type: int
        * * Description: Numeric value for NumericRating question types.`),
    TextValue: z.string().nullable().describe(`
        * * Field Name: TextValue
        * * Display Name: Text Value
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Text content for TextComment and TextArea question types.`),
    SelectionValue: z.string().nullable().describe(`
        * * Field Name: SelectionValue
        * * Display Name: Selection Value
        * * SQL Data Type: nvarchar(255)
        * * Description: Selected value for SelectionList question types.`),
    RubricLevelID: z.string().nullable().describe(`
        * * Field Name: RubricLevelID
        * * Display Name: Rubric Level ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Review Question Rubrics (vwReviewQuestionRubrics.ID)
        * * Description: References the specific rubric level selected for rating questions.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type FeedbackPlatformReviewResponseEntityType = z.infer<typeof FeedbackPlatformReviewResponseSchema>;

/**
 * zod schema definition for the entity Review Thank You Notes
 */
export const FeedbackPlatformReviewThankYouNoteSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ReviewID: z.string().describe(`
        * * Field Name: ReviewID
        * * Display Name: Review ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Reviews (vwReviews.ID)`),
    Note: z.string().describe(`
        * * Field Name: Note
        * * Display Name: Note
        * * SQL Data Type: nvarchar(2000)`),
    SentAt: z.date().describe(`
        * * Field Name: SentAt
        * * Display Name: Sent At
        * * SQL Data Type: datetimeoffset
        * * Default Value: sysdatetimeoffset()`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type FeedbackPlatformReviewThankYouNoteEntityType = z.infer<typeof FeedbackPlatformReviewThankYouNoteSchema>;

/**
 * zod schema definition for the entity Reviews
 */
export const FeedbackPlatformReviewSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ReviewAssignmentID: z.string().describe(`
        * * Field Name: ReviewAssignmentID
        * * Display Name: Review Assignment ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Review Assignments (vwReviewAssignments.ID)`),
    OverallRating: z.number().nullable().describe(`
        * * Field Name: OverallRating
        * * Display Name: Overall Rating
        * * SQL Data Type: int
        * * Description: Overall rating for the presentation (1-5 scale).`),
    GeneralComments: z.string().nullable().describe(`
        * * Field Name: GeneralComments
        * * Display Name: General Comments
        * * SQL Data Type: nvarchar(MAX)
        * * Description: General feedback comments visible to the presenter.`),
    PrivateComments: z.string().nullable().describe(`
        * * Field Name: PrivateComments
        * * Display Name: Private Comments
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Private comments for administrators only.`),
    StartedDate: z.date().nullable().describe(`
        * * Field Name: StartedDate
        * * Display Name: Started Date
        * * SQL Data Type: datetimeoffset
        * * Description: When the reviewer started working on the review.`),
    CompletedDate: z.date().nullable().describe(`
        * * Field Name: CompletedDate
        * * Display Name: Completed Date
        * * SQL Data Type: datetimeoffset
        * * Description: When the review was completed and submitted.`),
    Status: z.union([z.literal('Completed'), z.literal('InProgress'), z.literal('NotStarted'), z.literal('Submitted')]).describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: nvarchar(20)
        * * Default Value: NotStarted
    * * Value List Type: List
    * * Possible Values 
    *   * Completed
    *   * InProgress
    *   * NotStarted
    *   * Submitted
        * * Description: Current status of the review.`),
    OverallRecommendation: z.union([z.literal('Neutral'), z.literal('NotRecommend'), z.literal('Recommend'), z.literal('StronglyNotRecommend'), z.literal('StronglyRecommend')]).nullable().describe(`
        * * Field Name: OverallRecommendation
        * * Display Name: Overall Recommendation
        * * SQL Data Type: nvarchar(50)
    * * Value List Type: List
    * * Possible Values 
    *   * Neutral
    *   * NotRecommend
    *   * Recommend
    *   * StronglyNotRecommend
    *   * StronglyRecommend
        * * Description: Overall recommendation level for the presentation.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type FeedbackPlatformReviewEntityType = z.infer<typeof FeedbackPlatformReviewSchema>;

/**
 * zod schema definition for the entity Search Logs
 */
export const MAPSSearchLogSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    PartnerTokenID: z.string().nullable().describe(`
        * * Field Name: PartnerTokenID
        * * Display Name: Partner Token ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Partner Tokens (vwPartnerTokens.ID)
        * * Description: Legacy owning partner token. NULL for company-resolved sessions (staff SSO, company-scoped magic links); dual-written while the standalone cookie path lives; dropped in PR B. Ownership scoping uses CompanyID.`),
    AgentRunID: z.string().nullable().describe(`
        * * Field Name: AgentRunID
        * * Display Name: Agent Run ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: AI Agent Runs (vwAIAgentRuns.ID)
        * * Description: AIAgentRun produced by the re-rank agent for this search (Phase 4+). NULL in Phase 1-3 because the agent has not been added yet.`),
    ResultCount: z.number().describe(`
        * * Field Name: ResultCount
        * * Display Name: Result Count
        * * SQL Data Type: int
        * * Description: Total candidate count returned.`),
    StrongCount: z.number().describe(`
        * * Field Name: StrongCount
        * * Display Name: Strong Count
        * * SQL Data Type: int
        * * Description: Count of candidates assigned Strong match tier.`),
    MediumCount: z.number().describe(`
        * * Field Name: MediumCount
        * * Display Name: Medium Count
        * * SQL Data Type: int
        * * Description: Count of candidates assigned Medium match tier.`),
    WeakCount: z.number().describe(`
        * * Field Name: WeakCount
        * * Display Name: Weak Count
        * * SQL Data Type: int
        * * Description: Count of candidates assigned Weak match tier.`),
    CandidateSnapshot: z.string().describe(`
        * * Field Name: CandidateSnapshot
        * * Display Name: Candidate Snapshot
        * * SQL Data Type: nvarchar(MAX)
        * * Description: JSON-serialized SearchSnapshot — { version, candidates, parsedGuidance, agentRunId, createdAt }. The persistence mechanism for per-search state. Patched in place by mapsSummariesGenerate to attach whySuggested / thingsToLookInto.`),
    UserAgent: z.string().nullable().describe(`
        * * Field Name: UserAgent
        * * Display Name: User Agent
        * * SQL Data Type: nvarchar(512)
        * * Description: User-Agent header captured at request time. Anti-scrape signal.`),
    IPHash: z.string().nullable().describe(`
        * * Field Name: IPHash
        * * Display Name: IP Hash
        * * SQL Data Type: char(64)
        * * Description: sha256 of remote IP. Plaintext IP is never stored.`),
    Timestamp: z.date().describe(`
        * * Field Name: Timestamp
        * * Display Name: Timestamp
        * * SQL Data Type: datetimeoffset
        * * Default Value: sysdatetimeoffset()`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Title: z.string().nullable().describe(`
        * * Field Name: Title
        * * Display Name: Title
        * * SQL Data Type: nvarchar(200)
        * * Description: Display title for the saved-search list. LLM-generated from the job description at search completion (truncated JD text when generation fails) and user-editable afterward. NULL for searches that predate the Saved Searches feature; such rows are excluded from partner-facing saved-search lists.`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
        * * Description: Owning company. Authoritative attribution/scoping key for the partner-account model; NOT NULL is enforced at the app layer in PR A and at the DB in PR B.`),
    MagicLinkInviteID: z.string().nullable().describe(`
        * * Field Name: MagicLinkInviteID
        * * Display Name: Magic Link Invite ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Magic Link Invites (vwMagicLinkInvites.ID)
        * * Description: Owning magic-link invite (modern company-scoped links). Saved-search scoping key; precedence over PartnerTokenID/UserEmail. Re-pointed to the successor invite on rotation.`),
    UserEmail: z.string().nullable().describe(`
        * * Field Name: UserEmail
        * * Display Name: User Email
        * * SQL Data Type: nvarchar(255)
        * * Description: Lowercased staff email (staff SSO sessions only). Saved-search scoping key of last resort — used when the session has neither an invite nor a legacy token.`),
    AgentRun: z.string().nullable().describe(`
        * * Field Name: AgentRun
        * * Display Name: Agent Run
        * * SQL Data Type: nvarchar(255)`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type MAPSSearchLogEntityType = z.infer<typeof MAPSSearchLogSchema>;

/**
 * zod schema definition for the entity Seasons
 */
export const FeedbackPlatformSeasonSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    Code: z.string().describe(`
        * * Field Name: Code
        * * Display Name: Code
        * * SQL Data Type: nvarchar(20)`),
    Name: z.string().describe(`
        * * Field Name: Name
        * * Display Name: Name
        * * SQL Data Type: nvarchar(200)`),
    Phase: z.union([z.literal('Accepted'), z.literal('Complete'), z.literal('DraftClosed'), z.literal('DraftUpload'), z.literal('FeedbackReleased'), z.literal('Matched'), z.literal('Nominating'), z.literal('Reviewing'), z.literal('Selecting'), z.literal('Setup')]).describe(`
        * * Field Name: Phase
        * * Display Name: Phase
        * * SQL Data Type: nvarchar(20)
        * * Default Value: Setup
    * * Value List Type: List
    * * Possible Values 
    *   * Accepted
    *   * Complete
    *   * DraftClosed
    *   * DraftUpload
    *   * FeedbackReleased
    *   * Matched
    *   * Nominating
    *   * Reviewing
    *   * Selecting
    *   * Setup`),
    WinnersPerSection: z.number().describe(`
        * * Field Name: WinnersPerSection
        * * Display Name: Winners Per Section
        * * SQL Data Type: int
        * * Default Value: 3`),
    Timeline: z.string().nullable().describe(`
        * * Field Name: Timeline
        * * Display Name: Timeline
        * * SQL Data Type: nvarchar(MAX)`),
    Resources: z.string().nullable().describe(`
        * * Field Name: Resources
        * * Display Name: Resources
        * * SQL Data Type: nvarchar(MAX)`),
    IsActive: z.boolean().describe(`
        * * Field Name: IsActive
        * * Display Name: Is Active
        * * SQL Data Type: bit
        * * Default Value: 0`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type FeedbackPlatformSeasonEntityType = z.infer<typeof FeedbackPlatformSeasonSchema>;

/**
 * zod schema definition for the entity Sections
 */
export const FeedbackPlatformSectionSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    Code: z.string().describe(`
        * * Field Name: Code
        * * Display Name: Code
        * * SQL Data Type: nvarchar(20)`),
    Name: z.string().describe(`
        * * Field Name: Name
        * * Display Name: Name
        * * SQL Data Type: nvarchar(200)`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type FeedbackPlatformSectionEntityType = z.infer<typeof FeedbackPlatformSectionSchema>;

/**
 * zod schema definition for the entity Services
 */
export const ORCIDServiceSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ORCIDRecordID: z.string().describe(`
        * * Field Name: ORCIDRecordID
        * * Display Name: ORCID Record ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Records (vwRecords.ID)`),
    PutCode: z.number().describe(`
        * * Field Name: PutCode
        * * Display Name: Put Code
        * * SQL Data Type: bigint`),
    DepartmentName: z.string().nullable().describe(`
        * * Field Name: DepartmentName
        * * Display Name: Department Name
        * * SQL Data Type: nvarchar(500)`),
    RoleTitle: z.string().nullable().describe(`
        * * Field Name: RoleTitle
        * * Display Name: Role Title
        * * SQL Data Type: nvarchar(500)`),
    StartYear: z.number().nullable().describe(`
        * * Field Name: StartYear
        * * Display Name: Start Year
        * * SQL Data Type: smallint`),
    StartMonth: z.number().nullable().describe(`
        * * Field Name: StartMonth
        * * Display Name: Start Month
        * * SQL Data Type: tinyint`),
    StartDay: z.number().nullable().describe(`
        * * Field Name: StartDay
        * * Display Name: Start Day
        * * SQL Data Type: tinyint`),
    EndYear: z.number().nullable().describe(`
        * * Field Name: EndYear
        * * Display Name: End Year
        * * SQL Data Type: smallint`),
    EndMonth: z.number().nullable().describe(`
        * * Field Name: EndMonth
        * * Display Name: End Month
        * * SQL Data Type: tinyint`),
    EndDay: z.number().nullable().describe(`
        * * Field Name: EndDay
        * * Display Name: End Day
        * * SQL Data Type: tinyint`),
    OrgName: z.string().nullable().describe(`
        * * Field Name: OrgName
        * * Display Name: Org Name
        * * SQL Data Type: nvarchar(500)`),
    OrgCity: z.string().nullable().describe(`
        * * Field Name: OrgCity
        * * Display Name: Org City
        * * SQL Data Type: nvarchar(255)`),
    OrgRegion: z.string().nullable().describe(`
        * * Field Name: OrgRegion
        * * Display Name: Org Region
        * * SQL Data Type: nvarchar(255)`),
    OrgCountry: z.string().nullable().describe(`
        * * Field Name: OrgCountry
        * * Display Name: Org Country
        * * SQL Data Type: nvarchar(10)`),
    OrgDisambiguatedID: z.string().nullable().describe(`
        * * Field Name: OrgDisambiguatedID
        * * Display Name: Org Disambiguated ID
        * * SQL Data Type: nvarchar(255)`),
    OrgDisambiguationSource: z.string().nullable().describe(`
        * * Field Name: OrgDisambiguationSource
        * * Display Name: Org Disambiguation Source
        * * SQL Data Type: nvarchar(50)`),
    Url: z.string().nullable().describe(`
        * * Field Name: Url
        * * Display Name: Url
        * * SQL Data Type: nvarchar(2000)`),
    Visibility: z.string().nullable().describe(`
        * * Field Name: Visibility
        * * Display Name: Visibility
        * * SQL Data Type: nvarchar(20)`),
    SourceName: z.string().nullable().describe(`
        * * Field Name: SourceName
        * * Display Name: Source Name
        * * SQL Data Type: nvarchar(255)`),
    SourceOrcid: z.string().nullable().describe(`
        * * Field Name: SourceOrcid
        * * Display Name: Source Orcid
        * * SQL Data Type: nvarchar(19)`),
    OrcidCreatedDate: z.date().nullable().describe(`
        * * Field Name: OrcidCreatedDate
        * * Display Name: Orcid Created Date
        * * SQL Data Type: datetimeoffset`),
    OrcidLastModifiedDate: z.date().nullable().describe(`
        * * Field Name: OrcidLastModifiedDate
        * * Display Name: Orcid Last Modified Date
        * * SQL Data Type: datetimeoffset`),
    RawJSON: z.string().nullable().describe(`
        * * Field Name: RawJSON
        * * Display Name: Raw JSON
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncStatus: z.string().describe(`
        * * Field Name: __mj_integration_SyncStatus
        * * Display Name: Mj Integration Sync Status
        * * SQL Data Type: nvarchar(50)
        * * Default Value: Active`),
    __mj_integration_LastSyncedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedAt
        * * Display Name: Mj Integration Last Synced At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastSyncedSnapshot: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedSnapshot
        * * Display Name: Mj Integration Last Synced Snapshot
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncMessage: z.string().nullable().describe(`
        * * Field Name: __mj_integration_SyncMessage
        * * Display Name: Mj Integration Sync Message
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ContentHash: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ContentHash
        * * Display Name: Mj Integration Content Hash
        * * SQL Data Type: nvarchar(64)`),
    __mj_integration_CustomOverflow: z.string().nullable().describe(`
        * * Field Name: __mj_integration_CustomOverflow
        * * Display Name: Mj Integration Custom Overflow
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ExternalVersion: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ExternalVersion
        * * Display Name: Mj Integration External Version
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastSeenModifiedValue: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSeenModifiedValue
        * * Display Name: Mj Integration Last Seen Modified Value
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastReconciledAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastReconciledAt
        * * Display Name: Mj Integration Last Reconciled At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastWriterDirection: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastWriterDirection
        * * Display Name: Mj Integration Last Writer Direction
        * * SQL Data Type: nvarchar(10)`),
    __mj_integration_IsTombstoned: z.boolean().describe(`
        * * Field Name: __mj_integration_IsTombstoned
        * * Display Name: Mj Integration Is Tombstoned
        * * SQL Data Type: bit
        * * Default Value: 0`),
    __mj_integration_DeletedDetectedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_DeletedDetectedAt
        * * Display Name: Mj Integration Deleted Detected At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type ORCIDServiceEntityType = z.infer<typeof ORCIDServiceSchema>;

/**
 * zod schema definition for the entity Session Nominators
 */
export const FeedbackPlatformSessionNominatorSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    SessionID: z.string().describe(`
        * * Field Name: SessionID
        * * Display Name: Session ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Sessions (vwSessions.ID)`),
    UserID: z.string().describe(`
        * * Field Name: UserID
        * * Display Name: User ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Users (vwUsers.ID)`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Session: z.string().describe(`
        * * Field Name: Session
        * * Display Name: Session
        * * SQL Data Type: nvarchar(500)`),
    User: z.string().describe(`
        * * Field Name: User
        * * Display Name: User
        * * SQL Data Type: nvarchar(100)`),
});

export type FeedbackPlatformSessionNominatorEntityType = z.infer<typeof FeedbackPlatformSessionNominatorSchema>;

/**
 * zod schema definition for the entity Sessions
 */
export const FeedbackPlatformSessionSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    SeasonID: z.string().describe(`
        * * Field Name: SeasonID
        * * Display Name: Season ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Seasons (vwSeasons.ID)`),
    SectionID: z.string().describe(`
        * * Field Name: SectionID
        * * Display Name: Section ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Sections (vwSections.ID)`),
    Code: z.string().describe(`
        * * Field Name: Code
        * * Display Name: Code
        * * SQL Data Type: nvarchar(20)`),
    Title: z.string().describe(`
        * * Field Name: Title
        * * Display Name: Title
        * * SQL Data Type: nvarchar(500)`),
    Format: z.union([z.literal('Oral'), z.literal('Poster')]).describe(`
        * * Field Name: Format
        * * Display Name: Format
        * * SQL Data Type: nvarchar(10)
    * * Value List Type: List
    * * Possible Values 
    *   * Oral
    *   * Poster`),
    ScheduledAt: z.date().nullable().describe(`
        * * Field Name: ScheduledAt
        * * Display Name: Scheduled At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Season: z.string().describe(`
        * * Field Name: Season
        * * Display Name: Season
        * * SQL Data Type: nvarchar(200)`),
    Section: z.string().describe(`
        * * Field Name: Section
        * * Display Name: Section
        * * SQL Data Type: nvarchar(200)`),
});

export type FeedbackPlatformSessionEntityType = z.infer<typeof FeedbackPlatformSessionSchema>;

/**
 * zod schema definition for the entity Skills
 */
export const CoreSignalSkillSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ProfileID: z.string().describe(`
        * * Field Name: ProfileID
        * * Display Name: Profile ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Profiles (vwProfiles.ID)`),
    OrderInProfile: z.number().describe(`
        * * Field Name: OrderInProfile
        * * Display Name: Order In Profile
        * * SQL Data Type: int`),
    Skill: z.string().describe(`
        * * Field Name: Skill
        * * Display Name: Skill
        * * SQL Data Type: nvarchar(255)`),
    Source: z.union([z.literal('historical'), z.literal('inferred')]).describe(`
        * * Field Name: Source
        * * Display Name: Source
        * * SQL Data Type: nvarchar(20)
    * * Value List Type: List
    * * Possible Values 
    *   * historical
    *   * inferred`),
    __mj_integration_SyncStatus: z.string().describe(`
        * * Field Name: __mj_integration_SyncStatus
        * * Display Name: Mj Integration Sync Status
        * * SQL Data Type: nvarchar(50)
        * * Default Value: Active`),
    __mj_integration_LastSyncedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedAt
        * * Display Name: Mj Integration Last Synced At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastSyncedSnapshot: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedSnapshot
        * * Display Name: Mj Integration Last Synced Snapshot
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncMessage: z.string().nullable().describe(`
        * * Field Name: __mj_integration_SyncMessage
        * * Display Name: Mj Integration Sync Message
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ContentHash: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ContentHash
        * * Display Name: Mj Integration Content Hash
        * * SQL Data Type: nvarchar(64)`),
    __mj_integration_CustomOverflow: z.string().nullable().describe(`
        * * Field Name: __mj_integration_CustomOverflow
        * * Display Name: Mj Integration Custom Overflow
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ExternalVersion: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ExternalVersion
        * * Display Name: Mj Integration External Version
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastSeenModifiedValue: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSeenModifiedValue
        * * Display Name: Mj Integration Last Seen Modified Value
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastReconciledAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastReconciledAt
        * * Display Name: Mj Integration Last Reconciled At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastWriterDirection: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastWriterDirection
        * * Display Name: Mj Integration Last Writer Direction
        * * SQL Data Type: nvarchar(10)`),
    __mj_integration_IsTombstoned: z.boolean().describe(`
        * * Field Name: __mj_integration_IsTombstoned
        * * Display Name: Mj Integration Is Tombstoned
        * * SQL Data Type: bit
        * * Default Value: 0`),
    __mj_integration_DeletedDetectedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_DeletedDetectedAt
        * * Display Name: Mj Integration Deleted Detected At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type CoreSignalSkillEntityType = z.infer<typeof CoreSignalSkillSchema>;

/**
 * zod schema definition for the entity System Settings
 */
export const FeedbackPlatformSystemSettingSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    Key: z.string().describe(`
        * * Field Name: Key
        * * Display Name: Key
        * * SQL Data Type: nvarchar(255)
        * * Description: Configuration key/setting name.`),
    Value: z.string().describe(`
        * * Field Name: Value
        * * Display Name: Value
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Configuration value.`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Description of what this setting controls.`),
    DataType: z.union([z.literal('Boolean'), z.literal('DateTime'), z.literal('Integer'), z.literal('JSON'), z.literal('String')]).describe(`
        * * Field Name: DataType
        * * Display Name: Data Type
        * * SQL Data Type: nvarchar(50)
        * * Default Value: String
    * * Value List Type: List
    * * Possible Values 
    *   * Boolean
    *   * DateTime
    *   * Integer
    *   * JSON
    *   * String
        * * Description: Data type of the setting value.`),
    Status: z.union([z.literal('Active'), z.literal('Canceled'), z.literal('Pending')]).describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: nvarchar(20)
        * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Canceled
    *   * Pending
        * * Description: Current status of the setting.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type FeedbackPlatformSystemSettingEntityType = z.infer<typeof FeedbackPlatformSystemSettingSchema>;

/**
 * zod schema definition for the entity Uploaded Documents
 */
export const MAPSUploadedDocumentSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    PartnerTokenID: z.string().nullable().describe(`
        * * Field Name: PartnerTokenID
        * * Display Name: Partner Token ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Partner Tokens (vwPartnerTokens.ID)
        * * Description: Legacy owning partner token. NULL for company-resolved sessions; dual-written until PR B. Ownership scoping uses CompanyID.`),
    SearchLogID: z.string().nullable().describe(`
        * * Field Name: SearchLogID
        * * Display Name: Search Log ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Search Logs (vwSearchLogs.ID)
        * * Description: Linked after the search that consumes this upload. NULL until then.`),
    BlobPath: z.string().describe(`
        * * Field Name: BlobPath
        * * Display Name: Blob Path
        * * SQL Data Type: nvarchar(512)
        * * Description: Azure blob path: maps/uploads/{tokenId}/{ID}/{sanitizedFileName}.`),
    OriginalFileName: z.string().describe(`
        * * Field Name: OriginalFileName
        * * Display Name: Original File Name
        * * SQL Data Type: nvarchar(256)
        * * Description: Original (un-sanitized) file name as supplied by the client. Used as the section label in multi-doc concatenation.`),
    ContentType: z.string().describe(`
        * * Field Name: ContentType
        * * Display Name: Content Type
        * * SQL Data Type: nvarchar(128)
        * * Description: application/pdf, application/vnd.openxmlformats-officedocument.wordprocessingml.document, or text/plain.`),
    SizeBytes: z.number().nullable().describe(`
        * * Field Name: SizeBytes
        * * Display Name: Size Bytes
        * * SQL Data Type: bigint
        * * Description: Filled from blob metadata at finalize time. NULL until finalize confirms the blob exists.`),
    ExtractedText: z.string().nullable().describe(`
        * * Field Name: ExtractedText
        * * Display Name: Extracted Text
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Server-side parsed text. NULL until ExtractStatus='extracted'; also NULL when ExtractStatus='extract_failed'.`),
    ExtractStatus: z.union([z.literal('extract_failed'), z.literal('extracted'), z.literal('pending')]).describe(`
        * * Field Name: ExtractStatus
        * * Display Name: Extract Status
        * * SQL Data Type: nvarchar(16)
        * * Default Value: pending
    * * Value List Type: List
    * * Possible Values 
    *   * extract_failed
    *   * extracted
    *   * pending
        * * Description: pending | extracted | extract_failed. Drives the chip state in the upload UI.`),
    ExtractError: z.string().nullable().describe(`
        * * Field Name: ExtractError
        * * Display Name: Extract Error
        * * SQL Data Type: nvarchar(512)
        * * Description: Short failure reason (pdf_parse_failed, docx_parse_failed, empty_text, corrupt_file, blob_missing). NULL on success.`),
    UploadedAt: z.date().describe(`
        * * Field Name: UploadedAt
        * * Display Name: Uploaded At
        * * SQL Data Type: datetimeoffset
        * * Default Value: sysdatetimeoffset()`),
    ExtractedAt: z.date().nullable().describe(`
        * * Field Name: ExtractedAt
        * * Display Name: Extracted At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    CompanyID: z.string().nullable().describe(`
        * * Field Name: CompanyID
        * * Display Name: Company ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
        * * Description: Owning company. Authoritative attribution/scoping key for the partner-account model; NOT NULL is enforced at the app layer in PR A and at the DB in PR B.`),
    Company: z.string().nullable().describe(`
        * * Field Name: Company
        * * Display Name: Company
        * * SQL Data Type: nvarchar(50)`),
});

export type MAPSUploadedDocumentEntityType = z.infer<typeof MAPSUploadedDocumentSchema>;

/**
 * zod schema definition for the entity User Notification Settings
 */
export const FeedbackPlatformUserNotificationSettingSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    UserID: z.string().describe(`
        * * Field Name: UserID
        * * Display Name: User ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Users (vwUsers.ID)`),
    NotificationTypeID: z.string().describe(`
        * * Field Name: NotificationTypeID
        * * Display Name: Notification Type ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Notification Types (vwNotificationTypes.ID)`),
    IsEnabled: z.boolean().describe(`
        * * Field Name: IsEnabled
        * * Display Name: Is Enabled
        * * SQL Data Type: bit
        * * Default Value: 1
        * * Description: Whether this notification type is enabled for the user.`),
    EmailEnabled: z.boolean().describe(`
        * * Field Name: EmailEnabled
        * * Display Name: Email Enabled
        * * SQL Data Type: bit
        * * Default Value: 1
        * * Description: Whether to send email notifications for this type.`),
    InAppEnabled: z.boolean().describe(`
        * * Field Name: InAppEnabled
        * * Display Name: In App Enabled
        * * SQL Data Type: bit
        * * Default Value: 1
        * * Description: Whether to send in-app notifications for this type.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    User: z.string().describe(`
        * * Field Name: User
        * * Display Name: User
        * * SQL Data Type: nvarchar(100)`),
    NotificationType: z.string().describe(`
        * * Field Name: NotificationType
        * * Display Name: Notification Type
        * * SQL Data Type: nvarchar(100)`),
});

export type FeedbackPlatformUserNotificationSettingEntityType = z.infer<typeof FeedbackPlatformUserNotificationSettingSchema>;

/**
 * zod schema definition for the entity Work External IDs
 */
export const ORCIDWorkExternalIDSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    WorkID: z.string().describe(`
        * * Field Name: WorkID
        * * Display Name: Work ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Works (vwWorks.ID)`),
    ExternalIDType: z.string().nullable().describe(`
        * * Field Name: ExternalIDType
        * * Display Name: External ID Type
        * * SQL Data Type: nvarchar(100)`),
    ExternalIDValue: z.string().nullable().describe(`
        * * Field Name: ExternalIDValue
        * * Display Name: External ID Value
        * * SQL Data Type: nvarchar(500)`),
    ExternalIDNormalizedValue: z.string().nullable().describe(`
        * * Field Name: ExternalIDNormalizedValue
        * * Display Name: External ID Normalized Value
        * * SQL Data Type: nvarchar(500)`),
    ExternalIDUrl: z.string().nullable().describe(`
        * * Field Name: ExternalIDUrl
        * * Display Name: External ID Url
        * * SQL Data Type: nvarchar(2000)`),
    ExternalIDRelationship: z.string().nullable().describe(`
        * * Field Name: ExternalIDRelationship
        * * Display Name: External ID Relationship
        * * SQL Data Type: nvarchar(20)`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type ORCIDWorkExternalIDEntityType = z.infer<typeof ORCIDWorkExternalIDSchema>;

/**
 * zod schema definition for the entity Works
 */
export const ORCIDWorkSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ORCIDRecordID: z.string().describe(`
        * * Field Name: ORCIDRecordID
        * * Display Name: ORCID Record ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Records (vwRecords.ID)`),
    PutCode: z.number().describe(`
        * * Field Name: PutCode
        * * Display Name: Put Code
        * * SQL Data Type: bigint`),
    Title: z.string().nullable().describe(`
        * * Field Name: Title
        * * Display Name: Title
        * * SQL Data Type: nvarchar(2000)`),
    Subtitle: z.string().nullable().describe(`
        * * Field Name: Subtitle
        * * Display Name: Subtitle
        * * SQL Data Type: nvarchar(2000)`),
    TranslatedTitle: z.string().nullable().describe(`
        * * Field Name: TranslatedTitle
        * * Display Name: Translated Title
        * * SQL Data Type: nvarchar(2000)`),
    JournalTitle: z.string().nullable().describe(`
        * * Field Name: JournalTitle
        * * Display Name: Journal Title
        * * SQL Data Type: nvarchar(1000)`),
    WorkType: z.string().nullable().describe(`
        * * Field Name: WorkType
        * * Display Name: Work Type
        * * SQL Data Type: nvarchar(100)`),
    PublicationYear: z.number().nullable().describe(`
        * * Field Name: PublicationYear
        * * Display Name: Publication Year
        * * SQL Data Type: smallint`),
    PublicationMonth: z.number().nullable().describe(`
        * * Field Name: PublicationMonth
        * * Display Name: Publication Month
        * * SQL Data Type: tinyint`),
    PublicationDay: z.number().nullable().describe(`
        * * Field Name: PublicationDay
        * * Display Name: Publication Day
        * * SQL Data Type: tinyint`),
    Url: z.string().nullable().describe(`
        * * Field Name: Url
        * * Display Name: Url
        * * SQL Data Type: nvarchar(2000)`),
    Visibility: z.string().nullable().describe(`
        * * Field Name: Visibility
        * * Display Name: Visibility
        * * SQL Data Type: nvarchar(20)`),
    SourceName: z.string().nullable().describe(`
        * * Field Name: SourceName
        * * Display Name: Source Name
        * * SQL Data Type: nvarchar(255)`),
    SourceOrcid: z.string().nullable().describe(`
        * * Field Name: SourceOrcid
        * * Display Name: Source Orcid
        * * SQL Data Type: nvarchar(19)`),
    OrcidCreatedDate: z.date().nullable().describe(`
        * * Field Name: OrcidCreatedDate
        * * Display Name: Orcid Created Date
        * * SQL Data Type: datetimeoffset`),
    OrcidLastModifiedDate: z.date().nullable().describe(`
        * * Field Name: OrcidLastModifiedDate
        * * Display Name: Orcid Last Modified Date
        * * SQL Data Type: datetimeoffset`),
    RawJSON: z.string().nullable().describe(`
        * * Field Name: RawJSON
        * * Display Name: Raw JSON
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncStatus: z.string().describe(`
        * * Field Name: __mj_integration_SyncStatus
        * * Display Name: Mj Integration Sync Status
        * * SQL Data Type: nvarchar(50)
        * * Default Value: Active`),
    __mj_integration_LastSyncedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedAt
        * * Display Name: Mj Integration Last Synced At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastSyncedSnapshot: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSyncedSnapshot
        * * Display Name: Mj Integration Last Synced Snapshot
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_SyncMessage: z.string().nullable().describe(`
        * * Field Name: __mj_integration_SyncMessage
        * * Display Name: Mj Integration Sync Message
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ContentHash: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ContentHash
        * * Display Name: Mj Integration Content Hash
        * * SQL Data Type: nvarchar(64)`),
    __mj_integration_CustomOverflow: z.string().nullable().describe(`
        * * Field Name: __mj_integration_CustomOverflow
        * * Display Name: Mj Integration Custom Overflow
        * * SQL Data Type: nvarchar(MAX)`),
    __mj_integration_ExternalVersion: z.string().nullable().describe(`
        * * Field Name: __mj_integration_ExternalVersion
        * * Display Name: Mj Integration External Version
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastSeenModifiedValue: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastSeenModifiedValue
        * * Display Name: Mj Integration Last Seen Modified Value
        * * SQL Data Type: nvarchar(255)`),
    __mj_integration_LastReconciledAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_LastReconciledAt
        * * Display Name: Mj Integration Last Reconciled At
        * * SQL Data Type: datetimeoffset`),
    __mj_integration_LastWriterDirection: z.string().nullable().describe(`
        * * Field Name: __mj_integration_LastWriterDirection
        * * Display Name: Mj Integration Last Writer Direction
        * * SQL Data Type: nvarchar(10)`),
    __mj_integration_IsTombstoned: z.boolean().describe(`
        * * Field Name: __mj_integration_IsTombstoned
        * * Display Name: Mj Integration Is Tombstoned
        * * SQL Data Type: bit
        * * Default Value: 0`),
    __mj_integration_DeletedDetectedAt: z.date().nullable().describe(`
        * * Field Name: __mj_integration_DeletedDetectedAt
        * * Display Name: Mj Integration Deleted Detected At
        * * SQL Data Type: datetimeoffset`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type ORCIDWorkEntityType = z.infer<typeof ORCIDWorkSchema>;
 
 

/**
 * Addresses - strongly typed entity sub-class
 * * Schema: ORCID
 * * Base Table: Address
 * * Base View: vwAddresses
 * * @description A country/address entry (person.addresses).
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Addresses')
export class ORCIDAddressEntity extends BaseEntity<ORCIDAddressEntityType> {
    /**
    * Loads the Addresses record from the database
    * @param ID: string - primary key value to load the Addresses record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof ORCIDAddressEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ORCIDRecordID
    * * Display Name: ORCID Record ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Records (vwRecords.ID)
    */
    get ORCIDRecordID(): string {
        return this.Get('ORCIDRecordID');
    }
    set ORCIDRecordID(value: string) {
        this.Set('ORCIDRecordID', value);
    }

    /**
    * * Field Name: PutCode
    * * Display Name: Put Code
    * * SQL Data Type: bigint
    */
    get PutCode(): number {
        return this.Get('PutCode');
    }
    set PutCode(value: number) {
        this.Set('PutCode', value);
    }

    /**
    * * Field Name: Country
    * * Display Name: Country
    * * SQL Data Type: nvarchar(10)
    */
    get Country(): string | null {
        return this.Get('Country');
    }
    set Country(value: string | null) {
        this.Set('Country', value);
    }

    /**
    * * Field Name: DisplayIndex
    * * Display Name: Display Index
    * * SQL Data Type: int
    */
    get DisplayIndex(): number | null {
        return this.Get('DisplayIndex');
    }
    set DisplayIndex(value: number | null) {
        this.Set('DisplayIndex', value);
    }

    /**
    * * Field Name: Visibility
    * * Display Name: Visibility
    * * SQL Data Type: nvarchar(20)
    */
    get Visibility(): string | null {
        return this.Get('Visibility');
    }
    set Visibility(value: string | null) {
        this.Set('Visibility', value);
    }

    /**
    * * Field Name: SourceName
    * * Display Name: Source Name
    * * SQL Data Type: nvarchar(255)
    */
    get SourceName(): string | null {
        return this.Get('SourceName');
    }
    set SourceName(value: string | null) {
        this.Set('SourceName', value);
    }

    /**
    * * Field Name: SourceOrcid
    * * Display Name: Source Orcid
    * * SQL Data Type: nvarchar(19)
    */
    get SourceOrcid(): string | null {
        return this.Get('SourceOrcid');
    }
    set SourceOrcid(value: string | null) {
        this.Set('SourceOrcid', value);
    }

    /**
    * * Field Name: OrcidCreatedDate
    * * Display Name: Orcid Created Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidCreatedDate(): Date | null {
        return this.Get('OrcidCreatedDate');
    }
    set OrcidCreatedDate(value: Date | null) {
        this.Set('OrcidCreatedDate', value);
    }

    /**
    * * Field Name: OrcidLastModifiedDate
    * * Display Name: Orcid Last Modified Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidLastModifiedDate(): Date | null {
        return this.Get('OrcidLastModifiedDate');
    }
    set OrcidLastModifiedDate(value: Date | null) {
        this.Set('OrcidLastModifiedDate', value);
    }

    /**
    * * Field Name: RawJSON
    * * Display Name: Raw JSON
    * * SQL Data Type: nvarchar(MAX)
    */
    get RawJSON(): string | null {
        return this.Get('RawJSON');
    }
    set RawJSON(value: string | null) {
        this.Set('RawJSON', value);
    }

    /**
    * * Field Name: __mj_integration_SyncStatus
    * * Display Name: Mj Integration Sync Status
    * * SQL Data Type: nvarchar(50)
    * * Default Value: Active
    */
    get __mj_integration_SyncStatus(): string {
        return this.Get('__mj_integration_SyncStatus');
    }
    set __mj_integration_SyncStatus(value: string) {
        this.Set('__mj_integration_SyncStatus', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedAt
    * * Display Name: Mj Integration Last Synced At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastSyncedAt(): Date | null {
        return this.Get('__mj_integration_LastSyncedAt');
    }
    set __mj_integration_LastSyncedAt(value: Date | null) {
        this.Set('__mj_integration_LastSyncedAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedSnapshot
    * * Display Name: Mj Integration Last Synced Snapshot
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_LastSyncedSnapshot(): string | null {
        return this.Get('__mj_integration_LastSyncedSnapshot');
    }
    set __mj_integration_LastSyncedSnapshot(value: string | null) {
        this.Set('__mj_integration_LastSyncedSnapshot', value);
    }

    /**
    * * Field Name: __mj_integration_SyncMessage
    * * Display Name: Mj Integration Sync Message
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_SyncMessage(): string | null {
        return this.Get('__mj_integration_SyncMessage');
    }
    set __mj_integration_SyncMessage(value: string | null) {
        this.Set('__mj_integration_SyncMessage', value);
    }

    /**
    * * Field Name: __mj_integration_ContentHash
    * * Display Name: Mj Integration Content Hash
    * * SQL Data Type: nvarchar(64)
    */
    get __mj_integration_ContentHash(): string | null {
        return this.Get('__mj_integration_ContentHash');
    }
    set __mj_integration_ContentHash(value: string | null) {
        this.Set('__mj_integration_ContentHash', value);
    }

    /**
    * * Field Name: __mj_integration_CustomOverflow
    * * Display Name: Mj Integration Custom Overflow
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_CustomOverflow(): string | null {
        return this.Get('__mj_integration_CustomOverflow');
    }
    set __mj_integration_CustomOverflow(value: string | null) {
        this.Set('__mj_integration_CustomOverflow', value);
    }

    /**
    * * Field Name: __mj_integration_ExternalVersion
    * * Display Name: Mj Integration External Version
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_ExternalVersion(): string | null {
        return this.Get('__mj_integration_ExternalVersion');
    }
    set __mj_integration_ExternalVersion(value: string | null) {
        this.Set('__mj_integration_ExternalVersion', value);
    }

    /**
    * * Field Name: __mj_integration_LastSeenModifiedValue
    * * Display Name: Mj Integration Last Seen Modified Value
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_LastSeenModifiedValue(): string | null {
        return this.Get('__mj_integration_LastSeenModifiedValue');
    }
    set __mj_integration_LastSeenModifiedValue(value: string | null) {
        this.Set('__mj_integration_LastSeenModifiedValue', value);
    }

    /**
    * * Field Name: __mj_integration_LastReconciledAt
    * * Display Name: Mj Integration Last Reconciled At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastReconciledAt(): Date | null {
        return this.Get('__mj_integration_LastReconciledAt');
    }
    set __mj_integration_LastReconciledAt(value: Date | null) {
        this.Set('__mj_integration_LastReconciledAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastWriterDirection
    * * Display Name: Mj Integration Last Writer Direction
    * * SQL Data Type: nvarchar(10)
    */
    get __mj_integration_LastWriterDirection(): string | null {
        return this.Get('__mj_integration_LastWriterDirection');
    }
    set __mj_integration_LastWriterDirection(value: string | null) {
        this.Set('__mj_integration_LastWriterDirection', value);
    }

    /**
    * * Field Name: __mj_integration_IsTombstoned
    * * Display Name: Mj Integration Is Tombstoned
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get __mj_integration_IsTombstoned(): boolean {
        return this.Get('__mj_integration_IsTombstoned');
    }
    set __mj_integration_IsTombstoned(value: boolean) {
        this.Set('__mj_integration_IsTombstoned', value);
    }

    /**
    * * Field Name: __mj_integration_DeletedDetectedAt
    * * Display Name: Mj Integration Deleted Detected At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_DeletedDetectedAt(): Date | null {
        return this.Get('__mj_integration_DeletedDetectedAt');
    }
    set __mj_integration_DeletedDetectedAt(value: Date | null) {
        this.Set('__mj_integration_DeletedDetectedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Agu Persons - strongly typed entity sub-class
 * * Schema: Common
 * * Base Table: AguPerson
 * * Base View: vwAguPersons
 * * @description Canonical AGU person, one row per AGU member GUID (the cstkey / AGUPersonID that MAPS.Person.AGUPersonID and Recommendation.Contributor.CustomerID both carry). Seeded from those two tables; never created at sign-in. Apps reference a subset of these rows (Research.MemberProfile today; MAPS.Person and Contributor in a later migration). UserID links the MJ login once known; IdentityProviderSubject holds the ForgeRock sub claim.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Agu Persons')
export class CommonAguPersonEntity extends BaseEntity<CommonAguPersonEntityType> {
    /**
    * Loads the Agu Persons record from the database
    * @param ID: string - primary key value to load the Agu Persons record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof CommonAguPersonEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: AGUPersonID
    * * Display Name: AGU Person ID
    * * SQL Data Type: uniqueidentifier
    */
    get AGUPersonID(): string {
        return this.Get('AGUPersonID');
    }
    set AGUPersonID(value: string) {
        this.Set('AGUPersonID', value);
    }

    /**
    * * Field Name: FirstName
    * * Display Name: First Name
    * * SQL Data Type: nvarchar(200)
    */
    get FirstName(): string | null {
        return this.Get('FirstName');
    }
    set FirstName(value: string | null) {
        this.Set('FirstName', value);
    }

    /**
    * * Field Name: LastName
    * * Display Name: Last Name
    * * SQL Data Type: nvarchar(200)
    */
    get LastName(): string | null {
        return this.Get('LastName');
    }
    set LastName(value: string | null) {
        this.Set('LastName', value);
    }

    /**
    * * Field Name: Email
    * * Display Name: Email
    * * SQL Data Type: nvarchar(255)
    */
    get Email(): string | null {
        return this.Get('Email');
    }
    set Email(value: string | null) {
        this.Set('Email', value);
    }

    /**
    * * Field Name: OrcID
    * * Display Name: Orc ID
    * * SQL Data Type: nvarchar(50)
    */
    get OrcID(): string | null {
        return this.Get('OrcID');
    }
    set OrcID(value: string | null) {
        this.Set('OrcID', value);
    }

    /**
    * * Field Name: LinkedInUrl
    * * Display Name: Linked In Url
    * * SQL Data Type: nvarchar(500)
    */
    get LinkedInUrl(): string | null {
        return this.Get('LinkedInUrl');
    }
    set LinkedInUrl(value: string | null) {
        this.Set('LinkedInUrl', value);
    }

    /**
    * * Field Name: UserID
    * * Display Name: User ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Users (vwUsers.ID)
    * * Description: MJ login for this person. NULL until the person signs in (member provisioning) or the seed matched an existing user by email. Unique: one login per person. Deliberately NOT __mj.User.LinkedRecordType/LinkedEntityID, which staff accounts already use for Employee links.
    */
    get UserID(): string | null {
        return this.Get('UserID');
    }
    set UserID(value: string | null) {
        this.Set('UserID', value);
    }

    /**
    * * Field Name: IdentityProviderSubject
    * * Display Name: Identity Provider Subject
    * * SQL Data Type: nvarchar(200)
    */
    get IdentityProviderSubject(): string | null {
        return this.Get('IdentityProviderSubject');
    }
    set IdentityProviderSubject(value: string | null) {
        this.Set('IdentityProviderSubject', value);
    }

    /**
    * * Field Name: Source
    * * Display Name: Source
    * * SQL Data Type: nvarchar(20)
    * * Value List Type: List
    * * Possible Values 
    *   * Both
    *   * Contributor
    *   * MAPS
    *   * Roster
    *   * SSO
    * * Description: Which seed population supplied the row: MAPS (member feed only), Contributor (AGU content corpus only), Both.
    */
    get Source(): 'Both' | 'Contributor' | 'MAPS' | 'Roster' | 'SSO' {
        return this.Get('Source');
    }
    set Source(value: 'Both' | 'Contributor' | 'MAPS' | 'Roster' | 'SSO') {
        this.Set('Source', value);
    }

    /**
    * * Field Name: SeededAt
    * * Display Name: Seeded At
    * * SQL Data Type: datetimeoffset
    * * Default Value: sysdatetimeoffset()
    */
    get SeededAt(): Date {
        return this.Get('SeededAt');
    }
    set SeededAt(value: Date) {
        this.Set('SeededAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: User
    * * Display Name: User
    * * SQL Data Type: nvarchar(100)
    */
    get User(): string | null {
        return this.Get('User');
    }
}


/**
 * Application Users - strongly typed entity sub-class
 * * Schema: FeedbackPlatform
 * * Base Table: ApplicationUser
 * * Base View: vwApplicationUsers
 * * @description Application-specific user authentication data for the feedback platform.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Application Users')
export class FeedbackPlatformApplicationUserEntity extends BaseEntity<FeedbackPlatformApplicationUserEntityType> {
    /**
    * Loads the Application Users record from the database
    * @param ID: string - primary key value to load the Application Users record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof FeedbackPlatformApplicationUserEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * Validate() method override for Application Users entity. This is an auto-generated method that invokes the generated validators for this entity for the following fields:
    * * PasswordHash: The password hash must be exactly 64 characters in length to ensure it conforms to standard cryptographic hash formatting.
    * @public
    * @method
    * @override
    */
    public override Validate(): ValidationResult {
        const result = super.Validate();
        this.ValidatePasswordHashLength(result);
        result.Success = result.Success && (result.Errors.length === 0);

        return result;
    }

    /**
    * The password hash must be exactly 64 characters in length to ensure it conforms to standard cryptographic hash formatting.
    * @param result - the ValidationResult object to add any errors or warnings to
    * @public
    * @method
    */
    public ValidatePasswordHashLength(result: ValidationResult) {
    	if (this.PasswordHash != null && this.PasswordHash.length !== 64) {
    		result.Errors.push(new ValidationErrorInfo(
    			"PasswordHash",
    			"The password hash must be exactly 64 characters long.",
    			this.PasswordHash,
    			ValidationErrorType.Failure
    		));
    	}
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: UserID
    * * Display Name: User ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Users (vwUsers.ID)
    * * Description: Reference to the main User record in MemberJunction (unique).
    */
    get UserID(): string {
        return this.Get('UserID');
    }
    set UserID(value: string) {
        this.Set('UserID', value);
    }

    /**
    * * Field Name: PasswordHash
    * * Display Name: Password Hash
    * * SQL Data Type: nvarchar(64)
    * * Description: MD5 hash of the user password.
    */
    get PasswordHash(): string {
        return this.Get('PasswordHash');
    }
    set PasswordHash(value: string) {
        this.Set('PasswordHash', value);
    }

    /**
    * * Field Name: IsActive
    * * Display Name: Is Active
    * * SQL Data Type: bit
    * * Default Value: 1
    * * Description: Whether the application user account is active.
    */
    get IsActive(): boolean {
        return this.Get('IsActive');
    }
    set IsActive(value: boolean) {
        this.Set('IsActive', value);
    }

    /**
    * * Field Name: LastLoginDate
    * * Display Name: Last Login Date
    * * SQL Data Type: datetimeoffset
    * * Description: Last successful login timestamp.
    */
    get LastLoginDate(): Date | null {
        return this.Get('LastLoginDate');
    }
    set LastLoginDate(value: Date | null) {
        this.Set('LastLoginDate', value);
    }

    /**
    * * Field Name: PasswordResetToken
    * * Display Name: Password Reset Token
    * * SQL Data Type: nvarchar(255)
    * * Description: Token used for password reset functionality.
    */
    get PasswordResetToken(): string | null {
        return this.Get('PasswordResetToken');
    }
    set PasswordResetToken(value: string | null) {
        this.Set('PasswordResetToken', value);
    }

    /**
    * * Field Name: PasswordResetExpiry
    * * Display Name: Password Reset Expiry
    * * SQL Data Type: datetimeoffset
    * * Description: Expiry date/time for password reset token.
    */
    get PasswordResetExpiry(): Date | null {
        return this.Get('PasswordResetExpiry');
    }
    set PasswordResetExpiry(value: Date | null) {
        this.Set('PasswordResetExpiry', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: User
    * * Display Name: User
    * * SQL Data Type: nvarchar(100)
    */
    get User(): string {
        return this.Get('User');
    }
}


/**
 * Awards - strongly typed entity sub-class
 * * Schema: CoreSignal
 * * Base Table: Award
 * * Base View: vwAwards
 * * @description An award/honor from a CoreSignal profile.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Awards')
export class CoreSignalAwardEntity extends BaseEntity<CoreSignalAwardEntityType> {
    /**
    * Loads the Awards record from the database
    * @param ID: string - primary key value to load the Awards record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof CoreSignalAwardEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ProfileID
    * * Display Name: Profile ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Profiles (vwProfiles.ID)
    */
    get ProfileID(): string {
        return this.Get('ProfileID');
    }
    set ProfileID(value: string) {
        this.Set('ProfileID', value);
    }

    /**
    * * Field Name: OrderInProfile
    * * Display Name: Order In Profile
    * * SQL Data Type: int
    */
    get OrderInProfile(): number {
        return this.Get('OrderInProfile');
    }
    set OrderInProfile(value: number) {
        this.Set('OrderInProfile', value);
    }

    /**
    * * Field Name: Title
    * * Display Name: Title
    * * SQL Data Type: nvarchar(1000)
    */
    get Title(): string | null {
        return this.Get('Title');
    }
    set Title(value: string | null) {
        this.Set('Title', value);
    }

    /**
    * * Field Name: Issuer
    * * Display Name: Issuer
    * * SQL Data Type: nvarchar(500)
    */
    get Issuer(): string | null {
        return this.Get('Issuer');
    }
    set Issuer(value: string | null) {
        this.Set('Issuer', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(MAX)
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: DateYear
    * * Display Name: Date Year
    * * SQL Data Type: smallint
    */
    get DateYear(): number | null {
        return this.Get('DateYear');
    }
    set DateYear(value: number | null) {
        this.Set('DateYear', value);
    }

    /**
    * * Field Name: DateMonth
    * * Display Name: Date Month
    * * SQL Data Type: tinyint
    */
    get DateMonth(): number | null {
        return this.Get('DateMonth');
    }
    set DateMonth(value: number | null) {
        this.Set('DateMonth', value);
    }

    /**
    * * Field Name: __mj_integration_SyncStatus
    * * Display Name: Mj Integration Sync Status
    * * SQL Data Type: nvarchar(50)
    * * Default Value: Active
    */
    get __mj_integration_SyncStatus(): string {
        return this.Get('__mj_integration_SyncStatus');
    }
    set __mj_integration_SyncStatus(value: string) {
        this.Set('__mj_integration_SyncStatus', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedAt
    * * Display Name: Mj Integration Last Synced At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastSyncedAt(): Date | null {
        return this.Get('__mj_integration_LastSyncedAt');
    }
    set __mj_integration_LastSyncedAt(value: Date | null) {
        this.Set('__mj_integration_LastSyncedAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedSnapshot
    * * Display Name: Mj Integration Last Synced Snapshot
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_LastSyncedSnapshot(): string | null {
        return this.Get('__mj_integration_LastSyncedSnapshot');
    }
    set __mj_integration_LastSyncedSnapshot(value: string | null) {
        this.Set('__mj_integration_LastSyncedSnapshot', value);
    }

    /**
    * * Field Name: __mj_integration_SyncMessage
    * * Display Name: Mj Integration Sync Message
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_SyncMessage(): string | null {
        return this.Get('__mj_integration_SyncMessage');
    }
    set __mj_integration_SyncMessage(value: string | null) {
        this.Set('__mj_integration_SyncMessage', value);
    }

    /**
    * * Field Name: __mj_integration_ContentHash
    * * Display Name: Mj Integration Content Hash
    * * SQL Data Type: nvarchar(64)
    */
    get __mj_integration_ContentHash(): string | null {
        return this.Get('__mj_integration_ContentHash');
    }
    set __mj_integration_ContentHash(value: string | null) {
        this.Set('__mj_integration_ContentHash', value);
    }

    /**
    * * Field Name: __mj_integration_CustomOverflow
    * * Display Name: Mj Integration Custom Overflow
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_CustomOverflow(): string | null {
        return this.Get('__mj_integration_CustomOverflow');
    }
    set __mj_integration_CustomOverflow(value: string | null) {
        this.Set('__mj_integration_CustomOverflow', value);
    }

    /**
    * * Field Name: __mj_integration_ExternalVersion
    * * Display Name: Mj Integration External Version
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_ExternalVersion(): string | null {
        return this.Get('__mj_integration_ExternalVersion');
    }
    set __mj_integration_ExternalVersion(value: string | null) {
        this.Set('__mj_integration_ExternalVersion', value);
    }

    /**
    * * Field Name: __mj_integration_LastSeenModifiedValue
    * * Display Name: Mj Integration Last Seen Modified Value
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_LastSeenModifiedValue(): string | null {
        return this.Get('__mj_integration_LastSeenModifiedValue');
    }
    set __mj_integration_LastSeenModifiedValue(value: string | null) {
        this.Set('__mj_integration_LastSeenModifiedValue', value);
    }

    /**
    * * Field Name: __mj_integration_LastReconciledAt
    * * Display Name: Mj Integration Last Reconciled At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastReconciledAt(): Date | null {
        return this.Get('__mj_integration_LastReconciledAt');
    }
    set __mj_integration_LastReconciledAt(value: Date | null) {
        this.Set('__mj_integration_LastReconciledAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastWriterDirection
    * * Display Name: Mj Integration Last Writer Direction
    * * SQL Data Type: nvarchar(10)
    */
    get __mj_integration_LastWriterDirection(): string | null {
        return this.Get('__mj_integration_LastWriterDirection');
    }
    set __mj_integration_LastWriterDirection(value: string | null) {
        this.Set('__mj_integration_LastWriterDirection', value);
    }

    /**
    * * Field Name: __mj_integration_IsTombstoned
    * * Display Name: Mj Integration Is Tombstoned
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get __mj_integration_IsTombstoned(): boolean {
        return this.Get('__mj_integration_IsTombstoned');
    }
    set __mj_integration_IsTombstoned(value: boolean) {
        this.Set('__mj_integration_IsTombstoned', value);
    }

    /**
    * * Field Name: __mj_integration_DeletedDetectedAt
    * * Display Name: Mj Integration Deleted Detected At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_DeletedDetectedAt(): Date | null {
        return this.Get('__mj_integration_DeletedDetectedAt');
    }
    set __mj_integration_DeletedDetectedAt(value: Date | null) {
        this.Set('__mj_integration_DeletedDetectedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Certifications - strongly typed entity sub-class
 * * Schema: CoreSignal
 * * Base Table: Certification
 * * Base View: vwCertifications
 * * @description A certification from a CoreSignal profile.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Certifications')
export class CoreSignalCertificationEntity extends BaseEntity<CoreSignalCertificationEntityType> {
    /**
    * Loads the Certifications record from the database
    * @param ID: string - primary key value to load the Certifications record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof CoreSignalCertificationEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ProfileID
    * * Display Name: Profile ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Profiles (vwProfiles.ID)
    */
    get ProfileID(): string {
        return this.Get('ProfileID');
    }
    set ProfileID(value: string) {
        this.Set('ProfileID', value);
    }

    /**
    * * Field Name: OrderInProfile
    * * Display Name: Order In Profile
    * * SQL Data Type: int
    */
    get OrderInProfile(): number {
        return this.Get('OrderInProfile');
    }
    set OrderInProfile(value: number) {
        this.Set('OrderInProfile', value);
    }

    /**
    * * Field Name: Title
    * * Display Name: Title
    * * SQL Data Type: nvarchar(1000)
    */
    get Title(): string | null {
        return this.Get('Title');
    }
    set Title(value: string | null) {
        this.Set('Title', value);
    }

    /**
    * * Field Name: Issuer
    * * Display Name: Issuer
    * * SQL Data Type: nvarchar(500)
    */
    get Issuer(): string | null {
        return this.Get('Issuer');
    }
    set Issuer(value: string | null) {
        this.Set('Issuer', value);
    }

    /**
    * * Field Name: IssuerUrl
    * * Display Name: Issuer Url
    * * SQL Data Type: nvarchar(1000)
    */
    get IssuerUrl(): string | null {
        return this.Get('IssuerUrl');
    }
    set IssuerUrl(value: string | null) {
        this.Set('IssuerUrl', value);
    }

    /**
    * * Field Name: CertificateUrl
    * * Display Name: Certificate Url
    * * SQL Data Type: nvarchar(2000)
    */
    get CertificateUrl(): string | null {
        return this.Get('CertificateUrl');
    }
    set CertificateUrl(value: string | null) {
        this.Set('CertificateUrl', value);
    }

    /**
    * * Field Name: CredentialID
    * * Display Name: Credential ID
    * * SQL Data Type: nvarchar(255)
    */
    get CredentialID(): string | null {
        return this.Get('CredentialID');
    }
    set CredentialID(value: string | null) {
        this.Set('CredentialID', value);
    }

    /**
    * * Field Name: DateFromYear
    * * Display Name: Date From Year
    * * SQL Data Type: smallint
    */
    get DateFromYear(): number | null {
        return this.Get('DateFromYear');
    }
    set DateFromYear(value: number | null) {
        this.Set('DateFromYear', value);
    }

    /**
    * * Field Name: DateFromMonth
    * * Display Name: Date From Month
    * * SQL Data Type: tinyint
    */
    get DateFromMonth(): number | null {
        return this.Get('DateFromMonth');
    }
    set DateFromMonth(value: number | null) {
        this.Set('DateFromMonth', value);
    }

    /**
    * * Field Name: DateToYear
    * * Display Name: Date To Year
    * * SQL Data Type: smallint
    */
    get DateToYear(): number | null {
        return this.Get('DateToYear');
    }
    set DateToYear(value: number | null) {
        this.Set('DateToYear', value);
    }

    /**
    * * Field Name: DateToMonth
    * * Display Name: Date To Month
    * * SQL Data Type: tinyint
    */
    get DateToMonth(): number | null {
        return this.Get('DateToMonth');
    }
    set DateToMonth(value: number | null) {
        this.Set('DateToMonth', value);
    }

    /**
    * * Field Name: __mj_integration_SyncStatus
    * * Display Name: Mj Integration Sync Status
    * * SQL Data Type: nvarchar(50)
    * * Default Value: Active
    */
    get __mj_integration_SyncStatus(): string {
        return this.Get('__mj_integration_SyncStatus');
    }
    set __mj_integration_SyncStatus(value: string) {
        this.Set('__mj_integration_SyncStatus', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedAt
    * * Display Name: Mj Integration Last Synced At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastSyncedAt(): Date | null {
        return this.Get('__mj_integration_LastSyncedAt');
    }
    set __mj_integration_LastSyncedAt(value: Date | null) {
        this.Set('__mj_integration_LastSyncedAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedSnapshot
    * * Display Name: Mj Integration Last Synced Snapshot
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_LastSyncedSnapshot(): string | null {
        return this.Get('__mj_integration_LastSyncedSnapshot');
    }
    set __mj_integration_LastSyncedSnapshot(value: string | null) {
        this.Set('__mj_integration_LastSyncedSnapshot', value);
    }

    /**
    * * Field Name: __mj_integration_SyncMessage
    * * Display Name: Mj Integration Sync Message
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_SyncMessage(): string | null {
        return this.Get('__mj_integration_SyncMessage');
    }
    set __mj_integration_SyncMessage(value: string | null) {
        this.Set('__mj_integration_SyncMessage', value);
    }

    /**
    * * Field Name: __mj_integration_ContentHash
    * * Display Name: Mj Integration Content Hash
    * * SQL Data Type: nvarchar(64)
    */
    get __mj_integration_ContentHash(): string | null {
        return this.Get('__mj_integration_ContentHash');
    }
    set __mj_integration_ContentHash(value: string | null) {
        this.Set('__mj_integration_ContentHash', value);
    }

    /**
    * * Field Name: __mj_integration_CustomOverflow
    * * Display Name: Mj Integration Custom Overflow
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_CustomOverflow(): string | null {
        return this.Get('__mj_integration_CustomOverflow');
    }
    set __mj_integration_CustomOverflow(value: string | null) {
        this.Set('__mj_integration_CustomOverflow', value);
    }

    /**
    * * Field Name: __mj_integration_ExternalVersion
    * * Display Name: Mj Integration External Version
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_ExternalVersion(): string | null {
        return this.Get('__mj_integration_ExternalVersion');
    }
    set __mj_integration_ExternalVersion(value: string | null) {
        this.Set('__mj_integration_ExternalVersion', value);
    }

    /**
    * * Field Name: __mj_integration_LastSeenModifiedValue
    * * Display Name: Mj Integration Last Seen Modified Value
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_LastSeenModifiedValue(): string | null {
        return this.Get('__mj_integration_LastSeenModifiedValue');
    }
    set __mj_integration_LastSeenModifiedValue(value: string | null) {
        this.Set('__mj_integration_LastSeenModifiedValue', value);
    }

    /**
    * * Field Name: __mj_integration_LastReconciledAt
    * * Display Name: Mj Integration Last Reconciled At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastReconciledAt(): Date | null {
        return this.Get('__mj_integration_LastReconciledAt');
    }
    set __mj_integration_LastReconciledAt(value: Date | null) {
        this.Set('__mj_integration_LastReconciledAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastWriterDirection
    * * Display Name: Mj Integration Last Writer Direction
    * * SQL Data Type: nvarchar(10)
    */
    get __mj_integration_LastWriterDirection(): string | null {
        return this.Get('__mj_integration_LastWriterDirection');
    }
    set __mj_integration_LastWriterDirection(value: string | null) {
        this.Set('__mj_integration_LastWriterDirection', value);
    }

    /**
    * * Field Name: __mj_integration_IsTombstoned
    * * Display Name: Mj Integration Is Tombstoned
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get __mj_integration_IsTombstoned(): boolean {
        return this.Get('__mj_integration_IsTombstoned');
    }
    set __mj_integration_IsTombstoned(value: boolean) {
        this.Set('__mj_integration_IsTombstoned', value);
    }

    /**
    * * Field Name: __mj_integration_DeletedDetectedAt
    * * Display Name: Mj Integration Deleted Detected At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_DeletedDetectedAt(): Date | null {
        return this.Get('__mj_integration_DeletedDetectedAt');
    }
    set __mj_integration_DeletedDetectedAt(value: Date | null) {
        this.Set('__mj_integration_DeletedDetectedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Committee Members - strongly typed entity sub-class
 * * Schema: FeedbackPlatform
 * * Base Table: CommitteeMember
 * * Base View: vwCommitteeMembers
 * * @description A user on a section's OEP awards committee for a season.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Committee Members')
export class FeedbackPlatformCommitteeMemberEntity extends BaseEntity<FeedbackPlatformCommitteeMemberEntityType> {
    /**
    * Loads the Committee Members record from the database
    * @param ID: string - primary key value to load the Committee Members record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof FeedbackPlatformCommitteeMemberEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: SeasonID
    * * Display Name: Season ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Seasons (vwSeasons.ID)
    */
    get SeasonID(): string {
        return this.Get('SeasonID');
    }
    set SeasonID(value: string) {
        this.Set('SeasonID', value);
    }

    /**
    * * Field Name: SectionID
    * * Display Name: Section ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Sections (vwSections.ID)
    */
    get SectionID(): string {
        return this.Get('SectionID');
    }
    set SectionID(value: string) {
        this.Set('SectionID', value);
    }

    /**
    * * Field Name: UserID
    * * Display Name: User ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Users (vwUsers.ID)
    */
    get UserID(): string {
        return this.Get('UserID');
    }
    set UserID(value: string) {
        this.Set('UserID', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Season
    * * Display Name: Season
    * * SQL Data Type: nvarchar(200)
    */
    get Season(): string {
        return this.Get('Season');
    }

    /**
    * * Field Name: Section
    * * Display Name: Section
    * * SQL Data Type: nvarchar(200)
    */
    get Section(): string {
        return this.Get('Section');
    }

    /**
    * * Field Name: User
    * * Display Name: User
    * * SQL Data Type: nvarchar(100)
    */
    get User(): string {
        return this.Get('User');
    }
}


/**
 * Committee Selection Items - strongly typed entity sub-class
 * * Schema: FeedbackPlatform
 * * Base Table: CommitteeSelectionItem
 * * Base View: vwCommitteeSelectionItems
 * * @description One presentation picked as a winner on a committee slate. Visible to its presenter only after the season is Complete.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Committee Selection Items')
export class FeedbackPlatformCommitteeSelectionItemEntity extends BaseEntity<FeedbackPlatformCommitteeSelectionItemEntityType> {
    /**
    * Loads the Committee Selection Items record from the database
    * @param ID: string - primary key value to load the Committee Selection Items record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof FeedbackPlatformCommitteeSelectionItemEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * Validate() method override for Committee Selection Items entity. This is an auto-generated method that invokes the generated validators for this entity for the following fields:
    * * Slot: The slot number must be a value between 1 and 20 inclusive.
    * @public
    * @method
    * @override
    */
    public override Validate(): ValidationResult {
        const result = super.Validate();
        this.ValidateSlotRange(result);
        result.Success = result.Success && (result.Errors.length === 0);

        return result;
    }

    /**
    * The slot number must be a value between 1 and 20 inclusive.
    * @param result - the ValidationResult object to add any errors or warnings to
    * @public
    * @method
    */
    public ValidateSlotRange(result: ValidationResult) {
    	if (this.Slot != null && (this.Slot < 1 || this.Slot > 20)) {
    		result.Errors.push(new ValidationErrorInfo(
    			"Slot",
    			"Slot must be between 1 and 20.",
    			this.Slot,
    			ValidationErrorType.Failure
    		));
    	}
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: CommitteeSelectionID
    * * Display Name: Committee Selection ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Committee Selections (vwCommitteeSelections.ID)
    */
    get CommitteeSelectionID(): string {
        return this.Get('CommitteeSelectionID');
    }
    set CommitteeSelectionID(value: string) {
        this.Set('CommitteeSelectionID', value);
    }

    /**
    * * Field Name: PresentationID
    * * Display Name: Presentation ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Presentations (vwPresentations.ID)
    */
    get PresentationID(): string {
        return this.Get('PresentationID');
    }
    set PresentationID(value: string) {
        this.Set('PresentationID', value);
    }

    /**
    * * Field Name: Slot
    * * Display Name: Slot
    * * SQL Data Type: tinyint
    */
    get Slot(): number {
        return this.Get('Slot');
    }
    set Slot(value: number) {
        this.Set('Slot', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Presentation
    * * Display Name: Presentation
    * * SQL Data Type: nvarchar(500)
    */
    get Presentation(): string {
        return this.Get('Presentation');
    }
}


/**
 * Committee Selections - strongly typed entity sub-class
 * * Schema: FeedbackPlatform
 * * Base Table: CommitteeSelection
 * * Base View: vwCommitteeSelections
 * * @description A section committee's winner slate for a season; locked once Submitted.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Committee Selections')
export class FeedbackPlatformCommitteeSelectionEntity extends BaseEntity<FeedbackPlatformCommitteeSelectionEntityType> {
    /**
    * Loads the Committee Selections record from the database
    * @param ID: string - primary key value to load the Committee Selections record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof FeedbackPlatformCommitteeSelectionEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: SeasonID
    * * Display Name: Season ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Seasons (vwSeasons.ID)
    */
    get SeasonID(): string {
        return this.Get('SeasonID');
    }
    set SeasonID(value: string) {
        this.Set('SeasonID', value);
    }

    /**
    * * Field Name: SectionID
    * * Display Name: Section ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Sections (vwSections.ID)
    */
    get SectionID(): string {
        return this.Get('SectionID');
    }
    set SectionID(value: string) {
        this.Set('SectionID', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: nvarchar(20)
    * * Default Value: Draft
    * * Value List Type: List
    * * Possible Values 
    *   * Draft
    *   * Submitted
    */
    get Status(): 'Draft' | 'Submitted' {
        return this.Get('Status');
    }
    set Status(value: 'Draft' | 'Submitted') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: SubmittedAt
    * * Display Name: Submitted At
    * * SQL Data Type: datetimeoffset
    */
    get SubmittedAt(): Date | null {
        return this.Get('SubmittedAt');
    }
    set SubmittedAt(value: Date | null) {
        this.Set('SubmittedAt', value);
    }

    /**
    * * Field Name: SubmittedByUserID
    * * Display Name: Submitted By User ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Users (vwUsers.ID)
    */
    get SubmittedByUserID(): string | null {
        return this.Get('SubmittedByUserID');
    }
    set SubmittedByUserID(value: string | null) {
        this.Set('SubmittedByUserID', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Season
    * * Display Name: Season
    * * SQL Data Type: nvarchar(200)
    */
    get Season(): string {
        return this.Get('Season');
    }

    /**
    * * Field Name: Section
    * * Display Name: Section
    * * SQL Data Type: nvarchar(200)
    */
    get Section(): string {
        return this.Get('Section');
    }

    /**
    * * Field Name: SubmittedByUser
    * * Display Name: Submitted By User
    * * SQL Data Type: nvarchar(100)
    */
    get SubmittedByUser(): string | null {
        return this.Get('SubmittedByUser');
    }
}


/**
 * Conference Registrations - strongly typed entity sub-class
 * * Schema: MAPS
 * * Base Table: ConferenceRegistration
 * * Base View: vwConferenceRegistrations
 * * @description AGU annual meeting registration records sourced from the Maritz registration system, covering AGU23, AGU24, and AGU25.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Conference Registrations')
export class MAPSConferenceRegistrationEntity extends BaseEntity<MAPSConferenceRegistrationEntityType> {
    /**
    * Loads the Conference Registrations record from the database
    * @param ID: string - primary key value to load the Conference Registrations record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof MAPSConferenceRegistrationEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * Validate() method override for Conference Registrations entity. This is an auto-generated method that invokes the generated validators for this entity for the following fields:
    * * Year: The year must be between 2000 and 2100 inclusive.
    * @public
    * @method
    * @override
    */
    public override Validate(): ValidationResult {
        const result = super.Validate();
        this.ValidateYearRange(result);
        result.Success = result.Success && (result.Errors.length === 0);

        return result;
    }

    /**
    * The year must be between 2000 and 2100 inclusive.
    * @param result - the ValidationResult object to add any errors or warnings to
    * @public
    * @method
    */
    public ValidateYearRange(result: ValidationResult) {
    	if (this.Year != null && (this.Year < 2000 || this.Year > 2100)) {
    		result.Errors.push(new ValidationErrorInfo(
    			"Year",
    			"Year must be between 2000 and 2100.",
    			this.Year,
    			ValidationErrorType.Failure
    		));
    	}
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: PersonID
    * * Display Name: Person ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: People (vwPeople.ID)
    * * Description: References the person who registered for the meeting.
    */
    get PersonID(): string {
        return this.Get('PersonID');
    }
    set PersonID(value: string) {
        this.Set('PersonID', value);
    }

    /**
    * * Field Name: Year
    * * Display Name: Year
    * * SQL Data Type: int
    * * Description: Year of the AGU annual meeting (e.g., 2023, 2024, 2025).
    */
    get Year(): number {
        return this.Get('Year');
    }
    set Year(value: number) {
        this.Set('Year', value);
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(150)
    * * Description: Company or organization affiliation reported at registration time.
    */
    get Company(): string | null {
        return this.Get('Company');
    }
    set Company(value: string | null) {
        this.Set('Company', value);
    }

    /**
    * * Field Name: Title
    * * Display Name: Title
    * * SQL Data Type: nvarchar(150)
    * * Description: Job title reported at registration time.
    */
    get Title(): string | null {
        return this.Get('Title');
    }
    set Title(value: string | null) {
        this.Set('Title', value);
    }

    /**
    * * Field Name: City
    * * Display Name: City
    * * SQL Data Type: nvarchar(75)
    * * Description: City of affiliation reported at registration time.
    */
    get City(): string | null {
        return this.Get('City');
    }
    set City(value: string | null) {
        this.Set('City', value);
    }

    /**
    * * Field Name: State
    * * Display Name: State
    * * SQL Data Type: nvarchar(75)
    * * Description: State or province of affiliation reported at registration time.
    */
    get State(): string | null {
        return this.Get('State');
    }
    set State(value: string | null) {
        this.Set('State', value);
    }

    /**
    * * Field Name: Country
    * * Display Name: Country
    * * SQL Data Type: nvarchar(75)
    * * Description: Country of affiliation reported at registration time. Per source convention, blank when the country is the United States.
    */
    get Country(): string | null {
        return this.Get('Country');
    }
    set Country(value: string | null) {
        this.Set('Country', value);
    }

    /**
    * * Field Name: JobFunction
    * * Display Name: Job Function
    * * SQL Data Type: nvarchar(50)
    * * Description: Self-reported job function captured at registration. Only populated for the 2025 meeting; uses a value set distinct from MAPS.Person.JobFunction.
    */
    get JobFunction(): string | null {
        return this.Get('JobFunction');
    }
    set JobFunction(value: string | null) {
        this.Set('JobFunction', value);
    }

    /**
    * * Field Name: CareerStage
    * * Display Name: Career Stage
    * * SQL Data Type: nvarchar(20)
    * * Value List Type: List
    * * Possible Values 
    *   * Early Career
    *   * Experienced
    *   * Mid-Career
    *   * Retired
    *   * Student
    *   * Unknown
    * * Description: Self-reported career stage captured at registration. Only populated for the 2024 and 2025 meetings.
    */
    get CareerStage(): 'Early Career' | 'Experienced' | 'Mid-Career' | 'Retired' | 'Student' | 'Unknown' | null {
        return this.Get('CareerStage');
    }
    set CareerStage(value: 'Early Career' | 'Experienced' | 'Mid-Career' | 'Retired' | 'Student' | 'Unknown' | null) {
        this.Set('CareerStage', value);
    }

    /**
    * * Field Name: InstitutionType
    * * Display Name: Institution Type
    * * SQL Data Type: nvarchar(75)
    * * Description: Type of employing institution reported at registration. Includes some categories no longer offered on the current form (e.g., Military, Self Employed, Unemployed, Retired).
    */
    get InstitutionType(): string | null {
        return this.Get('InstitutionType');
    }
    set InstitutionType(value: string | null) {
        this.Set('InstitutionType', value);
    }

    /**
    * * Field Name: PrimarySection
    * * Display Name: Primary Section
    * * SQL Data Type: nvarchar(100)
    * * Description: Primary AGU scientific section the registrant affiliates with (e.g., Atmospheric Sciences, Hydrology, Seismology). May be blank.
    */
    get PrimarySection(): string | null {
        return this.Get('PrimarySection');
    }
    set PrimarySection(value: string | null) {
        this.Set('PrimarySection', value);
    }

    /**
    * * Field Name: FederalGovernment
    * * Display Name: Federal Government
    * * SQL Data Type: nvarchar(10)
    * * Value List Type: List
    * * Possible Values 
    *   * No
    *   * Unknown
    *   * Yes
    * * Description: Whether the registrant identified as a US federal government employee. Yes / No / Unknown.
    */
    get FederalGovernment(): 'No' | 'Unknown' | 'Yes' | null {
        return this.Get('FederalGovernment');
    }
    set FederalGovernment(value: 'No' | 'Unknown' | 'Yes' | null) {
        this.Set('FederalGovernment', value);
    }

    /**
    * * Field Name: FirstTimeAttendee
    * * Display Name: First Time Attendee
    * * SQL Data Type: nvarchar(10)
    * * Value List Type: List
    * * Possible Values 
    *   * No
    *   * Unknown
    *   * Yes
    * * Description: Whether the registrant identified as a first time attendee of the AGU annual meeting. Yes / No / Unknown.
    */
    get FirstTimeAttendee(): 'No' | 'Unknown' | 'Yes' | null {
        return this.Get('FirstTimeAttendee');
    }
    set FirstTimeAttendee(value: 'No' | 'Unknown' | 'Yes' | null) {
        this.Set('FirstTimeAttendee', value);
    }

    /**
    * * Field Name: EarlyCareerScientist
    * * Display Name: Early Career Scientist
    * * SQL Data Type: nvarchar(10)
    * * Value List Type: List
    * * Possible Values 
    *   * No
    *   * Unknown
    *   * Yes
    * * Description: Whether the registrant self-identified as an early career scientist. Yes / No / Unknown.
    */
    get EarlyCareerScientist(): 'No' | 'Unknown' | 'Yes' | null {
        return this.Get('EarlyCareerScientist');
    }
    set EarlyCareerScientist(value: 'No' | 'Unknown' | 'Yes' | null) {
        this.Set('EarlyCareerScientist', value);
    }

    /**
    * * Field Name: K12Educator
    * * Display Name: K12 Educator
    * * SQL Data Type: nvarchar(10)
    * * Value List Type: List
    * * Possible Values 
    *   * No
    *   * Unknown
    *   * Yes
    * * Description: Whether the registrant self-identified as a K-12 educator. Yes / No / Unknown.
    */
    get K12Educator(): 'No' | 'Unknown' | 'Yes' | null {
        return this.Get('K12Educator');
    }
    set K12Educator(value: 'No' | 'Unknown' | 'Yes' | null) {
        this.Set('K12Educator', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Contact Message Events - strongly typed entity sub-class
 * * Schema: MAPS
 * * Base Table: ContactMessageEvent
 * * Base View: vwContactMessageEvents
 * * @description One row per SendGrid Event Webhook event for a MAPS contact message — the exact engagement audit trail. The filtered unique index on SGEventID (SendGrid's globally-unique sg_event_id) makes webhook-retry handling exactly idempotent: the duplicate insert fails and the parent ContactMessage rollup update is skipped.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Contact Message Events')
export class MAPSContactMessageEventEntity extends BaseEntity<MAPSContactMessageEventEntityType> {
    /**
    * Loads the Contact Message Events record from the database
    * @param ID: string - primary key value to load the Contact Message Events record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof MAPSContactMessageEventEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ContactMessageID
    * * Display Name: Contact Message ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Contact Messages (vwContactMessages.ID)
    * * Description: Parent MAPS.ContactMessage — resolved from the event's custom_args.contactMessageId echo.
    */
    get ContactMessageID(): string {
        return this.Get('ContactMessageID');
    }
    set ContactMessageID(value: string) {
        this.Set('ContactMessageID', value);
    }

    /**
    * * Field Name: EventType
    * * Display Name: Event Type
    * * SQL Data Type: nvarchar(20)
    * * Value List Type: List
    * * Possible Values 
    *   * bounce
    *   * click
    *   * deferred
    *   * delivered
    *   * dropped
    *   * open
    *   * processed
    *   * spamreport
    * * Description: SendGrid's own event vocabulary, stored verbatim: processed, deferred, delivered, open, click, bounce, dropped, spamreport. processed/deferred are audit-only (no rollup change).
    */
    get EventType(): 'bounce' | 'click' | 'deferred' | 'delivered' | 'dropped' | 'open' | 'processed' | 'spamreport' {
        return this.Get('EventType');
    }
    set EventType(value: 'bounce' | 'click' | 'deferred' | 'delivered' | 'dropped' | 'open' | 'processed' | 'spamreport') {
        this.Set('EventType', value);
    }

    /**
    * * Field Name: OccurredAt
    * * Display Name: Occurred At
    * * SQL Data Type: datetimeoffset
    * * Description: When the event happened at SendGrid (the event's unix timestamp), distinct from __mj_CreatedAt (when the webhook delivered it here).
    */
    get OccurredAt(): Date {
        return this.Get('OccurredAt');
    }
    set OccurredAt(value: Date) {
        this.Set('OccurredAt', value);
    }

    /**
    * * Field Name: SGEventID
    * * Display Name: SG Event ID
    * * SQL Data Type: nvarchar(128)
    * * Description: SendGrid sg_event_id — globally unique per event; the exact-idempotency key for webhook retries (filtered unique index). Nullable defensively: an event missing the field is still recorded, just without dedup.
    */
    get SGEventID(): string | null {
        return this.Get('SGEventID');
    }
    set SGEventID(value: string | null) {
        this.Set('SGEventID', value);
    }

    /**
    * * Field Name: Url
    * * Display Name: Url
    * * SQL Data Type: nvarchar(2048)
    * * Description: Click events: the URL that was clicked. NULL for other event types.
    */
    get Url(): string | null {
        return this.Get('Url');
    }
    set Url(value: string | null) {
        this.Set('Url', value);
    }

    /**
    * * Field Name: Reason
    * * Display Name: Reason
    * * SQL Data Type: nvarchar(1000)
    * * Description: Bounce/dropped/spam detail (the event's reason field). NULL for other event types.
    */
    get Reason(): string | null {
        return this.Get('Reason');
    }
    set Reason(value: string | null) {
        this.Set('Reason', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: ContactMessage
    * * Display Name: Contact Message
    * * SQL Data Type: nvarchar(310)
    */
    get ContactMessage(): string | null {
        return this.Get('ContactMessage');
    }
}


/**
 * Contact Messages - strongly typed entity sub-class
 * * Schema: MAPS
 * * Base Table: ContactMessage
 * * Base View: vwContactMessages
 * * @description One row per recipient of a MAPS contact-candidates send. Carries the message content, the delivery-safety mode the send ran under (dryrun/sandbox/redirect/live), and rollup engagement columns maintained from SendGrid webhook events. ID doubles as the webhook correlation key (sent as SendGrid custom_args.contactMessageId). A bulk send writes one row per candidate, grouped by BatchID.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Contact Messages')
export class MAPSContactMessageEntity extends BaseEntity<MAPSContactMessageEntityType> {
    /**
    * Loads the Contact Messages record from the database
    * @param ID: string - primary key value to load the Contact Messages record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof MAPSContactMessageEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    * * Description: Owning company. Authoritative attribution/scoping key for the partner-account model (V031).
    */
    get CompanyID(): string {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: PartnerTokenID
    * * Display Name: Partner Token ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Partner Tokens (vwPartnerTokens.ID)
    * * Description: Legacy owning partner token. Dual-written while the standalone cookie path lives; dropped in PR B. Ownership scoping uses CompanyID.
    */
    get PartnerTokenID(): string | null {
        return this.Get('PartnerTokenID');
    }
    set PartnerTokenID(value: string | null) {
        this.Set('PartnerTokenID', value);
    }

    /**
    * * Field Name: SearchLogID
    * * Display Name: Search Log ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Search Logs (vwSearchLogs.ID)
    * * Description: Originating search (MAPS.SearchLog). The owned search snapshot is both the ownership check and the trusted source of the recipient email. Nullable so contact history outlives search-log retention.
    */
    get SearchLogID(): string | null {
        return this.Get('SearchLogID');
    }
    set SearchLogID(value: string | null) {
        this.Set('SearchLogID', value);
    }

    /**
    * * Field Name: BatchID
    * * Display Name: Batch ID
    * * SQL Data Type: uniqueidentifier
    * * Description: Groups the recipients of one mapsContactSend mutation call (one modal send). The history drawer groups rows on it.
    */
    get BatchID(): string {
        return this.Get('BatchID');
    }
    set BatchID(value: string) {
        this.Set('BatchID', value);
    }

    /**
    * * Field Name: CandidateID
    * * Display Name: Candidate ID
    * * SQL Data Type: nvarchar(200)
    * * Description: Engine contributor id (= MAPSCandidate.candidateId; matches Recommendation Contributor.CustomerID sizing). Not a FK — the corpus and this ledger have independent lifecycles.
    */
    get CandidateID(): string {
        return this.Get('CandidateID');
    }
    set CandidateID(value: string) {
        this.Set('CandidateID', value);
    }

    /**
    * * Field Name: RecipientName
    * * Display Name: Recipient Name
    * * SQL Data Type: nvarchar(310)
    * * Description: Candidate display name at send time (first + last from the snapshot).
    */
    get RecipientName(): string | null {
        return this.Get('RecipientName');
    }
    set RecipientName(value: string | null) {
        this.Set('RecipientName', value);
    }

    /**
    * * Field Name: RecipientEmail
    * * Display Name: Recipient Email
    * * SQL Data Type: nvarchar(255)
    * * Description: The candidate's email, resolved server-side from the owned search snapshot — never client-supplied. In redirect/safety-net modes this remains the INTENDED recipient; RedirectedTo records where the mail actually went.
    */
    get RecipientEmail(): string {
        return this.Get('RecipientEmail');
    }
    set RecipientEmail(value: string) {
        this.Set('RecipientEmail', value);
    }

    /**
    * * Field Name: ReplyTo
    * * Display Name: Reply To
    * * SQL Data Type: nvarchar(255)
    * * Description: Reply-To header: the requesting partner user's email, so candidate replies go straight to the recruiter. NULL when the session carries no user email.
    */
    get ReplyTo(): string | null {
        return this.Get('ReplyTo');
    }
    set ReplyTo(value: string | null) {
        this.Set('ReplyTo', value);
    }

    /**
    * * Field Name: Subject
    * * Display Name: Subject
    * * SQL Data Type: nvarchar(500)
    * * Description: Message subject as composed (before any redirect-mode test stamp).
    */
    get Subject(): string {
        return this.Get('Subject');
    }
    set Subject(value: string) {
        this.Set('Subject', value);
    }

    /**
    * * Field Name: Body
    * * Display Name: Body
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Message body as composed (plain text; before any redirect-mode banner).
    */
    get Body(): string {
        return this.Get('Body');
    }
    set Body(value: string) {
        this.Set('Body', value);
    }

    /**
    * * Field Name: DeliveryMode
    * * Display Name: Delivery Mode
    * * SQL Data Type: nvarchar(10)
    * * Value List Type: List
    * * Possible Values 
    *   * dryrun
    *   * live
    *   * redirect
    *   * sandbox
    * * Description: Delivery-safety mode the send ran under: dryrun (no external call), sandbox (SendGrid validates, no delivery), redirect (delivered to the test/catch inbox only), live (real member delivery; requires the two-key unlock). Partner-facing "contacted" signals count only live and redirect rows.
    */
    get DeliveryMode(): 'dryrun' | 'live' | 'redirect' | 'sandbox' {
        return this.Get('DeliveryMode');
    }
    set DeliveryMode(value: 'dryrun' | 'live' | 'redirect' | 'sandbox') {
        this.Set('DeliveryMode', value);
    }

    /**
    * * Field Name: RedirectedTo
    * * Display Name: Redirected To
    * * SQL Data Type: nvarchar(255)
    * * Description: Where the mail actually went when the safety net rewrote the recipient (redirect mode, or any non-live mode falling to the hard-coded catch address). NULL for live sends.
    */
    get RedirectedTo(): string | null {
        return this.Get('RedirectedTo');
    }
    set RedirectedTo(value: string | null) {
        this.Set('RedirectedTo', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: nvarchar(12)
    * * Default Value: Pending
    * * Value List Type: List
    * * Possible Values 
    *   * Bounced
    *   * Clicked
    *   * Delivered
    *   * Dropped
    *   * Failed
    *   * Opened
    *   * Pending
    *   * Sent
    *   * Spam
    * * Description: Rollup of send outcome + newest webhook engagement. Monotonic (Pending<Sent<Delivered<Opened<Clicked; Bounced/Dropped/Spam/Failed terminal) — webhook replays can never regress it.
    */
    get Status(): 'Bounced' | 'Clicked' | 'Delivered' | 'Dropped' | 'Failed' | 'Opened' | 'Pending' | 'Sent' | 'Spam' {
        return this.Get('Status');
    }
    set Status(value: 'Bounced' | 'Clicked' | 'Delivered' | 'Dropped' | 'Failed' | 'Opened' | 'Pending' | 'Sent' | 'Spam') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: SGMessageID
    * * Display Name: SG Message ID
    * * SQL Data Type: nvarchar(128)
    * * Description: SendGrid x-message-id captured from the send response. Support/dashboard lookup convenience only — webhook correlation uses custom_args.contactMessageId (= ID).
    */
    get SGMessageID(): string | null {
        return this.Get('SGMessageID');
    }
    set SGMessageID(value: string | null) {
        this.Set('SGMessageID', value);
    }

    /**
    * * Field Name: SentAt
    * * Display Name: Sent At
    * * SQL Data Type: datetimeoffset
    * * Description: When the send left this system (engine call returned success). NULL for rows that never sent (Failed before transport).
    */
    get SentAt(): Date | null {
        return this.Get('SentAt');
    }
    set SentAt(value: Date | null) {
        this.Set('SentAt', value);
    }

    /**
    * * Field Name: DeliveredAt
    * * Display Name: Delivered At
    * * SQL Data Type: datetimeoffset
    * * Description: First delivered event timestamp (set-if-null on webhook replay).
    */
    get DeliveredAt(): Date | null {
        return this.Get('DeliveredAt');
    }
    set DeliveredAt(value: Date | null) {
        this.Set('DeliveredAt', value);
    }

    /**
    * * Field Name: OpenedAt
    * * Display Name: Opened At
    * * SQL Data Type: datetimeoffset
    * * Description: First open event timestamp (set-if-null on webhook replay).
    */
    get OpenedAt(): Date | null {
        return this.Get('OpenedAt');
    }
    set OpenedAt(value: Date | null) {
        this.Set('OpenedAt', value);
    }

    /**
    * * Field Name: OpenCount
    * * Display Name: Open Count
    * * SQL Data Type: int
    * * Default Value: 0
    * * Description: Count of unique open events (deduped on sg_event_id — exact, not approximate).
    */
    get OpenCount(): number {
        return this.Get('OpenCount');
    }
    set OpenCount(value: number) {
        this.Set('OpenCount', value);
    }

    /**
    * * Field Name: ClickedAt
    * * Display Name: Clicked At
    * * SQL Data Type: datetimeoffset
    * * Description: First click event timestamp (set-if-null on webhook replay).
    */
    get ClickedAt(): Date | null {
        return this.Get('ClickedAt');
    }
    set ClickedAt(value: Date | null) {
        this.Set('ClickedAt', value);
    }

    /**
    * * Field Name: ClickCount
    * * Display Name: Click Count
    * * SQL Data Type: int
    * * Default Value: 0
    * * Description: Count of unique click events (deduped on sg_event_id — exact, not approximate).
    */
    get ClickCount(): number {
        return this.Get('ClickCount');
    }
    set ClickCount(value: number) {
        this.Set('ClickCount', value);
    }

    /**
    * * Field Name: BouncedAt
    * * Display Name: Bounced At
    * * SQL Data Type: datetimeoffset
    * * Description: Timestamp of the bounce event that made Status terminal.
    */
    get BouncedAt(): Date | null {
        return this.Get('BouncedAt');
    }
    set BouncedAt(value: Date | null) {
        this.Set('BouncedAt', value);
    }

    /**
    * * Field Name: BounceReason
    * * Display Name: Bounce Reason
    * * SQL Data Type: nvarchar(500)
    * * Description: Bounce/dropped/spam detail from the event that made Status terminal.
    */
    get BounceReason(): string | null {
        return this.Get('BounceReason');
    }
    set BounceReason(value: string | null) {
        this.Set('BounceReason', value);
    }

    /**
    * * Field Name: ErrorMessage
    * * Display Name: Error Message
    * * SQL Data Type: nvarchar(1000)
    * * Description: Send-path failure detail (Status=Failed): engine/provider error, or the safety reason when the recipient was blocked before transport.
    */
    get ErrorMessage(): string | null {
        return this.Get('ErrorMessage');
    }
    set ErrorMessage(value: string | null) {
        this.Set('ErrorMessage', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: InviteID
    * * Display Name: Invite ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Magic Link Invites (vwMagicLinkInvites.ID)
    * * Description: Magic-link invite the sending session was minted from (partner key sessions). Exact per-send provenance: rotation mints a new invite, so this splits across rotations — rotation-stable sender grouping uses ReplyTo. NULL on pre-V041 rows, staff sends, and cookie sessions.
    */
    get InviteID(): string | null {
        return this.Get('InviteID');
    }
    set InviteID(value: string | null) {
        this.Set('InviteID', value);
    }

    /**
    * * Field Name: UserID
    * * Display Name: User ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Users (vwUsers.ID)
    * * Description: Real MJ user for staff/named-user sends. Partner recruiters have no per-recruiter MJ user (shared anonymous link principal), so at most one of InviteID/UserID is set. NULL on pre-V041 rows and cookie sessions.
    */
    get UserID(): string | null {
        return this.Get('UserID');
    }
    set UserID(value: string | null) {
        this.Set('UserID', value);
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string {
        return this.Get('Company');
    }

    /**
    * * Field Name: User
    * * Display Name: User
    * * SQL Data Type: nvarchar(100)
    */
    get User(): string | null {
        return this.Get('User');
    }
}


/**
 * Contents - strongly typed entity sub-class
 * * Schema: Recommendation
 * * Base Table: Content
 * * Base View: vwContents
 * * @description Stores content items for the recommendation system including grants, articles, and other searchable content.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Contents')
export class RecommendationContentEntity extends BaseEntity<RecommendationContentEntityType> {
    /**
    * Loads the Contents record from the database
    * @param ID: number - primary key value to load the Contents record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof RecommendationContentEntity
    * @method
    * @override
    */
    public async Load(ID: number, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: int
    * * Description: Unique identifier for the content item.
    */
    get ID(): number {
        return this.Get('ID');
    }

    /**
    * * Field Name: SourceID
    * * Display Name: Source ID
    * * SQL Data Type: int
    * * Description: Identifier for the source system or dataset.
    */
    get SourceID(): number | null {
        return this.Get('SourceID');
    }
    set SourceID(value: number | null) {
        this.Set('SourceID', value);
    }

    /**
    * * Field Name: Source
    * * Display Name: Source
    * * SQL Data Type: nvarchar(100)
    * * Description: Name of the content source (e.g., NSF, NASA, NIH).
    */
    get Source(): string | null {
        return this.Get('Source');
    }
    set Source(value: string | null) {
        this.Set('Source', value);
    }

    /**
    * * Field Name: ContentID
    * * Display Name: Content ID
    * * SQL Data Type: nvarchar(100)
    * * Description: External identifier from the source system.
    */
    get ContentID(): string | null {
        return this.Get('ContentID');
    }
    set ContentID(value: string | null) {
        this.Set('ContentID', value);
    }

    /**
    * * Field Name: ContentType
    * * Display Name: Content Type
    * * SQL Data Type: nvarchar(100)
    * * Description: Type of content (e.g., Grant, Article, Report).
    */
    get ContentType(): string | null {
        return this.Get('ContentType');
    }
    set ContentType(value: string | null) {
        this.Set('ContentType', value);
    }

    /**
    * * Field Name: Title
    * * Display Name: Title
    * * SQL Data Type: nvarchar(500)
    * * Description: Title of the content item.
    */
    get Title(): string | null {
        return this.Get('Title');
    }
    set Title(value: string | null) {
        this.Set('Title', value);
    }

    /**
    * * Field Name: Text
    * * Display Name: Text
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Full text content or description.
    */
    get Text(): string | null {
        return this.Get('Text');
    }
    set Text(value: string | null) {
        this.Set('Text', value);
    }

    /**
    * * Field Name: DOI
    * * Display Name: DOI
    * * SQL Data Type: nvarchar(100)
    * * Description: Digital Object Identifier if applicable.
    */
    get DOI(): string | null {
        return this.Get('DOI');
    }
    set DOI(value: string | null) {
        this.Set('DOI', value);
    }

    /**
    * * Field Name: URL
    * * Display Name: URL
    * * SQL Data Type: nvarchar(MAX)
    * * Description: URL to the original content source.
    */
    get URL(): string | null {
        return this.Get('URL');
    }
    set URL(value: string | null) {
        this.Set('URL', value);
    }

    /**
    * * Field Name: Date
    * * Display Name: Date
    * * SQL Data Type: date
    * * Description: Publication or release date of the content.
    */
    get Date(): Date | null {
        return this.Get('Date');
    }
    set Date(value: Date | null) {
        this.Set('Date', value);
    }

    /**
    * * Field Name: EmbeddingID
    * * Display Name: Embedding ID
    * * SQL Data Type: nvarchar(50)
    * * Description: Identifier for the associated vector embedding.
    */
    get EmbeddingID(): string | null {
        return this.Get('EmbeddingID');
    }
    set EmbeddingID(value: string | null) {
        this.Set('EmbeddingID', value);
    }

    /**
    * * Field Name: UpdateVector
    * * Display Name: Update Vector
    * * SQL Data Type: bit
    * * Description: Flag indicating if the vector embedding needs to be updated.
    */
    get UpdateVector(): boolean {
        return this.Get('UpdateVector');
    }
    set UpdateVector(value: boolean) {
        this.Set('UpdateVector', value);
    }

    /**
    * * Field Name: isError
    * * Display Name: is Error
    * * SQL Data Type: bit
    * * Default Value: 0
    * * Description: Flag indicating if there was an error processing this content.
    */
    get isError(): boolean {
        return this.Get('isError');
    }
    set isError(value: boolean) {
        this.Set('isError', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    * * Description: Timestamp when the record was created.
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    * * Description: Timestamp when the record was last updated.
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Metadata
    * * Display Name: Metadata
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Additional metadata in JSON format.
    */
    get Metadata(): string | null {
        return this.Get('Metadata');
    }
    set Metadata(value: string | null) {
        this.Set('Metadata', value);
    }

    /**
    * * Field Name: DisplayTitle
    * * Display Name: Display Title
    * * SQL Data Type: nvarchar(400)
    */
    get DisplayTitle(): string | null {
        return this.Get('DisplayTitle');
    }
    set DisplayTitle(value: string | null) {
        this.Set('DisplayTitle', value);
    }

    /**
    * * Field Name: DisplayTitleVersion
    * * Display Name: Display Title Version
    * * SQL Data Type: nvarchar(16)
    */
    get DisplayTitleVersion(): string | null {
        return this.Get('DisplayTitleVersion');
    }
    set DisplayTitleVersion(value: string | null) {
        this.Set('DisplayTitleVersion', value);
    }
}


/**
 * Contributor Contents - strongly typed entity sub-class
 * * Schema: Recommendation
 * * Base Table: ContributorContent
 * * Base View: vwContributorContents
 * * @description Junction between Recommendation.Contributor and Recommendation.Content. ContentID is NULLABLE on prod.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Contributor Contents')
export class RecommendationContributorContentEntity extends BaseEntity<RecommendationContributorContentEntityType> {
    /**
    * Loads the Contributor Contents record from the database
    * @param ID: number - primary key value to load the Contributor Contents record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof RecommendationContributorContentEntity
    * @method
    * @override
    */
    public async Load(ID: number, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: int
    */
    get ID(): number {
        return this.Get('ID');
    }

    /**
    * * Field Name: SourceID
    * * Display Name: Source ID
    * * SQL Data Type: int
    */
    get SourceID(): number | null {
        return this.Get('SourceID');
    }
    set SourceID(value: number | null) {
        this.Set('SourceID', value);
    }

    /**
    * * Field Name: Source
    * * Display Name: Source
    * * SQL Data Type: varchar(15)
    */
    get Source(): string | null {
        return this.Get('Source');
    }
    set Source(value: string | null) {
        this.Set('Source', value);
    }

    /**
    * * Field Name: ContributorID
    * * Display Name: Contributor ID
    * * SQL Data Type: int
    * * Related Entity/Foreign Key: Contributors (vwContributors.ID)
    */
    get ContributorID(): number {
        return this.Get('ContributorID');
    }
    set ContributorID(value: number) {
        this.Set('ContributorID', value);
    }

    /**
    * * Field Name: ContentID
    * * Display Name: Content ID
    * * SQL Data Type: int
    * * Related Entity/Foreign Key: Contents (vwContents.ID)
    */
    get ContentID(): number | null {
        return this.Get('ContentID');
    }
    set ContentID(value: number | null) {
        this.Set('ContentID', value);
    }

    /**
    * * Field Name: DOI
    * * Display Name: Doi
    * * SQL Data Type: nvarchar(100)
    */
    get DOI(): string | null {
        return this.Get('DOI');
    }
    set DOI(value: string | null) {
        this.Set('DOI', value);
    }

    /**
    * * Field Name: Role1
    * * Display Name: Role 1
    * * SQL Data Type: nvarchar(100)
    */
    get Role1(): string | null {
        return this.Get('Role1');
    }
    set Role1(value: string | null) {
        this.Set('Role1', value);
    }

    /**
    * * Field Name: Role2
    * * Display Name: Role 2
    * * SQL Data Type: nvarchar(100)
    */
    get Role2(): string | null {
        return this.Get('Role2');
    }
    set Role2(value: string | null) {
        this.Set('Role2', value);
    }

    /**
    * * Field Name: CorrespondingAuthor
    * * Display Name: Corresponding Author
    * * SQL Data Type: int
    */
    get CorrespondingAuthor(): number | null {
        return this.Get('CorrespondingAuthor');
    }
    set CorrespondingAuthor(value: number | null) {
        this.Set('CorrespondingAuthor', value);
    }

    /**
    * * Field Name: Affiliation
    * * Display Name: Affiliation
    * * SQL Data Type: nvarchar(500)
    */
    get Affiliation(): string | null {
        return this.Get('Affiliation');
    }
    set Affiliation(value: string | null) {
        this.Set('Affiliation', value);
    }

    /**
    * * Field Name: Order
    * * Display Name: Order
    * * SQL Data Type: int
    */
    get Order(): number | null {
        return this.Get('Order');
    }
    set Order(value: number | null) {
        this.Set('Order', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Contributors - strongly typed entity sub-class
 * * Schema: Recommendation
 * * Base Table: Contributor
 * * Base View: vwContributors
 * * @description AGU person eligible to author / contribute to grant or content records. One row per unique CustomerID. Vector embeddings are aggregated from the Contributor's linked Content rows.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Contributors')
export class RecommendationContributorEntity extends BaseEntity<RecommendationContributorEntityType> {
    /**
    * Loads the Contributors record from the database
    * @param ID: number - primary key value to load the Contributors record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof RecommendationContributorEntity
    * @method
    * @override
    */
    public async Load(ID: number, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: int
    */
    get ID(): number {
        return this.Get('ID');
    }

    /**
    * * Field Name: CustomerID
    * * Display Name: Customer ID
    * * SQL Data Type: nvarchar(100)
    */
    get CustomerID(): string {
        return this.Get('CustomerID');
    }
    set CustomerID(value: string) {
        this.Set('CustomerID', value);
    }

    /**
    * * Field Name: Name
    * * Display Name: Name
    * * SQL Data Type: nvarchar(100)
    */
    get Name(): string | null {
        return this.Get('Name');
    }
    set Name(value: string | null) {
        this.Set('Name', value);
    }

    /**
    * * Field Name: Organization
    * * Display Name: Organization
    * * SQL Data Type: nvarchar(500)
    */
    get Organization(): string | null {
        return this.Get('Organization');
    }
    set Organization(value: string | null) {
        this.Set('Organization', value);
    }

    /**
    * * Field Name: JobTitle
    * * Display Name: Job Title
    * * SQL Data Type: nvarchar(500)
    */
    get JobTitle(): string | null {
        return this.Get('JobTitle');
    }
    set JobTitle(value: string | null) {
        this.Set('JobTitle', value);
    }

    /**
    * * Field Name: Email
    * * Display Name: Email
    * * SQL Data Type: nvarchar(255)
    */
    get Email(): string | null {
        return this.Get('Email');
    }
    set Email(value: string | null) {
        this.Set('Email', value);
    }

    /**
    * * Field Name: DoNotDisplay
    * * Display Name: Do Not Display
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get DoNotDisplay(): boolean {
        return this.Get('DoNotDisplay');
    }
    set DoNotDisplay(value: boolean) {
        this.Set('DoNotDisplay', value);
    }

    /**
    * * Field Name: EmbeddingID
    * * Display Name: Embedding ID
    * * SQL Data Type: nvarchar(50)
    */
    get EmbeddingID(): string | null {
        return this.Get('EmbeddingID');
    }
    set EmbeddingID(value: string | null) {
        this.Set('EmbeddingID', value);
    }

    /**
    * * Field Name: UpdateVector
    * * Display Name: Update Vector
    * * SQL Data Type: bit
    * * Default Value: 1
    */
    get UpdateVector(): boolean {
        return this.Get('UpdateVector');
    }
    set UpdateVector(value: boolean) {
        this.Set('UpdateVector', value);
    }

    /**
    * * Field Name: isError
    * * Display Name: is Error
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get isError(): boolean {
        return this.Get('isError');
    }
    set isError(value: boolean) {
        this.Set('isError', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: FirstName
    * * Display Name: First Name
    * * SQL Data Type: nvarchar(200)
    */
    get FirstName(): string | null {
        return this.Get('FirstName');
    }
    set FirstName(value: string | null) {
        this.Set('FirstName', value);
    }

    /**
    * * Field Name: LastName
    * * Display Name: Last Name
    * * SQL Data Type: nvarchar(200)
    */
    get LastName(): string | null {
        return this.Get('LastName');
    }
    set LastName(value: string | null) {
        this.Set('LastName', value);
    }

    /**
    * * Field Name: AGUPersonID
    * * Display Name: AGU Person ID
    * * SQL Data Type: uniqueidentifier
    */
    get AGUPersonID(): string | null {
        return this.Get('AGUPersonID');
    }

    /**
    * * Field Name: MergedIntoContributorID
    * * Display Name: Merged Into Contributor ID
    * * SQL Data Type: int
    * * Related Entity/Foreign Key: Contributors (vwContributors.ID)
    */
    get MergedIntoContributorID(): number | null {
        return this.Get('MergedIntoContributorID');
    }
    set MergedIntoContributorID(value: number | null) {
        this.Set('MergedIntoContributorID', value);
    }

    /**
    * * Field Name: RepresentationHashes
    * * Display Name: Representation Hashes
    * * SQL Data Type: nvarchar(MAX)
    */
    get RepresentationHashes(): string | null {
        return this.Get('RepresentationHashes');
    }
    set RepresentationHashes(value: string | null) {
        this.Set('RepresentationHashes', value);
    }

    /**
    * * Field Name: RepresentationsBuiltAt
    * * Display Name: Representations Built At
    * * SQL Data Type: datetimeoffset
    */
    get RepresentationsBuiltAt(): Date | null {
        return this.Get('RepresentationsBuiltAt');
    }
    set RepresentationsBuiltAt(value: Date | null) {
        this.Set('RepresentationsBuiltAt', value);
    }

    /**
    * * Field Name: MergedIntoContributor
    * * Display Name: Merged Into Contributor
    * * SQL Data Type: nvarchar(100)
    */
    get MergedIntoContributor(): string | null {
        return this.Get('MergedIntoContributor');
    }

    /**
    * * Field Name: RootMergedIntoContributorID
    * * Display Name: Root Merged Into Contributor ID
    * * SQL Data Type: int
    */
    get RootMergedIntoContributorID(): number | null {
        return this.Get('RootMergedIntoContributorID');
    }
}


/**
 * Conversation Messages - strongly typed entity sub-class
 * * Schema: FeedbackPlatform
 * * Base Table: ConversationMessage
 * * Base View: vwConversationMessages
 * * @description Individual messages within conversation threads.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Conversation Messages')
export class FeedbackPlatformConversationMessageEntity extends BaseEntity<FeedbackPlatformConversationMessageEntityType> {
    /**
    * Loads the Conversation Messages record from the database
    * @param ID: string - primary key value to load the Conversation Messages record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof FeedbackPlatformConversationMessageEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * Validate() method override for Conversation Messages entity. This is an auto-generated method that invokes the generated validators for this entity for the following fields:
    * * ThreadPosition: The thread position, if specified, must be a value between 1 and 3.
    * @public
    * @method
    * @override
    */
    public override Validate(): ValidationResult {
        const result = super.Validate();
        this.ValidateThreadPositionRange(result);
        result.Success = result.Success && (result.Errors.length === 0);

        return result;
    }

    /**
    * The thread position, if specified, must be a value between 1 and 3.
    * @param result - the ValidationResult object to add any errors or warnings to
    * @public
    * @method
    */
    public ValidateThreadPositionRange(result: ValidationResult) {
    	if (this.ThreadPosition != null && (this.ThreadPosition < 1 || this.ThreadPosition > 3)) {
    		result.Errors.push(new ValidationErrorInfo(
    			"ThreadPosition",
    			"Thread position must be between 1 and 3.",
    			this.ThreadPosition,
    			ValidationErrorType.Failure
    		));
    	}
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ConversationID
    * * Display Name: Conversation ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Conversations__FeedbackPlatform (vwConversations__FeedbackPlatform.ID)
    */
    get ConversationID(): string {
        return this.Get('ConversationID');
    }
    set ConversationID(value: string) {
        this.Set('ConversationID', value);
    }

    /**
    * * Field Name: SenderUserID
    * * Display Name: Sender User ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Users (vwUsers.ID)
    */
    get SenderUserID(): string {
        return this.Get('SenderUserID');
    }
    set SenderUserID(value: string) {
        this.Set('SenderUserID', value);
    }

    /**
    * * Field Name: MessageContent
    * * Display Name: Message Content
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Content of the message.
    */
    get MessageContent(): string {
        return this.Get('MessageContent');
    }
    set MessageContent(value: string) {
        this.Set('MessageContent', value);
    }

    /**
    * * Field Name: SentDate
    * * Display Name: Sent Date
    * * SQL Data Type: datetimeoffset
    * * Default Value: sysdatetimeoffset()
    * * Description: When the message was sent.
    */
    get SentDate(): Date {
        return this.Get('SentDate');
    }
    set SentDate(value: Date) {
        this.Set('SentDate', value);
    }

    /**
    * * Field Name: ReadDate
    * * Display Name: Read Date
    * * SQL Data Type: datetimeoffset
    * * Description: When the message was marked as read.
    */
    get ReadDate(): Date | null {
        return this.Get('ReadDate');
    }
    set ReadDate(value: Date | null) {
        this.Set('ReadDate', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: nvarchar(20)
    * * Default Value: Sent
    * * Value List Type: List
    * * Possible Values 
    *   * Deleted
    *   * Delivered
    *   * Failed
    *   * Read
    *   * Sent
    * * Description: Current status of the message (Sent, Delivered, Read, Deleted, Failed).
    */
    get Status(): 'Deleted' | 'Delivered' | 'Failed' | 'Read' | 'Sent' {
        return this.Get('Status');
    }
    set Status(value: 'Deleted' | 'Delivered' | 'Failed' | 'Read' | 'Sent') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: MessageType
    * * Display Name: Message Type
    * * SQL Data Type: nvarchar(50)
    * * Default Value: Message
    * * Value List Type: List
    * * Possible Values 
    *   * Message
    *   * Question
    *   * Response
    *   * SystemNotification
    * * Description: Type of message (regular message, question, response, or system notification).
    */
    get MessageType(): 'Message' | 'Question' | 'Response' | 'SystemNotification' {
        return this.Get('MessageType');
    }
    set MessageType(value: 'Message' | 'Question' | 'Response' | 'SystemNotification') {
        this.Set('MessageType', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: ThreadPosition
    * * Display Name: Thread Position
    * * SQL Data Type: tinyint
    */
    get ThreadPosition(): number | null {
        return this.Get('ThreadPosition');
    }
    set ThreadPosition(value: number | null) {
        this.Set('ThreadPosition', value);
    }

    /**
    * * Field Name: SenderUser
    * * Display Name: Sender User
    * * SQL Data Type: nvarchar(100)
    */
    get SenderUser(): string {
        return this.Get('SenderUser');
    }
}


/**
 * Conversations__FeedbackPlatform - strongly typed entity sub-class
 * * Schema: FeedbackPlatform
 * * Base Table: Conversation
 * * Base View: vwConversations__FeedbackPlatform
 * * @description Represents conversation threads between presenters and reviewers.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Conversations__FeedbackPlatform')
export class FeedbackPlatformConversation__FeedbackPlatformEntity extends BaseEntity<FeedbackPlatformConversation__FeedbackPlatformEntityType> {
    /**
    * Loads the Conversations__FeedbackPlatform record from the database
    * @param ID: string - primary key value to load the Conversations__FeedbackPlatform record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof FeedbackPlatformConversation__FeedbackPlatformEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ReviewID
    * * Display Name: Review ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Reviews (vwReviews.ID)
    */
    get ReviewID(): string {
        return this.Get('ReviewID');
    }
    set ReviewID(value: string) {
        this.Set('ReviewID', value);
    }

    /**
    * * Field Name: Subject
    * * Display Name: Subject
    * * SQL Data Type: nvarchar(255)
    * * Description: Subject line or topic of the conversation.
    */
    get Subject(): string | null {
        return this.Get('Subject');
    }
    set Subject(value: string | null) {
        this.Set('Subject', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: nvarchar(20)
    * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Archived
    *   * Closed
    * * Description: Current status of the conversation thread.
    */
    get Status(): 'Active' | 'Archived' | 'Closed' {
        return this.Get('Status');
    }
    set Status(value: 'Active' | 'Archived' | 'Closed') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: StartedDate
    * * Display Name: Started Date
    * * SQL Data Type: datetimeoffset
    * * Default Value: sysdatetimeoffset()
    * * Description: When the conversation was started.
    */
    get StartedDate(): Date {
        return this.Get('StartedDate');
    }
    set StartedDate(value: Date) {
        this.Set('StartedDate', value);
    }

    /**
    * * Field Name: LastMessageDate
    * * Display Name: Last Message Date
    * * SQL Data Type: datetimeoffset
    * * Description: Timestamp of the most recent message in this conversation.
    */
    get LastMessageDate(): Date | null {
        return this.Get('LastMessageDate');
    }
    set LastMessageDate(value: Date | null) {
        this.Set('LastMessageDate', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Courses - strongly typed entity sub-class
 * * Schema: CoreSignal
 * * Base Table: Course
 * * Base View: vwCourses
 * * @description A course from a CoreSignal profile.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Courses')
export class CoreSignalCourseEntity extends BaseEntity<CoreSignalCourseEntityType> {
    /**
    * Loads the Courses record from the database
    * @param ID: string - primary key value to load the Courses record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof CoreSignalCourseEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ProfileID
    * * Display Name: Profile ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Profiles (vwProfiles.ID)
    */
    get ProfileID(): string {
        return this.Get('ProfileID');
    }
    set ProfileID(value: string) {
        this.Set('ProfileID', value);
    }

    /**
    * * Field Name: OrderInProfile
    * * Display Name: Order In Profile
    * * SQL Data Type: int
    */
    get OrderInProfile(): number {
        return this.Get('OrderInProfile');
    }
    set OrderInProfile(value: number) {
        this.Set('OrderInProfile', value);
    }

    /**
    * * Field Name: Title
    * * Display Name: Title
    * * SQL Data Type: nvarchar(1000)
    */
    get Title(): string | null {
        return this.Get('Title');
    }
    set Title(value: string | null) {
        this.Set('Title', value);
    }

    /**
    * * Field Name: Organizer
    * * Display Name: Organizer
    * * SQL Data Type: nvarchar(500)
    */
    get Organizer(): string | null {
        return this.Get('Organizer');
    }
    set Organizer(value: string | null) {
        this.Set('Organizer', value);
    }

    /**
    * * Field Name: __mj_integration_SyncStatus
    * * Display Name: Mj Integration Sync Status
    * * SQL Data Type: nvarchar(50)
    * * Default Value: Active
    */
    get __mj_integration_SyncStatus(): string {
        return this.Get('__mj_integration_SyncStatus');
    }
    set __mj_integration_SyncStatus(value: string) {
        this.Set('__mj_integration_SyncStatus', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedAt
    * * Display Name: Mj Integration Last Synced At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastSyncedAt(): Date | null {
        return this.Get('__mj_integration_LastSyncedAt');
    }
    set __mj_integration_LastSyncedAt(value: Date | null) {
        this.Set('__mj_integration_LastSyncedAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedSnapshot
    * * Display Name: Mj Integration Last Synced Snapshot
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_LastSyncedSnapshot(): string | null {
        return this.Get('__mj_integration_LastSyncedSnapshot');
    }
    set __mj_integration_LastSyncedSnapshot(value: string | null) {
        this.Set('__mj_integration_LastSyncedSnapshot', value);
    }

    /**
    * * Field Name: __mj_integration_SyncMessage
    * * Display Name: Mj Integration Sync Message
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_SyncMessage(): string | null {
        return this.Get('__mj_integration_SyncMessage');
    }
    set __mj_integration_SyncMessage(value: string | null) {
        this.Set('__mj_integration_SyncMessage', value);
    }

    /**
    * * Field Name: __mj_integration_ContentHash
    * * Display Name: Mj Integration Content Hash
    * * SQL Data Type: nvarchar(64)
    */
    get __mj_integration_ContentHash(): string | null {
        return this.Get('__mj_integration_ContentHash');
    }
    set __mj_integration_ContentHash(value: string | null) {
        this.Set('__mj_integration_ContentHash', value);
    }

    /**
    * * Field Name: __mj_integration_CustomOverflow
    * * Display Name: Mj Integration Custom Overflow
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_CustomOverflow(): string | null {
        return this.Get('__mj_integration_CustomOverflow');
    }
    set __mj_integration_CustomOverflow(value: string | null) {
        this.Set('__mj_integration_CustomOverflow', value);
    }

    /**
    * * Field Name: __mj_integration_ExternalVersion
    * * Display Name: Mj Integration External Version
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_ExternalVersion(): string | null {
        return this.Get('__mj_integration_ExternalVersion');
    }
    set __mj_integration_ExternalVersion(value: string | null) {
        this.Set('__mj_integration_ExternalVersion', value);
    }

    /**
    * * Field Name: __mj_integration_LastSeenModifiedValue
    * * Display Name: Mj Integration Last Seen Modified Value
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_LastSeenModifiedValue(): string | null {
        return this.Get('__mj_integration_LastSeenModifiedValue');
    }
    set __mj_integration_LastSeenModifiedValue(value: string | null) {
        this.Set('__mj_integration_LastSeenModifiedValue', value);
    }

    /**
    * * Field Name: __mj_integration_LastReconciledAt
    * * Display Name: Mj Integration Last Reconciled At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastReconciledAt(): Date | null {
        return this.Get('__mj_integration_LastReconciledAt');
    }
    set __mj_integration_LastReconciledAt(value: Date | null) {
        this.Set('__mj_integration_LastReconciledAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastWriterDirection
    * * Display Name: Mj Integration Last Writer Direction
    * * SQL Data Type: nvarchar(10)
    */
    get __mj_integration_LastWriterDirection(): string | null {
        return this.Get('__mj_integration_LastWriterDirection');
    }
    set __mj_integration_LastWriterDirection(value: string | null) {
        this.Set('__mj_integration_LastWriterDirection', value);
    }

    /**
    * * Field Name: __mj_integration_IsTombstoned
    * * Display Name: Mj Integration Is Tombstoned
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get __mj_integration_IsTombstoned(): boolean {
        return this.Get('__mj_integration_IsTombstoned');
    }
    set __mj_integration_IsTombstoned(value: boolean) {
        this.Set('__mj_integration_IsTombstoned', value);
    }

    /**
    * * Field Name: __mj_integration_DeletedDetectedAt
    * * Display Name: Mj Integration Deleted Detected At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_DeletedDetectedAt(): Date | null {
        return this.Get('__mj_integration_DeletedDetectedAt');
    }
    set __mj_integration_DeletedDetectedAt(value: Date | null) {
        this.Set('__mj_integration_DeletedDetectedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Cv Parses - strongly typed entity sub-class
 * * Schema: Research
 * * Base Table: CvParse
 * * Base View: vwCvParses
 * * @description One provider parse attempt for a CV document: submission state, provider reference, progress, normalized segments and cached job matches. At most one live (submitting, processing, done) row per document and provider.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Cv Parses')
export class ResearchCvParseEntity extends BaseEntity<ResearchCvParseEntityType> {
    /**
    * Loads the Cv Parses record from the database
    * @param ID: string - primary key value to load the Cv Parses record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof ResearchCvParseEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: MemberDocumentID
    * * Display Name: Member Document ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Member Documents (vwMemberDocuments.ID)
    * * Description: The CV document that was submitted.
    */
    get MemberDocumentID(): string {
        return this.Get('MemberDocumentID');
    }
    set MemberDocumentID(value: string) {
        this.Set('MemberDocumentID', value);
    }

    /**
    * * Field Name: ProviderName
    * * Display Name: Provider Name
    * * SQL Data Type: nvarchar(50)
    * * Description: Parsing provider, for example Julius.
    */
    get ProviderName(): string {
        return this.Get('ProviderName');
    }
    set ProviderName(value: string) {
        this.Set('ProviderName', value);
    }

    /**
    * * Field Name: ProviderRef
    * * Display Name: Provider Ref
    * * SQL Data Type: nvarchar(200)
    * * Description: Provider-side identifier for the submitted CV.
    */
    get ProviderRef(): string | null {
        return this.Get('ProviderRef');
    }
    set ProviderRef(value: string | null) {
        this.Set('ProviderRef', value);
    }

    /**
    * * Field Name: State
    * * Display Name: State
    * * SQL Data Type: nvarchar(30)
    * * Value List Type: List
    * * Possible Values 
    *   * done
    *   * failed
    *   * processing
    *   * submitting
    *   * unsupported_format
    * * Description: submitting, processing, done, failed or unsupported_format.
    */
    get State(): 'done' | 'failed' | 'processing' | 'submitting' | 'unsupported_format' {
        return this.Get('State');
    }
    set State(value: 'done' | 'failed' | 'processing' | 'submitting' | 'unsupported_format') {
        this.Set('State', value);
    }

    /**
    * * Field Name: AttemptCount
    * * Display Name: Attempt Count
    * * SQL Data Type: int
    * * Default Value: 0
    * * Description: Number of submission attempts made.
    */
    get AttemptCount(): number {
        return this.Get('AttemptCount');
    }
    set AttemptCount(value: number) {
        this.Set('AttemptCount', value);
    }

    /**
    * * Field Name: ProgressCompleted
    * * Display Name: Progress Completed
    * * SQL Data Type: int
    * * Description: Provider-reported units completed while processing.
    */
    get ProgressCompleted(): number | null {
        return this.Get('ProgressCompleted');
    }
    set ProgressCompleted(value: number | null) {
        this.Set('ProgressCompleted', value);
    }

    /**
    * * Field Name: ProgressTotal
    * * Display Name: Progress Total
    * * SQL Data Type: int
    * * Description: Provider-reported total units while processing.
    */
    get ProgressTotal(): number | null {
        return this.Get('ProgressTotal');
    }
    set ProgressTotal(value: number | null) {
        this.Set('ProgressTotal', value);
    }

    /**
    * * Field Name: ErrorCode
    * * Display Name: Error Code
    * * SQL Data Type: nvarchar(50)
    * * Description: Short error code when State is failed; never contains CV content.
    */
    get ErrorCode(): string | null {
        return this.Get('ErrorCode');
    }
    set ErrorCode(value: string | null) {
        this.Set('ErrorCode', value);
    }

    /**
    * * Field Name: SubmittedAt
    * * Display Name: Submitted At
    * * SQL Data Type: datetimeoffset
    * * Description: When the CV was sent to the provider.
    */
    get SubmittedAt(): Date | null {
        return this.Get('SubmittedAt');
    }
    set SubmittedAt(value: Date | null) {
        this.Set('SubmittedAt', value);
    }

    /**
    * * Field Name: FinishedAt
    * * Display Name: Finished At
    * * SQL Data Type: datetimeoffset
    * * Description: When the provider finished or the parse failed.
    */
    get FinishedAt(): Date | null {
        return this.Get('FinishedAt');
    }
    set FinishedAt(value: Date | null) {
        this.Set('FinishedAt', value);
    }

    /**
    * * Field Name: ProviderExpiresAt
    * * Display Name: Provider Expires At
    * * SQL Data Type: datetimeoffset
    * * Description: When the provider will forget the submitted CV.
    */
    get ProviderExpiresAt(): Date | null {
        return this.Get('ProviderExpiresAt');
    }
    set ProviderExpiresAt(value: Date | null) {
        this.Set('ProviderExpiresAt', value);
    }

    /**
    * * Field Name: ProviderPurgedAt
    * * Display Name: Provider Purged At
    * * SQL Data Type: datetimeoffset
    * * Description: When the provider-side copy was observed gone (for example a 404 after expiry).
    */
    get ProviderPurgedAt(): Date | null {
        return this.Get('ProviderPurgedAt');
    }
    set ProviderPurgedAt(value: Date | null) {
        this.Set('ProviderPurgedAt', value);
    }

    /**
    * * Field Name: SegmentsJson
    * * Display Name: Segments Json
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Normalized CV segments (id, text, page locations) as JSON.
    */
    get SegmentsJson(): string | null {
        return this.Get('SegmentsJson');
    }
    set SegmentsJson(value: string | null) {
        this.Set('SegmentsJson', value);
    }

    /**
    * * Field Name: MatchesJson
    * * Display Name: Matches Json
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Cached provider job matches as JSON.
    */
    get MatchesJson(): string | null {
        return this.Get('MatchesJson');
    }
    set MatchesJson(value: string | null) {
        this.Set('MatchesJson', value);
    }

    /**
    * * Field Name: MatchesAreSample
    * * Display Name: Matches Are Sample
    * * SQL Data Type: bit
    * * Description: True when the cached matches are a sample rather than the full result.
    */
    get MatchesAreSample(): boolean | null {
        return this.Get('MatchesAreSample');
    }
    set MatchesAreSample(value: boolean | null) {
        this.Set('MatchesAreSample', value);
    }

    /**
    * * Field Name: MatchesFetchedAt
    * * Display Name: Matches Fetched At
    * * SQL Data Type: datetimeoffset
    * * Description: When MatchesJson was last fetched from the provider.
    */
    get MatchesFetchedAt(): Date | null {
        return this.Get('MatchesFetchedAt');
    }
    set MatchesFetchedAt(value: Date | null) {
        this.Set('MatchesFetchedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: NarrativesJson
    * * Display Name: Narratives Json
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Career Explorer narrative sections generated for this CV parse, stored as JSON.
    */
    get NarrativesJson(): string | null {
        return this.Get('NarrativesJson');
    }
    set NarrativesJson(value: string | null) {
        this.Set('NarrativesJson', value);
    }

    /**
    * * Field Name: MemberDocument
    * * Display Name: Member Document
    * * SQL Data Type: nvarchar(255)
    */
    get MemberDocument(): string {
        return this.Get('MemberDocument');
    }
}


/**
 * Cv Tags - strongly typed entity sub-class
 * * Schema: Research
 * * Base Table: CvTag
 * * Base View: vwCvTags
 * * @description One provider-extracted tag from a parsed CV, with the member review status. Review statuses are preserved when a parse is re-merged.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Cv Tags')
export class ResearchCvTagEntity extends BaseEntity<ResearchCvTagEntityType> {
    /**
    * Loads the Cv Tags record from the database
    * @param ID: string - primary key value to load the Cv Tags record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof ResearchCvTagEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: CvParseID
    * * Display Name: Cv Parse ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Cv Parses (vwCvParses.ID)
    * * Description: The parse this tag belongs to.
    */
    get CvParseID(): string {
        return this.Get('CvParseID');
    }
    set CvParseID(value: string) {
        this.Set('CvParseID', value);
    }

    /**
    * * Field Name: ProviderTagID
    * * Display Name: Provider Tag ID
    * * SQL Data Type: nvarchar(50)
    * * Description: Provider tag identifier.
    */
    get ProviderTagID(): string {
        return this.Get('ProviderTagID');
    }
    set ProviderTagID(value: string) {
        this.Set('ProviderTagID', value);
    }

    /**
    * * Field Name: Name
    * * Display Name: Name
    * * SQL Data Type: nvarchar(300)
    * * Description: Tag display name.
    */
    get Name(): string | null {
        return this.Get('Name');
    }
    set Name(value: string | null) {
        this.Set('Name', value);
    }

    /**
    * * Field Name: Type
    * * Display Name: Type
    * * SQL Data Type: nvarchar(50)
    * * Description: Provider tag type.
    */
    get Type(): string {
        return this.Get('Type');
    }
    set Type(value: string) {
        this.Set('Type', value);
    }

    /**
    * * Field Name: Subtype
    * * Display Name: Subtype
    * * SQL Data Type: nvarchar(100)
    * * Description: Provider tag subtype.
    */
    get Subtype(): string {
        return this.Get('Subtype');
    }
    set Subtype(value: string) {
        this.Set('Subtype', value);
    }

    /**
    * * Field Name: ConfidenceLevel
    * * Display Name: Confidence Level
    * * SQL Data Type: nvarchar(20)
    * * Description: Provider confidence level for the tag.
    */
    get ConfidenceLevel(): string {
        return this.Get('ConfidenceLevel');
    }
    set ConfidenceLevel(value: string) {
        this.Set('ConfidenceLevel', value);
    }

    /**
    * * Field Name: SegmentRef
    * * Display Name: Segment Ref
    * * SQL Data Type: nvarchar(50)
    * * Description: Segment of the CV the tag was found in.
    */
    get SegmentRef(): string {
        return this.Get('SegmentRef');
    }
    set SegmentRef(value: string) {
        this.Set('SegmentRef', value);
    }

    /**
    * * Field Name: ChainRef
    * * Display Name: Chain Ref
    * * SQL Data Type: nvarchar(50)
    * * Description: Provider chain identifier grouping related tags.
    */
    get ChainRef(): string {
        return this.Get('ChainRef');
    }
    set ChainRef(value: string) {
        this.Set('ChainRef', value);
    }

    /**
    * * Field Name: ChainPosition
    * * Display Name: Chain Position
    * * SQL Data Type: int
    * * Description: Position of the tag within its chain.
    */
    get ChainPosition(): number {
        return this.Get('ChainPosition');
    }
    set ChainPosition(value: number) {
        this.Set('ChainPosition', value);
    }

    /**
    * * Field Name: ReviewStatus
    * * Display Name: Review Status
    * * SQL Data Type: nvarchar(20)
    * * Default Value: unreviewed
    * * Value List Type: List
    * * Possible Values 
    *   * approved
    *   * declined
    *   * unreviewed
    * * Description: Member review: unreviewed, approved or declined.
    */
    get ReviewStatus(): 'approved' | 'declined' | 'unreviewed' {
        return this.Get('ReviewStatus');
    }
    set ReviewStatus(value: 'approved' | 'declined' | 'unreviewed') {
        this.Set('ReviewStatus', value);
    }

    /**
    * * Field Name: ReviewedAt
    * * Display Name: Reviewed At
    * * SQL Data Type: datetimeoffset
    * * Description: When the member last reviewed the tag.
    */
    get ReviewedAt(): Date | null {
        return this.Get('ReviewedAt');
    }
    set ReviewedAt(value: Date | null) {
        this.Set('ReviewedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CvParse
    * * Display Name: Cv Parse
    * * SQL Data Type: nvarchar(50)
    */
    get CvParse(): string {
        return this.Get('CvParse');
    }
}


/**
 * Distinctions - strongly typed entity sub-class
 * * Schema: ORCID
 * * Base Table: Distinction
 * * Base View: vwDistinctions
 * * @description A distinction/award (distinctions section).
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Distinctions')
export class ORCIDDistinctionEntity extends BaseEntity<ORCIDDistinctionEntityType> {
    /**
    * Loads the Distinctions record from the database
    * @param ID: string - primary key value to load the Distinctions record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof ORCIDDistinctionEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ORCIDRecordID
    * * Display Name: ORCID Record ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Records (vwRecords.ID)
    */
    get ORCIDRecordID(): string {
        return this.Get('ORCIDRecordID');
    }
    set ORCIDRecordID(value: string) {
        this.Set('ORCIDRecordID', value);
    }

    /**
    * * Field Name: PutCode
    * * Display Name: Put Code
    * * SQL Data Type: bigint
    */
    get PutCode(): number {
        return this.Get('PutCode');
    }
    set PutCode(value: number) {
        this.Set('PutCode', value);
    }

    /**
    * * Field Name: DepartmentName
    * * Display Name: Department Name
    * * SQL Data Type: nvarchar(500)
    */
    get DepartmentName(): string | null {
        return this.Get('DepartmentName');
    }
    set DepartmentName(value: string | null) {
        this.Set('DepartmentName', value);
    }

    /**
    * * Field Name: RoleTitle
    * * Display Name: Role Title
    * * SQL Data Type: nvarchar(500)
    */
    get RoleTitle(): string | null {
        return this.Get('RoleTitle');
    }
    set RoleTitle(value: string | null) {
        this.Set('RoleTitle', value);
    }

    /**
    * * Field Name: StartYear
    * * Display Name: Start Year
    * * SQL Data Type: smallint
    */
    get StartYear(): number | null {
        return this.Get('StartYear');
    }
    set StartYear(value: number | null) {
        this.Set('StartYear', value);
    }

    /**
    * * Field Name: StartMonth
    * * Display Name: Start Month
    * * SQL Data Type: tinyint
    */
    get StartMonth(): number | null {
        return this.Get('StartMonth');
    }
    set StartMonth(value: number | null) {
        this.Set('StartMonth', value);
    }

    /**
    * * Field Name: StartDay
    * * Display Name: Start Day
    * * SQL Data Type: tinyint
    */
    get StartDay(): number | null {
        return this.Get('StartDay');
    }
    set StartDay(value: number | null) {
        this.Set('StartDay', value);
    }

    /**
    * * Field Name: EndYear
    * * Display Name: End Year
    * * SQL Data Type: smallint
    */
    get EndYear(): number | null {
        return this.Get('EndYear');
    }
    set EndYear(value: number | null) {
        this.Set('EndYear', value);
    }

    /**
    * * Field Name: EndMonth
    * * Display Name: End Month
    * * SQL Data Type: tinyint
    */
    get EndMonth(): number | null {
        return this.Get('EndMonth');
    }
    set EndMonth(value: number | null) {
        this.Set('EndMonth', value);
    }

    /**
    * * Field Name: EndDay
    * * Display Name: End Day
    * * SQL Data Type: tinyint
    */
    get EndDay(): number | null {
        return this.Get('EndDay');
    }
    set EndDay(value: number | null) {
        this.Set('EndDay', value);
    }

    /**
    * * Field Name: OrgName
    * * Display Name: Org Name
    * * SQL Data Type: nvarchar(500)
    */
    get OrgName(): string | null {
        return this.Get('OrgName');
    }
    set OrgName(value: string | null) {
        this.Set('OrgName', value);
    }

    /**
    * * Field Name: OrgCity
    * * Display Name: Org City
    * * SQL Data Type: nvarchar(255)
    */
    get OrgCity(): string | null {
        return this.Get('OrgCity');
    }
    set OrgCity(value: string | null) {
        this.Set('OrgCity', value);
    }

    /**
    * * Field Name: OrgRegion
    * * Display Name: Org Region
    * * SQL Data Type: nvarchar(255)
    */
    get OrgRegion(): string | null {
        return this.Get('OrgRegion');
    }
    set OrgRegion(value: string | null) {
        this.Set('OrgRegion', value);
    }

    /**
    * * Field Name: OrgCountry
    * * Display Name: Org Country
    * * SQL Data Type: nvarchar(10)
    */
    get OrgCountry(): string | null {
        return this.Get('OrgCountry');
    }
    set OrgCountry(value: string | null) {
        this.Set('OrgCountry', value);
    }

    /**
    * * Field Name: OrgDisambiguatedID
    * * Display Name: Org Disambiguated ID
    * * SQL Data Type: nvarchar(255)
    */
    get OrgDisambiguatedID(): string | null {
        return this.Get('OrgDisambiguatedID');
    }
    set OrgDisambiguatedID(value: string | null) {
        this.Set('OrgDisambiguatedID', value);
    }

    /**
    * * Field Name: OrgDisambiguationSource
    * * Display Name: Org Disambiguation Source
    * * SQL Data Type: nvarchar(50)
    */
    get OrgDisambiguationSource(): string | null {
        return this.Get('OrgDisambiguationSource');
    }
    set OrgDisambiguationSource(value: string | null) {
        this.Set('OrgDisambiguationSource', value);
    }

    /**
    * * Field Name: Url
    * * Display Name: Url
    * * SQL Data Type: nvarchar(2000)
    */
    get Url(): string | null {
        return this.Get('Url');
    }
    set Url(value: string | null) {
        this.Set('Url', value);
    }

    /**
    * * Field Name: Visibility
    * * Display Name: Visibility
    * * SQL Data Type: nvarchar(20)
    */
    get Visibility(): string | null {
        return this.Get('Visibility');
    }
    set Visibility(value: string | null) {
        this.Set('Visibility', value);
    }

    /**
    * * Field Name: SourceName
    * * Display Name: Source Name
    * * SQL Data Type: nvarchar(255)
    */
    get SourceName(): string | null {
        return this.Get('SourceName');
    }
    set SourceName(value: string | null) {
        this.Set('SourceName', value);
    }

    /**
    * * Field Name: SourceOrcid
    * * Display Name: Source Orcid
    * * SQL Data Type: nvarchar(19)
    */
    get SourceOrcid(): string | null {
        return this.Get('SourceOrcid');
    }
    set SourceOrcid(value: string | null) {
        this.Set('SourceOrcid', value);
    }

    /**
    * * Field Name: OrcidCreatedDate
    * * Display Name: Orcid Created Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidCreatedDate(): Date | null {
        return this.Get('OrcidCreatedDate');
    }
    set OrcidCreatedDate(value: Date | null) {
        this.Set('OrcidCreatedDate', value);
    }

    /**
    * * Field Name: OrcidLastModifiedDate
    * * Display Name: Orcid Last Modified Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidLastModifiedDate(): Date | null {
        return this.Get('OrcidLastModifiedDate');
    }
    set OrcidLastModifiedDate(value: Date | null) {
        this.Set('OrcidLastModifiedDate', value);
    }

    /**
    * * Field Name: RawJSON
    * * Display Name: Raw JSON
    * * SQL Data Type: nvarchar(MAX)
    */
    get RawJSON(): string | null {
        return this.Get('RawJSON');
    }
    set RawJSON(value: string | null) {
        this.Set('RawJSON', value);
    }

    /**
    * * Field Name: __mj_integration_SyncStatus
    * * Display Name: Mj Integration Sync Status
    * * SQL Data Type: nvarchar(50)
    * * Default Value: Active
    */
    get __mj_integration_SyncStatus(): string {
        return this.Get('__mj_integration_SyncStatus');
    }
    set __mj_integration_SyncStatus(value: string) {
        this.Set('__mj_integration_SyncStatus', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedAt
    * * Display Name: Mj Integration Last Synced At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastSyncedAt(): Date | null {
        return this.Get('__mj_integration_LastSyncedAt');
    }
    set __mj_integration_LastSyncedAt(value: Date | null) {
        this.Set('__mj_integration_LastSyncedAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedSnapshot
    * * Display Name: Mj Integration Last Synced Snapshot
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_LastSyncedSnapshot(): string | null {
        return this.Get('__mj_integration_LastSyncedSnapshot');
    }
    set __mj_integration_LastSyncedSnapshot(value: string | null) {
        this.Set('__mj_integration_LastSyncedSnapshot', value);
    }

    /**
    * * Field Name: __mj_integration_SyncMessage
    * * Display Name: Mj Integration Sync Message
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_SyncMessage(): string | null {
        return this.Get('__mj_integration_SyncMessage');
    }
    set __mj_integration_SyncMessage(value: string | null) {
        this.Set('__mj_integration_SyncMessage', value);
    }

    /**
    * * Field Name: __mj_integration_ContentHash
    * * Display Name: Mj Integration Content Hash
    * * SQL Data Type: nvarchar(64)
    */
    get __mj_integration_ContentHash(): string | null {
        return this.Get('__mj_integration_ContentHash');
    }
    set __mj_integration_ContentHash(value: string | null) {
        this.Set('__mj_integration_ContentHash', value);
    }

    /**
    * * Field Name: __mj_integration_CustomOverflow
    * * Display Name: Mj Integration Custom Overflow
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_CustomOverflow(): string | null {
        return this.Get('__mj_integration_CustomOverflow');
    }
    set __mj_integration_CustomOverflow(value: string | null) {
        this.Set('__mj_integration_CustomOverflow', value);
    }

    /**
    * * Field Name: __mj_integration_ExternalVersion
    * * Display Name: Mj Integration External Version
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_ExternalVersion(): string | null {
        return this.Get('__mj_integration_ExternalVersion');
    }
    set __mj_integration_ExternalVersion(value: string | null) {
        this.Set('__mj_integration_ExternalVersion', value);
    }

    /**
    * * Field Name: __mj_integration_LastSeenModifiedValue
    * * Display Name: Mj Integration Last Seen Modified Value
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_LastSeenModifiedValue(): string | null {
        return this.Get('__mj_integration_LastSeenModifiedValue');
    }
    set __mj_integration_LastSeenModifiedValue(value: string | null) {
        this.Set('__mj_integration_LastSeenModifiedValue', value);
    }

    /**
    * * Field Name: __mj_integration_LastReconciledAt
    * * Display Name: Mj Integration Last Reconciled At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastReconciledAt(): Date | null {
        return this.Get('__mj_integration_LastReconciledAt');
    }
    set __mj_integration_LastReconciledAt(value: Date | null) {
        this.Set('__mj_integration_LastReconciledAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastWriterDirection
    * * Display Name: Mj Integration Last Writer Direction
    * * SQL Data Type: nvarchar(10)
    */
    get __mj_integration_LastWriterDirection(): string | null {
        return this.Get('__mj_integration_LastWriterDirection');
    }
    set __mj_integration_LastWriterDirection(value: string | null) {
        this.Set('__mj_integration_LastWriterDirection', value);
    }

    /**
    * * Field Name: __mj_integration_IsTombstoned
    * * Display Name: Mj Integration Is Tombstoned
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get __mj_integration_IsTombstoned(): boolean {
        return this.Get('__mj_integration_IsTombstoned');
    }
    set __mj_integration_IsTombstoned(value: boolean) {
        this.Set('__mj_integration_IsTombstoned', value);
    }

    /**
    * * Field Name: __mj_integration_DeletedDetectedAt
    * * Display Name: Mj Integration Deleted Detected At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_DeletedDetectedAt(): Date | null {
        return this.Get('__mj_integration_DeletedDetectedAt');
    }
    set __mj_integration_DeletedDetectedAt(value: Date | null) {
        this.Set('__mj_integration_DeletedDetectedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Educations - strongly typed entity sub-class
 * * Schema: ORCID
 * * Base Table: Education
 * * Base View: vwEducations
 * * @description An education affiliation (educations section).
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Educations')
export class ORCIDEducationEntity extends BaseEntity<ORCIDEducationEntityType> {
    /**
    * Loads the Educations record from the database
    * @param ID: string - primary key value to load the Educations record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof ORCIDEducationEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ORCIDRecordID
    * * Display Name: ORCID Record ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Records (vwRecords.ID)
    */
    get ORCIDRecordID(): string {
        return this.Get('ORCIDRecordID');
    }
    set ORCIDRecordID(value: string) {
        this.Set('ORCIDRecordID', value);
    }

    /**
    * * Field Name: PutCode
    * * Display Name: Put Code
    * * SQL Data Type: bigint
    */
    get PutCode(): number {
        return this.Get('PutCode');
    }
    set PutCode(value: number) {
        this.Set('PutCode', value);
    }

    /**
    * * Field Name: DepartmentName
    * * Display Name: Department Name
    * * SQL Data Type: nvarchar(500)
    */
    get DepartmentName(): string | null {
        return this.Get('DepartmentName');
    }
    set DepartmentName(value: string | null) {
        this.Set('DepartmentName', value);
    }

    /**
    * * Field Name: RoleTitle
    * * Display Name: Role Title
    * * SQL Data Type: nvarchar(500)
    */
    get RoleTitle(): string | null {
        return this.Get('RoleTitle');
    }
    set RoleTitle(value: string | null) {
        this.Set('RoleTitle', value);
    }

    /**
    * * Field Name: StartYear
    * * Display Name: Start Year
    * * SQL Data Type: smallint
    */
    get StartYear(): number | null {
        return this.Get('StartYear');
    }
    set StartYear(value: number | null) {
        this.Set('StartYear', value);
    }

    /**
    * * Field Name: StartMonth
    * * Display Name: Start Month
    * * SQL Data Type: tinyint
    */
    get StartMonth(): number | null {
        return this.Get('StartMonth');
    }
    set StartMonth(value: number | null) {
        this.Set('StartMonth', value);
    }

    /**
    * * Field Name: StartDay
    * * Display Name: Start Day
    * * SQL Data Type: tinyint
    */
    get StartDay(): number | null {
        return this.Get('StartDay');
    }
    set StartDay(value: number | null) {
        this.Set('StartDay', value);
    }

    /**
    * * Field Name: EndYear
    * * Display Name: End Year
    * * SQL Data Type: smallint
    */
    get EndYear(): number | null {
        return this.Get('EndYear');
    }
    set EndYear(value: number | null) {
        this.Set('EndYear', value);
    }

    /**
    * * Field Name: EndMonth
    * * Display Name: End Month
    * * SQL Data Type: tinyint
    */
    get EndMonth(): number | null {
        return this.Get('EndMonth');
    }
    set EndMonth(value: number | null) {
        this.Set('EndMonth', value);
    }

    /**
    * * Field Name: EndDay
    * * Display Name: End Day
    * * SQL Data Type: tinyint
    */
    get EndDay(): number | null {
        return this.Get('EndDay');
    }
    set EndDay(value: number | null) {
        this.Set('EndDay', value);
    }

    /**
    * * Field Name: OrgName
    * * Display Name: Org Name
    * * SQL Data Type: nvarchar(500)
    */
    get OrgName(): string | null {
        return this.Get('OrgName');
    }
    set OrgName(value: string | null) {
        this.Set('OrgName', value);
    }

    /**
    * * Field Name: OrgCity
    * * Display Name: Org City
    * * SQL Data Type: nvarchar(255)
    */
    get OrgCity(): string | null {
        return this.Get('OrgCity');
    }
    set OrgCity(value: string | null) {
        this.Set('OrgCity', value);
    }

    /**
    * * Field Name: OrgRegion
    * * Display Name: Org Region
    * * SQL Data Type: nvarchar(255)
    */
    get OrgRegion(): string | null {
        return this.Get('OrgRegion');
    }
    set OrgRegion(value: string | null) {
        this.Set('OrgRegion', value);
    }

    /**
    * * Field Name: OrgCountry
    * * Display Name: Org Country
    * * SQL Data Type: nvarchar(10)
    */
    get OrgCountry(): string | null {
        return this.Get('OrgCountry');
    }
    set OrgCountry(value: string | null) {
        this.Set('OrgCountry', value);
    }

    /**
    * * Field Name: OrgDisambiguatedID
    * * Display Name: Org Disambiguated ID
    * * SQL Data Type: nvarchar(255)
    */
    get OrgDisambiguatedID(): string | null {
        return this.Get('OrgDisambiguatedID');
    }
    set OrgDisambiguatedID(value: string | null) {
        this.Set('OrgDisambiguatedID', value);
    }

    /**
    * * Field Name: OrgDisambiguationSource
    * * Display Name: Org Disambiguation Source
    * * SQL Data Type: nvarchar(50)
    */
    get OrgDisambiguationSource(): string | null {
        return this.Get('OrgDisambiguationSource');
    }
    set OrgDisambiguationSource(value: string | null) {
        this.Set('OrgDisambiguationSource', value);
    }

    /**
    * * Field Name: Url
    * * Display Name: Url
    * * SQL Data Type: nvarchar(2000)
    */
    get Url(): string | null {
        return this.Get('Url');
    }
    set Url(value: string | null) {
        this.Set('Url', value);
    }

    /**
    * * Field Name: Visibility
    * * Display Name: Visibility
    * * SQL Data Type: nvarchar(20)
    */
    get Visibility(): string | null {
        return this.Get('Visibility');
    }
    set Visibility(value: string | null) {
        this.Set('Visibility', value);
    }

    /**
    * * Field Name: SourceName
    * * Display Name: Source Name
    * * SQL Data Type: nvarchar(255)
    */
    get SourceName(): string | null {
        return this.Get('SourceName');
    }
    set SourceName(value: string | null) {
        this.Set('SourceName', value);
    }

    /**
    * * Field Name: SourceOrcid
    * * Display Name: Source Orcid
    * * SQL Data Type: nvarchar(19)
    */
    get SourceOrcid(): string | null {
        return this.Get('SourceOrcid');
    }
    set SourceOrcid(value: string | null) {
        this.Set('SourceOrcid', value);
    }

    /**
    * * Field Name: OrcidCreatedDate
    * * Display Name: Orcid Created Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidCreatedDate(): Date | null {
        return this.Get('OrcidCreatedDate');
    }
    set OrcidCreatedDate(value: Date | null) {
        this.Set('OrcidCreatedDate', value);
    }

    /**
    * * Field Name: OrcidLastModifiedDate
    * * Display Name: Orcid Last Modified Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidLastModifiedDate(): Date | null {
        return this.Get('OrcidLastModifiedDate');
    }
    set OrcidLastModifiedDate(value: Date | null) {
        this.Set('OrcidLastModifiedDate', value);
    }

    /**
    * * Field Name: RawJSON
    * * Display Name: Raw JSON
    * * SQL Data Type: nvarchar(MAX)
    */
    get RawJSON(): string | null {
        return this.Get('RawJSON');
    }
    set RawJSON(value: string | null) {
        this.Set('RawJSON', value);
    }

    /**
    * * Field Name: __mj_integration_SyncStatus
    * * Display Name: Mj Integration Sync Status
    * * SQL Data Type: nvarchar(50)
    * * Default Value: Active
    */
    get __mj_integration_SyncStatus(): string {
        return this.Get('__mj_integration_SyncStatus');
    }
    set __mj_integration_SyncStatus(value: string) {
        this.Set('__mj_integration_SyncStatus', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedAt
    * * Display Name: Mj Integration Last Synced At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastSyncedAt(): Date | null {
        return this.Get('__mj_integration_LastSyncedAt');
    }
    set __mj_integration_LastSyncedAt(value: Date | null) {
        this.Set('__mj_integration_LastSyncedAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedSnapshot
    * * Display Name: Mj Integration Last Synced Snapshot
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_LastSyncedSnapshot(): string | null {
        return this.Get('__mj_integration_LastSyncedSnapshot');
    }
    set __mj_integration_LastSyncedSnapshot(value: string | null) {
        this.Set('__mj_integration_LastSyncedSnapshot', value);
    }

    /**
    * * Field Name: __mj_integration_SyncMessage
    * * Display Name: Mj Integration Sync Message
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_SyncMessage(): string | null {
        return this.Get('__mj_integration_SyncMessage');
    }
    set __mj_integration_SyncMessage(value: string | null) {
        this.Set('__mj_integration_SyncMessage', value);
    }

    /**
    * * Field Name: __mj_integration_ContentHash
    * * Display Name: Mj Integration Content Hash
    * * SQL Data Type: nvarchar(64)
    */
    get __mj_integration_ContentHash(): string | null {
        return this.Get('__mj_integration_ContentHash');
    }
    set __mj_integration_ContentHash(value: string | null) {
        this.Set('__mj_integration_ContentHash', value);
    }

    /**
    * * Field Name: __mj_integration_CustomOverflow
    * * Display Name: Mj Integration Custom Overflow
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_CustomOverflow(): string | null {
        return this.Get('__mj_integration_CustomOverflow');
    }
    set __mj_integration_CustomOverflow(value: string | null) {
        this.Set('__mj_integration_CustomOverflow', value);
    }

    /**
    * * Field Name: __mj_integration_ExternalVersion
    * * Display Name: Mj Integration External Version
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_ExternalVersion(): string | null {
        return this.Get('__mj_integration_ExternalVersion');
    }
    set __mj_integration_ExternalVersion(value: string | null) {
        this.Set('__mj_integration_ExternalVersion', value);
    }

    /**
    * * Field Name: __mj_integration_LastSeenModifiedValue
    * * Display Name: Mj Integration Last Seen Modified Value
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_LastSeenModifiedValue(): string | null {
        return this.Get('__mj_integration_LastSeenModifiedValue');
    }
    set __mj_integration_LastSeenModifiedValue(value: string | null) {
        this.Set('__mj_integration_LastSeenModifiedValue', value);
    }

    /**
    * * Field Name: __mj_integration_LastReconciledAt
    * * Display Name: Mj Integration Last Reconciled At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastReconciledAt(): Date | null {
        return this.Get('__mj_integration_LastReconciledAt');
    }
    set __mj_integration_LastReconciledAt(value: Date | null) {
        this.Set('__mj_integration_LastReconciledAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastWriterDirection
    * * Display Name: Mj Integration Last Writer Direction
    * * SQL Data Type: nvarchar(10)
    */
    get __mj_integration_LastWriterDirection(): string | null {
        return this.Get('__mj_integration_LastWriterDirection');
    }
    set __mj_integration_LastWriterDirection(value: string | null) {
        this.Set('__mj_integration_LastWriterDirection', value);
    }

    /**
    * * Field Name: __mj_integration_IsTombstoned
    * * Display Name: Mj Integration Is Tombstoned
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get __mj_integration_IsTombstoned(): boolean {
        return this.Get('__mj_integration_IsTombstoned');
    }
    set __mj_integration_IsTombstoned(value: boolean) {
        this.Set('__mj_integration_IsTombstoned', value);
    }

    /**
    * * Field Name: __mj_integration_DeletedDetectedAt
    * * Display Name: Mj Integration Deleted Detected At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_DeletedDetectedAt(): Date | null {
        return this.Get('__mj_integration_DeletedDetectedAt');
    }
    set __mj_integration_DeletedDetectedAt(value: Date | null) {
        this.Set('__mj_integration_DeletedDetectedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Educations__CoreSignal - strongly typed entity sub-class
 * * Schema: CoreSignal
 * * Base Table: Education
 * * Base View: vwEducations__CoreSignal
 * * @description An education entry from a CoreSignal profile (degree + institution + years). Read via CoreSignal.vwPersonEducation only.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Educations__CoreSignal')
export class CoreSignalEducation__CoreSignalEntity extends BaseEntity<CoreSignalEducation__CoreSignalEntityType> {
    /**
    * Loads the Educations__CoreSignal record from the database
    * @param ID: string - primary key value to load the Educations__CoreSignal record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof CoreSignalEducation__CoreSignalEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ProfileID
    * * Display Name: Profile ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Profiles (vwProfiles.ID)
    */
    get ProfileID(): string {
        return this.Get('ProfileID');
    }
    set ProfileID(value: string) {
        this.Set('ProfileID', value);
    }

    /**
    * * Field Name: OrderInProfile
    * * Display Name: Order In Profile
    * * SQL Data Type: int
    */
    get OrderInProfile(): number {
        return this.Get('OrderInProfile');
    }
    set OrderInProfile(value: number) {
        this.Set('OrderInProfile', value);
    }

    /**
    * * Field Name: Degree
    * * Display Name: Degree
    * * SQL Data Type: nvarchar(1000)
    */
    get Degree(): string | null {
        return this.Get('Degree');
    }
    set Degree(value: string | null) {
        this.Set('Degree', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(MAX)
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: ActivitiesAndSocieties
    * * Display Name: Activities And Societies
    * * SQL Data Type: nvarchar(MAX)
    */
    get ActivitiesAndSocieties(): string | null {
        return this.Get('ActivitiesAndSocieties');
    }
    set ActivitiesAndSocieties(value: string | null) {
        this.Set('ActivitiesAndSocieties', value);
    }

    /**
    * * Field Name: InstitutionCoreSignalID
    * * Display Name: Institution Core Signal ID
    * * SQL Data Type: bigint
    */
    get InstitutionCoreSignalID(): number | null {
        return this.Get('InstitutionCoreSignalID');
    }
    set InstitutionCoreSignalID(value: number | null) {
        this.Set('InstitutionCoreSignalID', value);
    }

    /**
    * * Field Name: InstitutionName
    * * Display Name: Institution Name
    * * SQL Data Type: nvarchar(500)
    */
    get InstitutionName(): string | null {
        return this.Get('InstitutionName');
    }
    set InstitutionName(value: string | null) {
        this.Set('InstitutionName', value);
    }

    /**
    * * Field Name: InstitutionUrl
    * * Display Name: Institution Url
    * * SQL Data Type: nvarchar(1000)
    */
    get InstitutionUrl(): string | null {
        return this.Get('InstitutionUrl');
    }
    set InstitutionUrl(value: string | null) {
        this.Set('InstitutionUrl', value);
    }

    /**
    * * Field Name: InstitutionWebsite
    * * Display Name: Institution Website
    * * SQL Data Type: nvarchar(1000)
    */
    get InstitutionWebsite(): string | null {
        return this.Get('InstitutionWebsite');
    }
    set InstitutionWebsite(value: string | null) {
        this.Set('InstitutionWebsite', value);
    }

    /**
    * * Field Name: InstitutionCity
    * * Display Name: Institution City
    * * SQL Data Type: nvarchar(255)
    */
    get InstitutionCity(): string | null {
        return this.Get('InstitutionCity');
    }
    set InstitutionCity(value: string | null) {
        this.Set('InstitutionCity', value);
    }

    /**
    * * Field Name: InstitutionState
    * * Display Name: Institution State
    * * SQL Data Type: nvarchar(255)
    */
    get InstitutionState(): string | null {
        return this.Get('InstitutionState');
    }
    set InstitutionState(value: string | null) {
        this.Set('InstitutionState', value);
    }

    /**
    * * Field Name: InstitutionCountryISO2
    * * Display Name: Institution Country ISO2
    * * SQL Data Type: nvarchar(10)
    */
    get InstitutionCountryISO2(): string | null {
        return this.Get('InstitutionCountryISO2');
    }
    set InstitutionCountryISO2(value: string | null) {
        this.Set('InstitutionCountryISO2', value);
    }

    /**
    * * Field Name: DateFromYear
    * * Display Name: Date From Year
    * * SQL Data Type: smallint
    */
    get DateFromYear(): number | null {
        return this.Get('DateFromYear');
    }
    set DateFromYear(value: number | null) {
        this.Set('DateFromYear', value);
    }

    /**
    * * Field Name: DateToYear
    * * Display Name: Date To Year
    * * SQL Data Type: smallint
    */
    get DateToYear(): number | null {
        return this.Get('DateToYear');
    }
    set DateToYear(value: number | null) {
        this.Set('DateToYear', value);
    }

    /**
    * * Field Name: __mj_integration_SyncStatus
    * * Display Name: Mj Integration Sync Status
    * * SQL Data Type: nvarchar(50)
    * * Default Value: Active
    */
    get __mj_integration_SyncStatus(): string {
        return this.Get('__mj_integration_SyncStatus');
    }
    set __mj_integration_SyncStatus(value: string) {
        this.Set('__mj_integration_SyncStatus', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedAt
    * * Display Name: Mj Integration Last Synced At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastSyncedAt(): Date | null {
        return this.Get('__mj_integration_LastSyncedAt');
    }
    set __mj_integration_LastSyncedAt(value: Date | null) {
        this.Set('__mj_integration_LastSyncedAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedSnapshot
    * * Display Name: Mj Integration Last Synced Snapshot
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_LastSyncedSnapshot(): string | null {
        return this.Get('__mj_integration_LastSyncedSnapshot');
    }
    set __mj_integration_LastSyncedSnapshot(value: string | null) {
        this.Set('__mj_integration_LastSyncedSnapshot', value);
    }

    /**
    * * Field Name: __mj_integration_SyncMessage
    * * Display Name: Mj Integration Sync Message
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_SyncMessage(): string | null {
        return this.Get('__mj_integration_SyncMessage');
    }
    set __mj_integration_SyncMessage(value: string | null) {
        this.Set('__mj_integration_SyncMessage', value);
    }

    /**
    * * Field Name: __mj_integration_ContentHash
    * * Display Name: Mj Integration Content Hash
    * * SQL Data Type: nvarchar(64)
    */
    get __mj_integration_ContentHash(): string | null {
        return this.Get('__mj_integration_ContentHash');
    }
    set __mj_integration_ContentHash(value: string | null) {
        this.Set('__mj_integration_ContentHash', value);
    }

    /**
    * * Field Name: __mj_integration_CustomOverflow
    * * Display Name: Mj Integration Custom Overflow
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_CustomOverflow(): string | null {
        return this.Get('__mj_integration_CustomOverflow');
    }
    set __mj_integration_CustomOverflow(value: string | null) {
        this.Set('__mj_integration_CustomOverflow', value);
    }

    /**
    * * Field Name: __mj_integration_ExternalVersion
    * * Display Name: Mj Integration External Version
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_ExternalVersion(): string | null {
        return this.Get('__mj_integration_ExternalVersion');
    }
    set __mj_integration_ExternalVersion(value: string | null) {
        this.Set('__mj_integration_ExternalVersion', value);
    }

    /**
    * * Field Name: __mj_integration_LastSeenModifiedValue
    * * Display Name: Mj Integration Last Seen Modified Value
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_LastSeenModifiedValue(): string | null {
        return this.Get('__mj_integration_LastSeenModifiedValue');
    }
    set __mj_integration_LastSeenModifiedValue(value: string | null) {
        this.Set('__mj_integration_LastSeenModifiedValue', value);
    }

    /**
    * * Field Name: __mj_integration_LastReconciledAt
    * * Display Name: Mj Integration Last Reconciled At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastReconciledAt(): Date | null {
        return this.Get('__mj_integration_LastReconciledAt');
    }
    set __mj_integration_LastReconciledAt(value: Date | null) {
        this.Set('__mj_integration_LastReconciledAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastWriterDirection
    * * Display Name: Mj Integration Last Writer Direction
    * * SQL Data Type: nvarchar(10)
    */
    get __mj_integration_LastWriterDirection(): string | null {
        return this.Get('__mj_integration_LastWriterDirection');
    }
    set __mj_integration_LastWriterDirection(value: string | null) {
        this.Set('__mj_integration_LastWriterDirection', value);
    }

    /**
    * * Field Name: __mj_integration_IsTombstoned
    * * Display Name: Mj Integration Is Tombstoned
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get __mj_integration_IsTombstoned(): boolean {
        return this.Get('__mj_integration_IsTombstoned');
    }
    set __mj_integration_IsTombstoned(value: boolean) {
        this.Set('__mj_integration_IsTombstoned', value);
    }

    /**
    * * Field Name: __mj_integration_DeletedDetectedAt
    * * Display Name: Mj Integration Deleted Detected At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_DeletedDetectedAt(): Date | null {
        return this.Get('__mj_integration_DeletedDetectedAt');
    }
    set __mj_integration_DeletedDetectedAt(value: Date | null) {
        this.Set('__mj_integration_DeletedDetectedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Emails - strongly typed entity sub-class
 * * Schema: ORCID
 * * Base Table: Email
 * * Base View: vwEmails
 * * @description A public email (person.emails). Most ORCID emails are private, so this is usually empty.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Emails')
export class ORCIDEmailEntity extends BaseEntity<ORCIDEmailEntityType> {
    /**
    * Loads the Emails record from the database
    * @param ID: string - primary key value to load the Emails record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof ORCIDEmailEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ORCIDRecordID
    * * Display Name: ORCID Record ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Records (vwRecords.ID)
    */
    get ORCIDRecordID(): string {
        return this.Get('ORCIDRecordID');
    }
    set ORCIDRecordID(value: string) {
        this.Set('ORCIDRecordID', value);
    }

    /**
    * * Field Name: PutCode
    * * Display Name: Put Code
    * * SQL Data Type: bigint
    */
    get PutCode(): number | null {
        return this.Get('PutCode');
    }
    set PutCode(value: number | null) {
        this.Set('PutCode', value);
    }

    /**
    * * Field Name: Email
    * * Display Name: Email
    * * SQL Data Type: nvarchar(255)
    */
    get Email(): string | null {
        return this.Get('Email');
    }
    set Email(value: string | null) {
        this.Set('Email', value);
    }

    /**
    * * Field Name: Verified
    * * Display Name: Verified
    * * SQL Data Type: bit
    */
    get Verified(): boolean | null {
        return this.Get('Verified');
    }
    set Verified(value: boolean | null) {
        this.Set('Verified', value);
    }

    /**
    * * Field Name: IsPrimary
    * * Display Name: Is Primary
    * * SQL Data Type: bit
    */
    get IsPrimary(): boolean | null {
        return this.Get('IsPrimary');
    }
    set IsPrimary(value: boolean | null) {
        this.Set('IsPrimary', value);
    }

    /**
    * * Field Name: DisplayIndex
    * * Display Name: Display Index
    * * SQL Data Type: int
    */
    get DisplayIndex(): number | null {
        return this.Get('DisplayIndex');
    }
    set DisplayIndex(value: number | null) {
        this.Set('DisplayIndex', value);
    }

    /**
    * * Field Name: Visibility
    * * Display Name: Visibility
    * * SQL Data Type: nvarchar(20)
    */
    get Visibility(): string | null {
        return this.Get('Visibility');
    }
    set Visibility(value: string | null) {
        this.Set('Visibility', value);
    }

    /**
    * * Field Name: SourceName
    * * Display Name: Source Name
    * * SQL Data Type: nvarchar(255)
    */
    get SourceName(): string | null {
        return this.Get('SourceName');
    }
    set SourceName(value: string | null) {
        this.Set('SourceName', value);
    }

    /**
    * * Field Name: SourceOrcid
    * * Display Name: Source Orcid
    * * SQL Data Type: nvarchar(19)
    */
    get SourceOrcid(): string | null {
        return this.Get('SourceOrcid');
    }
    set SourceOrcid(value: string | null) {
        this.Set('SourceOrcid', value);
    }

    /**
    * * Field Name: OrcidCreatedDate
    * * Display Name: Orcid Created Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidCreatedDate(): Date | null {
        return this.Get('OrcidCreatedDate');
    }
    set OrcidCreatedDate(value: Date | null) {
        this.Set('OrcidCreatedDate', value);
    }

    /**
    * * Field Name: OrcidLastModifiedDate
    * * Display Name: Orcid Last Modified Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidLastModifiedDate(): Date | null {
        return this.Get('OrcidLastModifiedDate');
    }
    set OrcidLastModifiedDate(value: Date | null) {
        this.Set('OrcidLastModifiedDate', value);
    }

    /**
    * * Field Name: RawJSON
    * * Display Name: Raw JSON
    * * SQL Data Type: nvarchar(MAX)
    */
    get RawJSON(): string | null {
        return this.Get('RawJSON');
    }
    set RawJSON(value: string | null) {
        this.Set('RawJSON', value);
    }

    /**
    * * Field Name: __mj_integration_SyncStatus
    * * Display Name: Mj Integration Sync Status
    * * SQL Data Type: nvarchar(50)
    * * Default Value: Active
    */
    get __mj_integration_SyncStatus(): string {
        return this.Get('__mj_integration_SyncStatus');
    }
    set __mj_integration_SyncStatus(value: string) {
        this.Set('__mj_integration_SyncStatus', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedAt
    * * Display Name: Mj Integration Last Synced At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastSyncedAt(): Date | null {
        return this.Get('__mj_integration_LastSyncedAt');
    }
    set __mj_integration_LastSyncedAt(value: Date | null) {
        this.Set('__mj_integration_LastSyncedAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedSnapshot
    * * Display Name: Mj Integration Last Synced Snapshot
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_LastSyncedSnapshot(): string | null {
        return this.Get('__mj_integration_LastSyncedSnapshot');
    }
    set __mj_integration_LastSyncedSnapshot(value: string | null) {
        this.Set('__mj_integration_LastSyncedSnapshot', value);
    }

    /**
    * * Field Name: __mj_integration_SyncMessage
    * * Display Name: Mj Integration Sync Message
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_SyncMessage(): string | null {
        return this.Get('__mj_integration_SyncMessage');
    }
    set __mj_integration_SyncMessage(value: string | null) {
        this.Set('__mj_integration_SyncMessage', value);
    }

    /**
    * * Field Name: __mj_integration_ContentHash
    * * Display Name: Mj Integration Content Hash
    * * SQL Data Type: nvarchar(64)
    */
    get __mj_integration_ContentHash(): string | null {
        return this.Get('__mj_integration_ContentHash');
    }
    set __mj_integration_ContentHash(value: string | null) {
        this.Set('__mj_integration_ContentHash', value);
    }

    /**
    * * Field Name: __mj_integration_CustomOverflow
    * * Display Name: Mj Integration Custom Overflow
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_CustomOverflow(): string | null {
        return this.Get('__mj_integration_CustomOverflow');
    }
    set __mj_integration_CustomOverflow(value: string | null) {
        this.Set('__mj_integration_CustomOverflow', value);
    }

    /**
    * * Field Name: __mj_integration_ExternalVersion
    * * Display Name: Mj Integration External Version
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_ExternalVersion(): string | null {
        return this.Get('__mj_integration_ExternalVersion');
    }
    set __mj_integration_ExternalVersion(value: string | null) {
        this.Set('__mj_integration_ExternalVersion', value);
    }

    /**
    * * Field Name: __mj_integration_LastSeenModifiedValue
    * * Display Name: Mj Integration Last Seen Modified Value
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_LastSeenModifiedValue(): string | null {
        return this.Get('__mj_integration_LastSeenModifiedValue');
    }
    set __mj_integration_LastSeenModifiedValue(value: string | null) {
        this.Set('__mj_integration_LastSeenModifiedValue', value);
    }

    /**
    * * Field Name: __mj_integration_LastReconciledAt
    * * Display Name: Mj Integration Last Reconciled At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastReconciledAt(): Date | null {
        return this.Get('__mj_integration_LastReconciledAt');
    }
    set __mj_integration_LastReconciledAt(value: Date | null) {
        this.Set('__mj_integration_LastReconciledAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastWriterDirection
    * * Display Name: Mj Integration Last Writer Direction
    * * SQL Data Type: nvarchar(10)
    */
    get __mj_integration_LastWriterDirection(): string | null {
        return this.Get('__mj_integration_LastWriterDirection');
    }
    set __mj_integration_LastWriterDirection(value: string | null) {
        this.Set('__mj_integration_LastWriterDirection', value);
    }

    /**
    * * Field Name: __mj_integration_IsTombstoned
    * * Display Name: Mj Integration Is Tombstoned
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get __mj_integration_IsTombstoned(): boolean {
        return this.Get('__mj_integration_IsTombstoned');
    }
    set __mj_integration_IsTombstoned(value: boolean) {
        this.Set('__mj_integration_IsTombstoned', value);
    }

    /**
    * * Field Name: __mj_integration_DeletedDetectedAt
    * * Display Name: Mj Integration Deleted Detected At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_DeletedDetectedAt(): Date | null {
        return this.Get('__mj_integration_DeletedDetectedAt');
    }
    set __mj_integration_DeletedDetectedAt(value: Date | null) {
        this.Set('__mj_integration_DeletedDetectedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Employments - strongly typed entity sub-class
 * * Schema: ORCID
 * * Base Table: Employment
 * * Base View: vwEmployments
 * * @description An employment affiliation (employments section).
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Employments')
export class ORCIDEmploymentEntity extends BaseEntity<ORCIDEmploymentEntityType> {
    /**
    * Loads the Employments record from the database
    * @param ID: string - primary key value to load the Employments record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof ORCIDEmploymentEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ORCIDRecordID
    * * Display Name: ORCID Record ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Records (vwRecords.ID)
    */
    get ORCIDRecordID(): string {
        return this.Get('ORCIDRecordID');
    }
    set ORCIDRecordID(value: string) {
        this.Set('ORCIDRecordID', value);
    }

    /**
    * * Field Name: PutCode
    * * Display Name: Put Code
    * * SQL Data Type: bigint
    */
    get PutCode(): number {
        return this.Get('PutCode');
    }
    set PutCode(value: number) {
        this.Set('PutCode', value);
    }

    /**
    * * Field Name: DepartmentName
    * * Display Name: Department Name
    * * SQL Data Type: nvarchar(500)
    */
    get DepartmentName(): string | null {
        return this.Get('DepartmentName');
    }
    set DepartmentName(value: string | null) {
        this.Set('DepartmentName', value);
    }

    /**
    * * Field Name: RoleTitle
    * * Display Name: Role Title
    * * SQL Data Type: nvarchar(500)
    */
    get RoleTitle(): string | null {
        return this.Get('RoleTitle');
    }
    set RoleTitle(value: string | null) {
        this.Set('RoleTitle', value);
    }

    /**
    * * Field Name: StartYear
    * * Display Name: Start Year
    * * SQL Data Type: smallint
    */
    get StartYear(): number | null {
        return this.Get('StartYear');
    }
    set StartYear(value: number | null) {
        this.Set('StartYear', value);
    }

    /**
    * * Field Name: StartMonth
    * * Display Name: Start Month
    * * SQL Data Type: tinyint
    */
    get StartMonth(): number | null {
        return this.Get('StartMonth');
    }
    set StartMonth(value: number | null) {
        this.Set('StartMonth', value);
    }

    /**
    * * Field Name: StartDay
    * * Display Name: Start Day
    * * SQL Data Type: tinyint
    */
    get StartDay(): number | null {
        return this.Get('StartDay');
    }
    set StartDay(value: number | null) {
        this.Set('StartDay', value);
    }

    /**
    * * Field Name: EndYear
    * * Display Name: End Year
    * * SQL Data Type: smallint
    */
    get EndYear(): number | null {
        return this.Get('EndYear');
    }
    set EndYear(value: number | null) {
        this.Set('EndYear', value);
    }

    /**
    * * Field Name: EndMonth
    * * Display Name: End Month
    * * SQL Data Type: tinyint
    */
    get EndMonth(): number | null {
        return this.Get('EndMonth');
    }
    set EndMonth(value: number | null) {
        this.Set('EndMonth', value);
    }

    /**
    * * Field Name: EndDay
    * * Display Name: End Day
    * * SQL Data Type: tinyint
    */
    get EndDay(): number | null {
        return this.Get('EndDay');
    }
    set EndDay(value: number | null) {
        this.Set('EndDay', value);
    }

    /**
    * * Field Name: OrgName
    * * Display Name: Org Name
    * * SQL Data Type: nvarchar(500)
    */
    get OrgName(): string | null {
        return this.Get('OrgName');
    }
    set OrgName(value: string | null) {
        this.Set('OrgName', value);
    }

    /**
    * * Field Name: OrgCity
    * * Display Name: Org City
    * * SQL Data Type: nvarchar(255)
    */
    get OrgCity(): string | null {
        return this.Get('OrgCity');
    }
    set OrgCity(value: string | null) {
        this.Set('OrgCity', value);
    }

    /**
    * * Field Name: OrgRegion
    * * Display Name: Org Region
    * * SQL Data Type: nvarchar(255)
    */
    get OrgRegion(): string | null {
        return this.Get('OrgRegion');
    }
    set OrgRegion(value: string | null) {
        this.Set('OrgRegion', value);
    }

    /**
    * * Field Name: OrgCountry
    * * Display Name: Org Country
    * * SQL Data Type: nvarchar(10)
    */
    get OrgCountry(): string | null {
        return this.Get('OrgCountry');
    }
    set OrgCountry(value: string | null) {
        this.Set('OrgCountry', value);
    }

    /**
    * * Field Name: OrgDisambiguatedID
    * * Display Name: Org Disambiguated ID
    * * SQL Data Type: nvarchar(255)
    */
    get OrgDisambiguatedID(): string | null {
        return this.Get('OrgDisambiguatedID');
    }
    set OrgDisambiguatedID(value: string | null) {
        this.Set('OrgDisambiguatedID', value);
    }

    /**
    * * Field Name: OrgDisambiguationSource
    * * Display Name: Org Disambiguation Source
    * * SQL Data Type: nvarchar(50)
    */
    get OrgDisambiguationSource(): string | null {
        return this.Get('OrgDisambiguationSource');
    }
    set OrgDisambiguationSource(value: string | null) {
        this.Set('OrgDisambiguationSource', value);
    }

    /**
    * * Field Name: Url
    * * Display Name: Url
    * * SQL Data Type: nvarchar(2000)
    */
    get Url(): string | null {
        return this.Get('Url');
    }
    set Url(value: string | null) {
        this.Set('Url', value);
    }

    /**
    * * Field Name: Visibility
    * * Display Name: Visibility
    * * SQL Data Type: nvarchar(20)
    */
    get Visibility(): string | null {
        return this.Get('Visibility');
    }
    set Visibility(value: string | null) {
        this.Set('Visibility', value);
    }

    /**
    * * Field Name: SourceName
    * * Display Name: Source Name
    * * SQL Data Type: nvarchar(255)
    */
    get SourceName(): string | null {
        return this.Get('SourceName');
    }
    set SourceName(value: string | null) {
        this.Set('SourceName', value);
    }

    /**
    * * Field Name: SourceOrcid
    * * Display Name: Source Orcid
    * * SQL Data Type: nvarchar(19)
    */
    get SourceOrcid(): string | null {
        return this.Get('SourceOrcid');
    }
    set SourceOrcid(value: string | null) {
        this.Set('SourceOrcid', value);
    }

    /**
    * * Field Name: OrcidCreatedDate
    * * Display Name: Orcid Created Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidCreatedDate(): Date | null {
        return this.Get('OrcidCreatedDate');
    }
    set OrcidCreatedDate(value: Date | null) {
        this.Set('OrcidCreatedDate', value);
    }

    /**
    * * Field Name: OrcidLastModifiedDate
    * * Display Name: Orcid Last Modified Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidLastModifiedDate(): Date | null {
        return this.Get('OrcidLastModifiedDate');
    }
    set OrcidLastModifiedDate(value: Date | null) {
        this.Set('OrcidLastModifiedDate', value);
    }

    /**
    * * Field Name: RawJSON
    * * Display Name: Raw JSON
    * * SQL Data Type: nvarchar(MAX)
    */
    get RawJSON(): string | null {
        return this.Get('RawJSON');
    }
    set RawJSON(value: string | null) {
        this.Set('RawJSON', value);
    }

    /**
    * * Field Name: __mj_integration_SyncStatus
    * * Display Name: Mj Integration Sync Status
    * * SQL Data Type: nvarchar(50)
    * * Default Value: Active
    */
    get __mj_integration_SyncStatus(): string {
        return this.Get('__mj_integration_SyncStatus');
    }
    set __mj_integration_SyncStatus(value: string) {
        this.Set('__mj_integration_SyncStatus', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedAt
    * * Display Name: Mj Integration Last Synced At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastSyncedAt(): Date | null {
        return this.Get('__mj_integration_LastSyncedAt');
    }
    set __mj_integration_LastSyncedAt(value: Date | null) {
        this.Set('__mj_integration_LastSyncedAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedSnapshot
    * * Display Name: Mj Integration Last Synced Snapshot
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_LastSyncedSnapshot(): string | null {
        return this.Get('__mj_integration_LastSyncedSnapshot');
    }
    set __mj_integration_LastSyncedSnapshot(value: string | null) {
        this.Set('__mj_integration_LastSyncedSnapshot', value);
    }

    /**
    * * Field Name: __mj_integration_SyncMessage
    * * Display Name: Mj Integration Sync Message
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_SyncMessage(): string | null {
        return this.Get('__mj_integration_SyncMessage');
    }
    set __mj_integration_SyncMessage(value: string | null) {
        this.Set('__mj_integration_SyncMessage', value);
    }

    /**
    * * Field Name: __mj_integration_ContentHash
    * * Display Name: Mj Integration Content Hash
    * * SQL Data Type: nvarchar(64)
    */
    get __mj_integration_ContentHash(): string | null {
        return this.Get('__mj_integration_ContentHash');
    }
    set __mj_integration_ContentHash(value: string | null) {
        this.Set('__mj_integration_ContentHash', value);
    }

    /**
    * * Field Name: __mj_integration_CustomOverflow
    * * Display Name: Mj Integration Custom Overflow
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_CustomOverflow(): string | null {
        return this.Get('__mj_integration_CustomOverflow');
    }
    set __mj_integration_CustomOverflow(value: string | null) {
        this.Set('__mj_integration_CustomOverflow', value);
    }

    /**
    * * Field Name: __mj_integration_ExternalVersion
    * * Display Name: Mj Integration External Version
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_ExternalVersion(): string | null {
        return this.Get('__mj_integration_ExternalVersion');
    }
    set __mj_integration_ExternalVersion(value: string | null) {
        this.Set('__mj_integration_ExternalVersion', value);
    }

    /**
    * * Field Name: __mj_integration_LastSeenModifiedValue
    * * Display Name: Mj Integration Last Seen Modified Value
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_LastSeenModifiedValue(): string | null {
        return this.Get('__mj_integration_LastSeenModifiedValue');
    }
    set __mj_integration_LastSeenModifiedValue(value: string | null) {
        this.Set('__mj_integration_LastSeenModifiedValue', value);
    }

    /**
    * * Field Name: __mj_integration_LastReconciledAt
    * * Display Name: Mj Integration Last Reconciled At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastReconciledAt(): Date | null {
        return this.Get('__mj_integration_LastReconciledAt');
    }
    set __mj_integration_LastReconciledAt(value: Date | null) {
        this.Set('__mj_integration_LastReconciledAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastWriterDirection
    * * Display Name: Mj Integration Last Writer Direction
    * * SQL Data Type: nvarchar(10)
    */
    get __mj_integration_LastWriterDirection(): string | null {
        return this.Get('__mj_integration_LastWriterDirection');
    }
    set __mj_integration_LastWriterDirection(value: string | null) {
        this.Set('__mj_integration_LastWriterDirection', value);
    }

    /**
    * * Field Name: __mj_integration_IsTombstoned
    * * Display Name: Mj Integration Is Tombstoned
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get __mj_integration_IsTombstoned(): boolean {
        return this.Get('__mj_integration_IsTombstoned');
    }
    set __mj_integration_IsTombstoned(value: boolean) {
        this.Set('__mj_integration_IsTombstoned', value);
    }

    /**
    * * Field Name: __mj_integration_DeletedDetectedAt
    * * Display Name: Mj Integration Deleted Detected At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_DeletedDetectedAt(): Date | null {
        return this.Get('__mj_integration_DeletedDetectedAt');
    }
    set __mj_integration_DeletedDetectedAt(value: Date | null) {
        this.Set('__mj_integration_DeletedDetectedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Experiences - strongly typed entity sub-class
 * * Schema: CoreSignal
 * * Base Table: Experience
 * * Base View: vwExperiences
 * * @description An employment spell from a CoreSignal profile's experience array, with month-precision dates and denormalized company metadata. Read via CoreSignal.vwPersonEmployment only.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Experiences')
export class CoreSignalExperienceEntity extends BaseEntity<CoreSignalExperienceEntityType> {
    /**
    * Loads the Experiences record from the database
    * @param ID: string - primary key value to load the Experiences record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof CoreSignalExperienceEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ProfileID
    * * Display Name: Profile ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Profiles (vwProfiles.ID)
    */
    get ProfileID(): string {
        return this.Get('ProfileID');
    }
    set ProfileID(value: string) {
        this.Set('ProfileID', value);
    }

    /**
    * * Field Name: OrderInProfile
    * * Display Name: Order In Profile
    * * SQL Data Type: int
    * * Description: Position within the feed's experience array; with ProfileID it is the sync natural key (CoreSignal items carry no source-assigned id). Order shifts between deliveries are absorbed by content-hash + tombstoning.
    */
    get OrderInProfile(): number {
        return this.Get('OrderInProfile');
    }
    set OrderInProfile(value: number) {
        this.Set('OrderInProfile', value);
    }

    /**
    * * Field Name: PositionTitle
    * * Display Name: Position Title
    * * SQL Data Type: nvarchar(500)
    */
    get PositionTitle(): string | null {
        return this.Get('PositionTitle');
    }
    set PositionTitle(value: string | null) {
        this.Set('PositionTitle', value);
    }

    /**
    * * Field Name: Department
    * * Display Name: Department
    * * SQL Data Type: nvarchar(255)
    */
    get Department(): string | null {
        return this.Get('Department');
    }
    set Department(value: string | null) {
        this.Set('Department', value);
    }

    /**
    * * Field Name: ManagementLevel
    * * Display Name: Management Level
    * * SQL Data Type: nvarchar(255)
    */
    get ManagementLevel(): string | null {
        return this.Get('ManagementLevel');
    }
    set ManagementLevel(value: string | null) {
        this.Set('ManagementLevel', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(MAX)
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: Location
    * * Display Name: Location
    * * SQL Data Type: nvarchar(500)
    */
    get Location(): string | null {
        return this.Get('Location');
    }
    set Location(value: string | null) {
        this.Set('Location', value);
    }

    /**
    * * Field Name: DateFromYear
    * * Display Name: Date From Year
    * * SQL Data Type: smallint
    */
    get DateFromYear(): number | null {
        return this.Get('DateFromYear');
    }
    set DateFromYear(value: number | null) {
        this.Set('DateFromYear', value);
    }

    /**
    * * Field Name: DateFromMonth
    * * Display Name: Date From Month
    * * SQL Data Type: tinyint
    */
    get DateFromMonth(): number | null {
        return this.Get('DateFromMonth');
    }
    set DateFromMonth(value: number | null) {
        this.Set('DateFromMonth', value);
    }

    /**
    * * Field Name: DateToYear
    * * Display Name: Date To Year
    * * SQL Data Type: smallint
    */
    get DateToYear(): number | null {
        return this.Get('DateToYear');
    }
    set DateToYear(value: number | null) {
        this.Set('DateToYear', value);
    }

    /**
    * * Field Name: DateToMonth
    * * Display Name: Date To Month
    * * SQL Data Type: tinyint
    */
    get DateToMonth(): number | null {
        return this.Get('DateToMonth');
    }
    set DateToMonth(value: number | null) {
        this.Set('DateToMonth', value);
    }

    /**
    * * Field Name: DurationMonths
    * * Display Name: Duration Months
    * * SQL Data Type: int
    */
    get DurationMonths(): number | null {
        return this.Get('DurationMonths');
    }
    set DurationMonths(value: number | null) {
        this.Set('DurationMonths', value);
    }

    /**
    * * Field Name: IsActive
    * * Display Name: Is Active
    * * SQL Data Type: bit
    * * Default Value: 0
    * * Description: Feed active_experience: the profile claims this position is current. Qualify with StaleCurrentSuspect + Profile.SourceCheckedAt before presenting as "Current".
    */
    get IsActive(): boolean {
        return this.Get('IsActive');
    }
    set IsActive(value: boolean) {
        this.Set('IsActive', value);
    }

    /**
    * * Field Name: StaleCurrentSuspect
    * * Display Name: Stale Current Suspect
    * * SQL Data Type: bit
    * * Default Value: 0
    * * Description: 1 = IsActive but the matched person's ORCID employment at a matching org ended 3+ years ago — the abandoned-LinkedIn-profile signature (measured on 14% of shared spells). Set by the "Run CoreSignal Ingest" action's stale-flag pass; consumers must caveat these.
    */
    get StaleCurrentSuspect(): boolean {
        return this.Get('StaleCurrentSuspect');
    }
    set StaleCurrentSuspect(value: boolean) {
        this.Set('StaleCurrentSuspect', value);
    }

    /**
    * * Field Name: CompanyCoreSignalID
    * * Display Name: Company Core Signal ID
    * * SQL Data Type: bigint
    */
    get CompanyCoreSignalID(): number | null {
        return this.Get('CompanyCoreSignalID');
    }
    set CompanyCoreSignalID(value: number | null) {
        this.Set('CompanyCoreSignalID', value);
    }

    /**
    * * Field Name: CompanyName
    * * Display Name: Company Name
    * * SQL Data Type: nvarchar(500)
    */
    get CompanyName(): string | null {
        return this.Get('CompanyName');
    }
    set CompanyName(value: string | null) {
        this.Set('CompanyName', value);
    }

    /**
    * * Field Name: CompanyType
    * * Display Name: Company Type
    * * SQL Data Type: nvarchar(255)
    */
    get CompanyType(): string | null {
        return this.Get('CompanyType');
    }
    set CompanyType(value: string | null) {
        this.Set('CompanyType', value);
    }

    /**
    * * Field Name: CompanyIndustry
    * * Display Name: Company Industry
    * * SQL Data Type: nvarchar(255)
    */
    get CompanyIndustry(): string | null {
        return this.Get('CompanyIndustry');
    }
    set CompanyIndustry(value: string | null) {
        this.Set('CompanyIndustry', value);
    }

    /**
    * * Field Name: CompanySizeRange
    * * Display Name: Company Size Range
    * * SQL Data Type: nvarchar(50)
    */
    get CompanySizeRange(): string | null {
        return this.Get('CompanySizeRange');
    }
    set CompanySizeRange(value: string | null) {
        this.Set('CompanySizeRange', value);
    }

    /**
    * * Field Name: CompanyEmployeesCount
    * * Display Name: Company Employees Count
    * * SQL Data Type: int
    */
    get CompanyEmployeesCount(): number | null {
        return this.Get('CompanyEmployeesCount');
    }
    set CompanyEmployeesCount(value: number | null) {
        this.Set('CompanyEmployeesCount', value);
    }

    /**
    * * Field Name: CompanyWebsite
    * * Display Name: Company Website
    * * SQL Data Type: nvarchar(1000)
    */
    get CompanyWebsite(): string | null {
        return this.Get('CompanyWebsite');
    }
    set CompanyWebsite(value: string | null) {
        this.Set('CompanyWebsite', value);
    }

    /**
    * * Field Name: CompanyLinkedInUrl
    * * Display Name: Company Linked In Url
    * * SQL Data Type: nvarchar(1000)
    */
    get CompanyLinkedInUrl(): string | null {
        return this.Get('CompanyLinkedInUrl');
    }
    set CompanyLinkedInUrl(value: string | null) {
        this.Set('CompanyLinkedInUrl', value);
    }

    /**
    * * Field Name: CompanyHQCity
    * * Display Name: Company HQ City
    * * SQL Data Type: nvarchar(255)
    */
    get CompanyHQCity(): string | null {
        return this.Get('CompanyHQCity');
    }
    set CompanyHQCity(value: string | null) {
        this.Set('CompanyHQCity', value);
    }

    /**
    * * Field Name: CompanyHQState
    * * Display Name: Company HQ State
    * * SQL Data Type: nvarchar(255)
    */
    get CompanyHQState(): string | null {
        return this.Get('CompanyHQState');
    }
    set CompanyHQState(value: string | null) {
        this.Set('CompanyHQState', value);
    }

    /**
    * * Field Name: CompanyHQCountry
    * * Display Name: Company HQ Country
    * * SQL Data Type: nvarchar(255)
    */
    get CompanyHQCountry(): string | null {
        return this.Get('CompanyHQCountry');
    }
    set CompanyHQCountry(value: string | null) {
        this.Set('CompanyHQCountry', value);
    }

    /**
    * * Field Name: CompanyHQCountryISO2
    * * Display Name: Company HQ Country ISO2
    * * SQL Data Type: nvarchar(10)
    */
    get CompanyHQCountryISO2(): string | null {
        return this.Get('CompanyHQCountryISO2');
    }
    set CompanyHQCountryISO2(value: string | null) {
        this.Set('CompanyHQCountryISO2', value);
    }

    /**
    * * Field Name: __mj_integration_SyncStatus
    * * Display Name: Mj Integration Sync Status
    * * SQL Data Type: nvarchar(50)
    * * Default Value: Active
    */
    get __mj_integration_SyncStatus(): string {
        return this.Get('__mj_integration_SyncStatus');
    }
    set __mj_integration_SyncStatus(value: string) {
        this.Set('__mj_integration_SyncStatus', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedAt
    * * Display Name: Mj Integration Last Synced At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastSyncedAt(): Date | null {
        return this.Get('__mj_integration_LastSyncedAt');
    }
    set __mj_integration_LastSyncedAt(value: Date | null) {
        this.Set('__mj_integration_LastSyncedAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedSnapshot
    * * Display Name: Mj Integration Last Synced Snapshot
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_LastSyncedSnapshot(): string | null {
        return this.Get('__mj_integration_LastSyncedSnapshot');
    }
    set __mj_integration_LastSyncedSnapshot(value: string | null) {
        this.Set('__mj_integration_LastSyncedSnapshot', value);
    }

    /**
    * * Field Name: __mj_integration_SyncMessage
    * * Display Name: Mj Integration Sync Message
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_SyncMessage(): string | null {
        return this.Get('__mj_integration_SyncMessage');
    }
    set __mj_integration_SyncMessage(value: string | null) {
        this.Set('__mj_integration_SyncMessage', value);
    }

    /**
    * * Field Name: __mj_integration_ContentHash
    * * Display Name: Mj Integration Content Hash
    * * SQL Data Type: nvarchar(64)
    */
    get __mj_integration_ContentHash(): string | null {
        return this.Get('__mj_integration_ContentHash');
    }
    set __mj_integration_ContentHash(value: string | null) {
        this.Set('__mj_integration_ContentHash', value);
    }

    /**
    * * Field Name: __mj_integration_CustomOverflow
    * * Display Name: Mj Integration Custom Overflow
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_CustomOverflow(): string | null {
        return this.Get('__mj_integration_CustomOverflow');
    }
    set __mj_integration_CustomOverflow(value: string | null) {
        this.Set('__mj_integration_CustomOverflow', value);
    }

    /**
    * * Field Name: __mj_integration_ExternalVersion
    * * Display Name: Mj Integration External Version
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_ExternalVersion(): string | null {
        return this.Get('__mj_integration_ExternalVersion');
    }
    set __mj_integration_ExternalVersion(value: string | null) {
        this.Set('__mj_integration_ExternalVersion', value);
    }

    /**
    * * Field Name: __mj_integration_LastSeenModifiedValue
    * * Display Name: Mj Integration Last Seen Modified Value
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_LastSeenModifiedValue(): string | null {
        return this.Get('__mj_integration_LastSeenModifiedValue');
    }
    set __mj_integration_LastSeenModifiedValue(value: string | null) {
        this.Set('__mj_integration_LastSeenModifiedValue', value);
    }

    /**
    * * Field Name: __mj_integration_LastReconciledAt
    * * Display Name: Mj Integration Last Reconciled At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastReconciledAt(): Date | null {
        return this.Get('__mj_integration_LastReconciledAt');
    }
    set __mj_integration_LastReconciledAt(value: Date | null) {
        this.Set('__mj_integration_LastReconciledAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastWriterDirection
    * * Display Name: Mj Integration Last Writer Direction
    * * SQL Data Type: nvarchar(10)
    */
    get __mj_integration_LastWriterDirection(): string | null {
        return this.Get('__mj_integration_LastWriterDirection');
    }
    set __mj_integration_LastWriterDirection(value: string | null) {
        this.Set('__mj_integration_LastWriterDirection', value);
    }

    /**
    * * Field Name: __mj_integration_IsTombstoned
    * * Display Name: Mj Integration Is Tombstoned
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get __mj_integration_IsTombstoned(): boolean {
        return this.Get('__mj_integration_IsTombstoned');
    }
    set __mj_integration_IsTombstoned(value: boolean) {
        this.Set('__mj_integration_IsTombstoned', value);
    }

    /**
    * * Field Name: __mj_integration_DeletedDetectedAt
    * * Display Name: Mj Integration Deleted Detected At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_DeletedDetectedAt(): Date | null {
        return this.Get('__mj_integration_DeletedDetectedAt');
    }
    set __mj_integration_DeletedDetectedAt(value: Date | null) {
        this.Set('__mj_integration_DeletedDetectedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Export Logs - strongly typed entity sub-class
 * * Schema: MAPS
 * * Base Table: ExportLog
 * * Base View: vwExportLogs
 * * @description One row per export download initiated via mapsExportCreate (Phase 3+).
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Export Logs')
export class MAPSExportLogEntity extends BaseEntity<MAPSExportLogEntityType> {
    /**
    * Loads the Export Logs record from the database
    * @param ID: string - primary key value to load the Export Logs record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof MAPSExportLogEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: PartnerTokenID
    * * Display Name: Partner Token ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Partner Tokens (vwPartnerTokens.ID)
    * * Description: Legacy owning partner token. NULL for company-resolved sessions; dual-written until PR B. Ownership scoping uses CompanyID.
    */
    get PartnerTokenID(): string | null {
        return this.Get('PartnerTokenID');
    }
    set PartnerTokenID(value: string | null) {
        this.Set('PartnerTokenID', value);
    }

    /**
    * * Field Name: SearchLogID
    * * Display Name: Search Log ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Search Logs (vwSearchLogs.ID)
    * * Description: Search this export was sourced from. Nullable to permit out-of-band exports later.
    */
    get SearchLogID(): string | null {
        return this.Get('SearchLogID');
    }
    set SearchLogID(value: string | null) {
        this.Set('SearchLogID', value);
    }

    /**
    * * Field Name: Format
    * * Display Name: Format
    * * SQL Data Type: nvarchar(8)
    * * Value List Type: List
    * * Possible Values 
    *   * csv
    *   * xlsx
    * * Description: Export file format: xlsx or csv.
    */
    get Format(): 'csv' | 'xlsx' {
        return this.Get('Format');
    }
    set Format(value: 'csv' | 'xlsx') {
        this.Set('Format', value);
    }

    /**
    * * Field Name: SelectedCount
    * * Display Name: Selected Count
    * * SQL Data Type: int
    * * Description: Number of candidates included in this export. Server enforces a 30-row cap.
    */
    get SelectedCount(): number {
        return this.Get('SelectedCount');
    }
    set SelectedCount(value: number) {
        this.Set('SelectedCount', value);
    }

    /**
    * * Field Name: IncludesSummaries
    * * Display Name: Includes Summaries
    * * SQL Data Type: bit
    * * Description: 1 if the export includes the generated Why Suggested / Things to Look Into text; 0 otherwise.
    */
    get IncludesSummaries(): boolean {
        return this.Get('IncludesSummaries');
    }
    set IncludesSummaries(value: boolean) {
        this.Set('IncludesSummaries', value);
    }

    /**
    * * Field Name: Timestamp
    * * Display Name: Timestamp
    * * SQL Data Type: datetimeoffset
    * * Default Value: sysdatetimeoffset()
    */
    get Timestamp(): Date {
        return this.Get('Timestamp');
    }
    set Timestamp(value: Date) {
        this.Set('Timestamp', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    * * Description: Owning company. Authoritative attribution/scoping key for the partner-account model; NOT NULL is enforced at the app layer in PR A and at the DB in PR B.
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
}


/**
 * External Identifiers - strongly typed entity sub-class
 * * Schema: ORCID
 * * Base Table: ExternalIdentifier
 * * Base View: vwExternalIdentifiers
 * * @description A person-level external identifier (person.external-identifiers): ResearcherID, Scopus Author ID, Loop, etc.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'External Identifiers')
export class ORCIDExternalIdentifierEntity extends BaseEntity<ORCIDExternalIdentifierEntityType> {
    /**
    * Loads the External Identifiers record from the database
    * @param ID: string - primary key value to load the External Identifiers record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof ORCIDExternalIdentifierEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ORCIDRecordID
    * * Display Name: ORCID Record ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Records (vwRecords.ID)
    */
    get ORCIDRecordID(): string {
        return this.Get('ORCIDRecordID');
    }
    set ORCIDRecordID(value: string) {
        this.Set('ORCIDRecordID', value);
    }

    /**
    * * Field Name: PutCode
    * * Display Name: Put Code
    * * SQL Data Type: bigint
    */
    get PutCode(): number {
        return this.Get('PutCode');
    }
    set PutCode(value: number) {
        this.Set('PutCode', value);
    }

    /**
    * * Field Name: ExternalIDType
    * * Display Name: External ID Type
    * * SQL Data Type: nvarchar(255)
    */
    get ExternalIDType(): string | null {
        return this.Get('ExternalIDType');
    }
    set ExternalIDType(value: string | null) {
        this.Set('ExternalIDType', value);
    }

    /**
    * * Field Name: ExternalIDValue
    * * Display Name: External ID Value
    * * SQL Data Type: nvarchar(500)
    */
    get ExternalIDValue(): string | null {
        return this.Get('ExternalIDValue');
    }
    set ExternalIDValue(value: string | null) {
        this.Set('ExternalIDValue', value);
    }

    /**
    * * Field Name: ExternalIDUrl
    * * Display Name: External ID Url
    * * SQL Data Type: nvarchar(2000)
    */
    get ExternalIDUrl(): string | null {
        return this.Get('ExternalIDUrl');
    }
    set ExternalIDUrl(value: string | null) {
        this.Set('ExternalIDUrl', value);
    }

    /**
    * * Field Name: ExternalIDRelationship
    * * Display Name: External ID Relationship
    * * SQL Data Type: nvarchar(20)
    */
    get ExternalIDRelationship(): string | null {
        return this.Get('ExternalIDRelationship');
    }
    set ExternalIDRelationship(value: string | null) {
        this.Set('ExternalIDRelationship', value);
    }

    /**
    * * Field Name: DisplayIndex
    * * Display Name: Display Index
    * * SQL Data Type: int
    */
    get DisplayIndex(): number | null {
        return this.Get('DisplayIndex');
    }
    set DisplayIndex(value: number | null) {
        this.Set('DisplayIndex', value);
    }

    /**
    * * Field Name: Visibility
    * * Display Name: Visibility
    * * SQL Data Type: nvarchar(20)
    */
    get Visibility(): string | null {
        return this.Get('Visibility');
    }
    set Visibility(value: string | null) {
        this.Set('Visibility', value);
    }

    /**
    * * Field Name: SourceName
    * * Display Name: Source Name
    * * SQL Data Type: nvarchar(255)
    */
    get SourceName(): string | null {
        return this.Get('SourceName');
    }
    set SourceName(value: string | null) {
        this.Set('SourceName', value);
    }

    /**
    * * Field Name: SourceOrcid
    * * Display Name: Source Orcid
    * * SQL Data Type: nvarchar(19)
    */
    get SourceOrcid(): string | null {
        return this.Get('SourceOrcid');
    }
    set SourceOrcid(value: string | null) {
        this.Set('SourceOrcid', value);
    }

    /**
    * * Field Name: OrcidCreatedDate
    * * Display Name: Orcid Created Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidCreatedDate(): Date | null {
        return this.Get('OrcidCreatedDate');
    }
    set OrcidCreatedDate(value: Date | null) {
        this.Set('OrcidCreatedDate', value);
    }

    /**
    * * Field Name: OrcidLastModifiedDate
    * * Display Name: Orcid Last Modified Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidLastModifiedDate(): Date | null {
        return this.Get('OrcidLastModifiedDate');
    }
    set OrcidLastModifiedDate(value: Date | null) {
        this.Set('OrcidLastModifiedDate', value);
    }

    /**
    * * Field Name: RawJSON
    * * Display Name: Raw JSON
    * * SQL Data Type: nvarchar(MAX)
    */
    get RawJSON(): string | null {
        return this.Get('RawJSON');
    }
    set RawJSON(value: string | null) {
        this.Set('RawJSON', value);
    }

    /**
    * * Field Name: __mj_integration_SyncStatus
    * * Display Name: Mj Integration Sync Status
    * * SQL Data Type: nvarchar(50)
    * * Default Value: Active
    */
    get __mj_integration_SyncStatus(): string {
        return this.Get('__mj_integration_SyncStatus');
    }
    set __mj_integration_SyncStatus(value: string) {
        this.Set('__mj_integration_SyncStatus', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedAt
    * * Display Name: Mj Integration Last Synced At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastSyncedAt(): Date | null {
        return this.Get('__mj_integration_LastSyncedAt');
    }
    set __mj_integration_LastSyncedAt(value: Date | null) {
        this.Set('__mj_integration_LastSyncedAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedSnapshot
    * * Display Name: Mj Integration Last Synced Snapshot
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_LastSyncedSnapshot(): string | null {
        return this.Get('__mj_integration_LastSyncedSnapshot');
    }
    set __mj_integration_LastSyncedSnapshot(value: string | null) {
        this.Set('__mj_integration_LastSyncedSnapshot', value);
    }

    /**
    * * Field Name: __mj_integration_SyncMessage
    * * Display Name: Mj Integration Sync Message
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_SyncMessage(): string | null {
        return this.Get('__mj_integration_SyncMessage');
    }
    set __mj_integration_SyncMessage(value: string | null) {
        this.Set('__mj_integration_SyncMessage', value);
    }

    /**
    * * Field Name: __mj_integration_ContentHash
    * * Display Name: Mj Integration Content Hash
    * * SQL Data Type: nvarchar(64)
    */
    get __mj_integration_ContentHash(): string | null {
        return this.Get('__mj_integration_ContentHash');
    }
    set __mj_integration_ContentHash(value: string | null) {
        this.Set('__mj_integration_ContentHash', value);
    }

    /**
    * * Field Name: __mj_integration_CustomOverflow
    * * Display Name: Mj Integration Custom Overflow
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_CustomOverflow(): string | null {
        return this.Get('__mj_integration_CustomOverflow');
    }
    set __mj_integration_CustomOverflow(value: string | null) {
        this.Set('__mj_integration_CustomOverflow', value);
    }

    /**
    * * Field Name: __mj_integration_ExternalVersion
    * * Display Name: Mj Integration External Version
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_ExternalVersion(): string | null {
        return this.Get('__mj_integration_ExternalVersion');
    }
    set __mj_integration_ExternalVersion(value: string | null) {
        this.Set('__mj_integration_ExternalVersion', value);
    }

    /**
    * * Field Name: __mj_integration_LastSeenModifiedValue
    * * Display Name: Mj Integration Last Seen Modified Value
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_LastSeenModifiedValue(): string | null {
        return this.Get('__mj_integration_LastSeenModifiedValue');
    }
    set __mj_integration_LastSeenModifiedValue(value: string | null) {
        this.Set('__mj_integration_LastSeenModifiedValue', value);
    }

    /**
    * * Field Name: __mj_integration_LastReconciledAt
    * * Display Name: Mj Integration Last Reconciled At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastReconciledAt(): Date | null {
        return this.Get('__mj_integration_LastReconciledAt');
    }
    set __mj_integration_LastReconciledAt(value: Date | null) {
        this.Set('__mj_integration_LastReconciledAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastWriterDirection
    * * Display Name: Mj Integration Last Writer Direction
    * * SQL Data Type: nvarchar(10)
    */
    get __mj_integration_LastWriterDirection(): string | null {
        return this.Get('__mj_integration_LastWriterDirection');
    }
    set __mj_integration_LastWriterDirection(value: string | null) {
        this.Set('__mj_integration_LastWriterDirection', value);
    }

    /**
    * * Field Name: __mj_integration_IsTombstoned
    * * Display Name: Mj Integration Is Tombstoned
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get __mj_integration_IsTombstoned(): boolean {
        return this.Get('__mj_integration_IsTombstoned');
    }
    set __mj_integration_IsTombstoned(value: boolean) {
        this.Set('__mj_integration_IsTombstoned', value);
    }

    /**
    * * Field Name: __mj_integration_DeletedDetectedAt
    * * Display Name: Mj Integration Deleted Detected At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_DeletedDetectedAt(): Date | null {
        return this.Get('__mj_integration_DeletedDetectedAt');
    }
    set __mj_integration_DeletedDetectedAt(value: Date | null) {
        this.Set('__mj_integration_DeletedDetectedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Flyway _schema _histories - strongly typed entity sub-class
 * * Schema: dbo
 * * Base Table: flyway_schema_history
 * * Base View: vwFlyway_schema_histories
 * * Primary Key: installed_rank
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Flyway _schema _histories')
export class dboflyway_schema_historyEntity extends BaseEntity<dboflyway_schema_historyEntityType> {
    /**
    * Loads the Flyway _schema _histories record from the database
    * @param installed_rank: number - primary key value to load the Flyway _schema _histories record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof dboflyway_schema_historyEntity
    * @method
    * @override
    */
    public async Load(installed_rank: number, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'installed_rank', Value: installed_rank });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: installed_rank
    * * Display Name: installed _rank
    * * SQL Data Type: int
    */
    get installed_rank(): number {
        return this.Get('installed_rank');
    }
    set installed_rank(value: number) {
        this.Set('installed_rank', value);
    }

    /**
    * * Field Name: version
    * * Display Name: version
    * * SQL Data Type: nvarchar(50)
    */
    get version(): string | null {
        return this.Get('version');
    }
    set version(value: string | null) {
        this.Set('version', value);
    }

    /**
    * * Field Name: description
    * * Display Name: description
    * * SQL Data Type: nvarchar(200)
    */
    get description(): string | null {
        return this.Get('description');
    }
    set description(value: string | null) {
        this.Set('description', value);
    }

    /**
    * * Field Name: type
    * * Display Name: type
    * * SQL Data Type: nvarchar(20)
    */
    get type(): string {
        return this.Get('type');
    }
    set type(value: string) {
        this.Set('type', value);
    }

    /**
    * * Field Name: script
    * * Display Name: script
    * * SQL Data Type: nvarchar(1000)
    */
    get script(): string {
        return this.Get('script');
    }
    set script(value: string) {
        this.Set('script', value);
    }

    /**
    * * Field Name: checksum
    * * Display Name: checksum
    * * SQL Data Type: int
    */
    get checksum(): number | null {
        return this.Get('checksum');
    }
    set checksum(value: number | null) {
        this.Set('checksum', value);
    }

    /**
    * * Field Name: installed_by
    * * Display Name: installed _by
    * * SQL Data Type: nvarchar(100)
    */
    get installed_by(): string {
        return this.Get('installed_by');
    }
    set installed_by(value: string) {
        this.Set('installed_by', value);
    }

    /**
    * * Field Name: installed_on
    * * Display Name: installed _on
    * * SQL Data Type: datetime
    * * Default Value: getdate()
    */
    get installed_on(): Date {
        return this.Get('installed_on');
    }
    set installed_on(value: Date) {
        this.Set('installed_on', value);
    }

    /**
    * * Field Name: execution_time
    * * Display Name: execution _time
    * * SQL Data Type: int
    */
    get execution_time(): number {
        return this.Get('execution_time');
    }
    set execution_time(value: number) {
        this.Set('execution_time', value);
    }

    /**
    * * Field Name: success
    * * Display Name: success
    * * SQL Data Type: bit
    */
    get success(): boolean {
        return this.Get('success');
    }
    set success(value: boolean) {
        this.Set('success', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Funding External IDs - strongly typed entity sub-class
 * * Schema: ORCID
 * * Base Table: FundingExternalID
 * * Base View: vwFundingExternalIDs
 * * @description An external identifier (grant number, etc.) for a funding, exploded from Funding.RawJSON by the enrichment action.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Funding External IDs')
export class ORCIDFundingExternalIDEntity extends BaseEntity<ORCIDFundingExternalIDEntityType> {
    /**
    * Loads the Funding External IDs record from the database
    * @param ID: string - primary key value to load the Funding External IDs record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof ORCIDFundingExternalIDEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: FundingID
    * * Display Name: Funding ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Fundings (vwFundings.ID)
    */
    get FundingID(): string {
        return this.Get('FundingID');
    }
    set FundingID(value: string) {
        this.Set('FundingID', value);
    }

    /**
    * * Field Name: ExternalIDType
    * * Display Name: External ID Type
    * * SQL Data Type: nvarchar(100)
    */
    get ExternalIDType(): string | null {
        return this.Get('ExternalIDType');
    }
    set ExternalIDType(value: string | null) {
        this.Set('ExternalIDType', value);
    }

    /**
    * * Field Name: ExternalIDValue
    * * Display Name: External ID Value
    * * SQL Data Type: nvarchar(500)
    */
    get ExternalIDValue(): string | null {
        return this.Get('ExternalIDValue');
    }
    set ExternalIDValue(value: string | null) {
        this.Set('ExternalIDValue', value);
    }

    /**
    * * Field Name: ExternalIDUrl
    * * Display Name: External ID Url
    * * SQL Data Type: nvarchar(2000)
    */
    get ExternalIDUrl(): string | null {
        return this.Get('ExternalIDUrl');
    }
    set ExternalIDUrl(value: string | null) {
        this.Set('ExternalIDUrl', value);
    }

    /**
    * * Field Name: ExternalIDRelationship
    * * Display Name: External ID Relationship
    * * SQL Data Type: nvarchar(20)
    */
    get ExternalIDRelationship(): string | null {
        return this.Get('ExternalIDRelationship');
    }
    set ExternalIDRelationship(value: string | null) {
        this.Set('ExternalIDRelationship', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Fundings - strongly typed entity sub-class
 * * Schema: ORCID
 * * Base Table: Funding
 * * Base View: vwFundings
 * * @description A funding/grant (fundings section). external-ids → ORCID.FundingExternalID.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Fundings')
export class ORCIDFundingEntity extends BaseEntity<ORCIDFundingEntityType> {
    /**
    * Loads the Fundings record from the database
    * @param ID: string - primary key value to load the Fundings record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof ORCIDFundingEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ORCIDRecordID
    * * Display Name: ORCID Record ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Records (vwRecords.ID)
    */
    get ORCIDRecordID(): string {
        return this.Get('ORCIDRecordID');
    }
    set ORCIDRecordID(value: string) {
        this.Set('ORCIDRecordID', value);
    }

    /**
    * * Field Name: PutCode
    * * Display Name: Put Code
    * * SQL Data Type: bigint
    */
    get PutCode(): number {
        return this.Get('PutCode');
    }
    set PutCode(value: number) {
        this.Set('PutCode', value);
    }

    /**
    * * Field Name: Title
    * * Display Name: Title
    * * SQL Data Type: nvarchar(2000)
    */
    get Title(): string | null {
        return this.Get('Title');
    }
    set Title(value: string | null) {
        this.Set('Title', value);
    }

    /**
    * * Field Name: TranslatedTitle
    * * Display Name: Translated Title
    * * SQL Data Type: nvarchar(2000)
    */
    get TranslatedTitle(): string | null {
        return this.Get('TranslatedTitle');
    }
    set TranslatedTitle(value: string | null) {
        this.Set('TranslatedTitle', value);
    }

    /**
    * * Field Name: FundingType
    * * Display Name: Funding Type
    * * SQL Data Type: nvarchar(100)
    */
    get FundingType(): string | null {
        return this.Get('FundingType');
    }
    set FundingType(value: string | null) {
        this.Set('FundingType', value);
    }

    /**
    * * Field Name: OrganizationDefinedType
    * * Display Name: Organization Defined Type
    * * SQL Data Type: nvarchar(500)
    */
    get OrganizationDefinedType(): string | null {
        return this.Get('OrganizationDefinedType');
    }
    set OrganizationDefinedType(value: string | null) {
        this.Set('OrganizationDefinedType', value);
    }

    /**
    * * Field Name: AmountValue
    * * Display Name: Amount Value
    * * SQL Data Type: nvarchar(100)
    */
    get AmountValue(): string | null {
        return this.Get('AmountValue');
    }
    set AmountValue(value: string | null) {
        this.Set('AmountValue', value);
    }

    /**
    * * Field Name: AmountCurrency
    * * Display Name: Amount Currency
    * * SQL Data Type: nvarchar(10)
    */
    get AmountCurrency(): string | null {
        return this.Get('AmountCurrency');
    }
    set AmountCurrency(value: string | null) {
        this.Set('AmountCurrency', value);
    }

    /**
    * * Field Name: StartYear
    * * Display Name: Start Year
    * * SQL Data Type: smallint
    */
    get StartYear(): number | null {
        return this.Get('StartYear');
    }
    set StartYear(value: number | null) {
        this.Set('StartYear', value);
    }

    /**
    * * Field Name: StartMonth
    * * Display Name: Start Month
    * * SQL Data Type: tinyint
    */
    get StartMonth(): number | null {
        return this.Get('StartMonth');
    }
    set StartMonth(value: number | null) {
        this.Set('StartMonth', value);
    }

    /**
    * * Field Name: StartDay
    * * Display Name: Start Day
    * * SQL Data Type: tinyint
    */
    get StartDay(): number | null {
        return this.Get('StartDay');
    }
    set StartDay(value: number | null) {
        this.Set('StartDay', value);
    }

    /**
    * * Field Name: EndYear
    * * Display Name: End Year
    * * SQL Data Type: smallint
    */
    get EndYear(): number | null {
        return this.Get('EndYear');
    }
    set EndYear(value: number | null) {
        this.Set('EndYear', value);
    }

    /**
    * * Field Name: EndMonth
    * * Display Name: End Month
    * * SQL Data Type: tinyint
    */
    get EndMonth(): number | null {
        return this.Get('EndMonth');
    }
    set EndMonth(value: number | null) {
        this.Set('EndMonth', value);
    }

    /**
    * * Field Name: EndDay
    * * Display Name: End Day
    * * SQL Data Type: tinyint
    */
    get EndDay(): number | null {
        return this.Get('EndDay');
    }
    set EndDay(value: number | null) {
        this.Set('EndDay', value);
    }

    /**
    * * Field Name: OrgName
    * * Display Name: Org Name
    * * SQL Data Type: nvarchar(500)
    */
    get OrgName(): string | null {
        return this.Get('OrgName');
    }
    set OrgName(value: string | null) {
        this.Set('OrgName', value);
    }

    /**
    * * Field Name: OrgCity
    * * Display Name: Org City
    * * SQL Data Type: nvarchar(255)
    */
    get OrgCity(): string | null {
        return this.Get('OrgCity');
    }
    set OrgCity(value: string | null) {
        this.Set('OrgCity', value);
    }

    /**
    * * Field Name: OrgRegion
    * * Display Name: Org Region
    * * SQL Data Type: nvarchar(255)
    */
    get OrgRegion(): string | null {
        return this.Get('OrgRegion');
    }
    set OrgRegion(value: string | null) {
        this.Set('OrgRegion', value);
    }

    /**
    * * Field Name: OrgCountry
    * * Display Name: Org Country
    * * SQL Data Type: nvarchar(10)
    */
    get OrgCountry(): string | null {
        return this.Get('OrgCountry');
    }
    set OrgCountry(value: string | null) {
        this.Set('OrgCountry', value);
    }

    /**
    * * Field Name: OrgDisambiguatedID
    * * Display Name: Org Disambiguated ID
    * * SQL Data Type: nvarchar(255)
    */
    get OrgDisambiguatedID(): string | null {
        return this.Get('OrgDisambiguatedID');
    }
    set OrgDisambiguatedID(value: string | null) {
        this.Set('OrgDisambiguatedID', value);
    }

    /**
    * * Field Name: OrgDisambiguationSource
    * * Display Name: Org Disambiguation Source
    * * SQL Data Type: nvarchar(50)
    */
    get OrgDisambiguationSource(): string | null {
        return this.Get('OrgDisambiguationSource');
    }
    set OrgDisambiguationSource(value: string | null) {
        this.Set('OrgDisambiguationSource', value);
    }

    /**
    * * Field Name: Url
    * * Display Name: Url
    * * SQL Data Type: nvarchar(2000)
    */
    get Url(): string | null {
        return this.Get('Url');
    }
    set Url(value: string | null) {
        this.Set('Url', value);
    }

    /**
    * * Field Name: Visibility
    * * Display Name: Visibility
    * * SQL Data Type: nvarchar(20)
    */
    get Visibility(): string | null {
        return this.Get('Visibility');
    }
    set Visibility(value: string | null) {
        this.Set('Visibility', value);
    }

    /**
    * * Field Name: SourceName
    * * Display Name: Source Name
    * * SQL Data Type: nvarchar(255)
    */
    get SourceName(): string | null {
        return this.Get('SourceName');
    }
    set SourceName(value: string | null) {
        this.Set('SourceName', value);
    }

    /**
    * * Field Name: SourceOrcid
    * * Display Name: Source Orcid
    * * SQL Data Type: nvarchar(19)
    */
    get SourceOrcid(): string | null {
        return this.Get('SourceOrcid');
    }
    set SourceOrcid(value: string | null) {
        this.Set('SourceOrcid', value);
    }

    /**
    * * Field Name: OrcidCreatedDate
    * * Display Name: Orcid Created Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidCreatedDate(): Date | null {
        return this.Get('OrcidCreatedDate');
    }
    set OrcidCreatedDate(value: Date | null) {
        this.Set('OrcidCreatedDate', value);
    }

    /**
    * * Field Name: OrcidLastModifiedDate
    * * Display Name: Orcid Last Modified Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidLastModifiedDate(): Date | null {
        return this.Get('OrcidLastModifiedDate');
    }
    set OrcidLastModifiedDate(value: Date | null) {
        this.Set('OrcidLastModifiedDate', value);
    }

    /**
    * * Field Name: RawJSON
    * * Display Name: Raw JSON
    * * SQL Data Type: nvarchar(MAX)
    */
    get RawJSON(): string | null {
        return this.Get('RawJSON');
    }
    set RawJSON(value: string | null) {
        this.Set('RawJSON', value);
    }

    /**
    * * Field Name: __mj_integration_SyncStatus
    * * Display Name: Mj Integration Sync Status
    * * SQL Data Type: nvarchar(50)
    * * Default Value: Active
    */
    get __mj_integration_SyncStatus(): string {
        return this.Get('__mj_integration_SyncStatus');
    }
    set __mj_integration_SyncStatus(value: string) {
        this.Set('__mj_integration_SyncStatus', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedAt
    * * Display Name: Mj Integration Last Synced At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastSyncedAt(): Date | null {
        return this.Get('__mj_integration_LastSyncedAt');
    }
    set __mj_integration_LastSyncedAt(value: Date | null) {
        this.Set('__mj_integration_LastSyncedAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedSnapshot
    * * Display Name: Mj Integration Last Synced Snapshot
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_LastSyncedSnapshot(): string | null {
        return this.Get('__mj_integration_LastSyncedSnapshot');
    }
    set __mj_integration_LastSyncedSnapshot(value: string | null) {
        this.Set('__mj_integration_LastSyncedSnapshot', value);
    }

    /**
    * * Field Name: __mj_integration_SyncMessage
    * * Display Name: Mj Integration Sync Message
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_SyncMessage(): string | null {
        return this.Get('__mj_integration_SyncMessage');
    }
    set __mj_integration_SyncMessage(value: string | null) {
        this.Set('__mj_integration_SyncMessage', value);
    }

    /**
    * * Field Name: __mj_integration_ContentHash
    * * Display Name: Mj Integration Content Hash
    * * SQL Data Type: nvarchar(64)
    */
    get __mj_integration_ContentHash(): string | null {
        return this.Get('__mj_integration_ContentHash');
    }
    set __mj_integration_ContentHash(value: string | null) {
        this.Set('__mj_integration_ContentHash', value);
    }

    /**
    * * Field Name: __mj_integration_CustomOverflow
    * * Display Name: Mj Integration Custom Overflow
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_CustomOverflow(): string | null {
        return this.Get('__mj_integration_CustomOverflow');
    }
    set __mj_integration_CustomOverflow(value: string | null) {
        this.Set('__mj_integration_CustomOverflow', value);
    }

    /**
    * * Field Name: __mj_integration_ExternalVersion
    * * Display Name: Mj Integration External Version
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_ExternalVersion(): string | null {
        return this.Get('__mj_integration_ExternalVersion');
    }
    set __mj_integration_ExternalVersion(value: string | null) {
        this.Set('__mj_integration_ExternalVersion', value);
    }

    /**
    * * Field Name: __mj_integration_LastSeenModifiedValue
    * * Display Name: Mj Integration Last Seen Modified Value
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_LastSeenModifiedValue(): string | null {
        return this.Get('__mj_integration_LastSeenModifiedValue');
    }
    set __mj_integration_LastSeenModifiedValue(value: string | null) {
        this.Set('__mj_integration_LastSeenModifiedValue', value);
    }

    /**
    * * Field Name: __mj_integration_LastReconciledAt
    * * Display Name: Mj Integration Last Reconciled At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastReconciledAt(): Date | null {
        return this.Get('__mj_integration_LastReconciledAt');
    }
    set __mj_integration_LastReconciledAt(value: Date | null) {
        this.Set('__mj_integration_LastReconciledAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastWriterDirection
    * * Display Name: Mj Integration Last Writer Direction
    * * SQL Data Type: nvarchar(10)
    */
    get __mj_integration_LastWriterDirection(): string | null {
        return this.Get('__mj_integration_LastWriterDirection');
    }
    set __mj_integration_LastWriterDirection(value: string | null) {
        this.Set('__mj_integration_LastWriterDirection', value);
    }

    /**
    * * Field Name: __mj_integration_IsTombstoned
    * * Display Name: Mj Integration Is Tombstoned
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get __mj_integration_IsTombstoned(): boolean {
        return this.Get('__mj_integration_IsTombstoned');
    }
    set __mj_integration_IsTombstoned(value: boolean) {
        this.Set('__mj_integration_IsTombstoned', value);
    }

    /**
    * * Field Name: __mj_integration_DeletedDetectedAt
    * * Display Name: Mj Integration Deleted Detected At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_DeletedDetectedAt(): Date | null {
        return this.Get('__mj_integration_DeletedDetectedAt');
    }
    set __mj_integration_DeletedDetectedAt(value: Date | null) {
        this.Set('__mj_integration_DeletedDetectedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: OwnerRole
    * * Display Name: Owner Role
    * * SQL Data Type: nvarchar(50)
    * * Description: The record owner's contributor-role on this grant from the funding detail endpoint (lead=PI, co-lead, supported-by, other-contribution); NULL when ORCID exposes no role. The "PI experience" signal.
    */
    get OwnerRole(): string | null {
        return this.Get('OwnerRole');
    }
    set OwnerRole(value: string | null) {
        this.Set('OwnerRole', value);
    }

    /**
    * * Field Name: ShortDescription
    * * Display Name: Short Description
    * * SQL Data Type: nvarchar(MAX)
    * * Description: The grant abstract (short-description) from the funding detail endpoint; NULL when absent.
    */
    get ShortDescription(): string | null {
        return this.Get('ShortDescription');
    }
    set ShortDescription(value: string | null) {
        this.Set('ShortDescription', value);
    }

    /**
    * * Field Name: DetailFetchedAt
    * * Display Name: Detail Fetched At
    * * SQL Data Type: datetimeoffset
    * * Description: Ledger: when the funding detail endpoint was last successfully fetched for this row. Drives the enrichment worklist (re-fetch when NULL, on error, or when OrcidLastModifiedDate is newer).
    */
    get DetailFetchedAt(): Date | null {
        return this.Get('DetailFetchedAt');
    }
    set DetailFetchedAt(value: Date | null) {
        this.Set('DetailFetchedAt', value);
    }

    /**
    * * Field Name: DetailFetchStatus
    * * Display Name: Detail Fetch Status
    * * SQL Data Type: nvarchar(20)
    * * Description: Ledger: outcome of the last detail fetch — ok | notfound (404, deleted put-code) | forbidden (403, private funding) | error (transient, retried).
    */
    get DetailFetchStatus(): string | null {
        return this.Get('DetailFetchStatus');
    }
    set DetailFetchStatus(value: string | null) {
        this.Set('DetailFetchStatus', value);
    }
}


/**
 * Ingest Batches - strongly typed entity sub-class
 * * Schema: Jobs
 * * Base Table: IngestBatch
 * * Base View: vwIngestBatches
 * * @description Nightly-ingest ledger: one row per (DumpType, DumpDate). The worker's idempotency anchor (a complete row short-circuits replay), the polling target for the MJ "Jobs Ingest Orchestrator" scheduled job (which alerts when no complete row exists by the feed deadline), and the Explorer-visible ops surface. Stage advances parsing → diffing → gating → writing → vectorsync → complete; any failure records failed + ErrorDetail and blocks later dates (state chains by date).
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Ingest Batches')
export class JobsIngestBatchEntity extends BaseEntity<JobsIngestBatchEntityType> {
    /**
    * Loads the Ingest Batches record from the database
    * @param ID: string - primary key value to load the Ingest Batches record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof JobsIngestBatchEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: IngestBatchID
    * * Display Name: Ingest Batch ID
    * * SQL Data Type: int
    * * Description: Compact INT identity used as the reference target for Jobs.JobPosting.IngestBatchID (the GUID ID exists for MJ/CodeGen conformance).
    */
    get IngestBatchID(): number {
        return this.Get('IngestBatchID');
    }
    set IngestBatchID(value: number) {
        this.Set('IngestBatchID', value);
    }

    /**
    * * Field Name: DumpType
    * * Display Name: Dump Type
    * * SQL Data Type: nvarchar(10)
    * * Value List Type: List
    * * Possible Values 
    *   * daily
    *   * initial
    *   * reconcile
    * * Description: daily = one feed dump date; initial = the one-time seed/backfill; reconcile = the weekly SQL↔Pinecone drift sweep (counts land in the Vectors* columns).
    */
    get DumpType(): 'daily' | 'initial' | 'reconcile' {
        return this.Get('DumpType');
    }
    set DumpType(value: 'daily' | 'initial' | 'reconcile') {
        this.Set('DumpType', value);
    }

    /**
    * * Field Name: DumpDate
    * * Display Name: Dump Date
    * * SQL Data Type: date
    */
    get DumpDate(): Date {
        return this.Get('DumpDate');
    }
    set DumpDate(value: Date) {
        this.Set('DumpDate', value);
    }

    /**
    * * Field Name: BlobPrefix
    * * Display Name: Blob Prefix
    * * SQL Data Type: nvarchar(500)
    */
    get BlobPrefix(): string {
        return this.Get('BlobPrefix');
    }
    set BlobPrefix(value: string) {
        this.Set('BlobPrefix', value);
    }

    /**
    * * Field Name: ManifestRowCount
    * * Display Name: Manifest Row Count
    * * SQL Data Type: bigint
    */
    get ManifestRowCount(): number | null {
        return this.Get('ManifestRowCount');
    }
    set ManifestRowCount(value: number | null) {
        this.Set('ManifestRowCount', value);
    }

    /**
    * * Field Name: ParsedRowCount
    * * Display Name: Parsed Row Count
    * * SQL Data Type: bigint
    */
    get ParsedRowCount(): number | null {
        return this.Get('ParsedRowCount');
    }
    set ParsedRowCount(value: number | null) {
        this.Set('ParsedRowCount', value);
    }

    /**
    * * Field Name: DiffNew
    * * Display Name: Diff New
    * * SQL Data Type: bigint
    */
    get DiffNew(): number | null {
        return this.Get('DiffNew');
    }
    set DiffNew(value: number | null) {
        this.Set('DiffNew', value);
    }

    /**
    * * Field Name: DiffUpdated
    * * Display Name: Diff Updated
    * * SQL Data Type: bigint
    */
    get DiffUpdated(): number | null {
        return this.Get('DiffUpdated');
    }
    set DiffUpdated(value: number | null) {
        this.Set('DiffUpdated', value);
    }

    /**
    * * Field Name: DiffExpired
    * * Display Name: Diff Expired
    * * SQL Data Type: bigint
    */
    get DiffExpired(): number | null {
        return this.Get('DiffExpired');
    }
    set DiffExpired(value: number | null) {
        this.Set('DiffExpired', value);
    }

    /**
    * * Field Name: DiffSkipped
    * * Display Name: Diff Skipped
    * * SQL Data Type: bigint
    */
    get DiffSkipped(): number | null {
        return this.Get('DiffSkipped');
    }
    set DiffSkipped(value: number | null) {
        this.Set('DiffSkipped', value);
    }

    /**
    * * Field Name: GateKept
    * * Display Name: Gate Kept
    * * SQL Data Type: bigint
    */
    get GateKept(): number | null {
        return this.Get('GateKept');
    }
    set GateKept(value: number | null) {
        this.Set('GateKept', value);
    }

    /**
    * * Field Name: GateRejected
    * * Display Name: Gate Rejected
    * * SQL Data Type: bigint
    */
    get GateRejected(): number | null {
        return this.Get('GateRejected');
    }
    set GateRejected(value: number | null) {
        this.Set('GateRejected', value);
    }

    /**
    * * Field Name: VectorsUpserted
    * * Display Name: Vectors Upserted
    * * SQL Data Type: bigint
    */
    get VectorsUpserted(): number | null {
        return this.Get('VectorsUpserted');
    }
    set VectorsUpserted(value: number | null) {
        this.Set('VectorsUpserted', value);
    }

    /**
    * * Field Name: VectorsMetadataUpdated
    * * Display Name: Vectors Metadata Updated
    * * SQL Data Type: bigint
    */
    get VectorsMetadataUpdated(): number | null {
        return this.Get('VectorsMetadataUpdated');
    }
    set VectorsMetadataUpdated(value: number | null) {
        this.Set('VectorsMetadataUpdated', value);
    }

    /**
    * * Field Name: VectorsDeleted
    * * Display Name: Vectors Deleted
    * * SQL Data Type: bigint
    */
    get VectorsDeleted(): number | null {
        return this.Get('VectorsDeleted');
    }
    set VectorsDeleted(value: number | null) {
        this.Set('VectorsDeleted', value);
    }

    /**
    * * Field Name: Stage
    * * Display Name: Stage
    * * SQL Data Type: nvarchar(20)
    * * Value List Type: List
    * * Possible Values 
    *   * complete
    *   * diffing
    *   * failed
    *   * gating
    *   * parsing
    *   * vectorsync
    *   * writing
    */
    get Stage(): 'complete' | 'diffing' | 'failed' | 'gating' | 'parsing' | 'vectorsync' | 'writing' {
        return this.Get('Stage');
    }
    set Stage(value: 'complete' | 'diffing' | 'failed' | 'gating' | 'parsing' | 'vectorsync' | 'writing') {
        this.Set('Stage', value);
    }

    /**
    * * Field Name: ErrorDetail
    * * Display Name: Error Detail
    * * SQL Data Type: nvarchar(MAX)
    */
    get ErrorDetail(): string | null {
        return this.Get('ErrorDetail');
    }
    set ErrorDetail(value: string | null) {
        this.Set('ErrorDetail', value);
    }

    /**
    * * Field Name: StartedAt
    * * Display Name: Started At
    * * SQL Data Type: datetimeoffset
    * * Default Value: sysdatetimeoffset()
    */
    get StartedAt(): Date {
        return this.Get('StartedAt');
    }
    set StartedAt(value: Date) {
        this.Set('StartedAt', value);
    }

    /**
    * * Field Name: CompletedAt
    * * Display Name: Completed At
    * * SQL Data Type: datetimeoffset
    */
    get CompletedAt(): Date | null {
        return this.Get('CompletedAt');
    }
    set CompletedAt(value: Date | null) {
        this.Set('CompletedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Ingestion Runs - strongly typed entity sub-class
 * * Schema: admin
 * * Base Table: IngestionRun
 * * Base View: vwIngestionRuns
 * * @description One row per source file processed by the MAPS ingestion pipeline. Tracks status, per-table row counts (RowCountsJson), and any error; provides idempotency and observability.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Ingestion Runs')
export class adminIngestionRunEntity extends BaseEntity<adminIngestionRunEntityType> {
    /**
    * Loads the Ingestion Runs record from the database
    * @param ID: string - primary key value to load the Ingestion Runs record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof adminIngestionRunEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: IngestionTypeID
    * * Display Name: Ingestion Type ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Ingestion Types (vwIngestionTypes.ID)
    * * Description: The admin.IngestionType this file was processed as.
    */
    get IngestionTypeID(): string | null {
        return this.Get('IngestionTypeID');
    }
    set IngestionTypeID(value: string | null) {
        this.Set('IngestionTypeID', value);
    }

    /**
    * * Field Name: SourceFilePath
    * * Display Name: Source File Path
    * * SQL Data Type: nvarchar(800)
    * * Description: Original file path/URL as dropped (SharePoint or blob).
    */
    get SourceFilePath(): string {
        return this.Get('SourceFilePath');
    }
    set SourceFilePath(value: string) {
        this.Set('SourceFilePath', value);
    }

    /**
    * * Field Name: FinalFilePath
    * * Display Name: Final File Path
    * * SQL Data Type: nvarchar(800)
    * * Description: Post-processing destination (e.g., archive path). NULL until archived.
    */
    get FinalFilePath(): string | null {
        return this.Get('FinalFilePath');
    }
    set FinalFilePath(value: string | null) {
        this.Set('FinalFilePath', value);
    }

    /**
    * * Field Name: FileSizeBytes
    * * Display Name: File Size Bytes
    * * SQL Data Type: bigint
    * * Description: Size of the source file in bytes. Audit only.
    */
    get FileSizeBytes(): number | null {
        return this.Get('FileSizeBytes');
    }
    set FileSizeBytes(value: number | null) {
        this.Set('FileSizeBytes', value);
    }

    /**
    * * Field Name: FileHashSha256
    * * Display Name: File Hash Sha 256
    * * SQL Data Type: char(64)
    * * Description: SHA-256 of the file bytes. Audit only; not used for dedup.
    */
    get FileHashSha256(): string | null {
        return this.Get('FileHashSha256');
    }
    set FileHashSha256(value: string | null) {
        this.Set('FileHashSha256', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: nvarchar(50)
    * * Default Value: Pending
    * * Description: Pending | Loading | Succeeded | Failed.
    */
    get Status(): string {
        return this.Get('Status');
    }
    set Status(value: string) {
        this.Set('Status', value);
    }

    /**
    * * Field Name: RowCountsJson
    * * Display Name: Row Counts Json
    * * SQL Data Type: nvarchar(MAX)
    * * Description: JSON counts returned by the upsert SP (inserted/updated and the various skipped-row tallies).
    */
    get RowCountsJson(): string | null {
        return this.Get('RowCountsJson');
    }
    set RowCountsJson(value: string | null) {
        this.Set('RowCountsJson', value);
    }

    /**
    * * Field Name: ErrorMessage
    * * Display Name: Error Message
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Failure detail, set by admin.spIngestionMarkFailed on the ADF failure path.
    */
    get ErrorMessage(): string | null {
        return this.Get('ErrorMessage');
    }
    set ErrorMessage(value: string | null) {
        this.Set('ErrorMessage', value);
    }

    /**
    * * Field Name: StartedAt
    * * Display Name: Started At
    * * SQL Data Type: datetimeoffset
    * * Description: When the upsert SP began loading this run.
    */
    get StartedAt(): Date | null {
        return this.Get('StartedAt');
    }
    set StartedAt(value: Date | null) {
        this.Set('StartedAt', value);
    }

    /**
    * * Field Name: CompletedAt
    * * Display Name: Completed At
    * * SQL Data Type: datetimeoffset
    * * Description: When the run reached a terminal state (Succeeded/Failed).
    */
    get CompletedAt(): Date | null {
        return this.Get('CompletedAt');
    }
    set CompletedAt(value: Date | null) {
        this.Set('CompletedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Ingestion Types - strongly typed entity sub-class
 * * Schema: admin
 * * Base Table: IngestionType
 * * Base View: vwIngestionTypes
 * * @description Registry of the file types ingested into MAPS. One row per kind of source file; maps a SharePoint drop folder to its staging table, upsert stored procedure, and target MAPS table. Add a new file type = add a row + author its upsert SP.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Ingestion Types')
export class adminIngestionTypeEntity extends BaseEntity<adminIngestionTypeEntityType> {
    /**
    * Loads the Ingestion Types record from the database
    * @param ID: string - primary key value to load the Ingestion Types record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof adminIngestionTypeEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: TypeName
    * * Display Name: Type Name
    * * SQL Data Type: nvarchar(100)
    * * Description: Unique name of the ingestion type (e.g., "TA Tool Individuals", "Maritz").
    */
    get TypeName(): string {
        return this.Get('TypeName');
    }
    set TypeName(value: string) {
        this.Set('TypeName', value);
    }

    /**
    * * Field Name: DesignatedFolder
    * * Display Name: Designated Folder
    * * SQL Data Type: nvarchar(200)
    * * Description: SharePoint folder this type is dropped into. The folder is the type signal (no content-based routing).
    */
    get DesignatedFolder(): string {
        return this.Get('DesignatedFolder');
    }
    set DesignatedFolder(value: string) {
        this.Set('DesignatedFolder', value);
    }

    /**
    * * Field Name: TargetSchema
    * * Display Name: Target Schema
    * * SQL Data Type: nvarchar(50)
    * * Default Value: MAPS
    * * Description: Schema of the primary target table. Always MAPS for this deployment.
    */
    get TargetSchema(): string {
        return this.Get('TargetSchema');
    }
    set TargetSchema(value: string) {
        this.Set('TargetSchema', value);
    }

    /**
    * * Field Name: TargetTable
    * * Display Name: Target Table
    * * SQL Data Type: nvarchar(100)
    * * Description: Primary MAPS target table (e.g., Person, ConferenceRegistration). Informational; the upsert SP may write child tables too.
    */
    get TargetTable(): string {
        return this.Get('TargetTable');
    }
    set TargetTable(value: string) {
        this.Set('TargetTable', value);
    }

    /**
    * * Field Name: StagingTable
    * * Display Name: Staging Table
    * * SQL Data Type: nvarchar(200)
    * * Description: Fully-qualified staging table the ADF Copy lands raw rows into (e.g., staging.TAToolIndividual).
    */
    get StagingTable(): string {
        return this.Get('StagingTable');
    }
    set StagingTable(value: string) {
        this.Set('StagingTable', value);
    }

    /**
    * * Field Name: UpsertStoredProcedure
    * * Display Name: Upsert Stored Procedure
    * * SQL Data Type: nvarchar(200)
    * * Description: Stored procedure that transforms staging rows into the MAPS target(s) (e.g., admin.spIngestTAToolIndividuals).
    */
    get UpsertStoredProcedure(): string {
        return this.Get('UpsertStoredProcedure');
    }
    set UpsertStoredProcedure(value: string) {
        this.Set('UpsertStoredProcedure', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(500)
    * * Description: Human-readable description of the type and its target mapping.
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: IsActive
    * * Display Name: Is Active
    * * SQL Data Type: bit
    * * Default Value: 1
    * * Description: 0 disables the type (pipeline ignores its drops). 1 = active.
    */
    get IsActive(): boolean {
        return this.Get('IsActive');
    }
    set IsActive(value: boolean) {
        this.Set('IsActive', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Invited Positions - strongly typed entity sub-class
 * * Schema: ORCID
 * * Base Table: InvitedPosition
 * * Base View: vwInvitedPositions
 * * @description An invited position (invited-positions section).
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Invited Positions')
export class ORCIDInvitedPositionEntity extends BaseEntity<ORCIDInvitedPositionEntityType> {
    /**
    * Loads the Invited Positions record from the database
    * @param ID: string - primary key value to load the Invited Positions record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof ORCIDInvitedPositionEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ORCIDRecordID
    * * Display Name: ORCID Record ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Records (vwRecords.ID)
    */
    get ORCIDRecordID(): string {
        return this.Get('ORCIDRecordID');
    }
    set ORCIDRecordID(value: string) {
        this.Set('ORCIDRecordID', value);
    }

    /**
    * * Field Name: PutCode
    * * Display Name: Put Code
    * * SQL Data Type: bigint
    */
    get PutCode(): number {
        return this.Get('PutCode');
    }
    set PutCode(value: number) {
        this.Set('PutCode', value);
    }

    /**
    * * Field Name: DepartmentName
    * * Display Name: Department Name
    * * SQL Data Type: nvarchar(500)
    */
    get DepartmentName(): string | null {
        return this.Get('DepartmentName');
    }
    set DepartmentName(value: string | null) {
        this.Set('DepartmentName', value);
    }

    /**
    * * Field Name: RoleTitle
    * * Display Name: Role Title
    * * SQL Data Type: nvarchar(500)
    */
    get RoleTitle(): string | null {
        return this.Get('RoleTitle');
    }
    set RoleTitle(value: string | null) {
        this.Set('RoleTitle', value);
    }

    /**
    * * Field Name: StartYear
    * * Display Name: Start Year
    * * SQL Data Type: smallint
    */
    get StartYear(): number | null {
        return this.Get('StartYear');
    }
    set StartYear(value: number | null) {
        this.Set('StartYear', value);
    }

    /**
    * * Field Name: StartMonth
    * * Display Name: Start Month
    * * SQL Data Type: tinyint
    */
    get StartMonth(): number | null {
        return this.Get('StartMonth');
    }
    set StartMonth(value: number | null) {
        this.Set('StartMonth', value);
    }

    /**
    * * Field Name: StartDay
    * * Display Name: Start Day
    * * SQL Data Type: tinyint
    */
    get StartDay(): number | null {
        return this.Get('StartDay');
    }
    set StartDay(value: number | null) {
        this.Set('StartDay', value);
    }

    /**
    * * Field Name: EndYear
    * * Display Name: End Year
    * * SQL Data Type: smallint
    */
    get EndYear(): number | null {
        return this.Get('EndYear');
    }
    set EndYear(value: number | null) {
        this.Set('EndYear', value);
    }

    /**
    * * Field Name: EndMonth
    * * Display Name: End Month
    * * SQL Data Type: tinyint
    */
    get EndMonth(): number | null {
        return this.Get('EndMonth');
    }
    set EndMonth(value: number | null) {
        this.Set('EndMonth', value);
    }

    /**
    * * Field Name: EndDay
    * * Display Name: End Day
    * * SQL Data Type: tinyint
    */
    get EndDay(): number | null {
        return this.Get('EndDay');
    }
    set EndDay(value: number | null) {
        this.Set('EndDay', value);
    }

    /**
    * * Field Name: OrgName
    * * Display Name: Org Name
    * * SQL Data Type: nvarchar(500)
    */
    get OrgName(): string | null {
        return this.Get('OrgName');
    }
    set OrgName(value: string | null) {
        this.Set('OrgName', value);
    }

    /**
    * * Field Name: OrgCity
    * * Display Name: Org City
    * * SQL Data Type: nvarchar(255)
    */
    get OrgCity(): string | null {
        return this.Get('OrgCity');
    }
    set OrgCity(value: string | null) {
        this.Set('OrgCity', value);
    }

    /**
    * * Field Name: OrgRegion
    * * Display Name: Org Region
    * * SQL Data Type: nvarchar(255)
    */
    get OrgRegion(): string | null {
        return this.Get('OrgRegion');
    }
    set OrgRegion(value: string | null) {
        this.Set('OrgRegion', value);
    }

    /**
    * * Field Name: OrgCountry
    * * Display Name: Org Country
    * * SQL Data Type: nvarchar(10)
    */
    get OrgCountry(): string | null {
        return this.Get('OrgCountry');
    }
    set OrgCountry(value: string | null) {
        this.Set('OrgCountry', value);
    }

    /**
    * * Field Name: OrgDisambiguatedID
    * * Display Name: Org Disambiguated ID
    * * SQL Data Type: nvarchar(255)
    */
    get OrgDisambiguatedID(): string | null {
        return this.Get('OrgDisambiguatedID');
    }
    set OrgDisambiguatedID(value: string | null) {
        this.Set('OrgDisambiguatedID', value);
    }

    /**
    * * Field Name: OrgDisambiguationSource
    * * Display Name: Org Disambiguation Source
    * * SQL Data Type: nvarchar(50)
    */
    get OrgDisambiguationSource(): string | null {
        return this.Get('OrgDisambiguationSource');
    }
    set OrgDisambiguationSource(value: string | null) {
        this.Set('OrgDisambiguationSource', value);
    }

    /**
    * * Field Name: Url
    * * Display Name: Url
    * * SQL Data Type: nvarchar(2000)
    */
    get Url(): string | null {
        return this.Get('Url');
    }
    set Url(value: string | null) {
        this.Set('Url', value);
    }

    /**
    * * Field Name: Visibility
    * * Display Name: Visibility
    * * SQL Data Type: nvarchar(20)
    */
    get Visibility(): string | null {
        return this.Get('Visibility');
    }
    set Visibility(value: string | null) {
        this.Set('Visibility', value);
    }

    /**
    * * Field Name: SourceName
    * * Display Name: Source Name
    * * SQL Data Type: nvarchar(255)
    */
    get SourceName(): string | null {
        return this.Get('SourceName');
    }
    set SourceName(value: string | null) {
        this.Set('SourceName', value);
    }

    /**
    * * Field Name: SourceOrcid
    * * Display Name: Source Orcid
    * * SQL Data Type: nvarchar(19)
    */
    get SourceOrcid(): string | null {
        return this.Get('SourceOrcid');
    }
    set SourceOrcid(value: string | null) {
        this.Set('SourceOrcid', value);
    }

    /**
    * * Field Name: OrcidCreatedDate
    * * Display Name: Orcid Created Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidCreatedDate(): Date | null {
        return this.Get('OrcidCreatedDate');
    }
    set OrcidCreatedDate(value: Date | null) {
        this.Set('OrcidCreatedDate', value);
    }

    /**
    * * Field Name: OrcidLastModifiedDate
    * * Display Name: Orcid Last Modified Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidLastModifiedDate(): Date | null {
        return this.Get('OrcidLastModifiedDate');
    }
    set OrcidLastModifiedDate(value: Date | null) {
        this.Set('OrcidLastModifiedDate', value);
    }

    /**
    * * Field Name: RawJSON
    * * Display Name: Raw JSON
    * * SQL Data Type: nvarchar(MAX)
    */
    get RawJSON(): string | null {
        return this.Get('RawJSON');
    }
    set RawJSON(value: string | null) {
        this.Set('RawJSON', value);
    }

    /**
    * * Field Name: __mj_integration_SyncStatus
    * * Display Name: Mj Integration Sync Status
    * * SQL Data Type: nvarchar(50)
    * * Default Value: Active
    */
    get __mj_integration_SyncStatus(): string {
        return this.Get('__mj_integration_SyncStatus');
    }
    set __mj_integration_SyncStatus(value: string) {
        this.Set('__mj_integration_SyncStatus', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedAt
    * * Display Name: Mj Integration Last Synced At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastSyncedAt(): Date | null {
        return this.Get('__mj_integration_LastSyncedAt');
    }
    set __mj_integration_LastSyncedAt(value: Date | null) {
        this.Set('__mj_integration_LastSyncedAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedSnapshot
    * * Display Name: Mj Integration Last Synced Snapshot
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_LastSyncedSnapshot(): string | null {
        return this.Get('__mj_integration_LastSyncedSnapshot');
    }
    set __mj_integration_LastSyncedSnapshot(value: string | null) {
        this.Set('__mj_integration_LastSyncedSnapshot', value);
    }

    /**
    * * Field Name: __mj_integration_SyncMessage
    * * Display Name: Mj Integration Sync Message
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_SyncMessage(): string | null {
        return this.Get('__mj_integration_SyncMessage');
    }
    set __mj_integration_SyncMessage(value: string | null) {
        this.Set('__mj_integration_SyncMessage', value);
    }

    /**
    * * Field Name: __mj_integration_ContentHash
    * * Display Name: Mj Integration Content Hash
    * * SQL Data Type: nvarchar(64)
    */
    get __mj_integration_ContentHash(): string | null {
        return this.Get('__mj_integration_ContentHash');
    }
    set __mj_integration_ContentHash(value: string | null) {
        this.Set('__mj_integration_ContentHash', value);
    }

    /**
    * * Field Name: __mj_integration_CustomOverflow
    * * Display Name: Mj Integration Custom Overflow
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_CustomOverflow(): string | null {
        return this.Get('__mj_integration_CustomOverflow');
    }
    set __mj_integration_CustomOverflow(value: string | null) {
        this.Set('__mj_integration_CustomOverflow', value);
    }

    /**
    * * Field Name: __mj_integration_ExternalVersion
    * * Display Name: Mj Integration External Version
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_ExternalVersion(): string | null {
        return this.Get('__mj_integration_ExternalVersion');
    }
    set __mj_integration_ExternalVersion(value: string | null) {
        this.Set('__mj_integration_ExternalVersion', value);
    }

    /**
    * * Field Name: __mj_integration_LastSeenModifiedValue
    * * Display Name: Mj Integration Last Seen Modified Value
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_LastSeenModifiedValue(): string | null {
        return this.Get('__mj_integration_LastSeenModifiedValue');
    }
    set __mj_integration_LastSeenModifiedValue(value: string | null) {
        this.Set('__mj_integration_LastSeenModifiedValue', value);
    }

    /**
    * * Field Name: __mj_integration_LastReconciledAt
    * * Display Name: Mj Integration Last Reconciled At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastReconciledAt(): Date | null {
        return this.Get('__mj_integration_LastReconciledAt');
    }
    set __mj_integration_LastReconciledAt(value: Date | null) {
        this.Set('__mj_integration_LastReconciledAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastWriterDirection
    * * Display Name: Mj Integration Last Writer Direction
    * * SQL Data Type: nvarchar(10)
    */
    get __mj_integration_LastWriterDirection(): string | null {
        return this.Get('__mj_integration_LastWriterDirection');
    }
    set __mj_integration_LastWriterDirection(value: string | null) {
        this.Set('__mj_integration_LastWriterDirection', value);
    }

    /**
    * * Field Name: __mj_integration_IsTombstoned
    * * Display Name: Mj Integration Is Tombstoned
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get __mj_integration_IsTombstoned(): boolean {
        return this.Get('__mj_integration_IsTombstoned');
    }
    set __mj_integration_IsTombstoned(value: boolean) {
        this.Set('__mj_integration_IsTombstoned', value);
    }

    /**
    * * Field Name: __mj_integration_DeletedDetectedAt
    * * Display Name: Mj Integration Deleted Detected At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_DeletedDetectedAt(): Date | null {
        return this.Get('__mj_integration_DeletedDetectedAt');
    }
    set __mj_integration_DeletedDetectedAt(value: Date | null) {
        this.Set('__mj_integration_DeletedDetectedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Job Posting Sources - strongly typed entity sub-class
 * * Schema: Jobs
 * * Base Table: JobPostingSource
 * * Base View: vwJobPostingSources
 * * @description Per-board provenance for a posting (linkedin | indeed | adzuna | glassdoor | simplyhired | learn4good | ats | …) with the board-local id, listing URL, and per-source status. The feed always delivers the full array, so the worker replaces a posting's sources wholesale on update; ON DELETE CASCADE cleans up on expiry.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Job Posting Sources')
export class JobsJobPostingSourceEntity extends BaseEntity<JobsJobPostingSourceEntityType> {
    /**
    * Loads the Job Posting Sources record from the database
    * @param ID: string - primary key value to load the Job Posting Sources record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof JobsJobPostingSourceEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: JobPostingID
    * * Display Name: Job Posting ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Job Postings (vwJobPostings.ID)
    */
    get JobPostingID(): string {
        return this.Get('JobPostingID');
    }
    set JobPostingID(value: string) {
        this.Set('JobPostingID', value);
    }

    /**
    * * Field Name: Source
    * * Display Name: Source
    * * SQL Data Type: nvarchar(50)
    */
    get Source(): string {
        return this.Get('Source');
    }
    set Source(value: string) {
        this.Set('Source', value);
    }

    /**
    * * Field Name: SourceID
    * * Display Name: Source ID
    * * SQL Data Type: nvarchar(200)
    */
    get SourceID(): string {
        return this.Get('SourceID');
    }
    set SourceID(value: string) {
        this.Set('SourceID', value);
    }

    /**
    * * Field Name: Url
    * * Display Name: Url
    * * SQL Data Type: nvarchar(2000)
    */
    get Url(): string | null {
        return this.Get('Url');
    }
    set Url(value: string | null) {
        this.Set('Url', value);
    }

    /**
    * * Field Name: SourceStatus
    * * Display Name: Source Status
    * * SQL Data Type: nvarchar(20)
    */
    get SourceStatus(): string | null {
        return this.Get('SourceStatus');
    }
    set SourceStatus(value: string | null) {
        this.Set('SourceStatus', value);
    }

    /**
    * * Field Name: SourceUpdatedAt
    * * Display Name: Source Updated At
    * * SQL Data Type: datetimeoffset
    */
    get SourceUpdatedAt(): Date | null {
        return this.Get('SourceUpdatedAt');
    }
    set SourceUpdatedAt(value: Date | null) {
        this.Set('SourceUpdatedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: JobPosting
    * * Display Name: Job Posting
    * * SQL Data Type: nvarchar(500)
    */
    get JobPosting(): string {
        return this.Get('JobPosting');
    }
}


/**
 * Job Postings - strongly typed entity sub-class
 * * Schema: Jobs
 * * Base Table: JobPosting
 * * Base View: vwJobPostings
 * * @description Serving store for the jobs corpus: full rows for exactly the postings that hold Pinecone vectors (relevance-gate keepers at the configured keep rule). Diff state for ALL in-scope jobs (including gate-rejected ones) lives in the worker's parquet state in blob. Rows are hard-deleted on feed expiry; the blob dumps are the historical archive. The vector lives in Pinecone (vector id = JobID as string), never here.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Job Postings')
export class JobsJobPostingEntity extends BaseEntity<JobsJobPostingEntityType> {
    /**
    * Loads the Job Postings record from the database
    * @param ID: string - primary key value to load the Job Postings record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof JobsJobPostingEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * Validate() method override for Job Postings entity. This is an auto-generated method that invokes the generated validators for this entity for the following fields:
    * * SalaryRaw: If raw salary information is provided, it must be formatted as a valid JSON string to ensure proper parsing and data consistency.
    * * SourceStatus: The source status must be set to a valid value of 1, 2, or 3.
    * * Table-Level: The minimum annual salary must be less than or equal to the maximum annual salary when both values are specified.
    * @public
    * @method
    * @override
    */
    public override Validate(): ValidationResult {
        const result = super.Validate();
        this.ValidateSalaryRawIsJson(result);
        this.ValidateSourceStatusAllowedValues(result);
        this.ValidateSalaryMinAnnualLessThanOrEqualToSalaryMaxAnnual(result);
        result.Success = result.Success && (result.Errors.length === 0);

        return result;
    }

    /**
    * If raw salary information is provided, it must be formatted as a valid JSON string to ensure proper parsing and data consistency.
    * @param result - the ValidationResult object to add any errors or warnings to
    * @public
    * @method
    */
    public ValidateSalaryRawIsJson(result: ValidationResult) {
    	// Check if SalaryRaw is provided and not empty
    	if (this.SalaryRaw != null && this.SalaryRaw.trim() !== "") {
    		try {
    			JSON.parse(this.SalaryRaw);
    		} catch (e) {
    			result.Errors.push(new ValidationErrorInfo(
    				"SalaryRaw",
    				"The raw salary data must be a valid JSON string.",
    				this.SalaryRaw,
    				ValidationErrorType.Failure
    			));
    		}
    	}
    }

    /**
    * The source status must be set to a valid value of 1, 2, or 3.
    * @param result - the ValidationResult object to add any errors or warnings to
    * @public
    * @method
    */
    public ValidateSourceStatusAllowedValues(result: ValidationResult) {
    	if (this.SourceStatus != null && this.SourceStatus !== 1 && this.SourceStatus !== 2 && this.SourceStatus !== 3) {
    		result.Errors.push(new ValidationErrorInfo(
    			"SourceStatus",
    			"The source status must be 1, 2, or 3.",
    			this.SourceStatus,
    			ValidationErrorType.Failure
    		));
    	}
    }

    /**
    * The minimum annual salary must be less than or equal to the maximum annual salary when both values are specified.
    * @param result - the ValidationResult object to add any errors or warnings to
    * @public
    * @method
    */
    public ValidateSalaryMinAnnualLessThanOrEqualToSalaryMaxAnnual(result: ValidationResult) {
    	if (this.SalaryMinAnnual != null && this.SalaryMaxAnnual != null && this.SalaryMinAnnual > this.SalaryMaxAnnual) {
    		result.Errors.push(new ValidationErrorInfo(
    			"SalaryMinAnnual",
    			"The minimum annual salary cannot be greater than the maximum annual salary.",
    			this.SalaryMinAnnual,
    			ValidationErrorType.Failure
    		));
    	}
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: JobID
    * * Display Name: Job ID
    * * SQL Data Type: bigint
    * * Description: Coresignal job_id — the stable cross-board natural key. Also the Pinecone vector ID (as a string).
    */
    get JobID(): number {
        return this.Get('JobID');
    }
    set JobID(value: number) {
        this.Set('JobID', value);
    }

    /**
    * * Field Name: Title
    * * Display Name: Title
    * * SQL Data Type: nvarchar(500)
    */
    get Title(): string {
        return this.Get('Title');
    }
    set Title(value: string) {
        this.Set('Title', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(MAX)
    */
    get Description(): string {
        return this.Get('Description');
    }
    set Description(value: string) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: CountryISO2
    * * Display Name: Country ISO2
    * * SQL Data Type: nvarchar(10)
    */
    get CountryISO2(): string | null {
        return this.Get('CountryISO2');
    }
    set CountryISO2(value: string | null) {
        this.Set('CountryISO2', value);
    }

    /**
    * * Field Name: CountryName
    * * Display Name: Country Name
    * * SQL Data Type: nvarchar(100)
    */
    get CountryName(): string | null {
        return this.Get('CountryName');
    }
    set CountryName(value: string | null) {
        this.Set('CountryName', value);
    }

    /**
    * * Field Name: StateName
    * * Display Name: State Name
    * * SQL Data Type: nvarchar(200)
    */
    get StateName(): string | null {
        return this.Get('StateName');
    }
    set StateName(value: string | null) {
        this.Set('StateName', value);
    }

    /**
    * * Field Name: City
    * * Display Name: City
    * * SQL Data Type: nvarchar(200)
    */
    get City(): string | null {
        return this.Get('City');
    }
    set City(value: string | null) {
        this.Set('City', value);
    }

    /**
    * * Field Name: Latitude
    * * Display Name: Latitude
    * * SQL Data Type: decimal(9, 6)
    */
    get Latitude(): number | null {
        return this.Get('Latitude');
    }
    set Latitude(value: number | null) {
        this.Set('Latitude', value);
    }

    /**
    * * Field Name: Longitude
    * * Display Name: Longitude
    * * SQL Data Type: decimal(9, 6)
    */
    get Longitude(): number | null {
        return this.Get('Longitude');
    }
    set Longitude(value: number | null) {
        this.Set('Longitude', value);
    }

    /**
    * * Field Name: Seniority
    * * Display Name: Seniority
    * * SQL Data Type: nvarchar(100)
    */
    get Seniority(): string | null {
        return this.Get('Seniority');
    }
    set Seniority(value: string | null) {
        this.Set('Seniority', value);
    }

    /**
    * * Field Name: ManagementLevel
    * * Display Name: Management Level
    * * SQL Data Type: nvarchar(100)
    */
    get ManagementLevel(): string | null {
        return this.Get('ManagementLevel');
    }
    set ManagementLevel(value: string | null) {
        this.Set('ManagementLevel', value);
    }

    /**
    * * Field Name: EmploymentType
    * * Display Name: Employment Type
    * * SQL Data Type: nvarchar(100)
    */
    get EmploymentType(): string | null {
        return this.Get('EmploymentType');
    }
    set EmploymentType(value: string | null) {
        this.Set('EmploymentType', value);
    }

    /**
    * * Field Name: AcceptsRemote
    * * Display Name: Accepts Remote
    * * SQL Data Type: bit
    */
    get AcceptsRemote(): boolean | null {
        return this.Get('AcceptsRemote');
    }
    set AcceptsRemote(value: boolean | null) {
        this.Set('AcceptsRemote', value);
    }

    /**
    * * Field Name: DatePosted
    * * Display Name: Date Posted
    * * SQL Data Type: datetimeoffset
    */
    get DatePosted(): Date | null {
        return this.Get('DatePosted');
    }
    set DatePosted(value: Date | null) {
        this.Set('DatePosted', value);
    }

    /**
    * * Field Name: DatePostedUnix
    * * Display Name: Date Posted Unix
    * * SQL Data Type: bigint
    * * Description: DatePosted as unix epoch seconds, written by the worker from the same source value. Exists because Pinecone metadata range filters ($gte/$lte) compare numbers, not ISO strings — this is the value mirrored into vector metadata as posted_ts.
    */
    get DatePostedUnix(): number | null {
        return this.Get('DatePostedUnix');
    }
    set DatePostedUnix(value: number | null) {
        this.Set('DatePostedUnix', value);
    }

    /**
    * * Field Name: ValidThrough
    * * Display Name: Valid Through
    * * SQL Data Type: datetimeoffset
    */
    get ValidThrough(): Date | null {
        return this.Get('ValidThrough');
    }
    set ValidThrough(value: Date | null) {
        this.Set('ValidThrough', value);
    }

    /**
    * * Field Name: SourceStatus
    * * Display Name: Source Status
    * * SQL Data Type: tinyint
    * * Description: Feed status: 1 = at least one source board active, 2 = all inactive, 3 = deleted (verified empirically against job_sources[].status). Steady state holds only 1 — status 2/3 rows are deleted by the nightly merge.
    */
    get SourceStatus(): number {
        return this.Get('SourceStatus');
    }
    set SourceStatus(value: number) {
        this.Set('SourceStatus', value);
    }

    /**
    * * Field Name: SourceCreatedAt
    * * Display Name: Source Created At
    * * SQL Data Type: datetimeoffset
    */
    get SourceCreatedAt(): Date | null {
        return this.Get('SourceCreatedAt');
    }
    set SourceCreatedAt(value: Date | null) {
        this.Set('SourceCreatedAt', value);
    }

    /**
    * * Field Name: SourceUpdatedAt
    * * Display Name: Source Updated At
    * * SQL Data Type: datetimeoffset
    * * Description: Feed updated_at (UTC). Recency tiebreak for the worker's diff; also the stale-sweep signal — a healthy feed touches every live posting periodically.
    */
    get SourceUpdatedAt(): Date {
        return this.Get('SourceUpdatedAt');
    }
    set SourceUpdatedAt(value: Date) {
        this.Set('SourceUpdatedAt', value);
    }

    /**
    * * Field Name: CompanyName
    * * Display Name: Company Name
    * * SQL Data Type: nvarchar(400)
    */
    get CompanyName(): string | null {
        return this.Get('CompanyName');
    }
    set CompanyName(value: string | null) {
        this.Set('CompanyName', value);
    }

    /**
    * * Field Name: CompanyDomain
    * * Display Name: Company Domain
    * * SQL Data Type: nvarchar(200)
    */
    get CompanyDomain(): string | null {
        return this.Get('CompanyDomain');
    }
    set CompanyDomain(value: string | null) {
        this.Set('CompanyDomain', value);
    }

    /**
    * * Field Name: CompanyIndustry
    * * Display Name: Company Industry
    * * SQL Data Type: nvarchar(200)
    */
    get CompanyIndustry(): string | null {
        return this.Get('CompanyIndustry');
    }
    set CompanyIndustry(value: string | null) {
        this.Set('CompanyIndustry', value);
    }

    /**
    * * Field Name: CompanySizeRange
    * * Display Name: Company Size Range
    * * SQL Data Type: nvarchar(50)
    */
    get CompanySizeRange(): string | null {
        return this.Get('CompanySizeRange');
    }
    set CompanySizeRange(value: string | null) {
        this.Set('CompanySizeRange', value);
    }

    /**
    * * Field Name: CompanyEmployeesCount
    * * Display Name: Company Employees Count
    * * SQL Data Type: int
    */
    get CompanyEmployeesCount(): number | null {
        return this.Get('CompanyEmployeesCount');
    }
    set CompanyEmployeesCount(value: number | null) {
        this.Set('CompanyEmployeesCount', value);
    }

    /**
    * * Field Name: SalaryMinAnnual
    * * Display Name: Salary Min Annual
    * * SQL Data Type: decimal(14, 2)
    * * Description: Annualized salary lower bound in the original currency, derived by the worker from SalaryRaw (prefer non-estimated sources; HOUR×2080 / DAY×260 / WEEK×52 / MONTH×12; mistyped periods rejected). Only USD rows are mirrored into Pinecone sal_min/sal_max metadata in v1.
    */
    get SalaryMinAnnual(): number | null {
        return this.Get('SalaryMinAnnual');
    }
    set SalaryMinAnnual(value: number | null) {
        this.Set('SalaryMinAnnual', value);
    }

    /**
    * * Field Name: SalaryMaxAnnual
    * * Display Name: Salary Max Annual
    * * SQL Data Type: decimal(14, 2)
    */
    get SalaryMaxAnnual(): number | null {
        return this.Get('SalaryMaxAnnual');
    }
    set SalaryMaxAnnual(value: number | null) {
        this.Set('SalaryMaxAnnual', value);
    }

    /**
    * * Field Name: SalaryCurrency
    * * Display Name: Salary Currency
    * * SQL Data Type: nvarchar(10)
    */
    get SalaryCurrency(): string | null {
        return this.Get('SalaryCurrency');
    }
    set SalaryCurrency(value: string | null) {
        this.Set('SalaryCurrency', value);
    }

    /**
    * * Field Name: SalaryIsEstimated
    * * Display Name: Salary Is Estimated
    * * SQL Data Type: bit
    */
    get SalaryIsEstimated(): boolean | null {
        return this.Get('SalaryIsEstimated');
    }
    set SalaryIsEstimated(value: boolean | null) {
        this.Set('SalaryIsEstimated', value);
    }

    /**
    * * Field Name: SalaryRaw
    * * Display Name: Salary Raw
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Original salary JSON array from the feed, verbatim — the one raw fragment kept (normalization is lossy; this drives re-derivation).
    */
    get SalaryRaw(): string | null {
        return this.Get('SalaryRaw');
    }
    set SalaryRaw(value: string | null) {
        this.Set('SalaryRaw', value);
    }

    /**
    * * Field Name: GateTier
    * * Display Name: Gate Tier
    * * SQL Data Type: nvarchar(20)
    * * Value List Type: List
    * * Possible Values 
    *   * core
    *   * professional
    * * Description: Which rubric tier kept this posting: core (requires specialized earth/space-science knowledge) or professional (degree-level role serving an earth/space system). Stored so the client keep rule (jobs.gateKeepTiers) can narrow/widen without re-classifying — rubric v4, plans/jobs/relevance-rubric.md.
    */
    get GateTier(): 'core' | 'professional' {
        return this.Get('GateTier');
    }
    set GateTier(value: 'core' | 'professional') {
        this.Set('GateTier', value);
    }

    /**
    * * Field Name: GateSection
    * * Display Name: Gate Section
    * * SQL Data Type: nvarchar(100)
    * * Description: AGU discipline-section tag emitted by the same gate stage-2 call that assigned GateTier (e.g. "Hydrology", "Space Physics and Aeronomy"). Nullable — present when the classifier names one. Future search facet.
    */
    get GateSection(): string | null {
        return this.Get('GateSection');
    }
    set GateSection(value: string | null) {
        this.Set('GateSection', value);
    }

    /**
    * * Field Name: ExternalUrl
    * * Display Name: External Url
    * * SQL Data Type: nvarchar(2000)
    */
    get ExternalUrl(): string | null {
        return this.Get('ExternalUrl');
    }
    set ExternalUrl(value: string | null) {
        this.Set('ExternalUrl', value);
    }

    /**
    * * Field Name: IngestBatchID
    * * Display Name: Ingest Batch ID
    * * SQL Data Type: int
    * * Description: Jobs.IngestBatch.IngestBatchID (compact INT identity) of the batch that last wrote this row. Deliberately not a declared FK: the nightly bulk MERGE must not pay per-row constraint checks, and the ledger row is guaranteed to exist by the worker's pass ordering.
    */
    get IngestBatchID(): number {
        return this.Get('IngestBatchID');
    }
    set IngestBatchID(value: number) {
        this.Set('IngestBatchID', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyLogoUrl
    * * Display Name: Company Logo Url
    * * SQL Data Type: nvarchar(1000)
    */
    get CompanyLogoUrl(): string | null {
        return this.Get('CompanyLogoUrl');
    }
    set CompanyLogoUrl(value: string | null) {
        this.Set('CompanyLogoUrl', value);
    }

    /**
    * * Field Name: CompanyType
    * * Display Name: Company Type
    * * SQL Data Type: nvarchar(100)
    */
    get CompanyType(): string | null {
        return this.Get('CompanyType');
    }
    set CompanyType(value: string | null) {
        this.Set('CompanyType', value);
    }

    /**
    * * Field Name: CompanyDescription
    * * Display Name: Company Description
    * * SQL Data Type: nvarchar(4000)
    */
    get CompanyDescription(): string | null {
        return this.Get('CompanyDescription');
    }
    set CompanyDescription(value: string | null) {
        this.Set('CompanyDescription', value);
    }

    /**
    * * Field Name: Benefits
    * * Display Name: Benefits
    * * SQL Data Type: nvarchar(MAX)
    */
    get Benefits(): string | null {
        return this.Get('Benefits');
    }
    set Benefits(value: string | null) {
        this.Set('Benefits', value);
    }

    /**
    * * Field Name: SalaryPeriod
    * * Display Name: Salary Period
    * * SQL Data Type: nvarchar(10)
    */
    get SalaryPeriod(): string | null {
        return this.Get('SalaryPeriod');
    }
    set SalaryPeriod(value: string | null) {
        this.Set('SalaryPeriod', value);
    }
}


/**
 * Keywords - strongly typed entity sub-class
 * * Schema: ORCID
 * * Base Table: Keyword
 * * Base View: vwKeywords
 * * @description A research keyword (person.keywords).
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Keywords')
export class ORCIDKeywordEntity extends BaseEntity<ORCIDKeywordEntityType> {
    /**
    * Loads the Keywords record from the database
    * @param ID: string - primary key value to load the Keywords record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof ORCIDKeywordEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ORCIDRecordID
    * * Display Name: ORCID Record ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Records (vwRecords.ID)
    */
    get ORCIDRecordID(): string {
        return this.Get('ORCIDRecordID');
    }
    set ORCIDRecordID(value: string) {
        this.Set('ORCIDRecordID', value);
    }

    /**
    * * Field Name: PutCode
    * * Display Name: Put Code
    * * SQL Data Type: bigint
    */
    get PutCode(): number {
        return this.Get('PutCode');
    }
    set PutCode(value: number) {
        this.Set('PutCode', value);
    }

    /**
    * * Field Name: Content
    * * Display Name: Content
    * * SQL Data Type: nvarchar(500)
    */
    get Content(): string | null {
        return this.Get('Content');
    }
    set Content(value: string | null) {
        this.Set('Content', value);
    }

    /**
    * * Field Name: DisplayIndex
    * * Display Name: Display Index
    * * SQL Data Type: int
    */
    get DisplayIndex(): number | null {
        return this.Get('DisplayIndex');
    }
    set DisplayIndex(value: number | null) {
        this.Set('DisplayIndex', value);
    }

    /**
    * * Field Name: Visibility
    * * Display Name: Visibility
    * * SQL Data Type: nvarchar(20)
    */
    get Visibility(): string | null {
        return this.Get('Visibility');
    }
    set Visibility(value: string | null) {
        this.Set('Visibility', value);
    }

    /**
    * * Field Name: SourceName
    * * Display Name: Source Name
    * * SQL Data Type: nvarchar(255)
    */
    get SourceName(): string | null {
        return this.Get('SourceName');
    }
    set SourceName(value: string | null) {
        this.Set('SourceName', value);
    }

    /**
    * * Field Name: SourceOrcid
    * * Display Name: Source Orcid
    * * SQL Data Type: nvarchar(19)
    */
    get SourceOrcid(): string | null {
        return this.Get('SourceOrcid');
    }
    set SourceOrcid(value: string | null) {
        this.Set('SourceOrcid', value);
    }

    /**
    * * Field Name: OrcidCreatedDate
    * * Display Name: Orcid Created Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidCreatedDate(): Date | null {
        return this.Get('OrcidCreatedDate');
    }
    set OrcidCreatedDate(value: Date | null) {
        this.Set('OrcidCreatedDate', value);
    }

    /**
    * * Field Name: OrcidLastModifiedDate
    * * Display Name: Orcid Last Modified Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidLastModifiedDate(): Date | null {
        return this.Get('OrcidLastModifiedDate');
    }
    set OrcidLastModifiedDate(value: Date | null) {
        this.Set('OrcidLastModifiedDate', value);
    }

    /**
    * * Field Name: RawJSON
    * * Display Name: Raw JSON
    * * SQL Data Type: nvarchar(MAX)
    */
    get RawJSON(): string | null {
        return this.Get('RawJSON');
    }
    set RawJSON(value: string | null) {
        this.Set('RawJSON', value);
    }

    /**
    * * Field Name: __mj_integration_SyncStatus
    * * Display Name: Mj Integration Sync Status
    * * SQL Data Type: nvarchar(50)
    * * Default Value: Active
    */
    get __mj_integration_SyncStatus(): string {
        return this.Get('__mj_integration_SyncStatus');
    }
    set __mj_integration_SyncStatus(value: string) {
        this.Set('__mj_integration_SyncStatus', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedAt
    * * Display Name: Mj Integration Last Synced At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastSyncedAt(): Date | null {
        return this.Get('__mj_integration_LastSyncedAt');
    }
    set __mj_integration_LastSyncedAt(value: Date | null) {
        this.Set('__mj_integration_LastSyncedAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedSnapshot
    * * Display Name: Mj Integration Last Synced Snapshot
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_LastSyncedSnapshot(): string | null {
        return this.Get('__mj_integration_LastSyncedSnapshot');
    }
    set __mj_integration_LastSyncedSnapshot(value: string | null) {
        this.Set('__mj_integration_LastSyncedSnapshot', value);
    }

    /**
    * * Field Name: __mj_integration_SyncMessage
    * * Display Name: Mj Integration Sync Message
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_SyncMessage(): string | null {
        return this.Get('__mj_integration_SyncMessage');
    }
    set __mj_integration_SyncMessage(value: string | null) {
        this.Set('__mj_integration_SyncMessage', value);
    }

    /**
    * * Field Name: __mj_integration_ContentHash
    * * Display Name: Mj Integration Content Hash
    * * SQL Data Type: nvarchar(64)
    */
    get __mj_integration_ContentHash(): string | null {
        return this.Get('__mj_integration_ContentHash');
    }
    set __mj_integration_ContentHash(value: string | null) {
        this.Set('__mj_integration_ContentHash', value);
    }

    /**
    * * Field Name: __mj_integration_CustomOverflow
    * * Display Name: Mj Integration Custom Overflow
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_CustomOverflow(): string | null {
        return this.Get('__mj_integration_CustomOverflow');
    }
    set __mj_integration_CustomOverflow(value: string | null) {
        this.Set('__mj_integration_CustomOverflow', value);
    }

    /**
    * * Field Name: __mj_integration_ExternalVersion
    * * Display Name: Mj Integration External Version
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_ExternalVersion(): string | null {
        return this.Get('__mj_integration_ExternalVersion');
    }
    set __mj_integration_ExternalVersion(value: string | null) {
        this.Set('__mj_integration_ExternalVersion', value);
    }

    /**
    * * Field Name: __mj_integration_LastSeenModifiedValue
    * * Display Name: Mj Integration Last Seen Modified Value
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_LastSeenModifiedValue(): string | null {
        return this.Get('__mj_integration_LastSeenModifiedValue');
    }
    set __mj_integration_LastSeenModifiedValue(value: string | null) {
        this.Set('__mj_integration_LastSeenModifiedValue', value);
    }

    /**
    * * Field Name: __mj_integration_LastReconciledAt
    * * Display Name: Mj Integration Last Reconciled At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastReconciledAt(): Date | null {
        return this.Get('__mj_integration_LastReconciledAt');
    }
    set __mj_integration_LastReconciledAt(value: Date | null) {
        this.Set('__mj_integration_LastReconciledAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastWriterDirection
    * * Display Name: Mj Integration Last Writer Direction
    * * SQL Data Type: nvarchar(10)
    */
    get __mj_integration_LastWriterDirection(): string | null {
        return this.Get('__mj_integration_LastWriterDirection');
    }
    set __mj_integration_LastWriterDirection(value: string | null) {
        this.Set('__mj_integration_LastWriterDirection', value);
    }

    /**
    * * Field Name: __mj_integration_IsTombstoned
    * * Display Name: Mj Integration Is Tombstoned
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get __mj_integration_IsTombstoned(): boolean {
        return this.Get('__mj_integration_IsTombstoned');
    }
    set __mj_integration_IsTombstoned(value: boolean) {
        this.Set('__mj_integration_IsTombstoned', value);
    }

    /**
    * * Field Name: __mj_integration_DeletedDetectedAt
    * * Display Name: Mj Integration Deleted Detected At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_DeletedDetectedAt(): Date | null {
        return this.Get('__mj_integration_DeletedDetectedAt');
    }
    set __mj_integration_DeletedDetectedAt(value: Date | null) {
        this.Set('__mj_integration_DeletedDetectedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Languages - strongly typed entity sub-class
 * * Schema: CoreSignal
 * * Base Table: Language
 * * Base View: vwLanguages
 * * @description A language + proficiency from a CoreSignal profile.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Languages')
export class CoreSignalLanguageEntity extends BaseEntity<CoreSignalLanguageEntityType> {
    /**
    * Loads the Languages record from the database
    * @param ID: string - primary key value to load the Languages record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof CoreSignalLanguageEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ProfileID
    * * Display Name: Profile ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Profiles (vwProfiles.ID)
    */
    get ProfileID(): string {
        return this.Get('ProfileID');
    }
    set ProfileID(value: string) {
        this.Set('ProfileID', value);
    }

    /**
    * * Field Name: OrderInProfile
    * * Display Name: Order In Profile
    * * SQL Data Type: int
    */
    get OrderInProfile(): number {
        return this.Get('OrderInProfile');
    }
    set OrderInProfile(value: number) {
        this.Set('OrderInProfile', value);
    }

    /**
    * * Field Name: Language
    * * Display Name: Language
    * * SQL Data Type: nvarchar(255)
    */
    get Language(): string | null {
        return this.Get('Language');
    }
    set Language(value: string | null) {
        this.Set('Language', value);
    }

    /**
    * * Field Name: Proficiency
    * * Display Name: Proficiency
    * * SQL Data Type: nvarchar(255)
    */
    get Proficiency(): string | null {
        return this.Get('Proficiency');
    }
    set Proficiency(value: string | null) {
        this.Set('Proficiency', value);
    }

    /**
    * * Field Name: __mj_integration_SyncStatus
    * * Display Name: Mj Integration Sync Status
    * * SQL Data Type: nvarchar(50)
    * * Default Value: Active
    */
    get __mj_integration_SyncStatus(): string {
        return this.Get('__mj_integration_SyncStatus');
    }
    set __mj_integration_SyncStatus(value: string) {
        this.Set('__mj_integration_SyncStatus', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedAt
    * * Display Name: Mj Integration Last Synced At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastSyncedAt(): Date | null {
        return this.Get('__mj_integration_LastSyncedAt');
    }
    set __mj_integration_LastSyncedAt(value: Date | null) {
        this.Set('__mj_integration_LastSyncedAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedSnapshot
    * * Display Name: Mj Integration Last Synced Snapshot
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_LastSyncedSnapshot(): string | null {
        return this.Get('__mj_integration_LastSyncedSnapshot');
    }
    set __mj_integration_LastSyncedSnapshot(value: string | null) {
        this.Set('__mj_integration_LastSyncedSnapshot', value);
    }

    /**
    * * Field Name: __mj_integration_SyncMessage
    * * Display Name: Mj Integration Sync Message
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_SyncMessage(): string | null {
        return this.Get('__mj_integration_SyncMessage');
    }
    set __mj_integration_SyncMessage(value: string | null) {
        this.Set('__mj_integration_SyncMessage', value);
    }

    /**
    * * Field Name: __mj_integration_ContentHash
    * * Display Name: Mj Integration Content Hash
    * * SQL Data Type: nvarchar(64)
    */
    get __mj_integration_ContentHash(): string | null {
        return this.Get('__mj_integration_ContentHash');
    }
    set __mj_integration_ContentHash(value: string | null) {
        this.Set('__mj_integration_ContentHash', value);
    }

    /**
    * * Field Name: __mj_integration_CustomOverflow
    * * Display Name: Mj Integration Custom Overflow
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_CustomOverflow(): string | null {
        return this.Get('__mj_integration_CustomOverflow');
    }
    set __mj_integration_CustomOverflow(value: string | null) {
        this.Set('__mj_integration_CustomOverflow', value);
    }

    /**
    * * Field Name: __mj_integration_ExternalVersion
    * * Display Name: Mj Integration External Version
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_ExternalVersion(): string | null {
        return this.Get('__mj_integration_ExternalVersion');
    }
    set __mj_integration_ExternalVersion(value: string | null) {
        this.Set('__mj_integration_ExternalVersion', value);
    }

    /**
    * * Field Name: __mj_integration_LastSeenModifiedValue
    * * Display Name: Mj Integration Last Seen Modified Value
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_LastSeenModifiedValue(): string | null {
        return this.Get('__mj_integration_LastSeenModifiedValue');
    }
    set __mj_integration_LastSeenModifiedValue(value: string | null) {
        this.Set('__mj_integration_LastSeenModifiedValue', value);
    }

    /**
    * * Field Name: __mj_integration_LastReconciledAt
    * * Display Name: Mj Integration Last Reconciled At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastReconciledAt(): Date | null {
        return this.Get('__mj_integration_LastReconciledAt');
    }
    set __mj_integration_LastReconciledAt(value: Date | null) {
        this.Set('__mj_integration_LastReconciledAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastWriterDirection
    * * Display Name: Mj Integration Last Writer Direction
    * * SQL Data Type: nvarchar(10)
    */
    get __mj_integration_LastWriterDirection(): string | null {
        return this.Get('__mj_integration_LastWriterDirection');
    }
    set __mj_integration_LastWriterDirection(value: string | null) {
        this.Set('__mj_integration_LastWriterDirection', value);
    }

    /**
    * * Field Name: __mj_integration_IsTombstoned
    * * Display Name: Mj Integration Is Tombstoned
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get __mj_integration_IsTombstoned(): boolean {
        return this.Get('__mj_integration_IsTombstoned');
    }
    set __mj_integration_IsTombstoned(value: boolean) {
        this.Set('__mj_integration_IsTombstoned', value);
    }

    /**
    * * Field Name: __mj_integration_DeletedDetectedAt
    * * Display Name: Mj Integration Deleted Detected At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_DeletedDetectedAt(): Date | null {
        return this.Get('__mj_integration_DeletedDetectedAt');
    }
    set __mj_integration_DeletedDetectedAt(value: Date | null) {
        this.Set('__mj_integration_DeletedDetectedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Member Documents - strongly typed entity sub-class
 * * Schema: Research
 * * Base Table: MemberDocument
 * * Base View: vwMemberDocuments
 * * @description User-uploaded files: the CV that feeds Career Map and Job Finder (Purpose = cv, at most one live per user) and documents attached to a single search (Purpose = search). Text is extracted server-side at finalize; blobs live under research/<userId>/ in the configured Azure container.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Member Documents')
export class ResearchMemberDocumentEntity extends BaseEntity<ResearchMemberDocumentEntityType> {
    /**
    * Loads the Member Documents record from the database
    * @param ID: string - primary key value to load the Member Documents record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof ResearchMemberDocumentEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * Validate() method override for Member Documents entity. This is an auto-generated method that invokes the generated validators for this entity for the following fields:
    * * Table-Level: Requires that either a User ID or an AGU Person GUID is provided, ensuring every record is associated with an identified user or person.
    * @public
    * @method
    * @override
    */
    public override Validate(): ValidationResult {
        const result = super.Validate();
        this.ValidateUserIDOrAguPersonGUIDRequired(result);
        result.Success = result.Success && (result.Errors.length === 0);

        return result;
    }

    /**
    * Requires that either a User ID or an AGU Person GUID is provided, ensuring every record is associated with an identified user or person.
    * @param result - the ValidationResult object to add any errors or warnings to
    * @public
    * @method
    */
    public ValidateUserIDOrAguPersonGUIDRequired(result: ValidationResult) {
    	if (this.UserID == null && this.AguPersonGUID == null) {
    		result.Errors.push(new ValidationErrorInfo(
    			"UserID",
    			"Either a User ID or an AGU Person GUID must be provided.",
    			this.UserID,
    			ValidationErrorType.Failure
    		));
    	}
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: UserID
    * * Display Name: User ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Users (vwUsers.ID)
    */
    get UserID(): string | null {
        return this.Get('UserID');
    }
    set UserID(value: string | null) {
        this.Set('UserID', value);
    }

    /**
    * * Field Name: Purpose
    * * Display Name: Purpose
    * * SQL Data Type: nvarchar(20)
    * * Value List Type: List
    * * Possible Values 
    *   * cv
    *   * search
    */
    get Purpose(): 'cv' | 'search' {
        return this.Get('Purpose');
    }
    set Purpose(value: 'cv' | 'search') {
        this.Set('Purpose', value);
    }

    /**
    * * Field Name: BlobPath
    * * Display Name: Blob Path
    * * SQL Data Type: nvarchar(500)
    */
    get BlobPath(): string {
        return this.Get('BlobPath');
    }
    set BlobPath(value: string) {
        this.Set('BlobPath', value);
    }

    /**
    * * Field Name: OriginalFileName
    * * Display Name: Original File Name
    * * SQL Data Type: nvarchar(255)
    */
    get OriginalFileName(): string {
        return this.Get('OriginalFileName');
    }
    set OriginalFileName(value: string) {
        this.Set('OriginalFileName', value);
    }

    /**
    * * Field Name: ContentType
    * * Display Name: Content Type
    * * SQL Data Type: nvarchar(100)
    */
    get ContentType(): string {
        return this.Get('ContentType');
    }
    set ContentType(value: string) {
        this.Set('ContentType', value);
    }

    /**
    * * Field Name: SizeBytes
    * * Display Name: Size Bytes
    * * SQL Data Type: bigint
    */
    get SizeBytes(): number {
        return this.Get('SizeBytes');
    }
    set SizeBytes(value: number) {
        this.Set('SizeBytes', value);
    }

    /**
    * * Field Name: ExtractedText
    * * Display Name: Extracted Text
    * * SQL Data Type: nvarchar(MAX)
    */
    get ExtractedText(): string | null {
        return this.Get('ExtractedText');
    }
    set ExtractedText(value: string | null) {
        this.Set('ExtractedText', value);
    }

    /**
    * * Field Name: ExtractStatus
    * * Display Name: Extract Status
    * * SQL Data Type: nvarchar(20)
    * * Default Value: pending
    * * Value List Type: List
    * * Possible Values 
    *   * extract_failed
    *   * extracted
    *   * pending
    */
    get ExtractStatus(): 'extract_failed' | 'extracted' | 'pending' {
        return this.Get('ExtractStatus');
    }
    set ExtractStatus(value: 'extract_failed' | 'extracted' | 'pending') {
        this.Set('ExtractStatus', value);
    }

    /**
    * * Field Name: ExtractError
    * * Display Name: Extract Error
    * * SQL Data Type: nvarchar(500)
    */
    get ExtractError(): string | null {
        return this.Get('ExtractError');
    }
    set ExtractError(value: string | null) {
        this.Set('ExtractError', value);
    }

    /**
    * * Field Name: UploadedAt
    * * Display Name: Uploaded At
    * * SQL Data Type: datetimeoffset
    * * Default Value: sysdatetimeoffset()
    */
    get UploadedAt(): Date {
        return this.Get('UploadedAt');
    }
    set UploadedAt(value: Date) {
        this.Set('UploadedAt', value);
    }

    /**
    * * Field Name: ExtractedAt
    * * Display Name: Extracted At
    * * SQL Data Type: datetimeoffset
    */
    get ExtractedAt(): Date | null {
        return this.Get('ExtractedAt');
    }
    set ExtractedAt(value: Date | null) {
        this.Set('ExtractedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: AguPersonGUID
    * * Display Name: Agu Person GUID
    * * SQL Data Type: uniqueidentifier
    * * Description: Owner as Common.AguPerson GUID. Set for external-drop CVs, whose owner has no MJ user; null owner UserID is allowed when this is set.
    */
    get AguPersonGUID(): string | null {
        return this.Get('AguPersonGUID');
    }
    set AguPersonGUID(value: string | null) {
        this.Set('AguPersonGUID', value);
    }

    /**
    * * Field Name: Source
    * * Display Name: Source
    * * SQL Data Type: nvarchar(20)
    * * Default Value: app
    * * Value List Type: List
    * * Possible Values 
    *   * app
    *   * external_drop
    * * Description: Where the document came from: app (uploaded in the portal) or external_drop (found in the person documents container under <AguPersonGUID>/CV/).
    */
    get Source(): 'app' | 'external_drop' {
        return this.Get('Source');
    }
    set Source(value: 'app' | 'external_drop') {
        this.Set('Source', value);
    }

    /**
    * * Field Name: StorageAccountID
    * * Display Name: Storage Account ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: File Storage Accounts (vwFileStorageAccounts.ID)
    * * Description: File storage account holding the blob at BlobPath.
    */
    get StorageAccountID(): string | null {
        return this.Get('StorageAccountID');
    }
    set StorageAccountID(value: string | null) {
        this.Set('StorageAccountID', value);
    }

    /**
    * * Field Name: DetectedFileType
    * * Display Name: Detected File Type
    * * SQL Data Type: nvarchar(10)
    * * Value List Type: List
    * * Possible Values 
    *   * doc
    *   * docx
    *   * other
    *   * pdf
    *   * txt
    * * Description: File type detected from the blob content: pdf, docx, doc, txt or other.
    */
    get DetectedFileType(): 'doc' | 'docx' | 'other' | 'pdf' | 'txt' | null {
        return this.Get('DetectedFileType');
    }
    set DetectedFileType(value: 'doc' | 'docx' | 'other' | 'pdf' | 'txt' | null) {
        this.Set('DetectedFileType', value);
    }

    /**
    * * Field Name: ExternalETag
    * * Display Name: External E Tag
    * * SQL Data Type: nvarchar(100)
    * * Description: Blob ETag last seen for an external-drop document; a change means the blob was overwritten in place.
    */
    get ExternalETag(): string | null {
        return this.Get('ExternalETag');
    }
    set ExternalETag(value: string | null) {
        this.Set('ExternalETag', value);
    }

    /**
    * * Field Name: DeletedAt
    * * Display Name: Deleted At
    * * SQL Data Type: datetimeoffset
    * * Description: Member tombstone: set when the member removes the document; the external blob itself is never deleted.
    */
    get DeletedAt(): Date | null {
        return this.Get('DeletedAt');
    }
    set DeletedAt(value: Date | null) {
        this.Set('DeletedAt', value);
    }

    /**
    * * Field Name: SourceMissingAt
    * * Display Name: Source Missing At
    * * SQL Data Type: datetimeoffset
    * * Description: Set when an external-drop blob vanished from the drop container.
    */
    get SourceMissingAt(): Date | null {
        return this.Get('SourceMissingAt');
    }
    set SourceMissingAt(value: Date | null) {
        this.Set('SourceMissingAt', value);
    }

    /**
    * * Field Name: ConsentedAt
    * * Display Name: Consented At
    * * SQL Data Type: datetimeoffset
    * * Description: When the member consented to Career Explorer processing of this document; NULL when no consent was recorded.
    */
    get ConsentedAt(): Date | null {
        return this.Get('ConsentedAt');
    }
    set ConsentedAt(value: Date | null) {
        this.Set('ConsentedAt', value);
    }

    /**
    * * Field Name: User
    * * Display Name: User
    * * SQL Data Type: nvarchar(100)
    */
    get User(): string | null {
        return this.Get('User');
    }

    /**
    * * Field Name: StorageAccount
    * * Display Name: Storage Account
    * * SQL Data Type: nvarchar(200)
    */
    get StorageAccount(): string | null {
        return this.Get('StorageAccount');
    }
}


/**
 * Member Item States - strongly typed entity sub-class
 * * Schema: Research
 * * Base Table: MemberItemState
 * * Base View: vwMemberItemStates
 * * @description Per-user Save / Pin / "Not for me" state for one grant, person or job. ItemKey is Recommendation.Content.ID for grants, Recommendation.Contributor.ID for people, Jobs.JobPosting.JobID for jobs.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Member Item States')
export class ResearchMemberItemStateEntity extends BaseEntity<ResearchMemberItemStateEntityType> {
    /**
    * Loads the Member Item States record from the database
    * @param ID: string - primary key value to load the Member Item States record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof ResearchMemberItemStateEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: UserID
    * * Display Name: User ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Users (vwUsers.ID)
    */
    get UserID(): string {
        return this.Get('UserID');
    }
    set UserID(value: string) {
        this.Set('UserID', value);
    }

    /**
    * * Field Name: ItemKind
    * * Display Name: Item Kind
    * * SQL Data Type: nvarchar(20)
    * * Value List Type: List
    * * Possible Values 
    *   * grant
    *   * job
    *   * person
    */
    get ItemKind(): 'grant' | 'job' | 'person' {
        return this.Get('ItemKind');
    }
    set ItemKind(value: 'grant' | 'job' | 'person') {
        this.Set('ItemKind', value);
    }

    /**
    * * Field Name: ItemKey
    * * Display Name: Item Key
    * * SQL Data Type: nvarchar(100)
    */
    get ItemKey(): string {
        return this.Get('ItemKey');
    }
    set ItemKey(value: string) {
        this.Set('ItemKey', value);
    }

    /**
    * * Field Name: IsSaved
    * * Display Name: Is Saved
    * * SQL Data Type: bit
    * * Default Value: 0
    * * NOTE: Property renamed to `IsSaved_` to avoid conflict with BaseEntity.IsSaved
    */
    get IsSaved_(): boolean {
        return this.Get('IsSaved');
    }
    set IsSaved_(value: boolean) {
        this.Set('IsSaved', value);
    }

    /**
    * * Field Name: IsPinned
    * * Display Name: Is Pinned
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get IsPinned(): boolean {
        return this.Get('IsPinned');
    }
    set IsPinned(value: boolean) {
        this.Set('IsPinned', value);
    }

    /**
    * * Field Name: IsDismissed
    * * Display Name: Is Dismissed
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get IsDismissed(): boolean {
        return this.Get('IsDismissed');
    }
    set IsDismissed(value: boolean) {
        this.Set('IsDismissed', value);
    }

    /**
    * * Field Name: SavedAt
    * * Display Name: Saved At
    * * SQL Data Type: datetimeoffset
    */
    get SavedAt(): Date | null {
        return this.Get('SavedAt');
    }
    set SavedAt(value: Date | null) {
        this.Set('SavedAt', value);
    }

    /**
    * * Field Name: PinnedAt
    * * Display Name: Pinned At
    * * SQL Data Type: datetimeoffset
    */
    get PinnedAt(): Date | null {
        return this.Get('PinnedAt');
    }
    set PinnedAt(value: Date | null) {
        this.Set('PinnedAt', value);
    }

    /**
    * * Field Name: DismissedAt
    * * Display Name: Dismissed At
    * * SQL Data Type: datetimeoffset
    */
    get DismissedAt(): Date | null {
        return this.Get('DismissedAt');
    }
    set DismissedAt(value: Date | null) {
        this.Set('DismissedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: User
    * * Display Name: User
    * * SQL Data Type: nvarchar(100)
    */
    get User(): string {
        return this.Get('User');
    }
}


/**
 * Member Organizations - strongly typed entity sub-class
 * * Schema: CoreSignal
 * * Base Table: MemberOrganization
 * * Base View: vwMemberOrganizations
 * * @description A membership/organization affiliation from a CoreSignal profile (the feed's organizations array — professional societies, boards, volunteer orgs). Named MemberOrganization to avoid colliding with the general "Organization" concept.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Member Organizations')
export class CoreSignalMemberOrganizationEntity extends BaseEntity<CoreSignalMemberOrganizationEntityType> {
    /**
    * Loads the Member Organizations record from the database
    * @param ID: string - primary key value to load the Member Organizations record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof CoreSignalMemberOrganizationEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ProfileID
    * * Display Name: Profile ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Profiles (vwProfiles.ID)
    */
    get ProfileID(): string {
        return this.Get('ProfileID');
    }
    set ProfileID(value: string) {
        this.Set('ProfileID', value);
    }

    /**
    * * Field Name: OrderInProfile
    * * Display Name: Order In Profile
    * * SQL Data Type: int
    */
    get OrderInProfile(): number {
        return this.Get('OrderInProfile');
    }
    set OrderInProfile(value: number) {
        this.Set('OrderInProfile', value);
    }

    /**
    * * Field Name: OrganizationName
    * * Display Name: Organization Name
    * * SQL Data Type: nvarchar(500)
    */
    get OrganizationName(): string | null {
        return this.Get('OrganizationName');
    }
    set OrganizationName(value: string | null) {
        this.Set('OrganizationName', value);
    }

    /**
    * * Field Name: Position
    * * Display Name: Position
    * * SQL Data Type: nvarchar(500)
    */
    get Position(): string | null {
        return this.Get('Position');
    }
    set Position(value: string | null) {
        this.Set('Position', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(MAX)
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: DateFromYear
    * * Display Name: Date From Year
    * * SQL Data Type: smallint
    */
    get DateFromYear(): number | null {
        return this.Get('DateFromYear');
    }
    set DateFromYear(value: number | null) {
        this.Set('DateFromYear', value);
    }

    /**
    * * Field Name: DateFromMonth
    * * Display Name: Date From Month
    * * SQL Data Type: tinyint
    */
    get DateFromMonth(): number | null {
        return this.Get('DateFromMonth');
    }
    set DateFromMonth(value: number | null) {
        this.Set('DateFromMonth', value);
    }

    /**
    * * Field Name: DateToYear
    * * Display Name: Date To Year
    * * SQL Data Type: smallint
    */
    get DateToYear(): number | null {
        return this.Get('DateToYear');
    }
    set DateToYear(value: number | null) {
        this.Set('DateToYear', value);
    }

    /**
    * * Field Name: DateToMonth
    * * Display Name: Date To Month
    * * SQL Data Type: tinyint
    */
    get DateToMonth(): number | null {
        return this.Get('DateToMonth');
    }
    set DateToMonth(value: number | null) {
        this.Set('DateToMonth', value);
    }

    /**
    * * Field Name: __mj_integration_SyncStatus
    * * Display Name: Mj Integration Sync Status
    * * SQL Data Type: nvarchar(50)
    * * Default Value: Active
    */
    get __mj_integration_SyncStatus(): string {
        return this.Get('__mj_integration_SyncStatus');
    }
    set __mj_integration_SyncStatus(value: string) {
        this.Set('__mj_integration_SyncStatus', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedAt
    * * Display Name: Mj Integration Last Synced At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastSyncedAt(): Date | null {
        return this.Get('__mj_integration_LastSyncedAt');
    }
    set __mj_integration_LastSyncedAt(value: Date | null) {
        this.Set('__mj_integration_LastSyncedAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedSnapshot
    * * Display Name: Mj Integration Last Synced Snapshot
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_LastSyncedSnapshot(): string | null {
        return this.Get('__mj_integration_LastSyncedSnapshot');
    }
    set __mj_integration_LastSyncedSnapshot(value: string | null) {
        this.Set('__mj_integration_LastSyncedSnapshot', value);
    }

    /**
    * * Field Name: __mj_integration_SyncMessage
    * * Display Name: Mj Integration Sync Message
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_SyncMessage(): string | null {
        return this.Get('__mj_integration_SyncMessage');
    }
    set __mj_integration_SyncMessage(value: string | null) {
        this.Set('__mj_integration_SyncMessage', value);
    }

    /**
    * * Field Name: __mj_integration_ContentHash
    * * Display Name: Mj Integration Content Hash
    * * SQL Data Type: nvarchar(64)
    */
    get __mj_integration_ContentHash(): string | null {
        return this.Get('__mj_integration_ContentHash');
    }
    set __mj_integration_ContentHash(value: string | null) {
        this.Set('__mj_integration_ContentHash', value);
    }

    /**
    * * Field Name: __mj_integration_CustomOverflow
    * * Display Name: Mj Integration Custom Overflow
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_CustomOverflow(): string | null {
        return this.Get('__mj_integration_CustomOverflow');
    }
    set __mj_integration_CustomOverflow(value: string | null) {
        this.Set('__mj_integration_CustomOverflow', value);
    }

    /**
    * * Field Name: __mj_integration_ExternalVersion
    * * Display Name: Mj Integration External Version
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_ExternalVersion(): string | null {
        return this.Get('__mj_integration_ExternalVersion');
    }
    set __mj_integration_ExternalVersion(value: string | null) {
        this.Set('__mj_integration_ExternalVersion', value);
    }

    /**
    * * Field Name: __mj_integration_LastSeenModifiedValue
    * * Display Name: Mj Integration Last Seen Modified Value
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_LastSeenModifiedValue(): string | null {
        return this.Get('__mj_integration_LastSeenModifiedValue');
    }
    set __mj_integration_LastSeenModifiedValue(value: string | null) {
        this.Set('__mj_integration_LastSeenModifiedValue', value);
    }

    /**
    * * Field Name: __mj_integration_LastReconciledAt
    * * Display Name: Mj Integration Last Reconciled At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastReconciledAt(): Date | null {
        return this.Get('__mj_integration_LastReconciledAt');
    }
    set __mj_integration_LastReconciledAt(value: Date | null) {
        this.Set('__mj_integration_LastReconciledAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastWriterDirection
    * * Display Name: Mj Integration Last Writer Direction
    * * SQL Data Type: nvarchar(10)
    */
    get __mj_integration_LastWriterDirection(): string | null {
        return this.Get('__mj_integration_LastWriterDirection');
    }
    set __mj_integration_LastWriterDirection(value: string | null) {
        this.Set('__mj_integration_LastWriterDirection', value);
    }

    /**
    * * Field Name: __mj_integration_IsTombstoned
    * * Display Name: Mj Integration Is Tombstoned
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get __mj_integration_IsTombstoned(): boolean {
        return this.Get('__mj_integration_IsTombstoned');
    }
    set __mj_integration_IsTombstoned(value: boolean) {
        this.Set('__mj_integration_IsTombstoned', value);
    }

    /**
    * * Field Name: __mj_integration_DeletedDetectedAt
    * * Display Name: Mj Integration Deleted Detected At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_DeletedDetectedAt(): Date | null {
        return this.Get('__mj_integration_DeletedDetectedAt');
    }
    set __mj_integration_DeletedDetectedAt(value: Date | null) {
        this.Set('__mj_integration_DeletedDetectedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Member Profiles - strongly typed entity sub-class
 * * Schema: Research
 * * Base Table: MemberProfile
 * * Base View: vwMemberProfiles
 * * @description Portal-only state for one AGU person: AI-inferred research areas and the visit counters behind the Home greeting and "new since your last visit" badges. Exists only for real members (rows in Common.AguPerson); staff without a member record use the portal in staff mode and have no row here.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Member Profiles')
export class ResearchMemberProfileEntity extends BaseEntity<ResearchMemberProfileEntityType> {
    /**
    * Loads the Member Profiles record from the database
    * @param ID: string - primary key value to load the Member Profiles record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof ResearchMemberProfileEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: AguPersonID
    * * Display Name: Agu Person ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Agu Persons (vwAguPersons.ID)
    */
    get AguPersonID(): string {
        return this.Get('AguPersonID');
    }
    set AguPersonID(value: string) {
        this.Set('AguPersonID', value);
    }

    /**
    * * Field Name: ResearchAreas
    * * Display Name: Research Areas
    * * SQL Data Type: nvarchar(MAX)
    */
    get ResearchAreas(): string | null {
        return this.Get('ResearchAreas');
    }
    set ResearchAreas(value: string | null) {
        this.Set('ResearchAreas', value);
    }

    /**
    * * Field Name: ResearchAreasInferredAt
    * * Display Name: Research Areas Inferred At
    * * SQL Data Type: datetimeoffset
    */
    get ResearchAreasInferredAt(): Date | null {
        return this.Get('ResearchAreasInferredAt');
    }
    set ResearchAreasInferredAt(value: Date | null) {
        this.Set('ResearchAreasInferredAt', value);
    }

    /**
    * * Field Name: LastVisitAt
    * * Display Name: Last Visit At
    * * SQL Data Type: datetimeoffset
    */
    get LastVisitAt(): Date | null {
        return this.Get('LastVisitAt');
    }
    set LastVisitAt(value: Date | null) {
        this.Set('LastVisitAt', value);
    }

    /**
    * * Field Name: PreviousVisitAt
    * * Display Name: Previous Visit At
    * * SQL Data Type: datetimeoffset
    * * Description: Start of the visit before the current one. "New since your last visit" counts recommendations first seen after this instant.
    */
    get PreviousVisitAt(): Date | null {
        return this.Get('PreviousVisitAt');
    }
    set PreviousVisitAt(value: Date | null) {
        this.Set('PreviousVisitAt', value);
    }

    /**
    * * Field Name: VisitCount
    * * Display Name: Visit Count
    * * SQL Data Type: int
    * * Default Value: 0
    */
    get VisitCount(): number {
        return this.Get('VisitCount');
    }
    set VisitCount(value: number) {
        this.Set('VisitCount', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Member Recommendations - strongly typed entity sub-class
 * * Schema: Research
 * * Base Table: MemberRecommendation
 * * Base View: vwMemberRecommendations
 * * @description Recommendations the portal has shown a user, one row per (user, kind, key). FirstSeenAt drives the "N new since your last visit" badges; LastSeenAt refreshes on each re-recommendation.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Member Recommendations')
export class ResearchMemberRecommendationEntity extends BaseEntity<ResearchMemberRecommendationEntityType> {
    /**
    * Loads the Member Recommendations record from the database
    * @param ID: string - primary key value to load the Member Recommendations record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof ResearchMemberRecommendationEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: UserID
    * * Display Name: User ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Users (vwUsers.ID)
    */
    get UserID(): string {
        return this.Get('UserID');
    }
    set UserID(value: string) {
        this.Set('UserID', value);
    }

    /**
    * * Field Name: ItemKind
    * * Display Name: Item Kind
    * * SQL Data Type: nvarchar(20)
    * * Value List Type: List
    * * Possible Values 
    *   * grant
    *   * job
    *   * person
    */
    get ItemKind(): 'grant' | 'job' | 'person' {
        return this.Get('ItemKind');
    }
    set ItemKind(value: 'grant' | 'job' | 'person') {
        this.Set('ItemKind', value);
    }

    /**
    * * Field Name: ItemKey
    * * Display Name: Item Key
    * * SQL Data Type: nvarchar(100)
    */
    get ItemKey(): string {
        return this.Get('ItemKey');
    }
    set ItemKey(value: string) {
        this.Set('ItemKey', value);
    }

    /**
    * * Field Name: Score
    * * Display Name: Score
    * * SQL Data Type: decimal(6, 5)
    */
    get Score(): number {
        return this.Get('Score');
    }
    set Score(value: number) {
        this.Set('Score', value);
    }

    /**
    * * Field Name: Reason
    * * Display Name: Reason
    * * SQL Data Type: nvarchar(500)
    */
    get Reason(): string | null {
        return this.Get('Reason');
    }
    set Reason(value: string | null) {
        this.Set('Reason', value);
    }

    /**
    * * Field Name: FirstSeenAt
    * * Display Name: First Seen At
    * * SQL Data Type: datetimeoffset
    * * Default Value: sysdatetimeoffset()
    */
    get FirstSeenAt(): Date {
        return this.Get('FirstSeenAt');
    }
    set FirstSeenAt(value: Date) {
        this.Set('FirstSeenAt', value);
    }

    /**
    * * Field Name: LastSeenAt
    * * Display Name: Last Seen At
    * * SQL Data Type: datetimeoffset
    * * Default Value: sysdatetimeoffset()
    */
    get LastSeenAt(): Date {
        return this.Get('LastSeenAt');
    }
    set LastSeenAt(value: Date) {
        this.Set('LastSeenAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: User
    * * Display Name: User
    * * SQL Data Type: nvarchar(100)
    */
    get User(): string {
        return this.Get('User');
    }
}


/**
 * Memberships - strongly typed entity sub-class
 * * Schema: ORCID
 * * Base Table: Membership
 * * Base View: vwMemberships
 * * @description A membership (memberships section).
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Memberships')
export class ORCIDMembershipEntity extends BaseEntity<ORCIDMembershipEntityType> {
    /**
    * Loads the Memberships record from the database
    * @param ID: string - primary key value to load the Memberships record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof ORCIDMembershipEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ORCIDRecordID
    * * Display Name: ORCID Record ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Records (vwRecords.ID)
    */
    get ORCIDRecordID(): string {
        return this.Get('ORCIDRecordID');
    }
    set ORCIDRecordID(value: string) {
        this.Set('ORCIDRecordID', value);
    }

    /**
    * * Field Name: PutCode
    * * Display Name: Put Code
    * * SQL Data Type: bigint
    */
    get PutCode(): number {
        return this.Get('PutCode');
    }
    set PutCode(value: number) {
        this.Set('PutCode', value);
    }

    /**
    * * Field Name: DepartmentName
    * * Display Name: Department Name
    * * SQL Data Type: nvarchar(500)
    */
    get DepartmentName(): string | null {
        return this.Get('DepartmentName');
    }
    set DepartmentName(value: string | null) {
        this.Set('DepartmentName', value);
    }

    /**
    * * Field Name: RoleTitle
    * * Display Name: Role Title
    * * SQL Data Type: nvarchar(500)
    */
    get RoleTitle(): string | null {
        return this.Get('RoleTitle');
    }
    set RoleTitle(value: string | null) {
        this.Set('RoleTitle', value);
    }

    /**
    * * Field Name: StartYear
    * * Display Name: Start Year
    * * SQL Data Type: smallint
    */
    get StartYear(): number | null {
        return this.Get('StartYear');
    }
    set StartYear(value: number | null) {
        this.Set('StartYear', value);
    }

    /**
    * * Field Name: StartMonth
    * * Display Name: Start Month
    * * SQL Data Type: tinyint
    */
    get StartMonth(): number | null {
        return this.Get('StartMonth');
    }
    set StartMonth(value: number | null) {
        this.Set('StartMonth', value);
    }

    /**
    * * Field Name: StartDay
    * * Display Name: Start Day
    * * SQL Data Type: tinyint
    */
    get StartDay(): number | null {
        return this.Get('StartDay');
    }
    set StartDay(value: number | null) {
        this.Set('StartDay', value);
    }

    /**
    * * Field Name: EndYear
    * * Display Name: End Year
    * * SQL Data Type: smallint
    */
    get EndYear(): number | null {
        return this.Get('EndYear');
    }
    set EndYear(value: number | null) {
        this.Set('EndYear', value);
    }

    /**
    * * Field Name: EndMonth
    * * Display Name: End Month
    * * SQL Data Type: tinyint
    */
    get EndMonth(): number | null {
        return this.Get('EndMonth');
    }
    set EndMonth(value: number | null) {
        this.Set('EndMonth', value);
    }

    /**
    * * Field Name: EndDay
    * * Display Name: End Day
    * * SQL Data Type: tinyint
    */
    get EndDay(): number | null {
        return this.Get('EndDay');
    }
    set EndDay(value: number | null) {
        this.Set('EndDay', value);
    }

    /**
    * * Field Name: OrgName
    * * Display Name: Org Name
    * * SQL Data Type: nvarchar(500)
    */
    get OrgName(): string | null {
        return this.Get('OrgName');
    }
    set OrgName(value: string | null) {
        this.Set('OrgName', value);
    }

    /**
    * * Field Name: OrgCity
    * * Display Name: Org City
    * * SQL Data Type: nvarchar(255)
    */
    get OrgCity(): string | null {
        return this.Get('OrgCity');
    }
    set OrgCity(value: string | null) {
        this.Set('OrgCity', value);
    }

    /**
    * * Field Name: OrgRegion
    * * Display Name: Org Region
    * * SQL Data Type: nvarchar(255)
    */
    get OrgRegion(): string | null {
        return this.Get('OrgRegion');
    }
    set OrgRegion(value: string | null) {
        this.Set('OrgRegion', value);
    }

    /**
    * * Field Name: OrgCountry
    * * Display Name: Org Country
    * * SQL Data Type: nvarchar(10)
    */
    get OrgCountry(): string | null {
        return this.Get('OrgCountry');
    }
    set OrgCountry(value: string | null) {
        this.Set('OrgCountry', value);
    }

    /**
    * * Field Name: OrgDisambiguatedID
    * * Display Name: Org Disambiguated ID
    * * SQL Data Type: nvarchar(255)
    */
    get OrgDisambiguatedID(): string | null {
        return this.Get('OrgDisambiguatedID');
    }
    set OrgDisambiguatedID(value: string | null) {
        this.Set('OrgDisambiguatedID', value);
    }

    /**
    * * Field Name: OrgDisambiguationSource
    * * Display Name: Org Disambiguation Source
    * * SQL Data Type: nvarchar(50)
    */
    get OrgDisambiguationSource(): string | null {
        return this.Get('OrgDisambiguationSource');
    }
    set OrgDisambiguationSource(value: string | null) {
        this.Set('OrgDisambiguationSource', value);
    }

    /**
    * * Field Name: Url
    * * Display Name: Url
    * * SQL Data Type: nvarchar(2000)
    */
    get Url(): string | null {
        return this.Get('Url');
    }
    set Url(value: string | null) {
        this.Set('Url', value);
    }

    /**
    * * Field Name: Visibility
    * * Display Name: Visibility
    * * SQL Data Type: nvarchar(20)
    */
    get Visibility(): string | null {
        return this.Get('Visibility');
    }
    set Visibility(value: string | null) {
        this.Set('Visibility', value);
    }

    /**
    * * Field Name: SourceName
    * * Display Name: Source Name
    * * SQL Data Type: nvarchar(255)
    */
    get SourceName(): string | null {
        return this.Get('SourceName');
    }
    set SourceName(value: string | null) {
        this.Set('SourceName', value);
    }

    /**
    * * Field Name: SourceOrcid
    * * Display Name: Source Orcid
    * * SQL Data Type: nvarchar(19)
    */
    get SourceOrcid(): string | null {
        return this.Get('SourceOrcid');
    }
    set SourceOrcid(value: string | null) {
        this.Set('SourceOrcid', value);
    }

    /**
    * * Field Name: OrcidCreatedDate
    * * Display Name: Orcid Created Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidCreatedDate(): Date | null {
        return this.Get('OrcidCreatedDate');
    }
    set OrcidCreatedDate(value: Date | null) {
        this.Set('OrcidCreatedDate', value);
    }

    /**
    * * Field Name: OrcidLastModifiedDate
    * * Display Name: Orcid Last Modified Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidLastModifiedDate(): Date | null {
        return this.Get('OrcidLastModifiedDate');
    }
    set OrcidLastModifiedDate(value: Date | null) {
        this.Set('OrcidLastModifiedDate', value);
    }

    /**
    * * Field Name: RawJSON
    * * Display Name: Raw JSON
    * * SQL Data Type: nvarchar(MAX)
    */
    get RawJSON(): string | null {
        return this.Get('RawJSON');
    }
    set RawJSON(value: string | null) {
        this.Set('RawJSON', value);
    }

    /**
    * * Field Name: __mj_integration_SyncStatus
    * * Display Name: Mj Integration Sync Status
    * * SQL Data Type: nvarchar(50)
    * * Default Value: Active
    */
    get __mj_integration_SyncStatus(): string {
        return this.Get('__mj_integration_SyncStatus');
    }
    set __mj_integration_SyncStatus(value: string) {
        this.Set('__mj_integration_SyncStatus', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedAt
    * * Display Name: Mj Integration Last Synced At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastSyncedAt(): Date | null {
        return this.Get('__mj_integration_LastSyncedAt');
    }
    set __mj_integration_LastSyncedAt(value: Date | null) {
        this.Set('__mj_integration_LastSyncedAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedSnapshot
    * * Display Name: Mj Integration Last Synced Snapshot
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_LastSyncedSnapshot(): string | null {
        return this.Get('__mj_integration_LastSyncedSnapshot');
    }
    set __mj_integration_LastSyncedSnapshot(value: string | null) {
        this.Set('__mj_integration_LastSyncedSnapshot', value);
    }

    /**
    * * Field Name: __mj_integration_SyncMessage
    * * Display Name: Mj Integration Sync Message
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_SyncMessage(): string | null {
        return this.Get('__mj_integration_SyncMessage');
    }
    set __mj_integration_SyncMessage(value: string | null) {
        this.Set('__mj_integration_SyncMessage', value);
    }

    /**
    * * Field Name: __mj_integration_ContentHash
    * * Display Name: Mj Integration Content Hash
    * * SQL Data Type: nvarchar(64)
    */
    get __mj_integration_ContentHash(): string | null {
        return this.Get('__mj_integration_ContentHash');
    }
    set __mj_integration_ContentHash(value: string | null) {
        this.Set('__mj_integration_ContentHash', value);
    }

    /**
    * * Field Name: __mj_integration_CustomOverflow
    * * Display Name: Mj Integration Custom Overflow
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_CustomOverflow(): string | null {
        return this.Get('__mj_integration_CustomOverflow');
    }
    set __mj_integration_CustomOverflow(value: string | null) {
        this.Set('__mj_integration_CustomOverflow', value);
    }

    /**
    * * Field Name: __mj_integration_ExternalVersion
    * * Display Name: Mj Integration External Version
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_ExternalVersion(): string | null {
        return this.Get('__mj_integration_ExternalVersion');
    }
    set __mj_integration_ExternalVersion(value: string | null) {
        this.Set('__mj_integration_ExternalVersion', value);
    }

    /**
    * * Field Name: __mj_integration_LastSeenModifiedValue
    * * Display Name: Mj Integration Last Seen Modified Value
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_LastSeenModifiedValue(): string | null {
        return this.Get('__mj_integration_LastSeenModifiedValue');
    }
    set __mj_integration_LastSeenModifiedValue(value: string | null) {
        this.Set('__mj_integration_LastSeenModifiedValue', value);
    }

    /**
    * * Field Name: __mj_integration_LastReconciledAt
    * * Display Name: Mj Integration Last Reconciled At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastReconciledAt(): Date | null {
        return this.Get('__mj_integration_LastReconciledAt');
    }
    set __mj_integration_LastReconciledAt(value: Date | null) {
        this.Set('__mj_integration_LastReconciledAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastWriterDirection
    * * Display Name: Mj Integration Last Writer Direction
    * * SQL Data Type: nvarchar(10)
    */
    get __mj_integration_LastWriterDirection(): string | null {
        return this.Get('__mj_integration_LastWriterDirection');
    }
    set __mj_integration_LastWriterDirection(value: string | null) {
        this.Set('__mj_integration_LastWriterDirection', value);
    }

    /**
    * * Field Name: __mj_integration_IsTombstoned
    * * Display Name: Mj Integration Is Tombstoned
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get __mj_integration_IsTombstoned(): boolean {
        return this.Get('__mj_integration_IsTombstoned');
    }
    set __mj_integration_IsTombstoned(value: boolean) {
        this.Set('__mj_integration_IsTombstoned', value);
    }

    /**
    * * Field Name: __mj_integration_DeletedDetectedAt
    * * Display Name: Mj Integration Deleted Detected At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_DeletedDetectedAt(): Date | null {
        return this.Get('__mj_integration_DeletedDetectedAt');
    }
    set __mj_integration_DeletedDetectedAt(value: Date | null) {
        this.Set('__mj_integration_DeletedDetectedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Nomination Responses - strongly typed entity sub-class
 * * Schema: FeedbackPlatform
 * * Base Table: NominationResponse
 * * Base View: vwNominationResponses
 * * @description One scored criterion of a nomination (1-5; 5 = Outstanding, which requires TextValue justification).
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Nomination Responses')
export class FeedbackPlatformNominationResponseEntity extends BaseEntity<FeedbackPlatformNominationResponseEntityType> {
    /**
    * Loads the Nomination Responses record from the database
    * @param ID: string - primary key value to load the Nomination Responses record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof FeedbackPlatformNominationResponseEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * Validate() method override for Nomination Responses entity. This is an auto-generated method that invokes the generated validators for this entity for the following fields:
    * * NumericValue: The numeric value, if provided, must be between 1 and 5 inclusive to ensure ratings or scores remain within the standard scale.
    * @public
    * @method
    * @override
    */
    public override Validate(): ValidationResult {
        const result = super.Validate();
        this.ValidateNumericValueRange(result);
        result.Success = result.Success && (result.Errors.length === 0);

        return result;
    }

    /**
    * The numeric value, if provided, must be between 1 and 5 inclusive to ensure ratings or scores remain within the standard scale.
    * @param result - the ValidationResult object to add any errors or warnings to
    * @public
    * @method
    */
    public ValidateNumericValueRange(result: ValidationResult) {
    	if (this.NumericValue != null && (this.NumericValue < 1 || this.NumericValue > 5)) {
    		result.Errors.push(new ValidationErrorInfo(
    			"NumericValue",
    			"The numeric value must be between 1 and 5.",
    			this.NumericValue,
    			ValidationErrorType.Failure
    		));
    	}
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: NominationID
    * * Display Name: Nomination ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Nominations (vwNominations.ID)
    */
    get NominationID(): string {
        return this.Get('NominationID');
    }
    set NominationID(value: string) {
        this.Set('NominationID', value);
    }

    /**
    * * Field Name: ReviewQuestionID
    * * Display Name: Review Question ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Review Questions (vwReviewQuestions.ID)
    */
    get ReviewQuestionID(): string {
        return this.Get('ReviewQuestionID');
    }
    set ReviewQuestionID(value: string) {
        this.Set('ReviewQuestionID', value);
    }

    /**
    * * Field Name: NumericValue
    * * Display Name: Numeric Value
    * * SQL Data Type: int
    */
    get NumericValue(): number | null {
        return this.Get('NumericValue');
    }
    set NumericValue(value: number | null) {
        this.Set('NumericValue', value);
    }

    /**
    * * Field Name: TextValue
    * * Display Name: Text Value
    * * SQL Data Type: nvarchar(MAX)
    */
    get TextValue(): string | null {
        return this.Get('TextValue');
    }
    set TextValue(value: string | null) {
        this.Set('TextValue', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Nominations - strongly typed entity sub-class
 * * Schema: FeedbackPlatform
 * * Base Table: Nomination
 * * Base View: vwNominations
 * * @description A session nominator's award nomination of a presentation. Never visible to presenters.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Nominations')
export class FeedbackPlatformNominationEntity extends BaseEntity<FeedbackPlatformNominationEntityType> {
    /**
    * Loads the Nominations record from the database
    * @param ID: string - primary key value to load the Nominations record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof FeedbackPlatformNominationEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: SeasonID
    * * Display Name: Season ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Seasons (vwSeasons.ID)
    */
    get SeasonID(): string {
        return this.Get('SeasonID');
    }
    set SeasonID(value: string) {
        this.Set('SeasonID', value);
    }

    /**
    * * Field Name: PresentationID
    * * Display Name: Presentation ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Presentations (vwPresentations.ID)
    */
    get PresentationID(): string {
        return this.Get('PresentationID');
    }
    set PresentationID(value: string) {
        this.Set('PresentationID', value);
    }

    /**
    * * Field Name: NominatorUserID
    * * Display Name: Nominator User ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Users (vwUsers.ID)
    */
    get NominatorUserID(): string {
        return this.Get('NominatorUserID');
    }
    set NominatorUserID(value: string) {
        this.Set('NominatorUserID', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: nvarchar(20)
    * * Default Value: Draft
    * * Value List Type: List
    * * Possible Values 
    *   * Draft
    *   * Submitted
    */
    get Status(): 'Draft' | 'Submitted' {
        return this.Get('Status');
    }
    set Status(value: 'Draft' | 'Submitted') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: NoConflictAttested
    * * Display Name: No Conflict Attested
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get NoConflictAttested(): boolean {
        return this.Get('NoConflictAttested');
    }
    set NoConflictAttested(value: boolean) {
        this.Set('NoConflictAttested', value);
    }

    /**
    * * Field Name: AdditionalReasoning
    * * Display Name: Additional Reasoning
    * * SQL Data Type: nvarchar(MAX)
    */
    get AdditionalReasoning(): string | null {
        return this.Get('AdditionalReasoning');
    }
    set AdditionalReasoning(value: string | null) {
        this.Set('AdditionalReasoning', value);
    }

    /**
    * * Field Name: SubmittedAt
    * * Display Name: Submitted At
    * * SQL Data Type: datetimeoffset
    */
    get SubmittedAt(): Date | null {
        return this.Get('SubmittedAt');
    }
    set SubmittedAt(value: Date | null) {
        this.Set('SubmittedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Season
    * * Display Name: Season
    * * SQL Data Type: nvarchar(200)
    */
    get Season(): string {
        return this.Get('Season');
    }

    /**
    * * Field Name: Presentation
    * * Display Name: Presentation
    * * SQL Data Type: nvarchar(500)
    */
    get Presentation(): string {
        return this.Get('Presentation');
    }

    /**
    * * Field Name: NominatorUser
    * * Display Name: Nominator User
    * * SQL Data Type: nvarchar(100)
    */
    get NominatorUser(): string {
        return this.Get('NominatorUser');
    }
}


/**
 * Notification Types - strongly typed entity sub-class
 * * Schema: FeedbackPlatform
 * * Base Table: NotificationType
 * * Base View: vwNotificationTypes
 * * @description Defines types of notifications that can be sent to users.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Notification Types')
export class FeedbackPlatformNotificationTypeEntity extends BaseEntity<FeedbackPlatformNotificationTypeEntityType> {
    /**
    * Loads the Notification Types record from the database
    * @param ID: string - primary key value to load the Notification Types record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof FeedbackPlatformNotificationTypeEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: Name
    * * Display Name: Name
    * * SQL Data Type: nvarchar(100)
    * * Description: Name of the notification type (e.g., "ReviewAssigned", "ReviewCompleted").
    */
    get Name(): string {
        return this.Get('Name');
    }
    set Name(value: string) {
        this.Set('Name', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Description of when this notification is sent.
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: DefaultEnabled
    * * Display Name: Default Enabled
    * * SQL Data Type: bit
    * * Default Value: 1
    * * Description: Whether this notification type is enabled by default for new users.
    */
    get DefaultEnabled(): boolean {
        return this.Get('DefaultEnabled');
    }
    set DefaultEnabled(value: boolean) {
        this.Set('DefaultEnabled', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: nvarchar(20)
    * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Canceled
    *   * Pending
    * * Description: Current status of the notification type.
    */
    get Status(): 'Active' | 'Canceled' | 'Pending' {
        return this.Get('Status');
    }
    set Status(value: 'Active' | 'Canceled' | 'Pending') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Notifications - strongly typed entity sub-class
 * * Schema: FeedbackPlatform
 * * Base Table: Notification
 * * Base View: vwNotifications
 * * @description Individual notification instances sent to users.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Notifications')
export class FeedbackPlatformNotificationEntity extends BaseEntity<FeedbackPlatformNotificationEntityType> {
    /**
    * Loads the Notifications record from the database
    * @param ID: string - primary key value to load the Notifications record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof FeedbackPlatformNotificationEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: RecipientUserID
    * * Display Name: Recipient User ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Users (vwUsers.ID)
    */
    get RecipientUserID(): string {
        return this.Get('RecipientUserID');
    }
    set RecipientUserID(value: string) {
        this.Set('RecipientUserID', value);
    }

    /**
    * * Field Name: NotificationTypeID
    * * Display Name: Notification Type ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Notification Types (vwNotificationTypes.ID)
    */
    get NotificationTypeID(): string {
        return this.Get('NotificationTypeID');
    }
    set NotificationTypeID(value: string) {
        this.Set('NotificationTypeID', value);
    }

    /**
    * * Field Name: Title
    * * Display Name: Title
    * * SQL Data Type: nvarchar(255)
    * * Description: Title or subject of the notification.
    */
    get Title(): string {
        return this.Get('Title');
    }
    set Title(value: string) {
        this.Set('Title', value);
    }

    /**
    * * Field Name: Message
    * * Display Name: Message
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Content of the notification message.
    */
    get Message(): string {
        return this.Get('Message');
    }
    set Message(value: string) {
        this.Set('Message', value);
    }

    /**
    * * Field Name: RelatedEntityType
    * * Display Name: Related Entity Type
    * * SQL Data Type: nvarchar(50)
    * * Value List Type: List
    * * Possible Values 
    *   * Conversation
    *   * Presentation
    *   * Review
    *   * ReviewAssignment
    * * Description: Type of entity this notification relates to.
    */
    get RelatedEntityType(): 'Conversation' | 'Presentation' | 'Review' | 'ReviewAssignment' | null {
        return this.Get('RelatedEntityType');
    }
    set RelatedEntityType(value: 'Conversation' | 'Presentation' | 'Review' | 'ReviewAssignment' | null) {
        this.Set('RelatedEntityType', value);
    }

    /**
    * * Field Name: RelatedEntityID
    * * Display Name: Related Entity ID
    * * SQL Data Type: uniqueidentifier
    * * Description: ID of the related entity.
    */
    get RelatedEntityID(): string | null {
        return this.Get('RelatedEntityID');
    }
    set RelatedEntityID(value: string | null) {
        this.Set('RelatedEntityID', value);
    }

    /**
    * * Field Name: SentDate
    * * Display Name: Sent Date
    * * SQL Data Type: datetimeoffset
    * * Description: When the notification was sent to the user.
    */
    get SentDate(): Date | null {
        return this.Get('SentDate');
    }
    set SentDate(value: Date | null) {
        this.Set('SentDate', value);
    }

    /**
    * * Field Name: ReadDate
    * * Display Name: Read Date
    * * SQL Data Type: datetimeoffset
    * * Description: When the user read/viewed the notification.
    */
    get ReadDate(): Date | null {
        return this.Get('ReadDate');
    }
    set ReadDate(value: Date | null) {
        this.Set('ReadDate', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: nvarchar(20)
    * * Default Value: Pending
    * * Value List Type: List
    * * Possible Values 
    *   * Canceled
    *   * Failed
    *   * Pending
    *   * Read
    *   * Sent
    * * Description: Current delivery status of the notification.
    */
    get Status(): 'Canceled' | 'Failed' | 'Pending' | 'Read' | 'Sent' {
        return this.Get('Status');
    }
    set Status(value: 'Canceled' | 'Failed' | 'Pending' | 'Read' | 'Sent') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: DeliveryMethod
    * * Display Name: Delivery Method
    * * SQL Data Type: nvarchar(20)
    * * Value List Type: List
    * * Possible Values 
    *   * Both
    *   * Email
    *   * InApp
    * * Description: Method used to deliver the notification.
    */
    get DeliveryMethod(): 'Both' | 'Email' | 'InApp' | null {
        return this.Get('DeliveryMethod');
    }
    set DeliveryMethod(value: 'Both' | 'Email' | 'InApp' | null) {
        this.Set('DeliveryMethod', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: RecipientUser
    * * Display Name: Recipient User
    * * SQL Data Type: nvarchar(100)
    */
    get RecipientUser(): string {
        return this.Get('RecipientUser');
    }

    /**
    * * Field Name: NotificationType
    * * Display Name: Notification Type
    * * SQL Data Type: nvarchar(100)
    */
    get NotificationType(): string {
        return this.Get('NotificationType');
    }
}


/**
 * Other Names - strongly typed entity sub-class
 * * Schema: ORCID
 * * Base Table: OtherName
 * * Base View: vwOtherNames
 * * @description A name a person is also known by (person.other-names).
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Other Names')
export class ORCIDOtherNameEntity extends BaseEntity<ORCIDOtherNameEntityType> {
    /**
    * Loads the Other Names record from the database
    * @param ID: string - primary key value to load the Other Names record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof ORCIDOtherNameEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ORCIDRecordID
    * * Display Name: ORCID Record ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Records (vwRecords.ID)
    */
    get ORCIDRecordID(): string {
        return this.Get('ORCIDRecordID');
    }
    set ORCIDRecordID(value: string) {
        this.Set('ORCIDRecordID', value);
    }

    /**
    * * Field Name: PutCode
    * * Display Name: Put Code
    * * SQL Data Type: bigint
    */
    get PutCode(): number {
        return this.Get('PutCode');
    }
    set PutCode(value: number) {
        this.Set('PutCode', value);
    }

    /**
    * * Field Name: Content
    * * Display Name: Content
    * * SQL Data Type: nvarchar(500)
    */
    get Content(): string | null {
        return this.Get('Content');
    }
    set Content(value: string | null) {
        this.Set('Content', value);
    }

    /**
    * * Field Name: DisplayIndex
    * * Display Name: Display Index
    * * SQL Data Type: int
    */
    get DisplayIndex(): number | null {
        return this.Get('DisplayIndex');
    }
    set DisplayIndex(value: number | null) {
        this.Set('DisplayIndex', value);
    }

    /**
    * * Field Name: Visibility
    * * Display Name: Visibility
    * * SQL Data Type: nvarchar(20)
    */
    get Visibility(): string | null {
        return this.Get('Visibility');
    }
    set Visibility(value: string | null) {
        this.Set('Visibility', value);
    }

    /**
    * * Field Name: SourceName
    * * Display Name: Source Name
    * * SQL Data Type: nvarchar(255)
    */
    get SourceName(): string | null {
        return this.Get('SourceName');
    }
    set SourceName(value: string | null) {
        this.Set('SourceName', value);
    }

    /**
    * * Field Name: SourceOrcid
    * * Display Name: Source Orcid
    * * SQL Data Type: nvarchar(19)
    */
    get SourceOrcid(): string | null {
        return this.Get('SourceOrcid');
    }
    set SourceOrcid(value: string | null) {
        this.Set('SourceOrcid', value);
    }

    /**
    * * Field Name: OrcidCreatedDate
    * * Display Name: Orcid Created Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidCreatedDate(): Date | null {
        return this.Get('OrcidCreatedDate');
    }
    set OrcidCreatedDate(value: Date | null) {
        this.Set('OrcidCreatedDate', value);
    }

    /**
    * * Field Name: OrcidLastModifiedDate
    * * Display Name: Orcid Last Modified Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidLastModifiedDate(): Date | null {
        return this.Get('OrcidLastModifiedDate');
    }
    set OrcidLastModifiedDate(value: Date | null) {
        this.Set('OrcidLastModifiedDate', value);
    }

    /**
    * * Field Name: RawJSON
    * * Display Name: Raw JSON
    * * SQL Data Type: nvarchar(MAX)
    */
    get RawJSON(): string | null {
        return this.Get('RawJSON');
    }
    set RawJSON(value: string | null) {
        this.Set('RawJSON', value);
    }

    /**
    * * Field Name: __mj_integration_SyncStatus
    * * Display Name: Mj Integration Sync Status
    * * SQL Data Type: nvarchar(50)
    * * Default Value: Active
    */
    get __mj_integration_SyncStatus(): string {
        return this.Get('__mj_integration_SyncStatus');
    }
    set __mj_integration_SyncStatus(value: string) {
        this.Set('__mj_integration_SyncStatus', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedAt
    * * Display Name: Mj Integration Last Synced At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastSyncedAt(): Date | null {
        return this.Get('__mj_integration_LastSyncedAt');
    }
    set __mj_integration_LastSyncedAt(value: Date | null) {
        this.Set('__mj_integration_LastSyncedAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedSnapshot
    * * Display Name: Mj Integration Last Synced Snapshot
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_LastSyncedSnapshot(): string | null {
        return this.Get('__mj_integration_LastSyncedSnapshot');
    }
    set __mj_integration_LastSyncedSnapshot(value: string | null) {
        this.Set('__mj_integration_LastSyncedSnapshot', value);
    }

    /**
    * * Field Name: __mj_integration_SyncMessage
    * * Display Name: Mj Integration Sync Message
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_SyncMessage(): string | null {
        return this.Get('__mj_integration_SyncMessage');
    }
    set __mj_integration_SyncMessage(value: string | null) {
        this.Set('__mj_integration_SyncMessage', value);
    }

    /**
    * * Field Name: __mj_integration_ContentHash
    * * Display Name: Mj Integration Content Hash
    * * SQL Data Type: nvarchar(64)
    */
    get __mj_integration_ContentHash(): string | null {
        return this.Get('__mj_integration_ContentHash');
    }
    set __mj_integration_ContentHash(value: string | null) {
        this.Set('__mj_integration_ContentHash', value);
    }

    /**
    * * Field Name: __mj_integration_CustomOverflow
    * * Display Name: Mj Integration Custom Overflow
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_CustomOverflow(): string | null {
        return this.Get('__mj_integration_CustomOverflow');
    }
    set __mj_integration_CustomOverflow(value: string | null) {
        this.Set('__mj_integration_CustomOverflow', value);
    }

    /**
    * * Field Name: __mj_integration_ExternalVersion
    * * Display Name: Mj Integration External Version
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_ExternalVersion(): string | null {
        return this.Get('__mj_integration_ExternalVersion');
    }
    set __mj_integration_ExternalVersion(value: string | null) {
        this.Set('__mj_integration_ExternalVersion', value);
    }

    /**
    * * Field Name: __mj_integration_LastSeenModifiedValue
    * * Display Name: Mj Integration Last Seen Modified Value
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_LastSeenModifiedValue(): string | null {
        return this.Get('__mj_integration_LastSeenModifiedValue');
    }
    set __mj_integration_LastSeenModifiedValue(value: string | null) {
        this.Set('__mj_integration_LastSeenModifiedValue', value);
    }

    /**
    * * Field Name: __mj_integration_LastReconciledAt
    * * Display Name: Mj Integration Last Reconciled At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastReconciledAt(): Date | null {
        return this.Get('__mj_integration_LastReconciledAt');
    }
    set __mj_integration_LastReconciledAt(value: Date | null) {
        this.Set('__mj_integration_LastReconciledAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastWriterDirection
    * * Display Name: Mj Integration Last Writer Direction
    * * SQL Data Type: nvarchar(10)
    */
    get __mj_integration_LastWriterDirection(): string | null {
        return this.Get('__mj_integration_LastWriterDirection');
    }
    set __mj_integration_LastWriterDirection(value: string | null) {
        this.Set('__mj_integration_LastWriterDirection', value);
    }

    /**
    * * Field Name: __mj_integration_IsTombstoned
    * * Display Name: Mj Integration Is Tombstoned
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get __mj_integration_IsTombstoned(): boolean {
        return this.Get('__mj_integration_IsTombstoned');
    }
    set __mj_integration_IsTombstoned(value: boolean) {
        this.Set('__mj_integration_IsTombstoned', value);
    }

    /**
    * * Field Name: __mj_integration_DeletedDetectedAt
    * * Display Name: Mj Integration Deleted Detected At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_DeletedDetectedAt(): Date | null {
        return this.Get('__mj_integration_DeletedDetectedAt');
    }
    set __mj_integration_DeletedDetectedAt(value: Date | null) {
        this.Set('__mj_integration_DeletedDetectedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Participants - strongly typed entity sub-class
 * * Schema: FeedbackPlatform
 * * Base Table: Participant
 * * Base View: vwParticipants
 * * @description Per-season attributes of an OEP participant (presenter career stage, institution shown to reviewers/nominators/committees).
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Participants')
export class FeedbackPlatformParticipantEntity extends BaseEntity<FeedbackPlatformParticipantEntityType> {
    /**
    * Loads the Participants record from the database
    * @param ID: string - primary key value to load the Participants record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof FeedbackPlatformParticipantEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: SeasonID
    * * Display Name: Season ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Seasons (vwSeasons.ID)
    */
    get SeasonID(): string {
        return this.Get('SeasonID');
    }
    set SeasonID(value: string) {
        this.Set('SeasonID', value);
    }

    /**
    * * Field Name: UserID
    * * Display Name: User ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Users (vwUsers.ID)
    */
    get UserID(): string {
        return this.Get('UserID');
    }
    set UserID(value: string) {
        this.Set('UserID', value);
    }

    /**
    * * Field Name: Institution
    * * Display Name: Institution
    * * SQL Data Type: nvarchar(255)
    */
    get Institution(): string | null {
        return this.Get('Institution');
    }
    set Institution(value: string | null) {
        this.Set('Institution', value);
    }

    /**
    * * Field Name: CareerStage
    * * Display Name: Career Stage
    * * SQL Data Type: nvarchar(40)
    * * Value List Type: List
    * * Possible Values 
    *   * Early career professional
    *   * Master’s student
    *   * PhD student
    *   * Postdoctoral researcher
    *   * Undergraduate
    */
    get CareerStage(): 'Early career professional' | 'Master’s student' | 'PhD student' | 'Postdoctoral researcher' | 'Undergraduate' | null {
        return this.Get('CareerStage');
    }
    set CareerStage(value: 'Early career professional' | 'Master’s student' | 'PhD student' | 'Postdoctoral researcher' | 'Undergraduate' | null) {
        this.Set('CareerStage', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Season
    * * Display Name: Season
    * * SQL Data Type: nvarchar(200)
    */
    get Season(): string {
        return this.Get('Season');
    }

    /**
    * * Field Name: User
    * * Display Name: User
    * * SQL Data Type: nvarchar(100)
    */
    get User(): string {
        return this.Get('User');
    }
}


/**
 * Partner Token Users - strongly typed entity sub-class
 * * Schema: MAPS
 * * Base Table: PartnerTokenUser
 * * Base View: vwPartnerTokenUsers
 * * @description Maps an MJ-authenticated user (magic-link recipient or internal staff), keyed by lowercased email, to the MAPS.PartnerToken that scopes their MAPS session. Used by mapsSessionMiddleware when MAPS is hosted inside MJExplorer: the signed-in MJ user's email resolves to a PartnerToken here, supplying the tokenId + companyId that all existing partner-scoped resolvers expect. An admin associates an invited recruiter email with a partner company's PartnerToken by inserting a row here.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Partner Token Users')
export class MAPSPartnerTokenUserEntity extends BaseEntity<MAPSPartnerTokenUserEntityType> {
    /**
    * Loads the Partner Token Users record from the database
    * @param ID: string - primary key value to load the Partner Token Users record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof MAPSPartnerTokenUserEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: PartnerTokenID
    * * Display Name: Partner Token ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Partner Tokens (vwPartnerTokens.ID)
    * * Description: Foreign key to MAPS.PartnerToken. The partner-scoping key (tokenId) the resolved MJ user's MAPS session is bound to; its CompanyID supplies the session companyId.
    */
    get PartnerTokenID(): string {
        return this.Get('PartnerTokenID');
    }
    set PartnerTokenID(value: string) {
        this.Set('PartnerTokenID', value);
    }

    /**
    * * Field Name: Email
    * * Display Name: Email
    * * SQL Data Type: nvarchar(80)
    * * Description: Magic-link recipient / staff email, stored lowercased. Unique. The server lowercases the inbound MJ email before lookup so matching is case-insensitive.
    */
    get Email(): string {
        return this.Get('Email');
    }
    set Email(value: string) {
        this.Set('Email', value);
    }

    /**
    * * Field Name: UserID
    * * Display Name: User ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Users (vwUsers.ID)
    * * Description: Optional FK to __mj.User. Nullable so an admin can pre-provision the mapping from just the invited email before the user record exists; populated once the invitee has signed in.
    */
    get UserID(): string | null {
        return this.Get('UserID');
    }
    set UserID(value: string | null) {
        this.Set('UserID', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: User
    * * Display Name: User
    * * SQL Data Type: nvarchar(100)
    */
    get User(): string | null {
        return this.Get('User');
    }
}


/**
 * Partner Tokens - strongly typed entity sub-class
 * * Schema: MAPS
 * * Base Table: PartnerToken
 * * Base View: vwPartnerTokens
 * * @description Per-company magic-link credentials for MAPS partner access. One row per issued magic key; the plaintext key is never stored after issuance — only sha256(key) in TokenHash. IsAdmin=1 marks AGU-staff admin tokens (still associated with AGU's own Company row).
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Partner Tokens')
export class MAPSPartnerTokenEntity extends BaseEntity<MAPSPartnerTokenEntityType> {
    /**
    * Loads the Partner Tokens record from the database
    * @param ID: string - primary key value to load the Partner Tokens record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof MAPSPartnerTokenEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * Validate() method override for Partner Tokens entity. This is an auto-generated method that invokes the generated validators for this entity for the following fields:
    * * Table-Level: If an expiration date is specified, it must be later than the issue date.
    * @public
    * @method
    * @override
    */
    public override Validate(): ValidationResult {
        const result = super.Validate();
        this.ValidateExpiresAtAfterIssuedAt(result);
        result.Success = result.Success && (result.Errors.length === 0);

        return result;
    }

    /**
    * If an expiration date is specified, it must be later than the issue date.
    * @param result - the ValidationResult object to add any errors or warnings to
    * @public
    * @method
    */
    public ValidateExpiresAtAfterIssuedAt(result: ValidationResult) {
    	if (this.ExpiresAt != null && this.IssuedAt != null) {
    		if (this.ExpiresAt.getTime() <= this.IssuedAt.getTime()) {
    			result.Errors.push(new ValidationErrorInfo(
    				"ExpiresAt",
    				"The expiration date must be after the issue date.",
    				this.ExpiresAt,
    				ValidationErrorType.Failure
    			));
    		}
    	}
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    * * Description: Foreign key to __mj.Company. Single source of truth for partner display name and metadata.
    */
    get CompanyID(): string {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: TokenHash
    * * Display Name: Token Hash
    * * SQL Data Type: char(64)
    * * Description: sha256 hex of the plaintext magic key. Lookups hash the inbound key and compare.
    */
    get TokenHash(): string {
        return this.Get('TokenHash');
    }
    set TokenHash(value: string) {
        this.Set('TokenHash', value);
    }

    /**
    * * Field Name: TokenLabel
    * * Display Name: Token Label
    * * SQL Data Type: nvarchar(40)
    * * Description: Optional human-readable label (e.g., "acme-q3-2026") for ops/support identification.
    */
    get TokenLabel(): string | null {
        return this.Get('TokenLabel');
    }
    set TokenLabel(value: string | null) {
        this.Set('TokenLabel', value);
    }

    /**
    * * Field Name: IsAdmin
    * * Display Name: Is Admin
    * * SQL Data Type: bit
    * * Default Value: 0
    * * Description: 1 = AGU-staff admin token (gates /admin/dashboard via requireMapsAdmin). 0 = standard partner token.
    */
    get IsAdmin(): boolean {
        return this.Get('IsAdmin');
    }
    set IsAdmin(value: boolean) {
        this.Set('IsAdmin', value);
    }

    /**
    * * Field Name: IssuedAt
    * * Display Name: Issued At
    * * SQL Data Type: datetimeoffset
    * * Default Value: sysdatetimeoffset()
    * * Description: When the token was issued.
    */
    get IssuedAt(): Date {
        return this.Get('IssuedAt');
    }
    set IssuedAt(value: Date) {
        this.Set('IssuedAt', value);
    }

    /**
    * * Field Name: ExpiresAt
    * * Display Name: Expires At
    * * SQL Data Type: datetimeoffset
    * * Description: Optional expiry. NULL means the token never expires; resolvers also check RevokedAt independently.
    */
    get ExpiresAt(): Date | null {
        return this.Get('ExpiresAt');
    }
    set ExpiresAt(value: Date | null) {
        this.Set('ExpiresAt', value);
    }

    /**
    * * Field Name: RevokedAt
    * * Display Name: Revoked At
    * * SQL Data Type: datetimeoffset
    * * Description: Set when the token has been revoked. Resolved sessions are rejected immediately when this is non-NULL.
    */
    get RevokedAt(): Date | null {
        return this.Get('RevokedAt');
    }
    set RevokedAt(value: Date | null) {
        this.Set('RevokedAt', value);
    }

    /**
    * * Field Name: LastUsedAt
    * * Display Name: Last Used At
    * * SQL Data Type: datetimeoffset
    * * Description: Last time mapsAccessValidate accepted this token. Touched on every successful magic-link redemption.
    */
    get LastUsedAt(): Date | null {
        return this.Get('LastUsedAt');
    }
    set LastUsedAt(value: Date | null) {
        this.Set('LastUsedAt', value);
    }

    /**
    * * Field Name: SoftSearchCapPerMonth
    * * Display Name: Soft Search Cap Per Month
    * * SQL Data Type: int
    * * Description: Soft per-month search cap. NULL = uncapped. Exceeding the cap logs a warning but does not block. Ignored for admin tokens.
    */
    get SoftSearchCapPerMonth(): number | null {
        return this.Get('SoftSearchCapPerMonth');
    }
    set SoftSearchCapPerMonth(value: number | null) {
        this.Set('SoftSearchCapPerMonth', value);
    }

    /**
    * * Field Name: Notes
    * * Display Name: Notes
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Free-form operational notes (e.g., issuance context, support handoff). Not surfaced to partners.
    */
    get Notes(): string | null {
        return this.Get('Notes');
    }
    set Notes(value: string | null) {
        this.Set('Notes', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string {
        return this.Get('Company');
    }
}


/**
 * Patents - strongly typed entity sub-class
 * * Schema: CoreSignal
 * * Base Table: Patent
 * * Base View: vwPatents
 * * @description A patent from a CoreSignal profile.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Patents')
export class CoreSignalPatentEntity extends BaseEntity<CoreSignalPatentEntityType> {
    /**
    * Loads the Patents record from the database
    * @param ID: string - primary key value to load the Patents record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof CoreSignalPatentEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ProfileID
    * * Display Name: Profile ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Profiles (vwProfiles.ID)
    */
    get ProfileID(): string {
        return this.Get('ProfileID');
    }
    set ProfileID(value: string) {
        this.Set('ProfileID', value);
    }

    /**
    * * Field Name: OrderInProfile
    * * Display Name: Order In Profile
    * * SQL Data Type: int
    */
    get OrderInProfile(): number {
        return this.Get('OrderInProfile');
    }
    set OrderInProfile(value: number) {
        this.Set('OrderInProfile', value);
    }

    /**
    * * Field Name: Title
    * * Display Name: Title
    * * SQL Data Type: nvarchar(1000)
    */
    get Title(): string | null {
        return this.Get('Title');
    }
    set Title(value: string | null) {
        this.Set('Title', value);
    }

    /**
    * * Field Name: PatentNumber
    * * Display Name: Patent Number
    * * SQL Data Type: nvarchar(255)
    */
    get PatentNumber(): string | null {
        return this.Get('PatentNumber');
    }
    set PatentNumber(value: string | null) {
        this.Set('PatentNumber', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: nvarchar(100)
    */
    get Status(): string | null {
        return this.Get('Status');
    }
    set Status(value: string | null) {
        this.Set('Status', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(MAX)
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: PatentUrl
    * * Display Name: Patent Url
    * * SQL Data Type: nvarchar(2000)
    */
    get PatentUrl(): string | null {
        return this.Get('PatentUrl');
    }
    set PatentUrl(value: string | null) {
        this.Set('PatentUrl', value);
    }

    /**
    * * Field Name: DateYear
    * * Display Name: Date Year
    * * SQL Data Type: smallint
    */
    get DateYear(): number | null {
        return this.Get('DateYear');
    }
    set DateYear(value: number | null) {
        this.Set('DateYear', value);
    }

    /**
    * * Field Name: DateMonth
    * * Display Name: Date Month
    * * SQL Data Type: tinyint
    */
    get DateMonth(): number | null {
        return this.Get('DateMonth');
    }
    set DateMonth(value: number | null) {
        this.Set('DateMonth', value);
    }

    /**
    * * Field Name: __mj_integration_SyncStatus
    * * Display Name: Mj Integration Sync Status
    * * SQL Data Type: nvarchar(50)
    * * Default Value: Active
    */
    get __mj_integration_SyncStatus(): string {
        return this.Get('__mj_integration_SyncStatus');
    }
    set __mj_integration_SyncStatus(value: string) {
        this.Set('__mj_integration_SyncStatus', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedAt
    * * Display Name: Mj Integration Last Synced At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastSyncedAt(): Date | null {
        return this.Get('__mj_integration_LastSyncedAt');
    }
    set __mj_integration_LastSyncedAt(value: Date | null) {
        this.Set('__mj_integration_LastSyncedAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedSnapshot
    * * Display Name: Mj Integration Last Synced Snapshot
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_LastSyncedSnapshot(): string | null {
        return this.Get('__mj_integration_LastSyncedSnapshot');
    }
    set __mj_integration_LastSyncedSnapshot(value: string | null) {
        this.Set('__mj_integration_LastSyncedSnapshot', value);
    }

    /**
    * * Field Name: __mj_integration_SyncMessage
    * * Display Name: Mj Integration Sync Message
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_SyncMessage(): string | null {
        return this.Get('__mj_integration_SyncMessage');
    }
    set __mj_integration_SyncMessage(value: string | null) {
        this.Set('__mj_integration_SyncMessage', value);
    }

    /**
    * * Field Name: __mj_integration_ContentHash
    * * Display Name: Mj Integration Content Hash
    * * SQL Data Type: nvarchar(64)
    */
    get __mj_integration_ContentHash(): string | null {
        return this.Get('__mj_integration_ContentHash');
    }
    set __mj_integration_ContentHash(value: string | null) {
        this.Set('__mj_integration_ContentHash', value);
    }

    /**
    * * Field Name: __mj_integration_CustomOverflow
    * * Display Name: Mj Integration Custom Overflow
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_CustomOverflow(): string | null {
        return this.Get('__mj_integration_CustomOverflow');
    }
    set __mj_integration_CustomOverflow(value: string | null) {
        this.Set('__mj_integration_CustomOverflow', value);
    }

    /**
    * * Field Name: __mj_integration_ExternalVersion
    * * Display Name: Mj Integration External Version
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_ExternalVersion(): string | null {
        return this.Get('__mj_integration_ExternalVersion');
    }
    set __mj_integration_ExternalVersion(value: string | null) {
        this.Set('__mj_integration_ExternalVersion', value);
    }

    /**
    * * Field Name: __mj_integration_LastSeenModifiedValue
    * * Display Name: Mj Integration Last Seen Modified Value
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_LastSeenModifiedValue(): string | null {
        return this.Get('__mj_integration_LastSeenModifiedValue');
    }
    set __mj_integration_LastSeenModifiedValue(value: string | null) {
        this.Set('__mj_integration_LastSeenModifiedValue', value);
    }

    /**
    * * Field Name: __mj_integration_LastReconciledAt
    * * Display Name: Mj Integration Last Reconciled At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastReconciledAt(): Date | null {
        return this.Get('__mj_integration_LastReconciledAt');
    }
    set __mj_integration_LastReconciledAt(value: Date | null) {
        this.Set('__mj_integration_LastReconciledAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastWriterDirection
    * * Display Name: Mj Integration Last Writer Direction
    * * SQL Data Type: nvarchar(10)
    */
    get __mj_integration_LastWriterDirection(): string | null {
        return this.Get('__mj_integration_LastWriterDirection');
    }
    set __mj_integration_LastWriterDirection(value: string | null) {
        this.Set('__mj_integration_LastWriterDirection', value);
    }

    /**
    * * Field Name: __mj_integration_IsTombstoned
    * * Display Name: Mj Integration Is Tombstoned
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get __mj_integration_IsTombstoned(): boolean {
        return this.Get('__mj_integration_IsTombstoned');
    }
    set __mj_integration_IsTombstoned(value: boolean) {
        this.Set('__mj_integration_IsTombstoned', value);
    }

    /**
    * * Field Name: __mj_integration_DeletedDetectedAt
    * * Display Name: Mj Integration Deleted Detected At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_DeletedDetectedAt(): Date | null {
        return this.Get('__mj_integration_DeletedDetectedAt');
    }
    set __mj_integration_DeletedDetectedAt(value: Date | null) {
        this.Set('__mj_integration_DeletedDetectedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Peer Reviews - strongly typed entity sub-class
 * * Schema: ORCID
 * * Base Table: PeerReview
 * * Base View: vwPeerReviews
 * * @description A peer-review activity (peer-reviews section): reviewer role, convening org, and the reviewed subject.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Peer Reviews')
export class ORCIDPeerReviewEntity extends BaseEntity<ORCIDPeerReviewEntityType> {
    /**
    * Loads the Peer Reviews record from the database
    * @param ID: string - primary key value to load the Peer Reviews record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof ORCIDPeerReviewEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ORCIDRecordID
    * * Display Name: ORCID Record ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Records (vwRecords.ID)
    */
    get ORCIDRecordID(): string {
        return this.Get('ORCIDRecordID');
    }
    set ORCIDRecordID(value: string) {
        this.Set('ORCIDRecordID', value);
    }

    /**
    * * Field Name: PutCode
    * * Display Name: Put Code
    * * SQL Data Type: bigint
    */
    get PutCode(): number {
        return this.Get('PutCode');
    }
    set PutCode(value: number) {
        this.Set('PutCode', value);
    }

    /**
    * * Field Name: ReviewerRole
    * * Display Name: Reviewer Role
    * * SQL Data Type: nvarchar(50)
    */
    get ReviewerRole(): string | null {
        return this.Get('ReviewerRole');
    }
    set ReviewerRole(value: string | null) {
        this.Set('ReviewerRole', value);
    }

    /**
    * * Field Name: ReviewType
    * * Display Name: Review Type
    * * SQL Data Type: nvarchar(50)
    */
    get ReviewType(): string | null {
        return this.Get('ReviewType');
    }
    set ReviewType(value: string | null) {
        this.Set('ReviewType', value);
    }

    /**
    * * Field Name: ReviewGroupID
    * * Display Name: Review Group ID
    * * SQL Data Type: nvarchar(255)
    */
    get ReviewGroupID(): string | null {
        return this.Get('ReviewGroupID');
    }
    set ReviewGroupID(value: string | null) {
        this.Set('ReviewGroupID', value);
    }

    /**
    * * Field Name: ReviewUrl
    * * Display Name: Review Url
    * * SQL Data Type: nvarchar(2000)
    */
    get ReviewUrl(): string | null {
        return this.Get('ReviewUrl');
    }
    set ReviewUrl(value: string | null) {
        this.Set('ReviewUrl', value);
    }

    /**
    * * Field Name: CompletionYear
    * * Display Name: Completion Year
    * * SQL Data Type: smallint
    */
    get CompletionYear(): number | null {
        return this.Get('CompletionYear');
    }
    set CompletionYear(value: number | null) {
        this.Set('CompletionYear', value);
    }

    /**
    * * Field Name: CompletionMonth
    * * Display Name: Completion Month
    * * SQL Data Type: tinyint
    */
    get CompletionMonth(): number | null {
        return this.Get('CompletionMonth');
    }
    set CompletionMonth(value: number | null) {
        this.Set('CompletionMonth', value);
    }

    /**
    * * Field Name: CompletionDay
    * * Display Name: Completion Day
    * * SQL Data Type: tinyint
    */
    get CompletionDay(): number | null {
        return this.Get('CompletionDay');
    }
    set CompletionDay(value: number | null) {
        this.Set('CompletionDay', value);
    }

    /**
    * * Field Name: ConveningOrgName
    * * Display Name: Convening Org Name
    * * SQL Data Type: nvarchar(500)
    */
    get ConveningOrgName(): string | null {
        return this.Get('ConveningOrgName');
    }
    set ConveningOrgName(value: string | null) {
        this.Set('ConveningOrgName', value);
    }

    /**
    * * Field Name: ConveningOrgCity
    * * Display Name: Convening Org City
    * * SQL Data Type: nvarchar(255)
    */
    get ConveningOrgCity(): string | null {
        return this.Get('ConveningOrgCity');
    }
    set ConveningOrgCity(value: string | null) {
        this.Set('ConveningOrgCity', value);
    }

    /**
    * * Field Name: ConveningOrgRegion
    * * Display Name: Convening Org Region
    * * SQL Data Type: nvarchar(255)
    */
    get ConveningOrgRegion(): string | null {
        return this.Get('ConveningOrgRegion');
    }
    set ConveningOrgRegion(value: string | null) {
        this.Set('ConveningOrgRegion', value);
    }

    /**
    * * Field Name: ConveningOrgCountry
    * * Display Name: Convening Org Country
    * * SQL Data Type: nvarchar(10)
    */
    get ConveningOrgCountry(): string | null {
        return this.Get('ConveningOrgCountry');
    }
    set ConveningOrgCountry(value: string | null) {
        this.Set('ConveningOrgCountry', value);
    }

    /**
    * * Field Name: ConveningOrgDisambiguatedID
    * * Display Name: Convening Org Disambiguated ID
    * * SQL Data Type: nvarchar(255)
    */
    get ConveningOrgDisambiguatedID(): string | null {
        return this.Get('ConveningOrgDisambiguatedID');
    }
    set ConveningOrgDisambiguatedID(value: string | null) {
        this.Set('ConveningOrgDisambiguatedID', value);
    }

    /**
    * * Field Name: ConveningOrgDisambiguationSource
    * * Display Name: Convening Org Disambiguation Source
    * * SQL Data Type: nvarchar(50)
    */
    get ConveningOrgDisambiguationSource(): string | null {
        return this.Get('ConveningOrgDisambiguationSource');
    }
    set ConveningOrgDisambiguationSource(value: string | null) {
        this.Set('ConveningOrgDisambiguationSource', value);
    }

    /**
    * * Field Name: SubjectType
    * * Display Name: Subject Type
    * * SQL Data Type: nvarchar(100)
    */
    get SubjectType(): string | null {
        return this.Get('SubjectType');
    }
    set SubjectType(value: string | null) {
        this.Set('SubjectType', value);
    }

    /**
    * * Field Name: SubjectName
    * * Display Name: Subject Name
    * * SQL Data Type: nvarchar(2000)
    */
    get SubjectName(): string | null {
        return this.Get('SubjectName');
    }
    set SubjectName(value: string | null) {
        this.Set('SubjectName', value);
    }

    /**
    * * Field Name: SubjectContainerName
    * * Display Name: Subject Container Name
    * * SQL Data Type: nvarchar(1000)
    */
    get SubjectContainerName(): string | null {
        return this.Get('SubjectContainerName');
    }
    set SubjectContainerName(value: string | null) {
        this.Set('SubjectContainerName', value);
    }

    /**
    * * Field Name: SubjectUrl
    * * Display Name: Subject Url
    * * SQL Data Type: nvarchar(2000)
    */
    get SubjectUrl(): string | null {
        return this.Get('SubjectUrl');
    }
    set SubjectUrl(value: string | null) {
        this.Set('SubjectUrl', value);
    }

    /**
    * * Field Name: SubjectExternalIDType
    * * Display Name: Subject External ID Type
    * * SQL Data Type: nvarchar(100)
    */
    get SubjectExternalIDType(): string | null {
        return this.Get('SubjectExternalIDType');
    }
    set SubjectExternalIDType(value: string | null) {
        this.Set('SubjectExternalIDType', value);
    }

    /**
    * * Field Name: SubjectExternalIDValue
    * * Display Name: Subject External ID Value
    * * SQL Data Type: nvarchar(500)
    */
    get SubjectExternalIDValue(): string | null {
        return this.Get('SubjectExternalIDValue');
    }
    set SubjectExternalIDValue(value: string | null) {
        this.Set('SubjectExternalIDValue', value);
    }

    /**
    * * Field Name: Visibility
    * * Display Name: Visibility
    * * SQL Data Type: nvarchar(20)
    */
    get Visibility(): string | null {
        return this.Get('Visibility');
    }
    set Visibility(value: string | null) {
        this.Set('Visibility', value);
    }

    /**
    * * Field Name: SourceName
    * * Display Name: Source Name
    * * SQL Data Type: nvarchar(255)
    */
    get SourceName(): string | null {
        return this.Get('SourceName');
    }
    set SourceName(value: string | null) {
        this.Set('SourceName', value);
    }

    /**
    * * Field Name: SourceOrcid
    * * Display Name: Source Orcid
    * * SQL Data Type: nvarchar(19)
    */
    get SourceOrcid(): string | null {
        return this.Get('SourceOrcid');
    }
    set SourceOrcid(value: string | null) {
        this.Set('SourceOrcid', value);
    }

    /**
    * * Field Name: OrcidCreatedDate
    * * Display Name: Orcid Created Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidCreatedDate(): Date | null {
        return this.Get('OrcidCreatedDate');
    }
    set OrcidCreatedDate(value: Date | null) {
        this.Set('OrcidCreatedDate', value);
    }

    /**
    * * Field Name: OrcidLastModifiedDate
    * * Display Name: Orcid Last Modified Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidLastModifiedDate(): Date | null {
        return this.Get('OrcidLastModifiedDate');
    }
    set OrcidLastModifiedDate(value: Date | null) {
        this.Set('OrcidLastModifiedDate', value);
    }

    /**
    * * Field Name: RawJSON
    * * Display Name: Raw JSON
    * * SQL Data Type: nvarchar(MAX)
    */
    get RawJSON(): string | null {
        return this.Get('RawJSON');
    }
    set RawJSON(value: string | null) {
        this.Set('RawJSON', value);
    }

    /**
    * * Field Name: __mj_integration_SyncStatus
    * * Display Name: Mj Integration Sync Status
    * * SQL Data Type: nvarchar(50)
    * * Default Value: Active
    */
    get __mj_integration_SyncStatus(): string {
        return this.Get('__mj_integration_SyncStatus');
    }
    set __mj_integration_SyncStatus(value: string) {
        this.Set('__mj_integration_SyncStatus', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedAt
    * * Display Name: Mj Integration Last Synced At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastSyncedAt(): Date | null {
        return this.Get('__mj_integration_LastSyncedAt');
    }
    set __mj_integration_LastSyncedAt(value: Date | null) {
        this.Set('__mj_integration_LastSyncedAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedSnapshot
    * * Display Name: Mj Integration Last Synced Snapshot
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_LastSyncedSnapshot(): string | null {
        return this.Get('__mj_integration_LastSyncedSnapshot');
    }
    set __mj_integration_LastSyncedSnapshot(value: string | null) {
        this.Set('__mj_integration_LastSyncedSnapshot', value);
    }

    /**
    * * Field Name: __mj_integration_SyncMessage
    * * Display Name: Mj Integration Sync Message
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_SyncMessage(): string | null {
        return this.Get('__mj_integration_SyncMessage');
    }
    set __mj_integration_SyncMessage(value: string | null) {
        this.Set('__mj_integration_SyncMessage', value);
    }

    /**
    * * Field Name: __mj_integration_ContentHash
    * * Display Name: Mj Integration Content Hash
    * * SQL Data Type: nvarchar(64)
    */
    get __mj_integration_ContentHash(): string | null {
        return this.Get('__mj_integration_ContentHash');
    }
    set __mj_integration_ContentHash(value: string | null) {
        this.Set('__mj_integration_ContentHash', value);
    }

    /**
    * * Field Name: __mj_integration_CustomOverflow
    * * Display Name: Mj Integration Custom Overflow
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_CustomOverflow(): string | null {
        return this.Get('__mj_integration_CustomOverflow');
    }
    set __mj_integration_CustomOverflow(value: string | null) {
        this.Set('__mj_integration_CustomOverflow', value);
    }

    /**
    * * Field Name: __mj_integration_ExternalVersion
    * * Display Name: Mj Integration External Version
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_ExternalVersion(): string | null {
        return this.Get('__mj_integration_ExternalVersion');
    }
    set __mj_integration_ExternalVersion(value: string | null) {
        this.Set('__mj_integration_ExternalVersion', value);
    }

    /**
    * * Field Name: __mj_integration_LastSeenModifiedValue
    * * Display Name: Mj Integration Last Seen Modified Value
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_LastSeenModifiedValue(): string | null {
        return this.Get('__mj_integration_LastSeenModifiedValue');
    }
    set __mj_integration_LastSeenModifiedValue(value: string | null) {
        this.Set('__mj_integration_LastSeenModifiedValue', value);
    }

    /**
    * * Field Name: __mj_integration_LastReconciledAt
    * * Display Name: Mj Integration Last Reconciled At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastReconciledAt(): Date | null {
        return this.Get('__mj_integration_LastReconciledAt');
    }
    set __mj_integration_LastReconciledAt(value: Date | null) {
        this.Set('__mj_integration_LastReconciledAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastWriterDirection
    * * Display Name: Mj Integration Last Writer Direction
    * * SQL Data Type: nvarchar(10)
    */
    get __mj_integration_LastWriterDirection(): string | null {
        return this.Get('__mj_integration_LastWriterDirection');
    }
    set __mj_integration_LastWriterDirection(value: string | null) {
        this.Set('__mj_integration_LastWriterDirection', value);
    }

    /**
    * * Field Name: __mj_integration_IsTombstoned
    * * Display Name: Mj Integration Is Tombstoned
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get __mj_integration_IsTombstoned(): boolean {
        return this.Get('__mj_integration_IsTombstoned');
    }
    set __mj_integration_IsTombstoned(value: boolean) {
        this.Set('__mj_integration_IsTombstoned', value);
    }

    /**
    * * Field Name: __mj_integration_DeletedDetectedAt
    * * Display Name: Mj Integration Deleted Detected At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_DeletedDetectedAt(): Date | null {
        return this.Get('__mj_integration_DeletedDetectedAt');
    }
    set __mj_integration_DeletedDetectedAt(value: Date | null) {
        this.Set('__mj_integration_DeletedDetectedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * People - strongly typed entity sub-class
 * * Schema: MAPS
 * * Base Table: Person
 * * Base View: vwPeople
 * * @description Represents an AGU person eligible for the Talent Acquisition tool. Inclusion criteria: personalization data available, AGU content in the last 5 years, known career stage, and a valid email.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'People')
export class MAPSPersonEntity extends BaseEntity<MAPSPersonEntityType> {
    /**
    * Loads the People record from the database
    * @param ID: string - primary key value to load the People record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof MAPSPersonEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * Validate() method override for People entity. This is an auto-generated method that invokes the generated validators for this entity for the following fields:
    * * LastContentYear: The last content year must be a realistic year between 1900 and 2100.
    * @public
    * @method
    * @override
    */
    public override Validate(): ValidationResult {
        const result = super.Validate();
        this.ValidateLastContentYearRange(result);
        result.Success = result.Success && (result.Errors.length === 0);

        return result;
    }

    /**
    * The last content year must be a realistic year between 1900 and 2100.
    * @param result - the ValidationResult object to add any errors or warnings to
    * @public
    * @method
    */
    public ValidateLastContentYearRange(result: ValidationResult) {
    	// Check nullable field first
    	if (this.LastContentYear != null && (this.LastContentYear < 1900 || this.LastContentYear > 2100)) {
    		result.Errors.push(new ValidationErrorInfo(
    			"LastContentYear",
    			"Last content year must be between 1900 and 2100.",
    			this.LastContentYear,
    			ValidationErrorType.Failure
    		));
    	}
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: AGUPersonID
    * * Display Name: AGU Person ID
    * * SQL Data Type: uniqueidentifier
    * * Description: AGU-assigned identifier for the person. This is the same ID used by the AGU personalization algorithm and is the join key back to AGU source systems.
    */
    get AGUPersonID(): string {
        return this.Get('AGUPersonID');
    }
    set AGUPersonID(value: string) {
        this.Set('AGUPersonID', value);
    }

    /**
    * * Field Name: FirstName
    * * Display Name: First Name
    * * SQL Data Type: nvarchar(75)
    * * Description: First name of the person.
    */
    get FirstName(): string {
        return this.Get('FirstName');
    }
    set FirstName(value: string) {
        this.Set('FirstName', value);
    }

    /**
    * * Field Name: LastName
    * * Display Name: Last Name
    * * SQL Data Type: nvarchar(75)
    * * Description: Last name of the person.
    */
    get LastName(): string {
        return this.Get('LastName');
    }
    set LastName(value: string) {
        this.Set('LastName', value);
    }

    /**
    * * Field Name: Bio
    * * Display Name: Bio
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Self-provided biography text. May be empty.
    */
    get Bio(): string | null {
        return this.Get('Bio');
    }
    set Bio(value: string | null) {
        this.Set('Bio', value);
    }

    /**
    * * Field Name: JobSector
    * * Display Name: Job Sector
    * * SQL Data Type: nvarchar(75)
    * * Description: Self-reported job sector (e.g., Corporate, Non-Profit, University/College/Higher Education). May be stale - collected at signup and rarely refreshed.
    */
    get JobSector(): string | null {
        return this.Get('JobSector');
    }
    set JobSector(value: string | null) {
        this.Set('JobSector', value);
    }

    /**
    * * Field Name: JobFunction
    * * Display Name: Job Function
    * * SQL Data Type: nvarchar(50)
    * * Description: Self-reported job function (e.g., Applied Research, Teaching, Management). May be stale - collected at signup and rarely refreshed.
    */
    get JobFunction(): string | null {
        return this.Get('JobFunction');
    }
    set JobFunction(value: string | null) {
        this.Set('JobFunction', value);
    }

    /**
    * * Field Name: Email
    * * Display Name: Email
    * * SQL Data Type: nvarchar(80)
    * * Description: Validated email address. A valid email is one of the inclusion criteria for the Talent Acquisition tool.
    */
    get Email(): string {
        return this.Get('Email');
    }
    set Email(value: string) {
        this.Set('Email', value);
    }

    /**
    * * Field Name: CurrentCareerStage
    * * Display Name: Current Career Stage
    * * SQL Data Type: nvarchar(20)
    * * Value List Type: List
    * * Possible Values 
    *   * Early Career
    *   * Experienced
    *   * Mid-Career
    *   * Retired
    *   * Student
    * * Description: Current career stage. Calculated from the highest-degree date when available, otherwise self-reported.
    */
    get CurrentCareerStage(): 'Early Career' | 'Experienced' | 'Mid-Career' | 'Retired' | 'Student' | null {
        return this.Get('CurrentCareerStage');
    }
    set CurrentCareerStage(value: 'Early Career' | 'Experienced' | 'Mid-Career' | 'Retired' | 'Student' | null) {
        this.Set('CurrentCareerStage', value);
    }

    /**
    * * Field Name: ReviewCountSince2021
    * * Display Name: Review Count Since 2021
    * * SQL Data Type: int
    * * Default Value: 0
    * * Description: Number of AGU journal reviews completed by this person since 2021.
    */
    get ReviewCountSince2021(): number {
        return this.Get('ReviewCountSince2021');
    }
    set ReviewCountSince2021(value: number) {
        this.Set('ReviewCountSince2021', value);
    }

    /**
    * * Field Name: LastContentYear
    * * Display Name: Last Content Year
    * * SQL Data Type: int
    * * Description: Year of the most recent AGU content authored or contributed to by this person. Intended as a recency proxy for internal use only - not to be used as a feature in the talent acquisition algorithm.
    */
    get LastContentYear(): number | null {
        return this.Get('LastContentYear');
    }
    set LastContentYear(value: number | null) {
        this.Set('LastContentYear', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: LinkedInUrl
    * * Display Name: Linked In Url
    * * SQL Data Type: nvarchar(255)
    * * Description: AGU member's LinkedIn profile URL (e.g., https://www.linkedin.com/in/...). NULL when not provided in the personalization feed. Per AGU guidance (6-8 client meeting), surfaced as a small icon when present; absence is not flagged on the card.
    */
    get LinkedInUrl(): string | null {
        return this.Get('LinkedInUrl');
    }
    set LinkedInUrl(value: string | null) {
        this.Set('LinkedInUrl', value);
    }

    /**
    * * Field Name: OrcID
    * * Display Name: Orc ID
    * * SQL Data Type: nvarchar(50)
    * * Description: AGU member's ORCID iD (e.g., 0000-0002-1825-0097, or a full https://orcid.org/... URL). NULL when not provided in the personalization feed. Surfaced as a small icon when present; absence is not flagged on the card.
    */
    get OrcID(): string | null {
        return this.Get('OrcID');
    }
    set OrcID(value: string | null) {
        this.Set('OrcID', value);
    }
}


/**
 * Person Honor Receiveds - strongly typed entity sub-class
 * * Schema: MAPS
 * * Base Table: PersonHonorReceived
 * * Base View: vwPersonHonorReceiveds
 * * @description AGU honors received by a person.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Person Honor Receiveds')
export class MAPSPersonHonorReceivedEntity extends BaseEntity<MAPSPersonHonorReceivedEntityType> {
    /**
    * Loads the Person Honor Receiveds record from the database
    * @param ID: string - primary key value to load the Person Honor Receiveds record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof MAPSPersonHonorReceivedEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * Validate() method override for Person Honor Receiveds entity. This is an auto-generated method that invokes the generated validators for this entity for the following fields:
    * * Year: The year must be between 1900 and 2100.
    * @public
    * @method
    * @override
    */
    public override Validate(): ValidationResult {
        const result = super.Validate();
        this.ValidateYearRange(result);
        result.Success = result.Success && (result.Errors.length === 0);

        return result;
    }

    /**
    * The year must be between 1900 and 2100.
    * @param result - the ValidationResult object to add any errors or warnings to
    * @public
    * @method
    */
    public ValidateYearRange(result: ValidationResult) {
    	if (this.Year != null && (this.Year < 1900 || this.Year > 2100)) {
    		result.Errors.push(new ValidationErrorInfo(
    			"Year",
    			"Year must be between 1900 and 2100.",
    			this.Year,
    			ValidationErrorType.Failure
    		));
    	}
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: PersonID
    * * Display Name: Person ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: People (vwPeople.ID)
    * * Description: References the person who received the honor.
    */
    get PersonID(): string {
        return this.Get('PersonID');
    }
    set PersonID(value: string) {
        this.Set('PersonID', value);
    }

    /**
    * * Field Name: HonorType
    * * Display Name: Honor Type
    * * SQL Data Type: nvarchar(100)
    * * Description: Category of honor (e.g., Fellow, Medal, Award).
    */
    get HonorType(): string {
        return this.Get('HonorType');
    }
    set HonorType(value: string) {
        this.Set('HonorType', value);
    }

    /**
    * * Field Name: HonorName
    * * Display Name: Honor Name
    * * SQL Data Type: nvarchar(255)
    * * Description: Specific name of the honor.
    */
    get HonorName(): string {
        return this.Get('HonorName');
    }
    set HonorName(value: string) {
        this.Set('HonorName', value);
    }

    /**
    * * Field Name: Year
    * * Display Name: Year
    * * SQL Data Type: int
    * * Description: Year the honor was received.
    */
    get Year(): number | null {
        return this.Get('Year');
    }
    set Year(value: number | null) {
        this.Set('Year', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Person Jobs - strongly typed entity sub-class
 * * Schema: MAPS
 * * Base Table: PersonJob
 * * Base View: vwPersonJobs
 * * @description Job history records for a person, sourced from AGU. May contain duplicates and rows with missing titles or dates.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Person Jobs')
export class MAPSPersonJobEntity extends BaseEntity<MAPSPersonJobEntityType> {
    /**
    * Loads the Person Jobs record from the database
    * @param ID: string - primary key value to load the Person Jobs record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof MAPSPersonJobEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * Validate() method override for Person Jobs entity. This is an auto-generated method that invokes the generated validators for this entity for the following fields:
    * * Table-Level: The end date must be on or after the start date when both dates are provided.
    * @public
    * @method
    * @override
    */
    public override Validate(): ValidationResult {
        const result = super.Validate();
        this.ValidateEndDateOnOrAfterStartDate(result);
        result.Success = result.Success && (result.Errors.length === 0);

        return result;
    }

    /**
    * The end date must be on or after the start date when both dates are provided.
    * @param result - the ValidationResult object to add any errors or warnings to
    * @public
    * @method
    */
    public ValidateEndDateOnOrAfterStartDate(result: ValidationResult) {
    	if (this.EndDate != null && this.StartDate != null) {
    		const end = new Date(this.EndDate);
    		const start = new Date(this.StartDate);
    		if (end < start) {
    			result.Errors.push(new ValidationErrorInfo(
    				"EndDate",
    				"The end date cannot be earlier than the start date.",
    				this.EndDate,
    				ValidationErrorType.Failure
    			));
    		}
    	}
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: PersonID
    * * Display Name: Person ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: People (vwPeople.ID)
    * * Description: References the person this job belongs to.
    */
    get PersonID(): string {
        return this.Get('PersonID');
    }
    set PersonID(value: string) {
        this.Set('PersonID', value);
    }

    /**
    * * Field Name: Organization
    * * Display Name: Organization
    * * SQL Data Type: nvarchar(150)
    * * Description: Name of the employing organization.
    */
    get Organization(): string {
        return this.Get('Organization');
    }
    set Organization(value: string) {
        this.Set('Organization', value);
    }

    /**
    * * Field Name: JobTitle
    * * Display Name: Job Title
    * * SQL Data Type: nvarchar(150)
    * * Description: Job title held at the organization. May be missing in source data.
    */
    get JobTitle(): string | null {
        return this.Get('JobTitle');
    }
    set JobTitle(value: string | null) {
        this.Set('JobTitle', value);
    }

    /**
    * * Field Name: StartDate
    * * Display Name: Start Date
    * * SQL Data Type: date
    * * Description: Start date of the job. NULL when source data reported "Unknown".
    */
    get StartDate(): Date | null {
        return this.Get('StartDate');
    }
    set StartDate(value: Date | null) {
        this.Set('StartDate', value);
    }

    /**
    * * Field Name: EndDate
    * * Display Name: End Date
    * * SQL Data Type: date
    * * Description: End date of the job. NULL for current jobs (Status = Current) or when otherwise unknown.
    */
    get EndDate(): Date | null {
        return this.Get('EndDate');
    }
    set EndDate(value: Date | null) {
        this.Set('EndDate', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: nvarchar(20)
    * * Default Value: Past
    * * Value List Type: List
    * * Possible Values 
    *   * Current
    *   * Past
    *   * Unknown
    * * Description: Status of this job: Current (still held, source said "Present"), Past (ended), or Unknown.
    */
    get Status(): 'Current' | 'Past' | 'Unknown' {
        return this.Get('Status');
    }
    set Status(value: 'Current' | 'Past' | 'Unknown') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Person Qualification Facts - strongly typed entity sub-class
 * * Schema: MAPS
 * * Base Table: PersonQualificationFacts
 * * Base View: vwPersonQualificationFacts
 * * @description Persisted snapshot of MAPS.vwPersonQualificationFacts (one row per MAPS.Person; ORCID-derived structured qualification facts). Refreshed by MAPS.spRefreshPersonQualificationFacts at the end of each ORCID enrichment run. Consumed by the similarity engine's contributor pool filter (verified-first retrieval) and the MAPS resolver. Absence of a row or a fact never excludes a candidate.
 * * Primary Key: AGUPersonID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Person Qualification Facts')
export class MAPSPersonQualificationFactsEntity extends BaseEntity<MAPSPersonQualificationFactsEntityType> {
    /**
    * Loads the Person Qualification Facts record from the database
    * @param AGUPersonID: string - primary key value to load the Person Qualification Facts record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof MAPSPersonQualificationFactsEntity
    * @method
    * @override
    */
    public async Load(AGUPersonID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'AGUPersonID', Value: AGUPersonID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: AGUPersonID
    * * Display Name: AGU Person ID
    * * SQL Data Type: uniqueidentifier
    */
    get AGUPersonID(): string {
        return this.Get('AGUPersonID');
    }
    set AGUPersonID(value: string) {
        this.Set('AGUPersonID', value);
    }

    /**
    * * Field Name: DegreeLevel
    * * Display Name: Degree Level
    * * SQL Data Type: tinyint
    */
    get DegreeLevel(): number | null {
        return this.Get('DegreeLevel');
    }
    set DegreeLevel(value: number | null) {
        this.Set('DegreeLevel', value);
    }

    /**
    * * Field Name: MaxDegreeYear
    * * Display Name: Max Degree Year
    * * SQL Data Type: int
    */
    get MaxDegreeYear(): number | null {
        return this.Get('MaxDegreeYear');
    }
    set MaxDegreeYear(value: number | null) {
        this.Set('MaxDegreeYear', value);
    }

    /**
    * * Field Name: YearsExperience
    * * Display Name: Years Experience
    * * SQL Data Type: int
    */
    get YearsExperience(): number | null {
        return this.Get('YearsExperience');
    }
    set YearsExperience(value: number | null) {
        this.Set('YearsExperience', value);
    }

    /**
    * * Field Name: HasFunding
    * * Display Name: Has Funding
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get HasFunding(): boolean {
        return this.Get('HasFunding');
    }
    set HasFunding(value: boolean) {
        this.Set('HasFunding', value);
    }

    /**
    * * Field Name: HasPIFunding
    * * Display Name: Has PI Funding
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get HasPIFunding(): boolean {
        return this.Get('HasPIFunding');
    }
    set HasPIFunding(value: boolean) {
        this.Set('HasPIFunding', value);
    }

    /**
    * * Field Name: HasGrantFunding
    * * Display Name: Has Grant Funding
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get HasGrantFunding(): boolean {
        return this.Get('HasGrantFunding');
    }
    set HasGrantFunding(value: boolean) {
        this.Set('HasGrantFunding', value);
    }

    /**
    * * Field Name: HasActiveFunding
    * * Display Name: Has Active Funding
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get HasActiveFunding(): boolean {
        return this.Get('HasActiveFunding');
    }
    set HasActiveFunding(value: boolean) {
        this.Set('HasActiveFunding', value);
    }

    /**
    * * Field Name: DistinctionCount
    * * Display Name: Distinction Count
    * * SQL Data Type: int
    * * Default Value: 0
    */
    get DistinctionCount(): number {
        return this.Get('DistinctionCount');
    }
    set DistinctionCount(value: number) {
        this.Set('DistinctionCount', value);
    }

    /**
    * * Field Name: RefreshedAt
    * * Display Name: Refreshed At
    * * SQL Data Type: datetime2
    * * Default Value: sysutcdatetime()
    */
    get RefreshedAt(): Date {
        return this.Get('RefreshedAt');
    }
    set RefreshedAt(value: Date) {
        this.Set('RefreshedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Person Volunteer Activities - strongly typed entity sub-class
 * * Schema: MAPS
 * * Base Table: PersonVolunteerActivity
 * * Base View: vwPersonVolunteerActivities
 * * @description AGU volunteer service for a person. Covers committee service (2011 onward) and journal editor roles.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Person Volunteer Activities')
export class MAPSPersonVolunteerActivityEntity extends BaseEntity<MAPSPersonVolunteerActivityEntityType> {
    /**
    * Loads the Person Volunteer Activities record from the database
    * @param ID: string - primary key value to load the Person Volunteer Activities record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof MAPSPersonVolunteerActivityEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * Validate() method override for Person Volunteer Activities entity. This is an auto-generated method that invokes the generated validators for this entity for the following fields:
    * * YearEnd: The end year must be a valid year between 1900 and 2100.
    * * YearStart: The start year must be a valid year between 1900 and 2100.
    * * Table-Level: The end year must be greater than or equal to the start year.
    * @public
    * @method
    * @override
    */
    public override Validate(): ValidationResult {
        const result = super.Validate();
        this.ValidateYearEndRange(result);
        this.ValidateYearStartRange(result);
        this.ValidateYearEndAfterYearStart(result);
        result.Success = result.Success && (result.Errors.length === 0);

        return result;
    }

    /**
    * The end year must be a valid year between 1900 and 2100.
    * @param result - the ValidationResult object to add any errors or warnings to
    * @public
    * @method
    */
    public ValidateYearEndRange(result: ValidationResult) {
    	if (this.YearEnd != null && (this.YearEnd < 1900 || this.YearEnd > 2100)) {
    		result.Errors.push(new ValidationErrorInfo(
    			"YearEnd",
    			"The end year must be between 1900 and 2100.",
    			this.YearEnd,
    			ValidationErrorType.Failure
    		));
    	}
    }

    /**
    * The start year must be a valid year between 1900 and 2100.
    * @param result - the ValidationResult object to add any errors or warnings to
    * @public
    * @method
    */
    public ValidateYearStartRange(result: ValidationResult) {
    	if (this.YearStart != null && (this.YearStart < 1900 || this.YearStart > 2100)) {
    		result.Errors.push(new ValidationErrorInfo(
    			"YearStart",
    			"Start year must be between 1900 and 2100.",
    			this.YearStart,
    			ValidationErrorType.Failure
    		));
    	}
    }

    /**
    * The end year must be greater than or equal to the start year.
    * @param result - the ValidationResult object to add any errors or warnings to
    * @public
    * @method
    */
    	public ValidateYearEndAfterYearStart(result: ValidationResult) {
    		if (this.YearStart != null && this.YearEnd != null && this.YearEnd < this.YearStart) {
    			result.Errors.push(new ValidationErrorInfo(
    				"YearEnd",
    				"The end year must be greater than or equal to the start year.",
    				this.YearEnd,
    				ValidationErrorType.Failure
    			));
    		}
    	}

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: PersonID
    * * Display Name: Person ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: People (vwPeople.ID)
    * * Description: References the person this volunteer activity belongs to.
    */
    get PersonID(): string {
        return this.Get('PersonID');
    }
    set PersonID(value: string) {
        this.Set('PersonID', value);
    }

    /**
    * * Field Name: RoleDescription
    * * Display Name: Role Description
    * * SQL Data Type: nvarchar(255)
    * * Description: Description of the volunteer role (committee name or journal/editor role).
    */
    get RoleDescription(): string {
        return this.Get('RoleDescription');
    }
    set RoleDescription(value: string) {
        this.Set('RoleDescription', value);
    }

    /**
    * * Field Name: YearStart
    * * Display Name: Year Start
    * * SQL Data Type: int
    * * Description: Year the volunteer service began. NULL when source data was missing (source sentinel "0" mapped to NULL on ingest).
    */
    get YearStart(): number | null {
        return this.Get('YearStart');
    }
    set YearStart(value: number | null) {
        this.Set('YearStart', value);
    }

    /**
    * * Field Name: YearEnd
    * * Display Name: Year End
    * * SQL Data Type: int
    * * Description: Year the volunteer service ended. NULL when source data was missing or service is ongoing.
    */
    get YearEnd(): number | null {
        return this.Get('YearEnd');
    }
    set YearEnd(value: number | null) {
        this.Set('YearEnd', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Presentation Categories - strongly typed entity sub-class
 * * Schema: FeedbackPlatform
 * * Base Table: PresentationCategory
 * * Base View: vwPresentationCategories
 * * @description Categories for organizing presentation types (e.g., Poster, Oral, Lightning Talk).
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Presentation Categories')
export class FeedbackPlatformPresentationCategoryEntity extends BaseEntity<FeedbackPlatformPresentationCategoryEntityType> {
    /**
    * Loads the Presentation Categories record from the database
    * @param ID: string - primary key value to load the Presentation Categories record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof FeedbackPlatformPresentationCategoryEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: Name
    * * Display Name: Name
    * * SQL Data Type: nvarchar(255)
    * * Description: Name of the presentation category.
    */
    get Name(): string {
        return this.Get('Name');
    }
    set Name(value: string) {
        this.Set('Name', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Detailed description of the category.
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: nvarchar(20)
    * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Canceled
    *   * Pending
    * * Description: Current status of the category.
    */
    get Status(): 'Active' | 'Canceled' | 'Pending' {
        return this.Get('Status');
    }
    set Status(value: 'Active' | 'Canceled' | 'Pending') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Presentations - strongly typed entity sub-class
 * * Schema: FeedbackPlatform
 * * Base Table: Presentation
 * * Base View: vwPresentations
 * * @description Represents a presentation submitted for review by a presenter.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Presentations')
export class FeedbackPlatformPresentationEntity extends BaseEntity<FeedbackPlatformPresentationEntityType> {
    /**
    * Loads the Presentations record from the database
    * @param ID: string - primary key value to load the Presentations record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof FeedbackPlatformPresentationEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * Validate() method override for Presentations entity. This is an auto-generated method that invokes the generated validators for this entity for the following fields:
    * * FileSize: The file size must be greater than or equal to 0. This ensures that negative file sizes, which are invalid, cannot be saved.
    * @public
    * @method
    * @override
    */
    public override Validate(): ValidationResult {
        const result = super.Validate();
        this.ValidateFileSizeNonNegative(result);
        result.Success = result.Success && (result.Errors.length === 0);

        return result;
    }

    /**
    * The file size must be greater than or equal to 0. This ensures that negative file sizes, which are invalid, cannot be saved.
    * @param result - the ValidationResult object to add any errors or warnings to
    * @public
    * @method
    */
    public ValidateFileSizeNonNegative(result: ValidationResult) {
    	if (this.FileSize != null && this.FileSize < 0) {
    		result.Errors.push(new ValidationErrorInfo(
    			"FileSize",
    			"File size must be greater than or equal to 0.",
    			this.FileSize,
    			ValidationErrorType.Failure
    		));
    	}
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: Title
    * * Display Name: Title
    * * SQL Data Type: nvarchar(500)
    * * Description: Title of the presentation.
    */
    get Title(): string {
        return this.Get('Title');
    }
    set Title(value: string) {
        this.Set('Title', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Detailed description of the presentation content.
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: Abstract
    * * Display Name: Abstract
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Academic abstract of the presentation.
    */
    get Abstract(): string | null {
        return this.Get('Abstract');
    }
    set Abstract(value: string | null) {
        this.Set('Abstract', value);
    }

    /**
    * * Field Name: PresenterUserID
    * * Display Name: Presenter User ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Users (vwUsers.ID)
    */
    get PresenterUserID(): string {
        return this.Get('PresenterUserID');
    }
    set PresenterUserID(value: string) {
        this.Set('PresenterUserID', value);
    }

    /**
    * * Field Name: PresentationCategoryID
    * * Display Name: Presentation Category ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Presentation Categories (vwPresentationCategories.ID)
    */
    get PresentationCategoryID(): string {
        return this.Get('PresentationCategoryID');
    }
    set PresentationCategoryID(value: string) {
        this.Set('PresentationCategoryID', value);
    }

    /**
    * * Field Name: SubmissionDate
    * * Display Name: Submission Date
    * * SQL Data Type: datetimeoffset
    * * Description: When the presentation was submitted for review.
    */
    get SubmissionDate(): Date | null {
        return this.Get('SubmissionDate');
    }
    set SubmissionDate(value: Date | null) {
        this.Set('SubmissionDate', value);
    }

    /**
    * * Field Name: ConferenceDate
    * * Display Name: Conference Date
    * * SQL Data Type: datetimeoffset
    * * Description: Scheduled conference presentation date.
    */
    get ConferenceDate(): Date | null {
        return this.Get('ConferenceDate');
    }
    set ConferenceDate(value: Date | null) {
        this.Set('ConferenceDate', value);
    }

    /**
    * * Field Name: FileURL
    * * Display Name: File URL
    * * SQL Data Type: nvarchar(1024)
    * * Description: URL to the presentation file (PDF, PPTX, DOCX, etc.).
    */
    get FileURL(): string | null {
        return this.Get('FileURL');
    }
    set FileURL(value: string | null) {
        this.Set('FileURL', value);
    }

    /**
    * * Field Name: FileName
    * * Display Name: File Name
    * * SQL Data Type: nvarchar(255)
    * * Description: Original filename of the uploaded presentation.
    */
    get FileName(): string | null {
        return this.Get('FileName');
    }
    set FileName(value: string | null) {
        this.Set('FileName', value);
    }

    /**
    * * Field Name: FileSize
    * * Display Name: File Size
    * * SQL Data Type: bigint
    * * Description: Size of the presentation file in bytes.
    */
    get FileSize(): number | null {
        return this.Get('FileSize');
    }
    set FileSize(value: number | null) {
        this.Set('FileSize', value);
    }

    /**
    * * Field Name: FileMimeType
    * * Display Name: File Mime Type
    * * SQL Data Type: nvarchar(100)
    * * Description: MIME type of the presentation file.
    */
    get FileMimeType(): string | null {
        return this.Get('FileMimeType');
    }
    set FileMimeType(value: string | null) {
        this.Set('FileMimeType', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: nvarchar(20)
    * * Default Value: Draft
    * * Value List Type: List
    * * Possible Values 
    *   * Archived
    *   * Canceled
    *   * Draft
    *   * ReviewCompleted
    *   * Submitted
    *   * UnderReview
    * * Description: Current status of the presentation in the review workflow.
    */
    get Status(): 'Archived' | 'Canceled' | 'Draft' | 'ReviewCompleted' | 'Submitted' | 'UnderReview' {
        return this.Get('Status');
    }
    set Status(value: 'Archived' | 'Canceled' | 'Draft' | 'ReviewCompleted' | 'Submitted' | 'UnderReview') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: ReviewDeadline
    * * Display Name: Review Deadline
    * * SQL Data Type: datetimeoffset
    * * Description: Deadline for reviewers to complete their reviews.
    */
    get ReviewDeadline(): Date | null {
        return this.Get('ReviewDeadline');
    }
    set ReviewDeadline(value: Date | null) {
        this.Set('ReviewDeadline', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: SeasonID
    * * Display Name: Season ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Seasons (vwSeasons.ID)
    */
    get SeasonID(): string | null {
        return this.Get('SeasonID');
    }
    set SeasonID(value: string | null) {
        this.Set('SeasonID', value);
    }

    /**
    * * Field Name: SectionID
    * * Display Name: Section ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Sections (vwSections.ID)
    */
    get SectionID(): string | null {
        return this.Get('SectionID');
    }
    set SectionID(value: string | null) {
        this.Set('SectionID', value);
    }

    /**
    * * Field Name: SessionID
    * * Display Name: Session ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Sessions (vwSessions.ID)
    */
    get SessionID(): string | null {
        return this.Get('SessionID');
    }
    set SessionID(value: string | null) {
        this.Set('SessionID', value);
    }

    /**
    * * Field Name: PresentationCode
    * * Display Name: Presentation Code
    * * SQL Data Type: nvarchar(20)
    */
    get PresentationCode(): string | null {
        return this.Get('PresentationCode');
    }
    set PresentationCode(value: string | null) {
        this.Set('PresentationCode', value);
    }

    /**
    * * Field Name: Format
    * * Display Name: Format
    * * SQL Data Type: nvarchar(10)
    * * Value List Type: List
    * * Possible Values 
    *   * Oral
    *   * Poster
    */
    get Format(): 'Oral' | 'Poster' | null {
        return this.Get('Format');
    }
    set Format(value: 'Oral' | 'Poster' | null) {
        this.Set('Format', value);
    }

    /**
    * * Field Name: PresenterUser
    * * Display Name: Presenter User
    * * SQL Data Type: nvarchar(100)
    */
    get PresenterUser(): string {
        return this.Get('PresenterUser');
    }

    /**
    * * Field Name: PresentationCategory
    * * Display Name: Presentation Category
    * * SQL Data Type: nvarchar(255)
    */
    get PresentationCategory(): string {
        return this.Get('PresentationCategory');
    }

    /**
    * * Field Name: Season
    * * Display Name: Season
    * * SQL Data Type: nvarchar(200)
    */
    get Season(): string | null {
        return this.Get('Season');
    }

    /**
    * * Field Name: Section
    * * Display Name: Section
    * * SQL Data Type: nvarchar(200)
    */
    get Section(): string | null {
        return this.Get('Section');
    }

    /**
    * * Field Name: Session
    * * Display Name: Session
    * * SQL Data Type: nvarchar(500)
    */
    get Session(): string | null {
        return this.Get('Session');
    }
}


/**
 * Profile Matches - strongly typed entity sub-class
 * * Schema: CoreSignal
 * * Base Table: ProfileMatch
 * * Base View: vwProfileMatches
 * * @description Adjudication ledger for the CoreSignal reverse match: one row per delivered (profile, AGU person) pair, never deleted. The reverse match was name+org based and measurably over-matches (1,826 people got 2+ profiles; worst case 109), and every multi-set profile contains the person's known org — so org overlap is CIRCULAR evidence and cannot disambiguate. MatchStatus + IsPrimary (set by the "Run CoreSignal Ingest" action: LinkedIn verification, then deterministic rules, then LLM adjudication) decide which single profile represents a person. Consumers read the vwPerson* views, which enforce Confirmed+IsPrimary.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Profile Matches')
export class CoreSignalProfileMatchEntity extends BaseEntity<CoreSignalProfileMatchEntityType> {
    /**
    * Loads the Profile Matches record from the database
    * @param ID: string - primary key value to load the Profile Matches record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof CoreSignalProfileMatchEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * Validate() method override for Profile Matches entity. This is an auto-generated method that invokes the generated validators for this entity for the following fields:
    * * Table-Level: If a match is marked as the primary match, its status must be 'Confirmed'.
    * @public
    * @method
    * @override
    */
    public override Validate(): ValidationResult {
        const result = super.Validate();
        this.ValidatePrimaryMatchStatus(result);
        result.Success = result.Success && (result.Errors.length === 0);

        return result;
    }

    /**
    * If a match is marked as the primary match, its status must be 'Confirmed'.
    * @param result - the ValidationResult object to add any errors or warnings to
    * @public
    * @method
    */
    public ValidatePrimaryMatchStatus(result: ValidationResult) {
        // If IsPrimary is true, MatchStatus must be 'Confirmed'
        if (this.IsPrimary && this.MatchStatus !== 'Confirmed') {
            result.Errors.push(new ValidationErrorInfo(
                "IsPrimary",
                "A match can only be marked as primary if its status is 'Confirmed'.",
                this.IsPrimary,
                ValidationErrorType.Failure
            ));
        }
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ProfileID
    * * Display Name: Profile ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Profiles (vwProfiles.ID)
    */
    get ProfileID(): string {
        return this.Get('ProfileID');
    }
    set ProfileID(value: string) {
        this.Set('ProfileID', value);
    }

    /**
    * * Field Name: AGUPersonID
    * * Display Name: AGU Person ID
    * * SQL Data Type: uniqueidentifier
    * * Description: The delivery CSV's Customer ID (= MAPS.Person.AGUPersonID). Kept even when no MAPS.Person row exists yet (MatchStatus=PersonNotFound; 800 in the initial delivery) so later ingests can resolve it.
    */
    get AGUPersonID(): string {
        return this.Get('AGUPersonID');
    }
    set AGUPersonID(value: string) {
        this.Set('AGUPersonID', value);
    }

    /**
    * * Field Name: PersonID
    * * Display Name: Person ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: People (vwPeople.ID)
    */
    get PersonID(): string | null {
        return this.Get('PersonID');
    }
    set PersonID(value: string | null) {
        this.Set('PersonID', value);
    }

    /**
    * * Field Name: MatchCardinality
    * * Display Name: Match Cardinality
    * * SQL Data Type: int
    * * Default Value: 1
    * * Description: Profiles matched to the same person in the delivery (1 = clean 1:1). High cardinality = name-collision shotgun from the name+org reverse match.
    */
    get MatchCardinality(): number {
        return this.Get('MatchCardinality');
    }
    set MatchCardinality(value: number) {
        this.Set('MatchCardinality', value);
    }

    /**
    * * Field Name: MatchStatus
    * * Display Name: Match Status
    * * SQL Data Type: nvarchar(20)
    * * Default Value: Pending
    * * Value List Type: List
    * * Possible Values 
    *   * Ambiguous
    *   * Confirmed
    *   * Duplicate
    *   * Pending
    *   * PersonNotFound
    *   * Rejected
    *   * Withdrawn
    * * Description: Pending (awaiting adjudication) | Confirmed (this profile IS the person) | Duplicate (same human, non-primary duplicate profile) | Rejected (different human) | Ambiguous (cannot decide — excluded from consumption but retained) | PersonNotFound (AGUPersonID unresolvable) | Withdrawn (pair absent from a later re-delivery).
    */
    get MatchStatus(): 'Ambiguous' | 'Confirmed' | 'Duplicate' | 'Pending' | 'PersonNotFound' | 'Rejected' | 'Withdrawn' {
        return this.Get('MatchStatus');
    }
    set MatchStatus(value: 'Ambiguous' | 'Confirmed' | 'Duplicate' | 'Pending' | 'PersonNotFound' | 'Rejected' | 'Withdrawn') {
        this.Set('MatchStatus', value);
    }

    /**
    * * Field Name: IsPrimary
    * * Display Name: Is Primary
    * * SQL Data Type: bit
    * * Default Value: 0
    * * Description: The single profile consumers read for this person (filtered unique index enforces one per person). Only Confirmed rows may be primary (CK).
    */
    get IsPrimary(): boolean {
        return this.Get('IsPrimary');
    }
    set IsPrimary(value: boolean) {
        this.Set('IsPrimary', value);
    }

    /**
    * * Field Name: LinkedInVerified
    * * Display Name: Linked In Verified
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get LinkedInVerified(): boolean {
        return this.Get('LinkedInVerified');
    }
    set LinkedInVerified(value: boolean) {
        this.Set('LinkedInVerified', value);
    }

    /**
    * * Field Name: NameExact
    * * Display Name: Name Exact
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get NameExact(): boolean {
        return this.Get('NameExact');
    }
    set NameExact(value: boolean) {
        this.Set('NameExact', value);
    }

    /**
    * * Field Name: ResearchSignal
    * * Display Name: Research Signal
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get ResearchSignal(): boolean {
        return this.Get('ResearchSignal');
    }
    set ResearchSignal(value: boolean) {
        this.Set('ResearchSignal', value);
    }

    /**
    * * Field Name: OrgOverlap
    * * Display Name: Org Overlap
    * * SQL Data Type: bit
    * * Default Value: 0
    * * Description: Person's known org appears in the profile's experience/education. Recorded for audit but CIRCULAR (the reverse match conditioned on it) — measured true for 100% of multi-set profiles; never use it to pick a winner.
    */
    get OrgOverlap(): boolean {
        return this.Get('OrgOverlap');
    }
    set OrgOverlap(value: boolean) {
        this.Set('OrgOverlap', value);
    }

    /**
    * * Field Name: TitleRankConflict
    * * Display Name: Title Rank Conflict
    * * SQL Data Type: bit
    * * Default Value: 0
    * * Description: Red flag: profile shows a Student/Junior-level title at the same org + overlapping years where ORCID shows Professor-level (or vice versa) — the wrong-person signature measured on 4.3% of rankable 1:1 pairs.
    */
    get TitleRankConflict(): boolean {
        return this.Get('TitleRankConflict');
    }
    set TitleRankConflict(value: boolean) {
        this.Set('TitleRankConflict', value);
    }

    /**
    * * Field Name: DisjointSpells
    * * Display Name: Disjoint Spells
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get DisjointSpells(): boolean {
        return this.Get('DisjointSpells');
    }
    set DisjointSpells(value: boolean) {
        this.Set('DisjointSpells', value);
    }

    /**
    * * Field Name: AllNewEmployers
    * * Display Name: All New Employers
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get AllNewEmployers(): boolean {
        return this.Get('AllNewEmployers');
    }
    set AllNewEmployers(value: boolean) {
        this.Set('AllNewEmployers', value);
    }

    /**
    * * Field Name: ConfidenceScore
    * * Display Name: Confidence Score
    * * SQL Data Type: decimal(4, 3)
    */
    get ConfidenceScore(): number | null {
        return this.Get('ConfidenceScore');
    }
    set ConfidenceScore(value: number | null) {
        this.Set('ConfidenceScore', value);
    }

    /**
    * * Field Name: AdjudicationMethod
    * * Display Name: Adjudication Method
    * * SQL Data Type: nvarchar(20)
    * * Value List Type: List
    * * Possible Values 
    *   * Deterministic
    *   * LLM
    *   * Manual
    */
    get AdjudicationMethod(): 'Deterministic' | 'LLM' | 'Manual' | null {
        return this.Get('AdjudicationMethod');
    }
    set AdjudicationMethod(value: 'Deterministic' | 'LLM' | 'Manual' | null) {
        this.Set('AdjudicationMethod', value);
    }

    /**
    * * Field Name: AdjudicationModel
    * * Display Name: Adjudication Model
    * * SQL Data Type: nvarchar(100)
    */
    get AdjudicationModel(): string | null {
        return this.Get('AdjudicationModel');
    }
    set AdjudicationModel(value: string | null) {
        this.Set('AdjudicationModel', value);
    }

    /**
    * * Field Name: AdjudicationNotes
    * * Display Name: Adjudication Notes
    * * SQL Data Type: nvarchar(MAX)
    */
    get AdjudicationNotes(): string | null {
        return this.Get('AdjudicationNotes');
    }
    set AdjudicationNotes(value: string | null) {
        this.Set('AdjudicationNotes', value);
    }

    /**
    * * Field Name: AdjudicatedAt
    * * Display Name: Adjudicated At
    * * SQL Data Type: datetimeoffset
    */
    get AdjudicatedAt(): Date | null {
        return this.Get('AdjudicatedAt');
    }
    set AdjudicatedAt(value: Date | null) {
        this.Set('AdjudicatedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Profiles - strongly typed entity sub-class
 * * Schema: CoreSignal
 * * Base Table: Profile
 * * Base View: vwProfiles
 * * @description A CoreSignal employee profile (2026-07 reverse-match delivery + re-drops), keyed by the CoreSignal profile id. UNTRUSTED until adjudicated: consumers must read via the vwPerson* views (confirmed-primary matches only), never join this table directly.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Profiles')
export class CoreSignalProfileEntity extends BaseEntity<CoreSignalProfileEntityType> {
    /**
    * Loads the Profiles record from the database
    * @param ID: string - primary key value to load the Profiles record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof CoreSignalProfileEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * Validate() method override for Profiles entity. This is an auto-generated method that invokes the generated validators for this entity for the following fields:
    * * RawJSON: If raw JSON data is provided, it must be in a valid JSON format to ensure that the payload can be successfully parsed and processed.
    * @public
    * @method
    * @override
    */
    public override Validate(): ValidationResult {
        const result = super.Validate();
        this.ValidateRawJSONFormat(result);
        result.Success = result.Success && (result.Errors.length === 0);

        return result;
    }

    /**
    * If raw JSON data is provided, it must be in a valid JSON format to ensure that the payload can be successfully parsed and processed.
    * @param result - the ValidationResult object to add any errors or warnings to
    * @public
    * @method
    */
    public ValidateRawJSONFormat(result: ValidationResult) {
    	if (this.RawJSON != null && this.RawJSON.trim() !== "") {
    		try {
    			JSON.parse(this.RawJSON);
    		} catch (e) {
    			result.Errors.push(new ValidationErrorInfo(
    				"RawJSON",
    				"The RawJSON field must be a valid JSON-formatted string.",
    				this.RawJSON,
    				ValidationErrorType.Failure
    			));
    		}
    	}
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: CoreSignalID
    * * Display Name: Core Signal ID
    * * SQL Data Type: bigint
    * * Description: CoreSignal profile id (feed "id") — the stable external key for sync.
    */
    get CoreSignalID(): number {
        return this.Get('CoreSignalID');
    }
    set CoreSignalID(value: number) {
        this.Set('CoreSignalID', value);
    }

    /**
    * * Field Name: FullName
    * * Display Name: Full Name
    * * SQL Data Type: nvarchar(500)
    */
    get FullName(): string | null {
        return this.Get('FullName');
    }
    set FullName(value: string | null) {
        this.Set('FullName', value);
    }

    /**
    * * Field Name: FirstName
    * * Display Name: First Name
    * * SQL Data Type: nvarchar(255)
    */
    get FirstName(): string | null {
        return this.Get('FirstName');
    }
    set FirstName(value: string | null) {
        this.Set('FirstName', value);
    }

    /**
    * * Field Name: LastName
    * * Display Name: Last Name
    * * SQL Data Type: nvarchar(255)
    */
    get LastName(): string | null {
        return this.Get('LastName');
    }
    set LastName(value: string | null) {
        this.Set('LastName', value);
    }

    /**
    * * Field Name: Headline
    * * Display Name: Headline
    * * SQL Data Type: nvarchar(1000)
    */
    get Headline(): string | null {
        return this.Get('Headline');
    }
    set Headline(value: string | null) {
        this.Set('Headline', value);
    }

    /**
    * * Field Name: Summary
    * * Display Name: Summary
    * * SQL Data Type: nvarchar(MAX)
    */
    get Summary(): string | null {
        return this.Get('Summary');
    }
    set Summary(value: string | null) {
        this.Set('Summary', value);
    }

    /**
    * * Field Name: PictureUrl
    * * Display Name: Picture Url
    * * SQL Data Type: nvarchar(2000)
    */
    get PictureUrl(): string | null {
        return this.Get('PictureUrl');
    }
    set PictureUrl(value: string | null) {
        this.Set('PictureUrl', value);
    }

    /**
    * * Field Name: LinkedInUrl
    * * Display Name: Linked In Url
    * * SQL Data Type: nvarchar(1000)
    */
    get LinkedInUrl(): string | null {
        return this.Get('LinkedInUrl');
    }
    set LinkedInUrl(value: string | null) {
        this.Set('LinkedInUrl', value);
    }

    /**
    * * Field Name: LinkedInShorthand
    * * Display Name: Linked In Shorthand
    * * SQL Data Type: nvarchar(255)
    * * Description: Canonical LinkedIn slug (feed linkedin_canonical_shorthand_name). Equality with MAPS.Person.LinkedInUrl's slug is the strongest match-verification signal.
    */
    get LinkedInShorthand(): string | null {
        return this.Get('LinkedInShorthand');
    }
    set LinkedInShorthand(value: string | null) {
        this.Set('LinkedInShorthand', value);
    }

    /**
    * * Field Name: WebsiteUrl
    * * Display Name: Website Url
    * * SQL Data Type: nvarchar(2000)
    */
    get WebsiteUrl(): string | null {
        return this.Get('WebsiteUrl');
    }
    set WebsiteUrl(value: string | null) {
        this.Set('WebsiteUrl', value);
    }

    /**
    * * Field Name: LocationCity
    * * Display Name: Location City
    * * SQL Data Type: nvarchar(255)
    */
    get LocationCity(): string | null {
        return this.Get('LocationCity');
    }
    set LocationCity(value: string | null) {
        this.Set('LocationCity', value);
    }

    /**
    * * Field Name: LocationState
    * * Display Name: Location State
    * * SQL Data Type: nvarchar(255)
    */
    get LocationState(): string | null {
        return this.Get('LocationState');
    }
    set LocationState(value: string | null) {
        this.Set('LocationState', value);
    }

    /**
    * * Field Name: LocationCountry
    * * Display Name: Location Country
    * * SQL Data Type: nvarchar(255)
    */
    get LocationCountry(): string | null {
        return this.Get('LocationCountry');
    }
    set LocationCountry(value: string | null) {
        this.Set('LocationCountry', value);
    }

    /**
    * * Field Name: LocationCountryISO2
    * * Display Name: Location Country ISO2
    * * SQL Data Type: nvarchar(10)
    */
    get LocationCountryISO2(): string | null {
        return this.Get('LocationCountryISO2');
    }
    set LocationCountryISO2(value: string | null) {
        this.Set('LocationCountryISO2', value);
    }

    /**
    * * Field Name: LocationFull
    * * Display Name: Location Full
    * * SQL Data Type: nvarchar(500)
    */
    get LocationFull(): string | null {
        return this.Get('LocationFull');
    }
    set LocationFull(value: string | null) {
        this.Set('LocationFull', value);
    }

    /**
    * * Field Name: IsDeleted
    * * Display Name: Is Deleted
    * * SQL Data Type: bit
    * * Default Value: 0
    * * Description: Feed is_deleted: CoreSignal's own duplicate/removed marker (1,833 in the initial delivery). Deleted profiles are never adjudicated primary and never surface in views.
    */
    get IsDeleted(): boolean {
        return this.Get('IsDeleted');
    }
    set IsDeleted(value: boolean) {
        this.Set('IsDeleted', value);
    }

    /**
    * * Field Name: IsWorking
    * * Display Name: Is Working
    * * SQL Data Type: bit
    */
    get IsWorking(): boolean | null {
        return this.Get('IsWorking');
    }
    set IsWorking(value: boolean | null) {
        this.Set('IsWorking', value);
    }

    /**
    * * Field Name: ActiveExperienceTitle
    * * Display Name: Active Experience Title
    * * SQL Data Type: nvarchar(500)
    */
    get ActiveExperienceTitle(): string | null {
        return this.Get('ActiveExperienceTitle');
    }
    set ActiveExperienceTitle(value: string | null) {
        this.Set('ActiveExperienceTitle', value);
    }

    /**
    * * Field Name: ActiveExperienceDepartment
    * * Display Name: Active Experience Department
    * * SQL Data Type: nvarchar(255)
    */
    get ActiveExperienceDepartment(): string | null {
        return this.Get('ActiveExperienceDepartment');
    }
    set ActiveExperienceDepartment(value: string | null) {
        this.Set('ActiveExperienceDepartment', value);
    }

    /**
    * * Field Name: ActiveExperienceManagementLevel
    * * Display Name: Active Experience Management Level
    * * SQL Data Type: nvarchar(255)
    */
    get ActiveExperienceManagementLevel(): string | null {
        return this.Get('ActiveExperienceManagementLevel');
    }
    set ActiveExperienceManagementLevel(value: string | null) {
        this.Set('ActiveExperienceManagementLevel', value);
    }

    /**
    * * Field Name: IsDecisionMaker
    * * Display Name: Is Decision Maker
    * * SQL Data Type: bit
    */
    get IsDecisionMaker(): boolean | null {
        return this.Get('IsDecisionMaker');
    }
    set IsDecisionMaker(value: boolean | null) {
        this.Set('IsDecisionMaker', value);
    }

    /**
    * * Field Name: TotalExperienceMonths
    * * Display Name: Total Experience Months
    * * SQL Data Type: int
    */
    get TotalExperienceMonths(): number | null {
        return this.Get('TotalExperienceMonths');
    }
    set TotalExperienceMonths(value: number | null) {
        this.Set('TotalExperienceMonths', value);
    }

    /**
    * * Field Name: ProfileScore
    * * Display Name: Profile Score
    * * SQL Data Type: decimal(4, 3)
    * * Description: CoreSignal profile-COMPLETENESS score (0-1). NOT match confidence — nearly all profiles score 0.9+; do not use it to rank match candidates.
    */
    get ProfileScore(): number | null {
        return this.Get('ProfileScore');
    }
    set ProfileScore(value: number | null) {
        this.Set('ProfileScore', value);
    }

    /**
    * * Field Name: ConnectionsCount
    * * Display Name: Connections Count
    * * SQL Data Type: int
    */
    get ConnectionsCount(): number | null {
        return this.Get('ConnectionsCount');
    }
    set ConnectionsCount(value: number | null) {
        this.Set('ConnectionsCount', value);
    }

    /**
    * * Field Name: FollowersCount
    * * Display Name: Followers Count
    * * SQL Data Type: int
    */
    get FollowersCount(): number | null {
        return this.Get('FollowersCount');
    }
    set FollowersCount(value: number | null) {
        this.Set('FollowersCount', value);
    }

    /**
    * * Field Name: SourceCreatedAt
    * * Display Name: Source Created At
    * * SQL Data Type: datetimeoffset
    */
    get SourceCreatedAt(): Date | null {
        return this.Get('SourceCreatedAt');
    }
    set SourceCreatedAt(value: Date | null) {
        this.Set('SourceCreatedAt', value);
    }

    /**
    * * Field Name: SourceUpdatedAt
    * * Display Name: Source Updated At
    * * SQL Data Type: datetimeoffset
    */
    get SourceUpdatedAt(): Date | null {
        return this.Get('SourceUpdatedAt');
    }
    set SourceUpdatedAt(value: Date | null) {
        this.Set('SourceUpdatedAt', value);
    }

    /**
    * * Field Name: SourceCheckedAt
    * * Display Name: Source Checked At
    * * SQL Data Type: datetimeoffset
    * * Description: Feed checked_at — when CoreSignal last verified the source profile. The freshness qualifier for IsWorking/active-experience claims (stale "present" positions on abandoned LinkedIn profiles are a measured 14% hazard).
    */
    get SourceCheckedAt(): Date | null {
        return this.Get('SourceCheckedAt');
    }
    set SourceCheckedAt(value: Date | null) {
        this.Set('SourceCheckedAt', value);
    }

    /**
    * * Field Name: SourceChangedAt
    * * Display Name: Source Changed At
    * * SQL Data Type: datetimeoffset
    */
    get SourceChangedAt(): Date | null {
        return this.Get('SourceChangedAt');
    }
    set SourceChangedAt(value: Date | null) {
        this.Set('SourceChangedAt', value);
    }

    /**
    * * Field Name: RawJSON
    * * Display Name: Raw JSON
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Full profile JSON preserved verbatim (includes non-normalized collections: activity, recommendations, interests).
    */
    get RawJSON(): string | null {
        return this.Get('RawJSON');
    }
    set RawJSON(value: string | null) {
        this.Set('RawJSON', value);
    }

    /**
    * * Field Name: __mj_integration_SyncStatus
    * * Display Name: Mj Integration Sync Status
    * * SQL Data Type: nvarchar(50)
    * * Default Value: Active
    */
    get __mj_integration_SyncStatus(): string {
        return this.Get('__mj_integration_SyncStatus');
    }
    set __mj_integration_SyncStatus(value: string) {
        this.Set('__mj_integration_SyncStatus', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedAt
    * * Display Name: Mj Integration Last Synced At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastSyncedAt(): Date | null {
        return this.Get('__mj_integration_LastSyncedAt');
    }
    set __mj_integration_LastSyncedAt(value: Date | null) {
        this.Set('__mj_integration_LastSyncedAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedSnapshot
    * * Display Name: Mj Integration Last Synced Snapshot
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_LastSyncedSnapshot(): string | null {
        return this.Get('__mj_integration_LastSyncedSnapshot');
    }
    set __mj_integration_LastSyncedSnapshot(value: string | null) {
        this.Set('__mj_integration_LastSyncedSnapshot', value);
    }

    /**
    * * Field Name: __mj_integration_SyncMessage
    * * Display Name: Mj Integration Sync Message
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_SyncMessage(): string | null {
        return this.Get('__mj_integration_SyncMessage');
    }
    set __mj_integration_SyncMessage(value: string | null) {
        this.Set('__mj_integration_SyncMessage', value);
    }

    /**
    * * Field Name: __mj_integration_ContentHash
    * * Display Name: Mj Integration Content Hash
    * * SQL Data Type: nvarchar(64)
    */
    get __mj_integration_ContentHash(): string | null {
        return this.Get('__mj_integration_ContentHash');
    }
    set __mj_integration_ContentHash(value: string | null) {
        this.Set('__mj_integration_ContentHash', value);
    }

    /**
    * * Field Name: __mj_integration_CustomOverflow
    * * Display Name: Mj Integration Custom Overflow
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_CustomOverflow(): string | null {
        return this.Get('__mj_integration_CustomOverflow');
    }
    set __mj_integration_CustomOverflow(value: string | null) {
        this.Set('__mj_integration_CustomOverflow', value);
    }

    /**
    * * Field Name: __mj_integration_ExternalVersion
    * * Display Name: Mj Integration External Version
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_ExternalVersion(): string | null {
        return this.Get('__mj_integration_ExternalVersion');
    }
    set __mj_integration_ExternalVersion(value: string | null) {
        this.Set('__mj_integration_ExternalVersion', value);
    }

    /**
    * * Field Name: __mj_integration_LastSeenModifiedValue
    * * Display Name: Mj Integration Last Seen Modified Value
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_LastSeenModifiedValue(): string | null {
        return this.Get('__mj_integration_LastSeenModifiedValue');
    }
    set __mj_integration_LastSeenModifiedValue(value: string | null) {
        this.Set('__mj_integration_LastSeenModifiedValue', value);
    }

    /**
    * * Field Name: __mj_integration_LastReconciledAt
    * * Display Name: Mj Integration Last Reconciled At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastReconciledAt(): Date | null {
        return this.Get('__mj_integration_LastReconciledAt');
    }
    set __mj_integration_LastReconciledAt(value: Date | null) {
        this.Set('__mj_integration_LastReconciledAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastWriterDirection
    * * Display Name: Mj Integration Last Writer Direction
    * * SQL Data Type: nvarchar(10)
    */
    get __mj_integration_LastWriterDirection(): string | null {
        return this.Get('__mj_integration_LastWriterDirection');
    }
    set __mj_integration_LastWriterDirection(value: string | null) {
        this.Set('__mj_integration_LastWriterDirection', value);
    }

    /**
    * * Field Name: __mj_integration_IsTombstoned
    * * Display Name: Mj Integration Is Tombstoned
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get __mj_integration_IsTombstoned(): boolean {
        return this.Get('__mj_integration_IsTombstoned');
    }
    set __mj_integration_IsTombstoned(value: boolean) {
        this.Set('__mj_integration_IsTombstoned', value);
    }

    /**
    * * Field Name: __mj_integration_DeletedDetectedAt
    * * Display Name: Mj Integration Deleted Detected At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_DeletedDetectedAt(): Date | null {
        return this.Get('__mj_integration_DeletedDetectedAt');
    }
    set __mj_integration_DeletedDetectedAt(value: Date | null) {
        this.Set('__mj_integration_DeletedDetectedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Projects - strongly typed entity sub-class
 * * Schema: CoreSignal
 * * Base Table: Project
 * * Base View: vwProjects
 * * @description A project from a CoreSignal profile.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Projects')
export class CoreSignalProjectEntity extends BaseEntity<CoreSignalProjectEntityType> {
    /**
    * Loads the Projects record from the database
    * @param ID: string - primary key value to load the Projects record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof CoreSignalProjectEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ProfileID
    * * Display Name: Profile ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Profiles (vwProfiles.ID)
    */
    get ProfileID(): string {
        return this.Get('ProfileID');
    }
    set ProfileID(value: string) {
        this.Set('ProfileID', value);
    }

    /**
    * * Field Name: OrderInProfile
    * * Display Name: Order In Profile
    * * SQL Data Type: int
    */
    get OrderInProfile(): number {
        return this.Get('OrderInProfile');
    }
    set OrderInProfile(value: number) {
        this.Set('OrderInProfile', value);
    }

    /**
    * * Field Name: Name
    * * Display Name: Name
    * * SQL Data Type: nvarchar(1000)
    */
    get Name(): string | null {
        return this.Get('Name');
    }
    set Name(value: string | null) {
        this.Set('Name', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(MAX)
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: ProjectUrl
    * * Display Name: Project Url
    * * SQL Data Type: nvarchar(2000)
    */
    get ProjectUrl(): string | null {
        return this.Get('ProjectUrl');
    }
    set ProjectUrl(value: string | null) {
        this.Set('ProjectUrl', value);
    }

    /**
    * * Field Name: DateFromYear
    * * Display Name: Date From Year
    * * SQL Data Type: smallint
    */
    get DateFromYear(): number | null {
        return this.Get('DateFromYear');
    }
    set DateFromYear(value: number | null) {
        this.Set('DateFromYear', value);
    }

    /**
    * * Field Name: DateFromMonth
    * * Display Name: Date From Month
    * * SQL Data Type: tinyint
    */
    get DateFromMonth(): number | null {
        return this.Get('DateFromMonth');
    }
    set DateFromMonth(value: number | null) {
        this.Set('DateFromMonth', value);
    }

    /**
    * * Field Name: DateToYear
    * * Display Name: Date To Year
    * * SQL Data Type: smallint
    */
    get DateToYear(): number | null {
        return this.Get('DateToYear');
    }
    set DateToYear(value: number | null) {
        this.Set('DateToYear', value);
    }

    /**
    * * Field Name: DateToMonth
    * * Display Name: Date To Month
    * * SQL Data Type: tinyint
    */
    get DateToMonth(): number | null {
        return this.Get('DateToMonth');
    }
    set DateToMonth(value: number | null) {
        this.Set('DateToMonth', value);
    }

    /**
    * * Field Name: __mj_integration_SyncStatus
    * * Display Name: Mj Integration Sync Status
    * * SQL Data Type: nvarchar(50)
    * * Default Value: Active
    */
    get __mj_integration_SyncStatus(): string {
        return this.Get('__mj_integration_SyncStatus');
    }
    set __mj_integration_SyncStatus(value: string) {
        this.Set('__mj_integration_SyncStatus', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedAt
    * * Display Name: Mj Integration Last Synced At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastSyncedAt(): Date | null {
        return this.Get('__mj_integration_LastSyncedAt');
    }
    set __mj_integration_LastSyncedAt(value: Date | null) {
        this.Set('__mj_integration_LastSyncedAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedSnapshot
    * * Display Name: Mj Integration Last Synced Snapshot
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_LastSyncedSnapshot(): string | null {
        return this.Get('__mj_integration_LastSyncedSnapshot');
    }
    set __mj_integration_LastSyncedSnapshot(value: string | null) {
        this.Set('__mj_integration_LastSyncedSnapshot', value);
    }

    /**
    * * Field Name: __mj_integration_SyncMessage
    * * Display Name: Mj Integration Sync Message
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_SyncMessage(): string | null {
        return this.Get('__mj_integration_SyncMessage');
    }
    set __mj_integration_SyncMessage(value: string | null) {
        this.Set('__mj_integration_SyncMessage', value);
    }

    /**
    * * Field Name: __mj_integration_ContentHash
    * * Display Name: Mj Integration Content Hash
    * * SQL Data Type: nvarchar(64)
    */
    get __mj_integration_ContentHash(): string | null {
        return this.Get('__mj_integration_ContentHash');
    }
    set __mj_integration_ContentHash(value: string | null) {
        this.Set('__mj_integration_ContentHash', value);
    }

    /**
    * * Field Name: __mj_integration_CustomOverflow
    * * Display Name: Mj Integration Custom Overflow
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_CustomOverflow(): string | null {
        return this.Get('__mj_integration_CustomOverflow');
    }
    set __mj_integration_CustomOverflow(value: string | null) {
        this.Set('__mj_integration_CustomOverflow', value);
    }

    /**
    * * Field Name: __mj_integration_ExternalVersion
    * * Display Name: Mj Integration External Version
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_ExternalVersion(): string | null {
        return this.Get('__mj_integration_ExternalVersion');
    }
    set __mj_integration_ExternalVersion(value: string | null) {
        this.Set('__mj_integration_ExternalVersion', value);
    }

    /**
    * * Field Name: __mj_integration_LastSeenModifiedValue
    * * Display Name: Mj Integration Last Seen Modified Value
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_LastSeenModifiedValue(): string | null {
        return this.Get('__mj_integration_LastSeenModifiedValue');
    }
    set __mj_integration_LastSeenModifiedValue(value: string | null) {
        this.Set('__mj_integration_LastSeenModifiedValue', value);
    }

    /**
    * * Field Name: __mj_integration_LastReconciledAt
    * * Display Name: Mj Integration Last Reconciled At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastReconciledAt(): Date | null {
        return this.Get('__mj_integration_LastReconciledAt');
    }
    set __mj_integration_LastReconciledAt(value: Date | null) {
        this.Set('__mj_integration_LastReconciledAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastWriterDirection
    * * Display Name: Mj Integration Last Writer Direction
    * * SQL Data Type: nvarchar(10)
    */
    get __mj_integration_LastWriterDirection(): string | null {
        return this.Get('__mj_integration_LastWriterDirection');
    }
    set __mj_integration_LastWriterDirection(value: string | null) {
        this.Set('__mj_integration_LastWriterDirection', value);
    }

    /**
    * * Field Name: __mj_integration_IsTombstoned
    * * Display Name: Mj Integration Is Tombstoned
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get __mj_integration_IsTombstoned(): boolean {
        return this.Get('__mj_integration_IsTombstoned');
    }
    set __mj_integration_IsTombstoned(value: boolean) {
        this.Set('__mj_integration_IsTombstoned', value);
    }

    /**
    * * Field Name: __mj_integration_DeletedDetectedAt
    * * Display Name: Mj Integration Deleted Detected At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_DeletedDetectedAt(): Date | null {
        return this.Get('__mj_integration_DeletedDetectedAt');
    }
    set __mj_integration_DeletedDetectedAt(value: Date | null) {
        this.Set('__mj_integration_DeletedDetectedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Publications - strongly typed entity sub-class
 * * Schema: CoreSignal
 * * Base Table: Publication
 * * Base View: vwPublications
 * * @description A publication listed on a CoreSignal profile (self-reported on LinkedIn — NOT the vetted publication corpus; Recommendation.Content remains the publication source of truth). PublisherNames is the feed's publisher_names array joined with "; ".
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Publications')
export class CoreSignalPublicationEntity extends BaseEntity<CoreSignalPublicationEntityType> {
    /**
    * Loads the Publications record from the database
    * @param ID: string - primary key value to load the Publications record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof CoreSignalPublicationEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ProfileID
    * * Display Name: Profile ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Profiles (vwProfiles.ID)
    */
    get ProfileID(): string {
        return this.Get('ProfileID');
    }
    set ProfileID(value: string) {
        this.Set('ProfileID', value);
    }

    /**
    * * Field Name: OrderInProfile
    * * Display Name: Order In Profile
    * * SQL Data Type: int
    */
    get OrderInProfile(): number {
        return this.Get('OrderInProfile');
    }
    set OrderInProfile(value: number) {
        this.Set('OrderInProfile', value);
    }

    /**
    * * Field Name: Title
    * * Display Name: Title
    * * SQL Data Type: nvarchar(2000)
    */
    get Title(): string | null {
        return this.Get('Title');
    }
    set Title(value: string | null) {
        this.Set('Title', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(MAX)
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: PublicationUrl
    * * Display Name: Publication Url
    * * SQL Data Type: nvarchar(2000)
    */
    get PublicationUrl(): string | null {
        return this.Get('PublicationUrl');
    }
    set PublicationUrl(value: string | null) {
        this.Set('PublicationUrl', value);
    }

    /**
    * * Field Name: PublisherNames
    * * Display Name: Publisher Names
    * * SQL Data Type: nvarchar(1000)
    */
    get PublisherNames(): string | null {
        return this.Get('PublisherNames');
    }
    set PublisherNames(value: string | null) {
        this.Set('PublisherNames', value);
    }

    /**
    * * Field Name: DateYear
    * * Display Name: Date Year
    * * SQL Data Type: smallint
    */
    get DateYear(): number | null {
        return this.Get('DateYear');
    }
    set DateYear(value: number | null) {
        this.Set('DateYear', value);
    }

    /**
    * * Field Name: DateMonth
    * * Display Name: Date Month
    * * SQL Data Type: tinyint
    */
    get DateMonth(): number | null {
        return this.Get('DateMonth');
    }
    set DateMonth(value: number | null) {
        this.Set('DateMonth', value);
    }

    /**
    * * Field Name: __mj_integration_SyncStatus
    * * Display Name: Mj Integration Sync Status
    * * SQL Data Type: nvarchar(50)
    * * Default Value: Active
    */
    get __mj_integration_SyncStatus(): string {
        return this.Get('__mj_integration_SyncStatus');
    }
    set __mj_integration_SyncStatus(value: string) {
        this.Set('__mj_integration_SyncStatus', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedAt
    * * Display Name: Mj Integration Last Synced At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastSyncedAt(): Date | null {
        return this.Get('__mj_integration_LastSyncedAt');
    }
    set __mj_integration_LastSyncedAt(value: Date | null) {
        this.Set('__mj_integration_LastSyncedAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedSnapshot
    * * Display Name: Mj Integration Last Synced Snapshot
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_LastSyncedSnapshot(): string | null {
        return this.Get('__mj_integration_LastSyncedSnapshot');
    }
    set __mj_integration_LastSyncedSnapshot(value: string | null) {
        this.Set('__mj_integration_LastSyncedSnapshot', value);
    }

    /**
    * * Field Name: __mj_integration_SyncMessage
    * * Display Name: Mj Integration Sync Message
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_SyncMessage(): string | null {
        return this.Get('__mj_integration_SyncMessage');
    }
    set __mj_integration_SyncMessage(value: string | null) {
        this.Set('__mj_integration_SyncMessage', value);
    }

    /**
    * * Field Name: __mj_integration_ContentHash
    * * Display Name: Mj Integration Content Hash
    * * SQL Data Type: nvarchar(64)
    */
    get __mj_integration_ContentHash(): string | null {
        return this.Get('__mj_integration_ContentHash');
    }
    set __mj_integration_ContentHash(value: string | null) {
        this.Set('__mj_integration_ContentHash', value);
    }

    /**
    * * Field Name: __mj_integration_CustomOverflow
    * * Display Name: Mj Integration Custom Overflow
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_CustomOverflow(): string | null {
        return this.Get('__mj_integration_CustomOverflow');
    }
    set __mj_integration_CustomOverflow(value: string | null) {
        this.Set('__mj_integration_CustomOverflow', value);
    }

    /**
    * * Field Name: __mj_integration_ExternalVersion
    * * Display Name: Mj Integration External Version
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_ExternalVersion(): string | null {
        return this.Get('__mj_integration_ExternalVersion');
    }
    set __mj_integration_ExternalVersion(value: string | null) {
        this.Set('__mj_integration_ExternalVersion', value);
    }

    /**
    * * Field Name: __mj_integration_LastSeenModifiedValue
    * * Display Name: Mj Integration Last Seen Modified Value
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_LastSeenModifiedValue(): string | null {
        return this.Get('__mj_integration_LastSeenModifiedValue');
    }
    set __mj_integration_LastSeenModifiedValue(value: string | null) {
        this.Set('__mj_integration_LastSeenModifiedValue', value);
    }

    /**
    * * Field Name: __mj_integration_LastReconciledAt
    * * Display Name: Mj Integration Last Reconciled At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastReconciledAt(): Date | null {
        return this.Get('__mj_integration_LastReconciledAt');
    }
    set __mj_integration_LastReconciledAt(value: Date | null) {
        this.Set('__mj_integration_LastReconciledAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastWriterDirection
    * * Display Name: Mj Integration Last Writer Direction
    * * SQL Data Type: nvarchar(10)
    */
    get __mj_integration_LastWriterDirection(): string | null {
        return this.Get('__mj_integration_LastWriterDirection');
    }
    set __mj_integration_LastWriterDirection(value: string | null) {
        this.Set('__mj_integration_LastWriterDirection', value);
    }

    /**
    * * Field Name: __mj_integration_IsTombstoned
    * * Display Name: Mj Integration Is Tombstoned
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get __mj_integration_IsTombstoned(): boolean {
        return this.Get('__mj_integration_IsTombstoned');
    }
    set __mj_integration_IsTombstoned(value: boolean) {
        this.Set('__mj_integration_IsTombstoned', value);
    }

    /**
    * * Field Name: __mj_integration_DeletedDetectedAt
    * * Display Name: Mj Integration Deleted Detected At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_DeletedDetectedAt(): Date | null {
        return this.Get('__mj_integration_DeletedDetectedAt');
    }
    set __mj_integration_DeletedDetectedAt(value: Date | null) {
        this.Set('__mj_integration_DeletedDetectedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Qualifications - strongly typed entity sub-class
 * * Schema: ORCID
 * * Base Table: Qualification
 * * Base View: vwQualifications
 * * @description A qualification affiliation (qualifications section).
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Qualifications')
export class ORCIDQualificationEntity extends BaseEntity<ORCIDQualificationEntityType> {
    /**
    * Loads the Qualifications record from the database
    * @param ID: string - primary key value to load the Qualifications record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof ORCIDQualificationEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ORCIDRecordID
    * * Display Name: ORCID Record ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Records (vwRecords.ID)
    */
    get ORCIDRecordID(): string {
        return this.Get('ORCIDRecordID');
    }
    set ORCIDRecordID(value: string) {
        this.Set('ORCIDRecordID', value);
    }

    /**
    * * Field Name: PutCode
    * * Display Name: Put Code
    * * SQL Data Type: bigint
    */
    get PutCode(): number {
        return this.Get('PutCode');
    }
    set PutCode(value: number) {
        this.Set('PutCode', value);
    }

    /**
    * * Field Name: DepartmentName
    * * Display Name: Department Name
    * * SQL Data Type: nvarchar(500)
    */
    get DepartmentName(): string | null {
        return this.Get('DepartmentName');
    }
    set DepartmentName(value: string | null) {
        this.Set('DepartmentName', value);
    }

    /**
    * * Field Name: RoleTitle
    * * Display Name: Role Title
    * * SQL Data Type: nvarchar(500)
    */
    get RoleTitle(): string | null {
        return this.Get('RoleTitle');
    }
    set RoleTitle(value: string | null) {
        this.Set('RoleTitle', value);
    }

    /**
    * * Field Name: StartYear
    * * Display Name: Start Year
    * * SQL Data Type: smallint
    */
    get StartYear(): number | null {
        return this.Get('StartYear');
    }
    set StartYear(value: number | null) {
        this.Set('StartYear', value);
    }

    /**
    * * Field Name: StartMonth
    * * Display Name: Start Month
    * * SQL Data Type: tinyint
    */
    get StartMonth(): number | null {
        return this.Get('StartMonth');
    }
    set StartMonth(value: number | null) {
        this.Set('StartMonth', value);
    }

    /**
    * * Field Name: StartDay
    * * Display Name: Start Day
    * * SQL Data Type: tinyint
    */
    get StartDay(): number | null {
        return this.Get('StartDay');
    }
    set StartDay(value: number | null) {
        this.Set('StartDay', value);
    }

    /**
    * * Field Name: EndYear
    * * Display Name: End Year
    * * SQL Data Type: smallint
    */
    get EndYear(): number | null {
        return this.Get('EndYear');
    }
    set EndYear(value: number | null) {
        this.Set('EndYear', value);
    }

    /**
    * * Field Name: EndMonth
    * * Display Name: End Month
    * * SQL Data Type: tinyint
    */
    get EndMonth(): number | null {
        return this.Get('EndMonth');
    }
    set EndMonth(value: number | null) {
        this.Set('EndMonth', value);
    }

    /**
    * * Field Name: EndDay
    * * Display Name: End Day
    * * SQL Data Type: tinyint
    */
    get EndDay(): number | null {
        return this.Get('EndDay');
    }
    set EndDay(value: number | null) {
        this.Set('EndDay', value);
    }

    /**
    * * Field Name: OrgName
    * * Display Name: Org Name
    * * SQL Data Type: nvarchar(500)
    */
    get OrgName(): string | null {
        return this.Get('OrgName');
    }
    set OrgName(value: string | null) {
        this.Set('OrgName', value);
    }

    /**
    * * Field Name: OrgCity
    * * Display Name: Org City
    * * SQL Data Type: nvarchar(255)
    */
    get OrgCity(): string | null {
        return this.Get('OrgCity');
    }
    set OrgCity(value: string | null) {
        this.Set('OrgCity', value);
    }

    /**
    * * Field Name: OrgRegion
    * * Display Name: Org Region
    * * SQL Data Type: nvarchar(255)
    */
    get OrgRegion(): string | null {
        return this.Get('OrgRegion');
    }
    set OrgRegion(value: string | null) {
        this.Set('OrgRegion', value);
    }

    /**
    * * Field Name: OrgCountry
    * * Display Name: Org Country
    * * SQL Data Type: nvarchar(10)
    */
    get OrgCountry(): string | null {
        return this.Get('OrgCountry');
    }
    set OrgCountry(value: string | null) {
        this.Set('OrgCountry', value);
    }

    /**
    * * Field Name: OrgDisambiguatedID
    * * Display Name: Org Disambiguated ID
    * * SQL Data Type: nvarchar(255)
    */
    get OrgDisambiguatedID(): string | null {
        return this.Get('OrgDisambiguatedID');
    }
    set OrgDisambiguatedID(value: string | null) {
        this.Set('OrgDisambiguatedID', value);
    }

    /**
    * * Field Name: OrgDisambiguationSource
    * * Display Name: Org Disambiguation Source
    * * SQL Data Type: nvarchar(50)
    */
    get OrgDisambiguationSource(): string | null {
        return this.Get('OrgDisambiguationSource');
    }
    set OrgDisambiguationSource(value: string | null) {
        this.Set('OrgDisambiguationSource', value);
    }

    /**
    * * Field Name: Url
    * * Display Name: Url
    * * SQL Data Type: nvarchar(2000)
    */
    get Url(): string | null {
        return this.Get('Url');
    }
    set Url(value: string | null) {
        this.Set('Url', value);
    }

    /**
    * * Field Name: Visibility
    * * Display Name: Visibility
    * * SQL Data Type: nvarchar(20)
    */
    get Visibility(): string | null {
        return this.Get('Visibility');
    }
    set Visibility(value: string | null) {
        this.Set('Visibility', value);
    }

    /**
    * * Field Name: SourceName
    * * Display Name: Source Name
    * * SQL Data Type: nvarchar(255)
    */
    get SourceName(): string | null {
        return this.Get('SourceName');
    }
    set SourceName(value: string | null) {
        this.Set('SourceName', value);
    }

    /**
    * * Field Name: SourceOrcid
    * * Display Name: Source Orcid
    * * SQL Data Type: nvarchar(19)
    */
    get SourceOrcid(): string | null {
        return this.Get('SourceOrcid');
    }
    set SourceOrcid(value: string | null) {
        this.Set('SourceOrcid', value);
    }

    /**
    * * Field Name: OrcidCreatedDate
    * * Display Name: Orcid Created Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidCreatedDate(): Date | null {
        return this.Get('OrcidCreatedDate');
    }
    set OrcidCreatedDate(value: Date | null) {
        this.Set('OrcidCreatedDate', value);
    }

    /**
    * * Field Name: OrcidLastModifiedDate
    * * Display Name: Orcid Last Modified Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidLastModifiedDate(): Date | null {
        return this.Get('OrcidLastModifiedDate');
    }
    set OrcidLastModifiedDate(value: Date | null) {
        this.Set('OrcidLastModifiedDate', value);
    }

    /**
    * * Field Name: RawJSON
    * * Display Name: Raw JSON
    * * SQL Data Type: nvarchar(MAX)
    */
    get RawJSON(): string | null {
        return this.Get('RawJSON');
    }
    set RawJSON(value: string | null) {
        this.Set('RawJSON', value);
    }

    /**
    * * Field Name: __mj_integration_SyncStatus
    * * Display Name: Mj Integration Sync Status
    * * SQL Data Type: nvarchar(50)
    * * Default Value: Active
    */
    get __mj_integration_SyncStatus(): string {
        return this.Get('__mj_integration_SyncStatus');
    }
    set __mj_integration_SyncStatus(value: string) {
        this.Set('__mj_integration_SyncStatus', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedAt
    * * Display Name: Mj Integration Last Synced At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastSyncedAt(): Date | null {
        return this.Get('__mj_integration_LastSyncedAt');
    }
    set __mj_integration_LastSyncedAt(value: Date | null) {
        this.Set('__mj_integration_LastSyncedAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedSnapshot
    * * Display Name: Mj Integration Last Synced Snapshot
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_LastSyncedSnapshot(): string | null {
        return this.Get('__mj_integration_LastSyncedSnapshot');
    }
    set __mj_integration_LastSyncedSnapshot(value: string | null) {
        this.Set('__mj_integration_LastSyncedSnapshot', value);
    }

    /**
    * * Field Name: __mj_integration_SyncMessage
    * * Display Name: Mj Integration Sync Message
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_SyncMessage(): string | null {
        return this.Get('__mj_integration_SyncMessage');
    }
    set __mj_integration_SyncMessage(value: string | null) {
        this.Set('__mj_integration_SyncMessage', value);
    }

    /**
    * * Field Name: __mj_integration_ContentHash
    * * Display Name: Mj Integration Content Hash
    * * SQL Data Type: nvarchar(64)
    */
    get __mj_integration_ContentHash(): string | null {
        return this.Get('__mj_integration_ContentHash');
    }
    set __mj_integration_ContentHash(value: string | null) {
        this.Set('__mj_integration_ContentHash', value);
    }

    /**
    * * Field Name: __mj_integration_CustomOverflow
    * * Display Name: Mj Integration Custom Overflow
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_CustomOverflow(): string | null {
        return this.Get('__mj_integration_CustomOverflow');
    }
    set __mj_integration_CustomOverflow(value: string | null) {
        this.Set('__mj_integration_CustomOverflow', value);
    }

    /**
    * * Field Name: __mj_integration_ExternalVersion
    * * Display Name: Mj Integration External Version
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_ExternalVersion(): string | null {
        return this.Get('__mj_integration_ExternalVersion');
    }
    set __mj_integration_ExternalVersion(value: string | null) {
        this.Set('__mj_integration_ExternalVersion', value);
    }

    /**
    * * Field Name: __mj_integration_LastSeenModifiedValue
    * * Display Name: Mj Integration Last Seen Modified Value
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_LastSeenModifiedValue(): string | null {
        return this.Get('__mj_integration_LastSeenModifiedValue');
    }
    set __mj_integration_LastSeenModifiedValue(value: string | null) {
        this.Set('__mj_integration_LastSeenModifiedValue', value);
    }

    /**
    * * Field Name: __mj_integration_LastReconciledAt
    * * Display Name: Mj Integration Last Reconciled At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastReconciledAt(): Date | null {
        return this.Get('__mj_integration_LastReconciledAt');
    }
    set __mj_integration_LastReconciledAt(value: Date | null) {
        this.Set('__mj_integration_LastReconciledAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastWriterDirection
    * * Display Name: Mj Integration Last Writer Direction
    * * SQL Data Type: nvarchar(10)
    */
    get __mj_integration_LastWriterDirection(): string | null {
        return this.Get('__mj_integration_LastWriterDirection');
    }
    set __mj_integration_LastWriterDirection(value: string | null) {
        this.Set('__mj_integration_LastWriterDirection', value);
    }

    /**
    * * Field Name: __mj_integration_IsTombstoned
    * * Display Name: Mj Integration Is Tombstoned
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get __mj_integration_IsTombstoned(): boolean {
        return this.Get('__mj_integration_IsTombstoned');
    }
    set __mj_integration_IsTombstoned(value: boolean) {
        this.Set('__mj_integration_IsTombstoned', value);
    }

    /**
    * * Field Name: __mj_integration_DeletedDetectedAt
    * * Display Name: Mj Integration Deleted Detected At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_DeletedDetectedAt(): Date | null {
        return this.Get('__mj_integration_DeletedDetectedAt');
    }
    set __mj_integration_DeletedDetectedAt(value: Date | null) {
        this.Set('__mj_integration_DeletedDetectedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Record Links - strongly typed entity sub-class
 * * Schema: ORCID
 * * Base Table: RecordLink
 * * Base View: vwRecordLinks
 * * @description Polymorphic link between an ORCID record and a domain record in ANY MJ entity (EntityID + RecordID). Populated by the "Run ORCID Enrichment" action from the curating List members.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Record Links')
export class ORCIDRecordLinkEntity extends BaseEntity<ORCIDRecordLinkEntityType> {
    /**
    * Loads the Record Links record from the database
    * @param ID: string - primary key value to load the Record Links record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof ORCIDRecordLinkEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ORCIDRecordID
    * * Display Name: ORCID Record ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Records (vwRecords.ID)
    */
    get ORCIDRecordID(): string {
        return this.Get('ORCIDRecordID');
    }
    set ORCIDRecordID(value: string) {
        this.Set('ORCIDRecordID', value);
    }

    /**
    * * Field Name: EntityID
    * * Display Name: Entity ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Entities (vwEntities.ID)
    */
    get EntityID(): string {
        return this.Get('EntityID');
    }
    set EntityID(value: string) {
        this.Set('EntityID', value);
    }

    /**
    * * Field Name: RecordID
    * * Display Name: Record ID
    * * SQL Data Type: nvarchar(450)
    */
    get RecordID(): string {
        return this.Get('RecordID');
    }
    set RecordID(value: string) {
        this.Set('RecordID', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: RecordGUID
    * * Display Name: Record GUID
    * * SQL Data Type: uniqueidentifier
    */
    get RecordGUID(): string | null {
        return this.Get('RecordGUID');
    }

    /**
    * * Field Name: Entity
    * * Display Name: Entity
    * * SQL Data Type: nvarchar(255)
    */
    get Entity(): string {
        return this.Get('Entity');
    }
}


/**
 * Records - strongly typed entity sub-class
 * * Schema: ORCID
 * * Base Table: Record
 * * Base View: vwRecords
 * * @description An ORCID public record (ORCID Public API v3.0), keyed by the global ORCID iD. Entity-agnostic enrichment store; link domain records via ORCID.RecordLink.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Records')
export class ORCIDRecordEntity extends BaseEntity<ORCIDRecordEntityType> {
    /**
    * Loads the Records record from the database
    * @param ID: string - primary key value to load the Records record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof ORCIDRecordEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * Validate() method override for Records entity. This is an auto-generated method that invokes the generated validators for this entity for the following fields:
    * * OrcID: The OrcID must be formatted as four groups of four characters separated by hyphens, with the final character being a digit or the letter 'X' (e.g., xxxx-xxxx-xxxx-xxxX).
    * @public
    * @method
    * @override
    */
    public override Validate(): ValidationResult {
        const result = super.Validate();
        this.ValidateOrcIDFormat(result);
        result.Success = result.Success && (result.Errors.length === 0);

        return result;
    }

    /**
    * The OrcID must be formatted as four groups of four characters separated by hyphens, with the final character being a digit or the letter 'X' (e.g., xxxx-xxxx-xxxx-xxxX).
    * @param result - the ValidationResult object to add any errors or warnings to
    * @public
    * @method
    */
    	public ValidateOrcIDFormat(result: ValidationResult) {
    		if (this.OrcID != null) {
    			const regex = /^.{4}-.{4}-.{4}-.{3}[0-9Xx]$/;
    			if (!regex.test(this.OrcID)) {
    				result.Errors.push(new ValidationErrorInfo(
    					"OrcID",
    					"The OrcID must be in the format 'xxxx-xxxx-xxxx-xxxx' where the last character is a digit or 'X'.",
    					this.OrcID,
    					ValidationErrorType.Failure
    				));
    			}
    		}
    	}

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: OrcID
    * * Display Name: Orc ID
    * * SQL Data Type: nvarchar(19)
    * * Description: Canonical ORCID iD (e.g., 0000-0002-1825-0097). Globally unique external key.
    */
    get OrcID(): string {
        return this.Get('OrcID');
    }
    set OrcID(value: string) {
        this.Set('OrcID', value);
    }

    /**
    * * Field Name: GivenNames
    * * Display Name: Given Names
    * * SQL Data Type: nvarchar(255)
    */
    get GivenNames(): string | null {
        return this.Get('GivenNames');
    }
    set GivenNames(value: string | null) {
        this.Set('GivenNames', value);
    }

    /**
    * * Field Name: FamilyName
    * * Display Name: Family Name
    * * SQL Data Type: nvarchar(255)
    */
    get FamilyName(): string | null {
        return this.Get('FamilyName');
    }
    set FamilyName(value: string | null) {
        this.Set('FamilyName', value);
    }

    /**
    * * Field Name: CreditName
    * * Display Name: Credit Name
    * * SQL Data Type: nvarchar(500)
    */
    get CreditName(): string | null {
        return this.Get('CreditName');
    }
    set CreditName(value: string | null) {
        this.Set('CreditName', value);
    }

    /**
    * * Field Name: Biography
    * * Display Name: Biography
    * * SQL Data Type: nvarchar(MAX)
    */
    get Biography(): string | null {
        return this.Get('Biography');
    }
    set Biography(value: string | null) {
        this.Set('Biography', value);
    }

    /**
    * * Field Name: OrcidCreatedDate
    * * Display Name: Orcid Created Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidCreatedDate(): Date | null {
        return this.Get('OrcidCreatedDate');
    }
    set OrcidCreatedDate(value: Date | null) {
        this.Set('OrcidCreatedDate', value);
    }

    /**
    * * Field Name: OrcidLastModifiedDate
    * * Display Name: Orcid Last Modified Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidLastModifiedDate(): Date | null {
        return this.Get('OrcidLastModifiedDate');
    }
    set OrcidLastModifiedDate(value: Date | null) {
        this.Set('OrcidLastModifiedDate', value);
    }

    /**
    * * Field Name: RawJSON
    * * Display Name: Raw JSON
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Full /record JSON payload, preserved verbatim.
    */
    get RawJSON(): string | null {
        return this.Get('RawJSON');
    }
    set RawJSON(value: string | null) {
        this.Set('RawJSON', value);
    }

    /**
    * * Field Name: __mj_integration_SyncStatus
    * * Display Name: Mj Integration Sync Status
    * * SQL Data Type: nvarchar(50)
    * * Default Value: Active
    */
    get __mj_integration_SyncStatus(): string {
        return this.Get('__mj_integration_SyncStatus');
    }
    set __mj_integration_SyncStatus(value: string) {
        this.Set('__mj_integration_SyncStatus', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedAt
    * * Display Name: Mj Integration Last Synced At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastSyncedAt(): Date | null {
        return this.Get('__mj_integration_LastSyncedAt');
    }
    set __mj_integration_LastSyncedAt(value: Date | null) {
        this.Set('__mj_integration_LastSyncedAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedSnapshot
    * * Display Name: Mj Integration Last Synced Snapshot
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_LastSyncedSnapshot(): string | null {
        return this.Get('__mj_integration_LastSyncedSnapshot');
    }
    set __mj_integration_LastSyncedSnapshot(value: string | null) {
        this.Set('__mj_integration_LastSyncedSnapshot', value);
    }

    /**
    * * Field Name: __mj_integration_SyncMessage
    * * Display Name: Mj Integration Sync Message
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_SyncMessage(): string | null {
        return this.Get('__mj_integration_SyncMessage');
    }
    set __mj_integration_SyncMessage(value: string | null) {
        this.Set('__mj_integration_SyncMessage', value);
    }

    /**
    * * Field Name: __mj_integration_ContentHash
    * * Display Name: Mj Integration Content Hash
    * * SQL Data Type: nvarchar(64)
    */
    get __mj_integration_ContentHash(): string | null {
        return this.Get('__mj_integration_ContentHash');
    }
    set __mj_integration_ContentHash(value: string | null) {
        this.Set('__mj_integration_ContentHash', value);
    }

    /**
    * * Field Name: __mj_integration_CustomOverflow
    * * Display Name: Mj Integration Custom Overflow
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_CustomOverflow(): string | null {
        return this.Get('__mj_integration_CustomOverflow');
    }
    set __mj_integration_CustomOverflow(value: string | null) {
        this.Set('__mj_integration_CustomOverflow', value);
    }

    /**
    * * Field Name: __mj_integration_ExternalVersion
    * * Display Name: Mj Integration External Version
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_ExternalVersion(): string | null {
        return this.Get('__mj_integration_ExternalVersion');
    }
    set __mj_integration_ExternalVersion(value: string | null) {
        this.Set('__mj_integration_ExternalVersion', value);
    }

    /**
    * * Field Name: __mj_integration_LastSeenModifiedValue
    * * Display Name: Mj Integration Last Seen Modified Value
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_LastSeenModifiedValue(): string | null {
        return this.Get('__mj_integration_LastSeenModifiedValue');
    }
    set __mj_integration_LastSeenModifiedValue(value: string | null) {
        this.Set('__mj_integration_LastSeenModifiedValue', value);
    }

    /**
    * * Field Name: __mj_integration_LastReconciledAt
    * * Display Name: Mj Integration Last Reconciled At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastReconciledAt(): Date | null {
        return this.Get('__mj_integration_LastReconciledAt');
    }
    set __mj_integration_LastReconciledAt(value: Date | null) {
        this.Set('__mj_integration_LastReconciledAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastWriterDirection
    * * Display Name: Mj Integration Last Writer Direction
    * * SQL Data Type: nvarchar(10)
    */
    get __mj_integration_LastWriterDirection(): string | null {
        return this.Get('__mj_integration_LastWriterDirection');
    }
    set __mj_integration_LastWriterDirection(value: string | null) {
        this.Set('__mj_integration_LastWriterDirection', value);
    }

    /**
    * * Field Name: __mj_integration_IsTombstoned
    * * Display Name: Mj Integration Is Tombstoned
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get __mj_integration_IsTombstoned(): boolean {
        return this.Get('__mj_integration_IsTombstoned');
    }
    set __mj_integration_IsTombstoned(value: boolean) {
        this.Set('__mj_integration_IsTombstoned', value);
    }

    /**
    * * Field Name: __mj_integration_DeletedDetectedAt
    * * Display Name: Mj Integration Deleted Detected At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_DeletedDetectedAt(): Date | null {
        return this.Get('__mj_integration_DeletedDetectedAt');
    }
    set __mj_integration_DeletedDetectedAt(value: Date | null) {
        this.Set('__mj_integration_DeletedDetectedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Research Resources - strongly typed entity sub-class
 * * Schema: ORCID
 * * Base Table: ResearchResource
 * * Base View: vwResearchResources
 * * @description A research resource (research-resources section). Variable shape; full payload in RawJSON.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Research Resources')
export class ORCIDResearchResourceEntity extends BaseEntity<ORCIDResearchResourceEntityType> {
    /**
    * Loads the Research Resources record from the database
    * @param ID: string - primary key value to load the Research Resources record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof ORCIDResearchResourceEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ORCIDRecordID
    * * Display Name: ORCID Record ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Records (vwRecords.ID)
    */
    get ORCIDRecordID(): string {
        return this.Get('ORCIDRecordID');
    }
    set ORCIDRecordID(value: string) {
        this.Set('ORCIDRecordID', value);
    }

    /**
    * * Field Name: PutCode
    * * Display Name: Put Code
    * * SQL Data Type: bigint
    */
    get PutCode(): number {
        return this.Get('PutCode');
    }
    set PutCode(value: number) {
        this.Set('PutCode', value);
    }

    /**
    * * Field Name: Title
    * * Display Name: Title
    * * SQL Data Type: nvarchar(2000)
    */
    get Title(): string | null {
        return this.Get('Title');
    }
    set Title(value: string | null) {
        this.Set('Title', value);
    }

    /**
    * * Field Name: StartYear
    * * Display Name: Start Year
    * * SQL Data Type: smallint
    */
    get StartYear(): number | null {
        return this.Get('StartYear');
    }
    set StartYear(value: number | null) {
        this.Set('StartYear', value);
    }

    /**
    * * Field Name: StartMonth
    * * Display Name: Start Month
    * * SQL Data Type: tinyint
    */
    get StartMonth(): number | null {
        return this.Get('StartMonth');
    }
    set StartMonth(value: number | null) {
        this.Set('StartMonth', value);
    }

    /**
    * * Field Name: StartDay
    * * Display Name: Start Day
    * * SQL Data Type: tinyint
    */
    get StartDay(): number | null {
        return this.Get('StartDay');
    }
    set StartDay(value: number | null) {
        this.Set('StartDay', value);
    }

    /**
    * * Field Name: EndYear
    * * Display Name: End Year
    * * SQL Data Type: smallint
    */
    get EndYear(): number | null {
        return this.Get('EndYear');
    }
    set EndYear(value: number | null) {
        this.Set('EndYear', value);
    }

    /**
    * * Field Name: EndMonth
    * * Display Name: End Month
    * * SQL Data Type: tinyint
    */
    get EndMonth(): number | null {
        return this.Get('EndMonth');
    }
    set EndMonth(value: number | null) {
        this.Set('EndMonth', value);
    }

    /**
    * * Field Name: EndDay
    * * Display Name: End Day
    * * SQL Data Type: tinyint
    */
    get EndDay(): number | null {
        return this.Get('EndDay');
    }
    set EndDay(value: number | null) {
        this.Set('EndDay', value);
    }

    /**
    * * Field Name: Url
    * * Display Name: Url
    * * SQL Data Type: nvarchar(2000)
    */
    get Url(): string | null {
        return this.Get('Url');
    }
    set Url(value: string | null) {
        this.Set('Url', value);
    }

    /**
    * * Field Name: Visibility
    * * Display Name: Visibility
    * * SQL Data Type: nvarchar(20)
    */
    get Visibility(): string | null {
        return this.Get('Visibility');
    }
    set Visibility(value: string | null) {
        this.Set('Visibility', value);
    }

    /**
    * * Field Name: SourceName
    * * Display Name: Source Name
    * * SQL Data Type: nvarchar(255)
    */
    get SourceName(): string | null {
        return this.Get('SourceName');
    }
    set SourceName(value: string | null) {
        this.Set('SourceName', value);
    }

    /**
    * * Field Name: SourceOrcid
    * * Display Name: Source Orcid
    * * SQL Data Type: nvarchar(19)
    */
    get SourceOrcid(): string | null {
        return this.Get('SourceOrcid');
    }
    set SourceOrcid(value: string | null) {
        this.Set('SourceOrcid', value);
    }

    /**
    * * Field Name: OrcidCreatedDate
    * * Display Name: Orcid Created Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidCreatedDate(): Date | null {
        return this.Get('OrcidCreatedDate');
    }
    set OrcidCreatedDate(value: Date | null) {
        this.Set('OrcidCreatedDate', value);
    }

    /**
    * * Field Name: OrcidLastModifiedDate
    * * Display Name: Orcid Last Modified Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidLastModifiedDate(): Date | null {
        return this.Get('OrcidLastModifiedDate');
    }
    set OrcidLastModifiedDate(value: Date | null) {
        this.Set('OrcidLastModifiedDate', value);
    }

    /**
    * * Field Name: RawJSON
    * * Display Name: Raw JSON
    * * SQL Data Type: nvarchar(MAX)
    */
    get RawJSON(): string | null {
        return this.Get('RawJSON');
    }
    set RawJSON(value: string | null) {
        this.Set('RawJSON', value);
    }

    /**
    * * Field Name: __mj_integration_SyncStatus
    * * Display Name: Mj Integration Sync Status
    * * SQL Data Type: nvarchar(50)
    * * Default Value: Active
    */
    get __mj_integration_SyncStatus(): string {
        return this.Get('__mj_integration_SyncStatus');
    }
    set __mj_integration_SyncStatus(value: string) {
        this.Set('__mj_integration_SyncStatus', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedAt
    * * Display Name: Mj Integration Last Synced At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastSyncedAt(): Date | null {
        return this.Get('__mj_integration_LastSyncedAt');
    }
    set __mj_integration_LastSyncedAt(value: Date | null) {
        this.Set('__mj_integration_LastSyncedAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedSnapshot
    * * Display Name: Mj Integration Last Synced Snapshot
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_LastSyncedSnapshot(): string | null {
        return this.Get('__mj_integration_LastSyncedSnapshot');
    }
    set __mj_integration_LastSyncedSnapshot(value: string | null) {
        this.Set('__mj_integration_LastSyncedSnapshot', value);
    }

    /**
    * * Field Name: __mj_integration_SyncMessage
    * * Display Name: Mj Integration Sync Message
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_SyncMessage(): string | null {
        return this.Get('__mj_integration_SyncMessage');
    }
    set __mj_integration_SyncMessage(value: string | null) {
        this.Set('__mj_integration_SyncMessage', value);
    }

    /**
    * * Field Name: __mj_integration_ContentHash
    * * Display Name: Mj Integration Content Hash
    * * SQL Data Type: nvarchar(64)
    */
    get __mj_integration_ContentHash(): string | null {
        return this.Get('__mj_integration_ContentHash');
    }
    set __mj_integration_ContentHash(value: string | null) {
        this.Set('__mj_integration_ContentHash', value);
    }

    /**
    * * Field Name: __mj_integration_CustomOverflow
    * * Display Name: Mj Integration Custom Overflow
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_CustomOverflow(): string | null {
        return this.Get('__mj_integration_CustomOverflow');
    }
    set __mj_integration_CustomOverflow(value: string | null) {
        this.Set('__mj_integration_CustomOverflow', value);
    }

    /**
    * * Field Name: __mj_integration_ExternalVersion
    * * Display Name: Mj Integration External Version
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_ExternalVersion(): string | null {
        return this.Get('__mj_integration_ExternalVersion');
    }
    set __mj_integration_ExternalVersion(value: string | null) {
        this.Set('__mj_integration_ExternalVersion', value);
    }

    /**
    * * Field Name: __mj_integration_LastSeenModifiedValue
    * * Display Name: Mj Integration Last Seen Modified Value
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_LastSeenModifiedValue(): string | null {
        return this.Get('__mj_integration_LastSeenModifiedValue');
    }
    set __mj_integration_LastSeenModifiedValue(value: string | null) {
        this.Set('__mj_integration_LastSeenModifiedValue', value);
    }

    /**
    * * Field Name: __mj_integration_LastReconciledAt
    * * Display Name: Mj Integration Last Reconciled At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastReconciledAt(): Date | null {
        return this.Get('__mj_integration_LastReconciledAt');
    }
    set __mj_integration_LastReconciledAt(value: Date | null) {
        this.Set('__mj_integration_LastReconciledAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastWriterDirection
    * * Display Name: Mj Integration Last Writer Direction
    * * SQL Data Type: nvarchar(10)
    */
    get __mj_integration_LastWriterDirection(): string | null {
        return this.Get('__mj_integration_LastWriterDirection');
    }
    set __mj_integration_LastWriterDirection(value: string | null) {
        this.Set('__mj_integration_LastWriterDirection', value);
    }

    /**
    * * Field Name: __mj_integration_IsTombstoned
    * * Display Name: Mj Integration Is Tombstoned
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get __mj_integration_IsTombstoned(): boolean {
        return this.Get('__mj_integration_IsTombstoned');
    }
    set __mj_integration_IsTombstoned(value: boolean) {
        this.Set('__mj_integration_IsTombstoned', value);
    }

    /**
    * * Field Name: __mj_integration_DeletedDetectedAt
    * * Display Name: Mj Integration Deleted Detected At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_DeletedDetectedAt(): Date | null {
        return this.Get('__mj_integration_DeletedDetectedAt');
    }
    set __mj_integration_DeletedDetectedAt(value: Date | null) {
        this.Set('__mj_integration_DeletedDetectedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Researcher Urls - strongly typed entity sub-class
 * * Schema: ORCID
 * * Base Table: ResearcherUrl
 * * Base View: vwResearcherUrls
 * * @description A researcher URL link (person.researcher-urls).
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Researcher Urls')
export class ORCIDResearcherUrlEntity extends BaseEntity<ORCIDResearcherUrlEntityType> {
    /**
    * Loads the Researcher Urls record from the database
    * @param ID: string - primary key value to load the Researcher Urls record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof ORCIDResearcherUrlEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ORCIDRecordID
    * * Display Name: ORCID Record ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Records (vwRecords.ID)
    */
    get ORCIDRecordID(): string {
        return this.Get('ORCIDRecordID');
    }
    set ORCIDRecordID(value: string) {
        this.Set('ORCIDRecordID', value);
    }

    /**
    * * Field Name: PutCode
    * * Display Name: Put Code
    * * SQL Data Type: bigint
    */
    get PutCode(): number {
        return this.Get('PutCode');
    }
    set PutCode(value: number) {
        this.Set('PutCode', value);
    }

    /**
    * * Field Name: UrlName
    * * Display Name: Url Name
    * * SQL Data Type: nvarchar(355)
    */
    get UrlName(): string | null {
        return this.Get('UrlName');
    }
    set UrlName(value: string | null) {
        this.Set('UrlName', value);
    }

    /**
    * * Field Name: Url
    * * Display Name: Url
    * * SQL Data Type: nvarchar(2000)
    */
    get Url(): string | null {
        return this.Get('Url');
    }
    set Url(value: string | null) {
        this.Set('Url', value);
    }

    /**
    * * Field Name: DisplayIndex
    * * Display Name: Display Index
    * * SQL Data Type: int
    */
    get DisplayIndex(): number | null {
        return this.Get('DisplayIndex');
    }
    set DisplayIndex(value: number | null) {
        this.Set('DisplayIndex', value);
    }

    /**
    * * Field Name: Visibility
    * * Display Name: Visibility
    * * SQL Data Type: nvarchar(20)
    */
    get Visibility(): string | null {
        return this.Get('Visibility');
    }
    set Visibility(value: string | null) {
        this.Set('Visibility', value);
    }

    /**
    * * Field Name: SourceName
    * * Display Name: Source Name
    * * SQL Data Type: nvarchar(255)
    */
    get SourceName(): string | null {
        return this.Get('SourceName');
    }
    set SourceName(value: string | null) {
        this.Set('SourceName', value);
    }

    /**
    * * Field Name: SourceOrcid
    * * Display Name: Source Orcid
    * * SQL Data Type: nvarchar(19)
    */
    get SourceOrcid(): string | null {
        return this.Get('SourceOrcid');
    }
    set SourceOrcid(value: string | null) {
        this.Set('SourceOrcid', value);
    }

    /**
    * * Field Name: OrcidCreatedDate
    * * Display Name: Orcid Created Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidCreatedDate(): Date | null {
        return this.Get('OrcidCreatedDate');
    }
    set OrcidCreatedDate(value: Date | null) {
        this.Set('OrcidCreatedDate', value);
    }

    /**
    * * Field Name: OrcidLastModifiedDate
    * * Display Name: Orcid Last Modified Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidLastModifiedDate(): Date | null {
        return this.Get('OrcidLastModifiedDate');
    }
    set OrcidLastModifiedDate(value: Date | null) {
        this.Set('OrcidLastModifiedDate', value);
    }

    /**
    * * Field Name: RawJSON
    * * Display Name: Raw JSON
    * * SQL Data Type: nvarchar(MAX)
    */
    get RawJSON(): string | null {
        return this.Get('RawJSON');
    }
    set RawJSON(value: string | null) {
        this.Set('RawJSON', value);
    }

    /**
    * * Field Name: __mj_integration_SyncStatus
    * * Display Name: Mj Integration Sync Status
    * * SQL Data Type: nvarchar(50)
    * * Default Value: Active
    */
    get __mj_integration_SyncStatus(): string {
        return this.Get('__mj_integration_SyncStatus');
    }
    set __mj_integration_SyncStatus(value: string) {
        this.Set('__mj_integration_SyncStatus', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedAt
    * * Display Name: Mj Integration Last Synced At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastSyncedAt(): Date | null {
        return this.Get('__mj_integration_LastSyncedAt');
    }
    set __mj_integration_LastSyncedAt(value: Date | null) {
        this.Set('__mj_integration_LastSyncedAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedSnapshot
    * * Display Name: Mj Integration Last Synced Snapshot
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_LastSyncedSnapshot(): string | null {
        return this.Get('__mj_integration_LastSyncedSnapshot');
    }
    set __mj_integration_LastSyncedSnapshot(value: string | null) {
        this.Set('__mj_integration_LastSyncedSnapshot', value);
    }

    /**
    * * Field Name: __mj_integration_SyncMessage
    * * Display Name: Mj Integration Sync Message
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_SyncMessage(): string | null {
        return this.Get('__mj_integration_SyncMessage');
    }
    set __mj_integration_SyncMessage(value: string | null) {
        this.Set('__mj_integration_SyncMessage', value);
    }

    /**
    * * Field Name: __mj_integration_ContentHash
    * * Display Name: Mj Integration Content Hash
    * * SQL Data Type: nvarchar(64)
    */
    get __mj_integration_ContentHash(): string | null {
        return this.Get('__mj_integration_ContentHash');
    }
    set __mj_integration_ContentHash(value: string | null) {
        this.Set('__mj_integration_ContentHash', value);
    }

    /**
    * * Field Name: __mj_integration_CustomOverflow
    * * Display Name: Mj Integration Custom Overflow
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_CustomOverflow(): string | null {
        return this.Get('__mj_integration_CustomOverflow');
    }
    set __mj_integration_CustomOverflow(value: string | null) {
        this.Set('__mj_integration_CustomOverflow', value);
    }

    /**
    * * Field Name: __mj_integration_ExternalVersion
    * * Display Name: Mj Integration External Version
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_ExternalVersion(): string | null {
        return this.Get('__mj_integration_ExternalVersion');
    }
    set __mj_integration_ExternalVersion(value: string | null) {
        this.Set('__mj_integration_ExternalVersion', value);
    }

    /**
    * * Field Name: __mj_integration_LastSeenModifiedValue
    * * Display Name: Mj Integration Last Seen Modified Value
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_LastSeenModifiedValue(): string | null {
        return this.Get('__mj_integration_LastSeenModifiedValue');
    }
    set __mj_integration_LastSeenModifiedValue(value: string | null) {
        this.Set('__mj_integration_LastSeenModifiedValue', value);
    }

    /**
    * * Field Name: __mj_integration_LastReconciledAt
    * * Display Name: Mj Integration Last Reconciled At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastReconciledAt(): Date | null {
        return this.Get('__mj_integration_LastReconciledAt');
    }
    set __mj_integration_LastReconciledAt(value: Date | null) {
        this.Set('__mj_integration_LastReconciledAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastWriterDirection
    * * Display Name: Mj Integration Last Writer Direction
    * * SQL Data Type: nvarchar(10)
    */
    get __mj_integration_LastWriterDirection(): string | null {
        return this.Get('__mj_integration_LastWriterDirection');
    }
    set __mj_integration_LastWriterDirection(value: string | null) {
        this.Set('__mj_integration_LastWriterDirection', value);
    }

    /**
    * * Field Name: __mj_integration_IsTombstoned
    * * Display Name: Mj Integration Is Tombstoned
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get __mj_integration_IsTombstoned(): boolean {
        return this.Get('__mj_integration_IsTombstoned');
    }
    set __mj_integration_IsTombstoned(value: boolean) {
        this.Set('__mj_integration_IsTombstoned', value);
    }

    /**
    * * Field Name: __mj_integration_DeletedDetectedAt
    * * Display Name: Mj Integration Deleted Detected At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_DeletedDetectedAt(): Date | null {
        return this.Get('__mj_integration_DeletedDetectedAt');
    }
    set __mj_integration_DeletedDetectedAt(value: Date | null) {
        this.Set('__mj_integration_DeletedDetectedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Review Assignments - strongly typed entity sub-class
 * * Schema: FeedbackPlatform
 * * Base Table: ReviewAssignment
 * * Base View: vwReviewAssignments
 * * @description Links reviewers to presentations they are assigned to review.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Review Assignments')
export class FeedbackPlatformReviewAssignmentEntity extends BaseEntity<FeedbackPlatformReviewAssignmentEntityType> {
    /**
    * Loads the Review Assignments record from the database
    * @param ID: string - primary key value to load the Review Assignments record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof FeedbackPlatformReviewAssignmentEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * Validate() method override for Review Assignments entity. This is an auto-generated method that invokes the generated validators for this entity for the following fields:
    * * ReminderCount: The number of reminders sent must be zero or a positive number.
    * @public
    * @method
    * @override
    */
    public override Validate(): ValidationResult {
        const result = super.Validate();
        this.ValidateReminderCountGreaterThanOrEqualToZero(result);
        result.Success = result.Success && (result.Errors.length === 0);

        return result;
    }

    /**
    * The number of reminders sent must be zero or a positive number.
    * @param result - the ValidationResult object to add any errors or warnings to
    * @public
    * @method
    */
    public ValidateReminderCountGreaterThanOrEqualToZero(result: ValidationResult) {
    	if (this.ReminderCount !== undefined && this.ReminderCount !== null && this.ReminderCount < 0) {
    		result.Errors.push(new ValidationErrorInfo(
    			"ReminderCount",
    			"Reminder count must be greater than or equal to 0.",
    			this.ReminderCount,
    			ValidationErrorType.Failure
    		));
    	}
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: PresentationID
    * * Display Name: Presentation ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Presentations (vwPresentations.ID)
    */
    get PresentationID(): string {
        return this.Get('PresentationID');
    }
    set PresentationID(value: string) {
        this.Set('PresentationID', value);
    }

    /**
    * * Field Name: ReviewerUserID
    * * Display Name: Reviewer User ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Users (vwUsers.ID)
    */
    get ReviewerUserID(): string {
        return this.Get('ReviewerUserID');
    }
    set ReviewerUserID(value: string) {
        this.Set('ReviewerUserID', value);
    }

    /**
    * * Field Name: AssignedDate
    * * Display Name: Assigned Date
    * * SQL Data Type: datetimeoffset
    * * Default Value: sysdatetimeoffset()
    * * Description: When the reviewer was assigned to this presentation.
    */
    get AssignedDate(): Date {
        return this.Get('AssignedDate');
    }
    set AssignedDate(value: Date) {
        this.Set('AssignedDate', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: nvarchar(20)
    * * Default Value: Assigned
    * * Value List Type: List
    * * Possible Values 
    *   * Assigned
    *   * Canceled
    *   * Completed
    *   * Declined
    *   * InProgress
    * * Description: Current status of the review assignment.
    */
    get Status(): 'Assigned' | 'Canceled' | 'Completed' | 'Declined' | 'InProgress' {
        return this.Get('Status');
    }
    set Status(value: 'Assigned' | 'Canceled' | 'Completed' | 'Declined' | 'InProgress') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: NotificationSent
    * * Display Name: Notification Sent
    * * SQL Data Type: bit
    * * Default Value: 0
    * * Description: Whether assignment notification has been sent to reviewer.
    */
    get NotificationSent(): boolean {
        return this.Get('NotificationSent');
    }
    set NotificationSent(value: boolean) {
        this.Set('NotificationSent', value);
    }

    /**
    * * Field Name: ReminderCount
    * * Display Name: Reminder Count
    * * SQL Data Type: int
    * * Default Value: 0
    * * Description: Number of reminder emails sent to the reviewer.
    */
    get ReminderCount(): number {
        return this.Get('ReminderCount');
    }
    set ReminderCount(value: number) {
        this.Set('ReminderCount', value);
    }

    /**
    * * Field Name: LastReminderDate
    * * Display Name: Last Reminder Date
    * * SQL Data Type: datetimeoffset
    * * Description: Date of the last reminder email sent.
    */
    get LastReminderDate(): Date | null {
        return this.Get('LastReminderDate');
    }
    set LastReminderDate(value: Date | null) {
        this.Set('LastReminderDate', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Presentation
    * * Display Name: Presentation
    * * SQL Data Type: nvarchar(500)
    */
    get Presentation(): string {
        return this.Get('Presentation');
    }

    /**
    * * Field Name: ReviewerUser
    * * Display Name: Reviewer User
    * * SQL Data Type: nvarchar(100)
    */
    get ReviewerUser(): string {
        return this.Get('ReviewerUser');
    }
}


/**
 * Review Question Rubrics - strongly typed entity sub-class
 * * Schema: FeedbackPlatform
 * * Base Table: ReviewQuestionRubric
 * * Base View: vwReviewQuestionRubrics
 * * @description Defines rubric criteria for each rating level of review questions.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Review Question Rubrics')
export class FeedbackPlatformReviewQuestionRubricEntity extends BaseEntity<FeedbackPlatformReviewQuestionRubricEntityType> {
    /**
    * Loads the Review Question Rubrics record from the database
    * @param ID: string - primary key value to load the Review Question Rubrics record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof FeedbackPlatformReviewQuestionRubricEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * Validate() method override for Review Question Rubrics entity. This is an auto-generated method that invokes the generated validators for this entity for the following fields:
    * * RatingValue: The rating value must be a whole number between 1 and 10, inclusive.
    * * Sequence: The sequence number must be greater than 0 to ensure a valid display order.
    * @public
    * @method
    * @override
    */
    public override Validate(): ValidationResult {
        const result = super.Validate();
        this.ValidateRatingValueRange(result);
        this.ValidateSequenceGreaterThanZero(result);
        result.Success = result.Success && (result.Errors.length === 0);

        return result;
    }

    /**
    * The rating value must be a whole number between 1 and 10, inclusive.
    * @param result - the ValidationResult object to add any errors or warnings to
    * @public
    * @method
    */
    public ValidateRatingValueRange(result: ValidationResult) {
    	if (this.RatingValue != null && (this.RatingValue < 1 || this.RatingValue > 10)) {
    		result.Errors.push(new ValidationErrorInfo(
    			"RatingValue",
    			"The rating value must be between 1 and 10.",
    			this.RatingValue,
    			ValidationErrorType.Failure
    		));
    	}
    }

    /**
    * The sequence number must be greater than 0 to ensure a valid display order.
    * @param result - the ValidationResult object to add any errors or warnings to
    * @public
    * @method
    */
    public ValidateSequenceGreaterThanZero(result: ValidationResult) {
    	if (this.Sequence !== undefined && this.Sequence !== null && this.Sequence <= 0) {
    		result.Errors.push(new ValidationErrorInfo(
    			"Sequence",
    			"Sequence must be greater than 0.",
    			this.Sequence,
    			ValidationErrorType.Failure
    		));
    	}
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ReviewQuestionID
    * * Display Name: Review Question ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Review Questions (vwReviewQuestions.ID)
    * * Description: References the review question this rubric applies to.
    */
    get ReviewQuestionID(): string {
        return this.Get('ReviewQuestionID');
    }
    set ReviewQuestionID(value: string) {
        this.Set('ReviewQuestionID', value);
    }

    /**
    * * Field Name: RatingValue
    * * Display Name: Rating Value
    * * SQL Data Type: int
    * * Description: Numeric value for this rubric level (1-10).
    */
    get RatingValue(): number {
        return this.Get('RatingValue');
    }
    set RatingValue(value: number) {
        this.Set('RatingValue', value);
    }

    /**
    * * Field Name: RatingLabel
    * * Display Name: Rating Label
    * * SQL Data Type: nvarchar(50)
    * * Description: Display label for this rubric level (e.g., "Not evident", "Emerging", "Proficient", "Exemplary").
    */
    get RatingLabel(): string {
        return this.Get('RatingLabel');
    }
    set RatingLabel(value: string) {
        this.Set('RatingLabel', value);
    }

    /**
    * * Field Name: Criteria
    * * Display Name: Criteria
    * * SQL Data Type: nvarchar(MAX)
    * * Description: JSON array of criteria/bullet points for this rubric level.
    */
    get Criteria(): string | null {
        return this.Get('Criteria');
    }
    set Criteria(value: string | null) {
        this.Set('Criteria', value);
    }

    /**
    * * Field Name: Sequence
    * * Display Name: Sequence
    * * SQL Data Type: int
    * * Description: Display order for this rubric level within the question.
    */
    get Sequence(): number {
        return this.Get('Sequence');
    }
    set Sequence(value: number) {
        this.Set('Sequence', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: nvarchar(20)
    * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Inactive
    * * Description: Current status of this rubric level.
    */
    get Status(): 'Active' | 'Inactive' {
        return this.Get('Status');
    }
    set Status(value: 'Active' | 'Inactive') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Review Question Types - strongly typed entity sub-class
 * * Schema: FeedbackPlatform
 * * Base Table: ReviewQuestionType
 * * Base View: vwReviewQuestionTypes
 * * @description Defines the different types of review question input controls and validation rules.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Review Question Types')
export class FeedbackPlatformReviewQuestionTypeEntity extends BaseEntity<FeedbackPlatformReviewQuestionTypeEntityType> {
    /**
    * Loads the Review Question Types record from the database
    * @param ID: string - primary key value to load the Review Question Types record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof FeedbackPlatformReviewQuestionTypeEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * Validate() method override for Review Question Types entity. This is an auto-generated method that invokes the generated validators for this entity for the following fields:
    * * MinValue: The minimum value must be 1 or greater.
    * * Table-Level: The maximum value must be greater than or equal to the minimum value when a maximum value is specified.
    * @public
    * @method
    * @override
    */
    public override Validate(): ValidationResult {
        const result = super.Validate();
        this.ValidateMinValueGreaterThanOrEqualToOne(result);
        this.ValidateMaxValueGreaterThanOrEqualToMinValue(result);
        result.Success = result.Success && (result.Errors.length === 0);

        return result;
    }

    /**
    * The minimum value must be 1 or greater.
    * @param result - the ValidationResult object to add any errors or warnings to
    * @public
    * @method
    */
    public ValidateMinValueGreaterThanOrEqualToOne(result: ValidationResult) {
    	if (this.MinValue != null && this.MinValue < 1) {
    		result.Errors.push(new ValidationErrorInfo(
    			"MinValue",
    			"The minimum value must be 1 or greater.",
    			this.MinValue,
    			ValidationErrorType.Failure
    		));
    	}
    }

    /**
    * The maximum value must be greater than or equal to the minimum value when a maximum value is specified.
    * @param result - the ValidationResult object to add any errors or warnings to
    * @public
    * @method
    */
    public ValidateMaxValueGreaterThanOrEqualToMinValue(result: ValidationResult) {
    	if (this.MaxValue != null && this.MinValue != null && this.MaxValue < this.MinValue) {
    		result.Errors.push(new ValidationErrorInfo(
    			"MaxValue",
    			"The maximum value cannot be less than the minimum value.",
    			this.MaxValue,
    			ValidationErrorType.Failure
    		));
    	}
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: Name
    * * Display Name: Name
    * * SQL Data Type: nvarchar(255)
    * * Description: Name of the review question input type (e.g., "1-5 Scale", "Recommendation Level", "Free Text").
    */
    get Name(): string {
        return this.Get('Name');
    }
    set Name(value: string) {
        this.Set('Name', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Detailed description of the input type and its validation rules.
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: InputType
    * * Display Name: Input Type
    * * SQL Data Type: nvarchar(50)
    * * Default Value: NumericRating
    * * Value List Type: List
    * * Possible Values 
    *   * NumericRating
    *   * SelectionList
    *   * TextArea
    *   * TextComment
    * * Description: Type of input control (NumericRating, SelectionList, TextComment, TextArea).
    */
    get InputType(): 'NumericRating' | 'SelectionList' | 'TextArea' | 'TextComment' {
        return this.Get('InputType');
    }
    set InputType(value: 'NumericRating' | 'SelectionList' | 'TextArea' | 'TextComment') {
        this.Set('InputType', value);
    }

    /**
    * * Field Name: MinValue
    * * Display Name: Min Value
    * * SQL Data Type: int
    * * Description: Minimum value for numeric ratings (applies to NumericRating type only).
    */
    get MinValue(): number | null {
        return this.Get('MinValue');
    }
    set MinValue(value: number | null) {
        this.Set('MinValue', value);
    }

    /**
    * * Field Name: MaxValue
    * * Display Name: Max Value
    * * SQL Data Type: int
    * * Description: Maximum value for numeric ratings (applies to NumericRating type only).
    */
    get MaxValue(): number | null {
        return this.Get('MaxValue');
    }
    set MaxValue(value: number | null) {
        this.Set('MaxValue', value);
    }

    /**
    * * Field Name: AllowedValues
    * * Display Name: Allowed Values
    * * SQL Data Type: nvarchar(MAX)
    * * Description: JSON array of allowed values for SelectionList type (e.g., ["Excellent", "Good", "Fair", "Poor"]).
    */
    get AllowedValues(): string | null {
        return this.Get('AllowedValues');
    }
    set AllowedValues(value: string | null) {
        this.Set('AllowedValues', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: nvarchar(20)
    * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Canceled
    *   * Pending
    * * Description: Current status of the review question type definition.
    */
    get Status(): 'Active' | 'Canceled' | 'Pending' {
        return this.Get('Status');
    }
    set Status(value: 'Active' | 'Canceled' | 'Pending') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Review Questions - strongly typed entity sub-class
 * * Schema: FeedbackPlatform
 * * Base Table: ReviewQuestion
 * * Base View: vwReviewQuestions
 * * @description Defines the specific questions/items that make up a review form.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Review Questions')
export class FeedbackPlatformReviewQuestionEntity extends BaseEntity<FeedbackPlatformReviewQuestionEntityType> {
    /**
    * Loads the Review Questions record from the database
    * @param ID: string - primary key value to load the Review Questions record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof FeedbackPlatformReviewQuestionEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * Validate() method override for Review Questions entity. This is an auto-generated method that invokes the generated validators for this entity for the following fields:
    * * Sequence: The sequence number must be greater than zero to ensure a valid display order.
    * @public
    * @method
    * @override
    */
    public override Validate(): ValidationResult {
        const result = super.Validate();
        this.ValidateSequenceGreaterThanZero(result);
        result.Success = result.Success && (result.Errors.length === 0);

        return result;
    }

    /**
    * The sequence number must be greater than zero to ensure a valid display order.
    * @param result - the ValidationResult object to add any errors or warnings to
    * @public
    * @method
    */
    public ValidateSequenceGreaterThanZero(result: ValidationResult) {
        if (this.Sequence !== undefined && this.Sequence !== null && this.Sequence <= 0) {
            result.Errors.push(new ValidationErrorInfo(
                "Sequence",
                "Sequence must be greater than 0.",
                this.Sequence,
                ValidationErrorType.Failure
            ));
        }
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: QuestionText
    * * Display Name: Question Text
    * * SQL Data Type: nvarchar(MAX)
    * * Description: The actual question or prompt text shown to reviewers.
    */
    get QuestionText(): string {
        return this.Get('QuestionText');
    }
    set QuestionText(value: string) {
        this.Set('QuestionText', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Additional context or instructions for this question.
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: ReviewQuestionTypeID
    * * Display Name: Review Question Type ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Review Question Types (vwReviewQuestionTypes.ID)
    * * Description: References the input type and validation rules for this question.
    */
    get ReviewQuestionTypeID(): string {
        return this.Get('ReviewQuestionTypeID');
    }
    set ReviewQuestionTypeID(value: string) {
        this.Set('ReviewQuestionTypeID', value);
    }

    /**
    * * Field Name: Sequence
    * * Display Name: Sequence
    * * SQL Data Type: int
    * * Description: Order in which this question appears in review forms.
    */
    get Sequence(): number {
        return this.Get('Sequence');
    }
    set Sequence(value: number) {
        this.Set('Sequence', value);
    }

    /**
    * * Field Name: IsRequired
    * * Display Name: Is Required
    * * SQL Data Type: bit
    * * Default Value: 1
    * * Description: Whether answering this question is required to complete the review.
    */
    get IsRequired(): boolean {
        return this.Get('IsRequired');
    }
    set IsRequired(value: boolean) {
        this.Set('IsRequired', value);
    }

    /**
    * * Field Name: PresentationCategoryID
    * * Display Name: Presentation Category ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Presentation Categories (vwPresentationCategories.ID)
    * * Description: Optional: restricts this question to specific presentation categories.
    */
    get PresentationCategoryID(): string | null {
        return this.Get('PresentationCategoryID');
    }
    set PresentationCategoryID(value: string | null) {
        this.Set('PresentationCategoryID', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: nvarchar(20)
    * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Canceled
    *   * Pending
    * * Description: Current status of the review question.
    */
    get Status(): 'Active' | 'Canceled' | 'Pending' {
        return this.Get('Status');
    }
    set Status(value: 'Active' | 'Canceled' | 'Pending') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: SeasonID
    * * Display Name: Season ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Seasons (vwSeasons.ID)
    */
    get SeasonID(): string | null {
        return this.Get('SeasonID');
    }
    set SeasonID(value: string | null) {
        this.Set('SeasonID', value);
    }

    /**
    * * Field Name: FormType
    * * Display Name: Form Type
    * * SQL Data Type: nvarchar(20)
    * * Value List Type: List
    * * Possible Values 
    *   * Nomination
    *   * Review
    */
    get FormType(): 'Nomination' | 'Review' | null {
        return this.Get('FormType');
    }
    set FormType(value: 'Nomination' | 'Review' | null) {
        this.Set('FormType', value);
    }

    /**
    * * Field Name: ReviewQuestionType
    * * Display Name: Review Question Type
    * * SQL Data Type: nvarchar(255)
    */
    get ReviewQuestionType(): string {
        return this.Get('ReviewQuestionType');
    }

    /**
    * * Field Name: PresentationCategory
    * * Display Name: Presentation Category
    * * SQL Data Type: nvarchar(255)
    */
    get PresentationCategory(): string | null {
        return this.Get('PresentationCategory');
    }

    /**
    * * Field Name: Season
    * * Display Name: Season
    * * SQL Data Type: nvarchar(200)
    */
    get Season(): string | null {
        return this.Get('Season');
    }
}


/**
 * Review Responses - strongly typed entity sub-class
 * * Schema: FeedbackPlatform
 * * Base Table: ReviewResponse
 * * Base View: vwReviewResponses
 * * @description Stores individual review responses for each question in a review.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Review Responses')
export class FeedbackPlatformReviewResponseEntity extends BaseEntity<FeedbackPlatformReviewResponseEntityType> {
    /**
    * Loads the Review Responses record from the database
    * @param ID: string - primary key value to load the Review Responses record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof FeedbackPlatformReviewResponseEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ReviewID
    * * Display Name: Review ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Reviews (vwReviews.ID)
    */
    get ReviewID(): string {
        return this.Get('ReviewID');
    }
    set ReviewID(value: string) {
        this.Set('ReviewID', value);
    }

    /**
    * * Field Name: ReviewQuestionID
    * * Display Name: Review Question ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Review Questions (vwReviewQuestions.ID)
    */
    get ReviewQuestionID(): string {
        return this.Get('ReviewQuestionID');
    }
    set ReviewQuestionID(value: string) {
        this.Set('ReviewQuestionID', value);
    }

    /**
    * * Field Name: NumericValue
    * * Display Name: Numeric Value
    * * SQL Data Type: int
    * * Description: Numeric value for NumericRating question types.
    */
    get NumericValue(): number | null {
        return this.Get('NumericValue');
    }
    set NumericValue(value: number | null) {
        this.Set('NumericValue', value);
    }

    /**
    * * Field Name: TextValue
    * * Display Name: Text Value
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Text content for TextComment and TextArea question types.
    */
    get TextValue(): string | null {
        return this.Get('TextValue');
    }
    set TextValue(value: string | null) {
        this.Set('TextValue', value);
    }

    /**
    * * Field Name: SelectionValue
    * * Display Name: Selection Value
    * * SQL Data Type: nvarchar(255)
    * * Description: Selected value for SelectionList question types.
    */
    get SelectionValue(): string | null {
        return this.Get('SelectionValue');
    }
    set SelectionValue(value: string | null) {
        this.Set('SelectionValue', value);
    }

    /**
    * * Field Name: RubricLevelID
    * * Display Name: Rubric Level ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Review Question Rubrics (vwReviewQuestionRubrics.ID)
    * * Description: References the specific rubric level selected for rating questions.
    */
    get RubricLevelID(): string | null {
        return this.Get('RubricLevelID');
    }
    set RubricLevelID(value: string | null) {
        this.Set('RubricLevelID', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Review Thank You Notes - strongly typed entity sub-class
 * * Schema: FeedbackPlatform
 * * Base Table: ReviewThankYouNote
 * * Base View: vwReviewThankYouNotes
 * * @description A presenter's thank-you note to one reviewer (one per review).
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Review Thank You Notes')
export class FeedbackPlatformReviewThankYouNoteEntity extends BaseEntity<FeedbackPlatformReviewThankYouNoteEntityType> {
    /**
    * Loads the Review Thank You Notes record from the database
    * @param ID: string - primary key value to load the Review Thank You Notes record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof FeedbackPlatformReviewThankYouNoteEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * Validate() method override for Review Thank You Notes entity. This is an auto-generated method that invokes the generated validators for this entity for the following fields:
    * * Note: The note field cannot be empty. It must contain at least one character to ensure that meaningful text is provided.
    * @public
    * @method
    * @override
    */
    public override Validate(): ValidationResult {
        const result = super.Validate();
        this.ValidateNoteNotEmpty(result);
        result.Success = result.Success && (result.Errors.length === 0);

        return result;
    }

    /**
    * The note field cannot be empty. It must contain at least one character to ensure that meaningful text is provided.
    * @param result - the ValidationResult object to add any errors or warnings to
    * @public
    * @method
    */
    public ValidateNoteNotEmpty(result: ValidationResult) {
    	if (this.Note != null && this.Note.trim().length === 0) {
    		result.Errors.push(new ValidationErrorInfo(
    			"Note",
    			"The Note field cannot be empty.",
    			this.Note,
    			ValidationErrorType.Failure
    		));
    	}
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ReviewID
    * * Display Name: Review ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Reviews (vwReviews.ID)
    */
    get ReviewID(): string {
        return this.Get('ReviewID');
    }
    set ReviewID(value: string) {
        this.Set('ReviewID', value);
    }

    /**
    * * Field Name: Note
    * * Display Name: Note
    * * SQL Data Type: nvarchar(2000)
    */
    get Note(): string {
        return this.Get('Note');
    }
    set Note(value: string) {
        this.Set('Note', value);
    }

    /**
    * * Field Name: SentAt
    * * Display Name: Sent At
    * * SQL Data Type: datetimeoffset
    * * Default Value: sysdatetimeoffset()
    */
    get SentAt(): Date {
        return this.Get('SentAt');
    }
    set SentAt(value: Date) {
        this.Set('SentAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Reviews - strongly typed entity sub-class
 * * Schema: FeedbackPlatform
 * * Base Table: Review
 * * Base View: vwReviews
 * * @description Contains the main review data submitted by reviewers.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Reviews')
export class FeedbackPlatformReviewEntity extends BaseEntity<FeedbackPlatformReviewEntityType> {
    /**
    * Loads the Reviews record from the database
    * @param ID: string - primary key value to load the Reviews record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof FeedbackPlatformReviewEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * Validate() method override for Reviews entity. This is an auto-generated method that invokes the generated validators for this entity for the following fields:
    * * OverallRating: The overall rating must be a value between 1 and 5.
    * @public
    * @method
    * @override
    */
    public override Validate(): ValidationResult {
        const result = super.Validate();
        this.ValidateOverallRatingRange(result);
        result.Success = result.Success && (result.Errors.length === 0);

        return result;
    }

    /**
    * The overall rating must be a value between 1 and 5.
    * @param result - the ValidationResult object to add any errors or warnings to
    * @public
    * @method
    */
    public ValidateOverallRatingRange(result: ValidationResult) {
    	if (this.OverallRating != null && (this.OverallRating < 1 || this.OverallRating > 5)) {
    		result.Errors.push(new ValidationErrorInfo(
    			"OverallRating",
    			"Overall Rating must be between 1 and 5.",
    			this.OverallRating,
    			ValidationErrorType.Failure
    		));
    	}
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ReviewAssignmentID
    * * Display Name: Review Assignment ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Review Assignments (vwReviewAssignments.ID)
    */
    get ReviewAssignmentID(): string {
        return this.Get('ReviewAssignmentID');
    }
    set ReviewAssignmentID(value: string) {
        this.Set('ReviewAssignmentID', value);
    }

    /**
    * * Field Name: OverallRating
    * * Display Name: Overall Rating
    * * SQL Data Type: int
    * * Description: Overall rating for the presentation (1-5 scale).
    */
    get OverallRating(): number | null {
        return this.Get('OverallRating');
    }
    set OverallRating(value: number | null) {
        this.Set('OverallRating', value);
    }

    /**
    * * Field Name: GeneralComments
    * * Display Name: General Comments
    * * SQL Data Type: nvarchar(MAX)
    * * Description: General feedback comments visible to the presenter.
    */
    get GeneralComments(): string | null {
        return this.Get('GeneralComments');
    }
    set GeneralComments(value: string | null) {
        this.Set('GeneralComments', value);
    }

    /**
    * * Field Name: PrivateComments
    * * Display Name: Private Comments
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Private comments for administrators only.
    */
    get PrivateComments(): string | null {
        return this.Get('PrivateComments');
    }
    set PrivateComments(value: string | null) {
        this.Set('PrivateComments', value);
    }

    /**
    * * Field Name: StartedDate
    * * Display Name: Started Date
    * * SQL Data Type: datetimeoffset
    * * Description: When the reviewer started working on the review.
    */
    get StartedDate(): Date | null {
        return this.Get('StartedDate');
    }
    set StartedDate(value: Date | null) {
        this.Set('StartedDate', value);
    }

    /**
    * * Field Name: CompletedDate
    * * Display Name: Completed Date
    * * SQL Data Type: datetimeoffset
    * * Description: When the review was completed and submitted.
    */
    get CompletedDate(): Date | null {
        return this.Get('CompletedDate');
    }
    set CompletedDate(value: Date | null) {
        this.Set('CompletedDate', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: nvarchar(20)
    * * Default Value: NotStarted
    * * Value List Type: List
    * * Possible Values 
    *   * Completed
    *   * InProgress
    *   * NotStarted
    *   * Submitted
    * * Description: Current status of the review.
    */
    get Status(): 'Completed' | 'InProgress' | 'NotStarted' | 'Submitted' {
        return this.Get('Status');
    }
    set Status(value: 'Completed' | 'InProgress' | 'NotStarted' | 'Submitted') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: OverallRecommendation
    * * Display Name: Overall Recommendation
    * * SQL Data Type: nvarchar(50)
    * * Value List Type: List
    * * Possible Values 
    *   * Neutral
    *   * NotRecommend
    *   * Recommend
    *   * StronglyNotRecommend
    *   * StronglyRecommend
    * * Description: Overall recommendation level for the presentation.
    */
    get OverallRecommendation(): 'Neutral' | 'NotRecommend' | 'Recommend' | 'StronglyNotRecommend' | 'StronglyRecommend' | null {
        return this.Get('OverallRecommendation');
    }
    set OverallRecommendation(value: 'Neutral' | 'NotRecommend' | 'Recommend' | 'StronglyNotRecommend' | 'StronglyRecommend' | null) {
        this.Set('OverallRecommendation', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Search Logs - strongly typed entity sub-class
 * * Schema: MAPS
 * * Base Table: SearchLog
 * * Base View: vwSearchLogs
 * * @description One row per partner search. Bridge between MAPS partner attribution (PartnerTokenID) and AI audit (AgentRunID -> __mj.AIAgentRun -> __mj.AIPromptRun).
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Search Logs')
export class MAPSSearchLogEntity extends BaseEntity<MAPSSearchLogEntityType> {
    /**
    * Loads the Search Logs record from the database
    * @param ID: string - primary key value to load the Search Logs record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof MAPSSearchLogEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: PartnerTokenID
    * * Display Name: Partner Token ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Partner Tokens (vwPartnerTokens.ID)
    * * Description: Legacy owning partner token. NULL for company-resolved sessions (staff SSO, company-scoped magic links); dual-written while the standalone cookie path lives; dropped in PR B. Ownership scoping uses CompanyID.
    */
    get PartnerTokenID(): string | null {
        return this.Get('PartnerTokenID');
    }
    set PartnerTokenID(value: string | null) {
        this.Set('PartnerTokenID', value);
    }

    /**
    * * Field Name: AgentRunID
    * * Display Name: Agent Run ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: AI Agent Runs (vwAIAgentRuns.ID)
    * * Description: AIAgentRun produced by the re-rank agent for this search (Phase 4+). NULL in Phase 1-3 because the agent has not been added yet.
    */
    get AgentRunID(): string | null {
        return this.Get('AgentRunID');
    }
    set AgentRunID(value: string | null) {
        this.Set('AgentRunID', value);
    }

    /**
    * * Field Name: ResultCount
    * * Display Name: Result Count
    * * SQL Data Type: int
    * * Description: Total candidate count returned.
    */
    get ResultCount(): number {
        return this.Get('ResultCount');
    }
    set ResultCount(value: number) {
        this.Set('ResultCount', value);
    }

    /**
    * * Field Name: StrongCount
    * * Display Name: Strong Count
    * * SQL Data Type: int
    * * Description: Count of candidates assigned Strong match tier.
    */
    get StrongCount(): number {
        return this.Get('StrongCount');
    }
    set StrongCount(value: number) {
        this.Set('StrongCount', value);
    }

    /**
    * * Field Name: MediumCount
    * * Display Name: Medium Count
    * * SQL Data Type: int
    * * Description: Count of candidates assigned Medium match tier.
    */
    get MediumCount(): number {
        return this.Get('MediumCount');
    }
    set MediumCount(value: number) {
        this.Set('MediumCount', value);
    }

    /**
    * * Field Name: WeakCount
    * * Display Name: Weak Count
    * * SQL Data Type: int
    * * Description: Count of candidates assigned Weak match tier.
    */
    get WeakCount(): number {
        return this.Get('WeakCount');
    }
    set WeakCount(value: number) {
        this.Set('WeakCount', value);
    }

    /**
    * * Field Name: CandidateSnapshot
    * * Display Name: Candidate Snapshot
    * * SQL Data Type: nvarchar(MAX)
    * * Description: JSON-serialized SearchSnapshot — { version, candidates, parsedGuidance, agentRunId, createdAt }. The persistence mechanism for per-search state. Patched in place by mapsSummariesGenerate to attach whySuggested / thingsToLookInto.
    */
    get CandidateSnapshot(): string {
        return this.Get('CandidateSnapshot');
    }
    set CandidateSnapshot(value: string) {
        this.Set('CandidateSnapshot', value);
    }

    /**
    * * Field Name: UserAgent
    * * Display Name: User Agent
    * * SQL Data Type: nvarchar(512)
    * * Description: User-Agent header captured at request time. Anti-scrape signal.
    */
    get UserAgent(): string | null {
        return this.Get('UserAgent');
    }
    set UserAgent(value: string | null) {
        this.Set('UserAgent', value);
    }

    /**
    * * Field Name: IPHash
    * * Display Name: IP Hash
    * * SQL Data Type: char(64)
    * * Description: sha256 of remote IP. Plaintext IP is never stored.
    */
    get IPHash(): string | null {
        return this.Get('IPHash');
    }
    set IPHash(value: string | null) {
        this.Set('IPHash', value);
    }

    /**
    * * Field Name: Timestamp
    * * Display Name: Timestamp
    * * SQL Data Type: datetimeoffset
    * * Default Value: sysdatetimeoffset()
    */
    get Timestamp(): Date {
        return this.Get('Timestamp');
    }
    set Timestamp(value: Date) {
        this.Set('Timestamp', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Title
    * * Display Name: Title
    * * SQL Data Type: nvarchar(200)
    * * Description: Display title for the saved-search list. LLM-generated from the job description at search completion (truncated JD text when generation fails) and user-editable afterward. NULL for searches that predate the Saved Searches feature; such rows are excluded from partner-facing saved-search lists.
    */
    get Title(): string | null {
        return this.Get('Title');
    }
    set Title(value: string | null) {
        this.Set('Title', value);
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    * * Description: Owning company. Authoritative attribution/scoping key for the partner-account model; NOT NULL is enforced at the app layer in PR A and at the DB in PR B.
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: MagicLinkInviteID
    * * Display Name: Magic Link Invite ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Magic Link Invites (vwMagicLinkInvites.ID)
    * * Description: Owning magic-link invite (modern company-scoped links). Saved-search scoping key; precedence over PartnerTokenID/UserEmail. Re-pointed to the successor invite on rotation.
    */
    get MagicLinkInviteID(): string | null {
        return this.Get('MagicLinkInviteID');
    }
    set MagicLinkInviteID(value: string | null) {
        this.Set('MagicLinkInviteID', value);
    }

    /**
    * * Field Name: UserEmail
    * * Display Name: User Email
    * * SQL Data Type: nvarchar(255)
    * * Description: Lowercased staff email (staff SSO sessions only). Saved-search scoping key of last resort — used when the session has neither an invite nor a legacy token.
    */
    get UserEmail(): string | null {
        return this.Get('UserEmail');
    }
    set UserEmail(value: string | null) {
        this.Set('UserEmail', value);
    }

    /**
    * * Field Name: AgentRun
    * * Display Name: Agent Run
    * * SQL Data Type: nvarchar(255)
    */
    get AgentRun(): string | null {
        return this.Get('AgentRun');
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
}


/**
 * Seasons - strongly typed entity sub-class
 * * Schema: FeedbackPlatform
 * * Base Table: Season
 * * Base View: vwSeasons
 * * @description An OEP program season (e.g. AGU26). Phase advances only when AGU staff run the next gate (OEP Season Engine); Timeline and Resources are JSON display copy (IOEPSeasonTimeline / IOEPRoleResources in agu_oep_core).
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Seasons')
export class FeedbackPlatformSeasonEntity extends BaseEntity<FeedbackPlatformSeasonEntityType> {
    /**
    * Loads the Seasons record from the database
    * @param ID: string - primary key value to load the Seasons record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof FeedbackPlatformSeasonEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * Validate() method override for Seasons entity. This is an auto-generated method that invokes the generated validators for this entity for the following fields:
    * * WinnersPerSection: The number of winners per section must be between 1 and 20.
    * @public
    * @method
    * @override
    */
    public override Validate(): ValidationResult {
        const result = super.Validate();
        this.ValidateWinnersPerSectionRange(result);
        result.Success = result.Success && (result.Errors.length === 0);

        return result;
    }

    /**
    * The number of winners per section must be between 1 and 20.
    * @param result - the ValidationResult object to add any errors or warnings to
    * @public
    * @method
    */
    public ValidateWinnersPerSectionRange(result: ValidationResult) {
    	if (this.WinnersPerSection != null && (this.WinnersPerSection < 1 || this.WinnersPerSection > 20)) {
    		result.Errors.push(new ValidationErrorInfo(
    			"WinnersPerSection",
    			"The number of winners per section must be between 1 and 20.",
    			this.WinnersPerSection,
    			ValidationErrorType.Failure
    		));
    	}
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: Code
    * * Display Name: Code
    * * SQL Data Type: nvarchar(20)
    */
    get Code(): string {
        return this.Get('Code');
    }
    set Code(value: string) {
        this.Set('Code', value);
    }

    /**
    * * Field Name: Name
    * * Display Name: Name
    * * SQL Data Type: nvarchar(200)
    */
    get Name(): string {
        return this.Get('Name');
    }
    set Name(value: string) {
        this.Set('Name', value);
    }

    /**
    * * Field Name: Phase
    * * Display Name: Phase
    * * SQL Data Type: nvarchar(20)
    * * Default Value: Setup
    * * Value List Type: List
    * * Possible Values 
    *   * Accepted
    *   * Complete
    *   * DraftClosed
    *   * DraftUpload
    *   * FeedbackReleased
    *   * Matched
    *   * Nominating
    *   * Reviewing
    *   * Selecting
    *   * Setup
    */
    get Phase(): 'Accepted' | 'Complete' | 'DraftClosed' | 'DraftUpload' | 'FeedbackReleased' | 'Matched' | 'Nominating' | 'Reviewing' | 'Selecting' | 'Setup' {
        return this.Get('Phase');
    }
    set Phase(value: 'Accepted' | 'Complete' | 'DraftClosed' | 'DraftUpload' | 'FeedbackReleased' | 'Matched' | 'Nominating' | 'Reviewing' | 'Selecting' | 'Setup') {
        this.Set('Phase', value);
    }

    /**
    * * Field Name: WinnersPerSection
    * * Display Name: Winners Per Section
    * * SQL Data Type: int
    * * Default Value: 3
    */
    get WinnersPerSection(): number {
        return this.Get('WinnersPerSection');
    }
    set WinnersPerSection(value: number) {
        this.Set('WinnersPerSection', value);
    }

    /**
    * * Field Name: Timeline
    * * Display Name: Timeline
    * * SQL Data Type: nvarchar(MAX)
    */
    get Timeline(): string | null {
        return this.Get('Timeline');
    }
    set Timeline(value: string | null) {
        this.Set('Timeline', value);
    }

    /**
    * * Field Name: Resources
    * * Display Name: Resources
    * * SQL Data Type: nvarchar(MAX)
    */
    get Resources(): string | null {
        return this.Get('Resources');
    }
    set Resources(value: string | null) {
        this.Set('Resources', value);
    }

    /**
    * * Field Name: IsActive
    * * Display Name: Is Active
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get IsActive(): boolean {
        return this.Get('IsActive');
    }
    set IsActive(value: boolean) {
        this.Set('IsActive', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Sections - strongly typed entity sub-class
 * * Schema: FeedbackPlatform
 * * Base Table: Section
 * * Base View: vwSections
 * * @description An AGU scientific section. Committees and presentations are scoped by section.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Sections')
export class FeedbackPlatformSectionEntity extends BaseEntity<FeedbackPlatformSectionEntityType> {
    /**
    * Loads the Sections record from the database
    * @param ID: string - primary key value to load the Sections record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof FeedbackPlatformSectionEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: Code
    * * Display Name: Code
    * * SQL Data Type: nvarchar(20)
    */
    get Code(): string {
        return this.Get('Code');
    }
    set Code(value: string) {
        this.Set('Code', value);
    }

    /**
    * * Field Name: Name
    * * Display Name: Name
    * * SQL Data Type: nvarchar(200)
    */
    get Name(): string {
        return this.Get('Name');
    }
    set Name(value: string) {
        this.Set('Name', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Services - strongly typed entity sub-class
 * * Schema: ORCID
 * * Base Table: Service
 * * Base View: vwServices
 * * @description A service affiliation (services section).
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Services')
export class ORCIDServiceEntity extends BaseEntity<ORCIDServiceEntityType> {
    /**
    * Loads the Services record from the database
    * @param ID: string - primary key value to load the Services record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof ORCIDServiceEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ORCIDRecordID
    * * Display Name: ORCID Record ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Records (vwRecords.ID)
    */
    get ORCIDRecordID(): string {
        return this.Get('ORCIDRecordID');
    }
    set ORCIDRecordID(value: string) {
        this.Set('ORCIDRecordID', value);
    }

    /**
    * * Field Name: PutCode
    * * Display Name: Put Code
    * * SQL Data Type: bigint
    */
    get PutCode(): number {
        return this.Get('PutCode');
    }
    set PutCode(value: number) {
        this.Set('PutCode', value);
    }

    /**
    * * Field Name: DepartmentName
    * * Display Name: Department Name
    * * SQL Data Type: nvarchar(500)
    */
    get DepartmentName(): string | null {
        return this.Get('DepartmentName');
    }
    set DepartmentName(value: string | null) {
        this.Set('DepartmentName', value);
    }

    /**
    * * Field Name: RoleTitle
    * * Display Name: Role Title
    * * SQL Data Type: nvarchar(500)
    */
    get RoleTitle(): string | null {
        return this.Get('RoleTitle');
    }
    set RoleTitle(value: string | null) {
        this.Set('RoleTitle', value);
    }

    /**
    * * Field Name: StartYear
    * * Display Name: Start Year
    * * SQL Data Type: smallint
    */
    get StartYear(): number | null {
        return this.Get('StartYear');
    }
    set StartYear(value: number | null) {
        this.Set('StartYear', value);
    }

    /**
    * * Field Name: StartMonth
    * * Display Name: Start Month
    * * SQL Data Type: tinyint
    */
    get StartMonth(): number | null {
        return this.Get('StartMonth');
    }
    set StartMonth(value: number | null) {
        this.Set('StartMonth', value);
    }

    /**
    * * Field Name: StartDay
    * * Display Name: Start Day
    * * SQL Data Type: tinyint
    */
    get StartDay(): number | null {
        return this.Get('StartDay');
    }
    set StartDay(value: number | null) {
        this.Set('StartDay', value);
    }

    /**
    * * Field Name: EndYear
    * * Display Name: End Year
    * * SQL Data Type: smallint
    */
    get EndYear(): number | null {
        return this.Get('EndYear');
    }
    set EndYear(value: number | null) {
        this.Set('EndYear', value);
    }

    /**
    * * Field Name: EndMonth
    * * Display Name: End Month
    * * SQL Data Type: tinyint
    */
    get EndMonth(): number | null {
        return this.Get('EndMonth');
    }
    set EndMonth(value: number | null) {
        this.Set('EndMonth', value);
    }

    /**
    * * Field Name: EndDay
    * * Display Name: End Day
    * * SQL Data Type: tinyint
    */
    get EndDay(): number | null {
        return this.Get('EndDay');
    }
    set EndDay(value: number | null) {
        this.Set('EndDay', value);
    }

    /**
    * * Field Name: OrgName
    * * Display Name: Org Name
    * * SQL Data Type: nvarchar(500)
    */
    get OrgName(): string | null {
        return this.Get('OrgName');
    }
    set OrgName(value: string | null) {
        this.Set('OrgName', value);
    }

    /**
    * * Field Name: OrgCity
    * * Display Name: Org City
    * * SQL Data Type: nvarchar(255)
    */
    get OrgCity(): string | null {
        return this.Get('OrgCity');
    }
    set OrgCity(value: string | null) {
        this.Set('OrgCity', value);
    }

    /**
    * * Field Name: OrgRegion
    * * Display Name: Org Region
    * * SQL Data Type: nvarchar(255)
    */
    get OrgRegion(): string | null {
        return this.Get('OrgRegion');
    }
    set OrgRegion(value: string | null) {
        this.Set('OrgRegion', value);
    }

    /**
    * * Field Name: OrgCountry
    * * Display Name: Org Country
    * * SQL Data Type: nvarchar(10)
    */
    get OrgCountry(): string | null {
        return this.Get('OrgCountry');
    }
    set OrgCountry(value: string | null) {
        this.Set('OrgCountry', value);
    }

    /**
    * * Field Name: OrgDisambiguatedID
    * * Display Name: Org Disambiguated ID
    * * SQL Data Type: nvarchar(255)
    */
    get OrgDisambiguatedID(): string | null {
        return this.Get('OrgDisambiguatedID');
    }
    set OrgDisambiguatedID(value: string | null) {
        this.Set('OrgDisambiguatedID', value);
    }

    /**
    * * Field Name: OrgDisambiguationSource
    * * Display Name: Org Disambiguation Source
    * * SQL Data Type: nvarchar(50)
    */
    get OrgDisambiguationSource(): string | null {
        return this.Get('OrgDisambiguationSource');
    }
    set OrgDisambiguationSource(value: string | null) {
        this.Set('OrgDisambiguationSource', value);
    }

    /**
    * * Field Name: Url
    * * Display Name: Url
    * * SQL Data Type: nvarchar(2000)
    */
    get Url(): string | null {
        return this.Get('Url');
    }
    set Url(value: string | null) {
        this.Set('Url', value);
    }

    /**
    * * Field Name: Visibility
    * * Display Name: Visibility
    * * SQL Data Type: nvarchar(20)
    */
    get Visibility(): string | null {
        return this.Get('Visibility');
    }
    set Visibility(value: string | null) {
        this.Set('Visibility', value);
    }

    /**
    * * Field Name: SourceName
    * * Display Name: Source Name
    * * SQL Data Type: nvarchar(255)
    */
    get SourceName(): string | null {
        return this.Get('SourceName');
    }
    set SourceName(value: string | null) {
        this.Set('SourceName', value);
    }

    /**
    * * Field Name: SourceOrcid
    * * Display Name: Source Orcid
    * * SQL Data Type: nvarchar(19)
    */
    get SourceOrcid(): string | null {
        return this.Get('SourceOrcid');
    }
    set SourceOrcid(value: string | null) {
        this.Set('SourceOrcid', value);
    }

    /**
    * * Field Name: OrcidCreatedDate
    * * Display Name: Orcid Created Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidCreatedDate(): Date | null {
        return this.Get('OrcidCreatedDate');
    }
    set OrcidCreatedDate(value: Date | null) {
        this.Set('OrcidCreatedDate', value);
    }

    /**
    * * Field Name: OrcidLastModifiedDate
    * * Display Name: Orcid Last Modified Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidLastModifiedDate(): Date | null {
        return this.Get('OrcidLastModifiedDate');
    }
    set OrcidLastModifiedDate(value: Date | null) {
        this.Set('OrcidLastModifiedDate', value);
    }

    /**
    * * Field Name: RawJSON
    * * Display Name: Raw JSON
    * * SQL Data Type: nvarchar(MAX)
    */
    get RawJSON(): string | null {
        return this.Get('RawJSON');
    }
    set RawJSON(value: string | null) {
        this.Set('RawJSON', value);
    }

    /**
    * * Field Name: __mj_integration_SyncStatus
    * * Display Name: Mj Integration Sync Status
    * * SQL Data Type: nvarchar(50)
    * * Default Value: Active
    */
    get __mj_integration_SyncStatus(): string {
        return this.Get('__mj_integration_SyncStatus');
    }
    set __mj_integration_SyncStatus(value: string) {
        this.Set('__mj_integration_SyncStatus', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedAt
    * * Display Name: Mj Integration Last Synced At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastSyncedAt(): Date | null {
        return this.Get('__mj_integration_LastSyncedAt');
    }
    set __mj_integration_LastSyncedAt(value: Date | null) {
        this.Set('__mj_integration_LastSyncedAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedSnapshot
    * * Display Name: Mj Integration Last Synced Snapshot
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_LastSyncedSnapshot(): string | null {
        return this.Get('__mj_integration_LastSyncedSnapshot');
    }
    set __mj_integration_LastSyncedSnapshot(value: string | null) {
        this.Set('__mj_integration_LastSyncedSnapshot', value);
    }

    /**
    * * Field Name: __mj_integration_SyncMessage
    * * Display Name: Mj Integration Sync Message
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_SyncMessage(): string | null {
        return this.Get('__mj_integration_SyncMessage');
    }
    set __mj_integration_SyncMessage(value: string | null) {
        this.Set('__mj_integration_SyncMessage', value);
    }

    /**
    * * Field Name: __mj_integration_ContentHash
    * * Display Name: Mj Integration Content Hash
    * * SQL Data Type: nvarchar(64)
    */
    get __mj_integration_ContentHash(): string | null {
        return this.Get('__mj_integration_ContentHash');
    }
    set __mj_integration_ContentHash(value: string | null) {
        this.Set('__mj_integration_ContentHash', value);
    }

    /**
    * * Field Name: __mj_integration_CustomOverflow
    * * Display Name: Mj Integration Custom Overflow
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_CustomOverflow(): string | null {
        return this.Get('__mj_integration_CustomOverflow');
    }
    set __mj_integration_CustomOverflow(value: string | null) {
        this.Set('__mj_integration_CustomOverflow', value);
    }

    /**
    * * Field Name: __mj_integration_ExternalVersion
    * * Display Name: Mj Integration External Version
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_ExternalVersion(): string | null {
        return this.Get('__mj_integration_ExternalVersion');
    }
    set __mj_integration_ExternalVersion(value: string | null) {
        this.Set('__mj_integration_ExternalVersion', value);
    }

    /**
    * * Field Name: __mj_integration_LastSeenModifiedValue
    * * Display Name: Mj Integration Last Seen Modified Value
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_LastSeenModifiedValue(): string | null {
        return this.Get('__mj_integration_LastSeenModifiedValue');
    }
    set __mj_integration_LastSeenModifiedValue(value: string | null) {
        this.Set('__mj_integration_LastSeenModifiedValue', value);
    }

    /**
    * * Field Name: __mj_integration_LastReconciledAt
    * * Display Name: Mj Integration Last Reconciled At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastReconciledAt(): Date | null {
        return this.Get('__mj_integration_LastReconciledAt');
    }
    set __mj_integration_LastReconciledAt(value: Date | null) {
        this.Set('__mj_integration_LastReconciledAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastWriterDirection
    * * Display Name: Mj Integration Last Writer Direction
    * * SQL Data Type: nvarchar(10)
    */
    get __mj_integration_LastWriterDirection(): string | null {
        return this.Get('__mj_integration_LastWriterDirection');
    }
    set __mj_integration_LastWriterDirection(value: string | null) {
        this.Set('__mj_integration_LastWriterDirection', value);
    }

    /**
    * * Field Name: __mj_integration_IsTombstoned
    * * Display Name: Mj Integration Is Tombstoned
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get __mj_integration_IsTombstoned(): boolean {
        return this.Get('__mj_integration_IsTombstoned');
    }
    set __mj_integration_IsTombstoned(value: boolean) {
        this.Set('__mj_integration_IsTombstoned', value);
    }

    /**
    * * Field Name: __mj_integration_DeletedDetectedAt
    * * Display Name: Mj Integration Deleted Detected At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_DeletedDetectedAt(): Date | null {
        return this.Get('__mj_integration_DeletedDetectedAt');
    }
    set __mj_integration_DeletedDetectedAt(value: Date | null) {
        this.Set('__mj_integration_DeletedDetectedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Session Nominators - strongly typed entity sub-class
 * * Schema: FeedbackPlatform
 * * Base Table: SessionNominator
 * * Base View: vwSessionNominators
 * * @description A user designated to nominate OEP presenters in one session.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Session Nominators')
export class FeedbackPlatformSessionNominatorEntity extends BaseEntity<FeedbackPlatformSessionNominatorEntityType> {
    /**
    * Loads the Session Nominators record from the database
    * @param ID: string - primary key value to load the Session Nominators record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof FeedbackPlatformSessionNominatorEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: SessionID
    * * Display Name: Session ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Sessions (vwSessions.ID)
    */
    get SessionID(): string {
        return this.Get('SessionID');
    }
    set SessionID(value: string) {
        this.Set('SessionID', value);
    }

    /**
    * * Field Name: UserID
    * * Display Name: User ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Users (vwUsers.ID)
    */
    get UserID(): string {
        return this.Get('UserID');
    }
    set UserID(value: string) {
        this.Set('UserID', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Session
    * * Display Name: Session
    * * SQL Data Type: nvarchar(500)
    */
    get Session(): string {
        return this.Get('Session');
    }

    /**
    * * Field Name: User
    * * Display Name: User
    * * SQL Data Type: nvarchar(100)
    */
    get User(): string {
        return this.Get('User');
    }
}


/**
 * Sessions - strongly typed entity sub-class
 * * Schema: FeedbackPlatform
 * * Base Table: Session
 * * Base View: vwSessions
 * * @description An annual-meeting session containing OEP presentations; nominators are designated per session.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Sessions')
export class FeedbackPlatformSessionEntity extends BaseEntity<FeedbackPlatformSessionEntityType> {
    /**
    * Loads the Sessions record from the database
    * @param ID: string - primary key value to load the Sessions record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof FeedbackPlatformSessionEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: SeasonID
    * * Display Name: Season ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Seasons (vwSeasons.ID)
    */
    get SeasonID(): string {
        return this.Get('SeasonID');
    }
    set SeasonID(value: string) {
        this.Set('SeasonID', value);
    }

    /**
    * * Field Name: SectionID
    * * Display Name: Section ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Sections (vwSections.ID)
    */
    get SectionID(): string {
        return this.Get('SectionID');
    }
    set SectionID(value: string) {
        this.Set('SectionID', value);
    }

    /**
    * * Field Name: Code
    * * Display Name: Code
    * * SQL Data Type: nvarchar(20)
    */
    get Code(): string {
        return this.Get('Code');
    }
    set Code(value: string) {
        this.Set('Code', value);
    }

    /**
    * * Field Name: Title
    * * Display Name: Title
    * * SQL Data Type: nvarchar(500)
    */
    get Title(): string {
        return this.Get('Title');
    }
    set Title(value: string) {
        this.Set('Title', value);
    }

    /**
    * * Field Name: Format
    * * Display Name: Format
    * * SQL Data Type: nvarchar(10)
    * * Value List Type: List
    * * Possible Values 
    *   * Oral
    *   * Poster
    */
    get Format(): 'Oral' | 'Poster' {
        return this.Get('Format');
    }
    set Format(value: 'Oral' | 'Poster') {
        this.Set('Format', value);
    }

    /**
    * * Field Name: ScheduledAt
    * * Display Name: Scheduled At
    * * SQL Data Type: datetimeoffset
    */
    get ScheduledAt(): Date | null {
        return this.Get('ScheduledAt');
    }
    set ScheduledAt(value: Date | null) {
        this.Set('ScheduledAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Season
    * * Display Name: Season
    * * SQL Data Type: nvarchar(200)
    */
    get Season(): string {
        return this.Get('Season');
    }

    /**
    * * Field Name: Section
    * * Display Name: Section
    * * SQL Data Type: nvarchar(200)
    */
    get Section(): string {
        return this.Get('Section');
    }
}


/**
 * Skills - strongly typed entity sub-class
 * * Schema: CoreSignal
 * * Base Table: Skill
 * * Base View: vwSkills
 * * @description A skill from a CoreSignal profile. Source distinguishes the feed's inferred_skills (derived by CoreSignal) from historical_skills (previously listed by the member). Read via CoreSignal.vwPersonSkill only.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Skills')
export class CoreSignalSkillEntity extends BaseEntity<CoreSignalSkillEntityType> {
    /**
    * Loads the Skills record from the database
    * @param ID: string - primary key value to load the Skills record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof CoreSignalSkillEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ProfileID
    * * Display Name: Profile ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Profiles (vwProfiles.ID)
    */
    get ProfileID(): string {
        return this.Get('ProfileID');
    }
    set ProfileID(value: string) {
        this.Set('ProfileID', value);
    }

    /**
    * * Field Name: OrderInProfile
    * * Display Name: Order In Profile
    * * SQL Data Type: int
    */
    get OrderInProfile(): number {
        return this.Get('OrderInProfile');
    }
    set OrderInProfile(value: number) {
        this.Set('OrderInProfile', value);
    }

    /**
    * * Field Name: Skill
    * * Display Name: Skill
    * * SQL Data Type: nvarchar(255)
    */
    get Skill(): string {
        return this.Get('Skill');
    }
    set Skill(value: string) {
        this.Set('Skill', value);
    }

    /**
    * * Field Name: Source
    * * Display Name: Source
    * * SQL Data Type: nvarchar(20)
    * * Value List Type: List
    * * Possible Values 
    *   * historical
    *   * inferred
    */
    get Source(): 'historical' | 'inferred' {
        return this.Get('Source');
    }
    set Source(value: 'historical' | 'inferred') {
        this.Set('Source', value);
    }

    /**
    * * Field Name: __mj_integration_SyncStatus
    * * Display Name: Mj Integration Sync Status
    * * SQL Data Type: nvarchar(50)
    * * Default Value: Active
    */
    get __mj_integration_SyncStatus(): string {
        return this.Get('__mj_integration_SyncStatus');
    }
    set __mj_integration_SyncStatus(value: string) {
        this.Set('__mj_integration_SyncStatus', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedAt
    * * Display Name: Mj Integration Last Synced At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastSyncedAt(): Date | null {
        return this.Get('__mj_integration_LastSyncedAt');
    }
    set __mj_integration_LastSyncedAt(value: Date | null) {
        this.Set('__mj_integration_LastSyncedAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedSnapshot
    * * Display Name: Mj Integration Last Synced Snapshot
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_LastSyncedSnapshot(): string | null {
        return this.Get('__mj_integration_LastSyncedSnapshot');
    }
    set __mj_integration_LastSyncedSnapshot(value: string | null) {
        this.Set('__mj_integration_LastSyncedSnapshot', value);
    }

    /**
    * * Field Name: __mj_integration_SyncMessage
    * * Display Name: Mj Integration Sync Message
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_SyncMessage(): string | null {
        return this.Get('__mj_integration_SyncMessage');
    }
    set __mj_integration_SyncMessage(value: string | null) {
        this.Set('__mj_integration_SyncMessage', value);
    }

    /**
    * * Field Name: __mj_integration_ContentHash
    * * Display Name: Mj Integration Content Hash
    * * SQL Data Type: nvarchar(64)
    */
    get __mj_integration_ContentHash(): string | null {
        return this.Get('__mj_integration_ContentHash');
    }
    set __mj_integration_ContentHash(value: string | null) {
        this.Set('__mj_integration_ContentHash', value);
    }

    /**
    * * Field Name: __mj_integration_CustomOverflow
    * * Display Name: Mj Integration Custom Overflow
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_CustomOverflow(): string | null {
        return this.Get('__mj_integration_CustomOverflow');
    }
    set __mj_integration_CustomOverflow(value: string | null) {
        this.Set('__mj_integration_CustomOverflow', value);
    }

    /**
    * * Field Name: __mj_integration_ExternalVersion
    * * Display Name: Mj Integration External Version
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_ExternalVersion(): string | null {
        return this.Get('__mj_integration_ExternalVersion');
    }
    set __mj_integration_ExternalVersion(value: string | null) {
        this.Set('__mj_integration_ExternalVersion', value);
    }

    /**
    * * Field Name: __mj_integration_LastSeenModifiedValue
    * * Display Name: Mj Integration Last Seen Modified Value
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_LastSeenModifiedValue(): string | null {
        return this.Get('__mj_integration_LastSeenModifiedValue');
    }
    set __mj_integration_LastSeenModifiedValue(value: string | null) {
        this.Set('__mj_integration_LastSeenModifiedValue', value);
    }

    /**
    * * Field Name: __mj_integration_LastReconciledAt
    * * Display Name: Mj Integration Last Reconciled At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastReconciledAt(): Date | null {
        return this.Get('__mj_integration_LastReconciledAt');
    }
    set __mj_integration_LastReconciledAt(value: Date | null) {
        this.Set('__mj_integration_LastReconciledAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastWriterDirection
    * * Display Name: Mj Integration Last Writer Direction
    * * SQL Data Type: nvarchar(10)
    */
    get __mj_integration_LastWriterDirection(): string | null {
        return this.Get('__mj_integration_LastWriterDirection');
    }
    set __mj_integration_LastWriterDirection(value: string | null) {
        this.Set('__mj_integration_LastWriterDirection', value);
    }

    /**
    * * Field Name: __mj_integration_IsTombstoned
    * * Display Name: Mj Integration Is Tombstoned
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get __mj_integration_IsTombstoned(): boolean {
        return this.Get('__mj_integration_IsTombstoned');
    }
    set __mj_integration_IsTombstoned(value: boolean) {
        this.Set('__mj_integration_IsTombstoned', value);
    }

    /**
    * * Field Name: __mj_integration_DeletedDetectedAt
    * * Display Name: Mj Integration Deleted Detected At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_DeletedDetectedAt(): Date | null {
        return this.Get('__mj_integration_DeletedDetectedAt');
    }
    set __mj_integration_DeletedDetectedAt(value: Date | null) {
        this.Set('__mj_integration_DeletedDetectedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * System Settings - strongly typed entity sub-class
 * * Schema: FeedbackPlatform
 * * Base Table: SystemSetting
 * * Base View: vwSystemSettings
 * * @description System-wide configuration settings for the feedback platform.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'System Settings')
export class FeedbackPlatformSystemSettingEntity extends BaseEntity<FeedbackPlatformSystemSettingEntityType> {
    /**
    * Loads the System Settings record from the database
    * @param ID: string - primary key value to load the System Settings record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof FeedbackPlatformSystemSettingEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: Key
    * * Display Name: Key
    * * SQL Data Type: nvarchar(255)
    * * Description: Configuration key/setting name.
    */
    get Key(): string {
        return this.Get('Key');
    }
    set Key(value: string) {
        this.Set('Key', value);
    }

    /**
    * * Field Name: Value
    * * Display Name: Value
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Configuration value.
    */
    get Value(): string {
        return this.Get('Value');
    }
    set Value(value: string) {
        this.Set('Value', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Description of what this setting controls.
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: DataType
    * * Display Name: Data Type
    * * SQL Data Type: nvarchar(50)
    * * Default Value: String
    * * Value List Type: List
    * * Possible Values 
    *   * Boolean
    *   * DateTime
    *   * Integer
    *   * JSON
    *   * String
    * * Description: Data type of the setting value.
    */
    get DataType(): 'Boolean' | 'DateTime' | 'Integer' | 'JSON' | 'String' {
        return this.Get('DataType');
    }
    set DataType(value: 'Boolean' | 'DateTime' | 'Integer' | 'JSON' | 'String') {
        this.Set('DataType', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: nvarchar(20)
    * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Canceled
    *   * Pending
    * * Description: Current status of the setting.
    */
    get Status(): 'Active' | 'Canceled' | 'Pending' {
        return this.Get('Status');
    }
    set Status(value: 'Active' | 'Canceled' | 'Pending') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Uploaded Documents - strongly typed entity sub-class
 * * Schema: MAPS
 * * Base Table: UploadedDocument
 * * Base View: vwUploadedDocuments
 * * @description Per-upload audit + content store. Every file a partner sends is captured here (raw blob in Azure, extracted text on the row). Retained indefinitely.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Uploaded Documents')
export class MAPSUploadedDocumentEntity extends BaseEntity<MAPSUploadedDocumentEntityType> {
    /**
    * Loads the Uploaded Documents record from the database
    * @param ID: string - primary key value to load the Uploaded Documents record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof MAPSUploadedDocumentEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: PartnerTokenID
    * * Display Name: Partner Token ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Partner Tokens (vwPartnerTokens.ID)
    * * Description: Legacy owning partner token. NULL for company-resolved sessions; dual-written until PR B. Ownership scoping uses CompanyID.
    */
    get PartnerTokenID(): string | null {
        return this.Get('PartnerTokenID');
    }
    set PartnerTokenID(value: string | null) {
        this.Set('PartnerTokenID', value);
    }

    /**
    * * Field Name: SearchLogID
    * * Display Name: Search Log ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Search Logs (vwSearchLogs.ID)
    * * Description: Linked after the search that consumes this upload. NULL until then.
    */
    get SearchLogID(): string | null {
        return this.Get('SearchLogID');
    }
    set SearchLogID(value: string | null) {
        this.Set('SearchLogID', value);
    }

    /**
    * * Field Name: BlobPath
    * * Display Name: Blob Path
    * * SQL Data Type: nvarchar(512)
    * * Description: Azure blob path: maps/uploads/{tokenId}/{ID}/{sanitizedFileName}.
    */
    get BlobPath(): string {
        return this.Get('BlobPath');
    }
    set BlobPath(value: string) {
        this.Set('BlobPath', value);
    }

    /**
    * * Field Name: OriginalFileName
    * * Display Name: Original File Name
    * * SQL Data Type: nvarchar(256)
    * * Description: Original (un-sanitized) file name as supplied by the client. Used as the section label in multi-doc concatenation.
    */
    get OriginalFileName(): string {
        return this.Get('OriginalFileName');
    }
    set OriginalFileName(value: string) {
        this.Set('OriginalFileName', value);
    }

    /**
    * * Field Name: ContentType
    * * Display Name: Content Type
    * * SQL Data Type: nvarchar(128)
    * * Description: application/pdf, application/vnd.openxmlformats-officedocument.wordprocessingml.document, or text/plain.
    */
    get ContentType(): string {
        return this.Get('ContentType');
    }
    set ContentType(value: string) {
        this.Set('ContentType', value);
    }

    /**
    * * Field Name: SizeBytes
    * * Display Name: Size Bytes
    * * SQL Data Type: bigint
    * * Description: Filled from blob metadata at finalize time. NULL until finalize confirms the blob exists.
    */
    get SizeBytes(): number | null {
        return this.Get('SizeBytes');
    }
    set SizeBytes(value: number | null) {
        this.Set('SizeBytes', value);
    }

    /**
    * * Field Name: ExtractedText
    * * Display Name: Extracted Text
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Server-side parsed text. NULL until ExtractStatus='extracted'; also NULL when ExtractStatus='extract_failed'.
    */
    get ExtractedText(): string | null {
        return this.Get('ExtractedText');
    }
    set ExtractedText(value: string | null) {
        this.Set('ExtractedText', value);
    }

    /**
    * * Field Name: ExtractStatus
    * * Display Name: Extract Status
    * * SQL Data Type: nvarchar(16)
    * * Default Value: pending
    * * Value List Type: List
    * * Possible Values 
    *   * extract_failed
    *   * extracted
    *   * pending
    * * Description: pending | extracted | extract_failed. Drives the chip state in the upload UI.
    */
    get ExtractStatus(): 'extract_failed' | 'extracted' | 'pending' {
        return this.Get('ExtractStatus');
    }
    set ExtractStatus(value: 'extract_failed' | 'extracted' | 'pending') {
        this.Set('ExtractStatus', value);
    }

    /**
    * * Field Name: ExtractError
    * * Display Name: Extract Error
    * * SQL Data Type: nvarchar(512)
    * * Description: Short failure reason (pdf_parse_failed, docx_parse_failed, empty_text, corrupt_file, blob_missing). NULL on success.
    */
    get ExtractError(): string | null {
        return this.Get('ExtractError');
    }
    set ExtractError(value: string | null) {
        this.Set('ExtractError', value);
    }

    /**
    * * Field Name: UploadedAt
    * * Display Name: Uploaded At
    * * SQL Data Type: datetimeoffset
    * * Default Value: sysdatetimeoffset()
    */
    get UploadedAt(): Date {
        return this.Get('UploadedAt');
    }
    set UploadedAt(value: Date) {
        this.Set('UploadedAt', value);
    }

    /**
    * * Field Name: ExtractedAt
    * * Display Name: Extracted At
    * * SQL Data Type: datetimeoffset
    */
    get ExtractedAt(): Date | null {
        return this.Get('ExtractedAt');
    }
    set ExtractedAt(value: Date | null) {
        this.Set('ExtractedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CompanyID
    * * Display Name: Company ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Companies (vwCompanies.ID)
    * * Description: Owning company. Authoritative attribution/scoping key for the partner-account model; NOT NULL is enforced at the app layer in PR A and at the DB in PR B.
    */
    get CompanyID(): string | null {
        return this.Get('CompanyID');
    }
    set CompanyID(value: string | null) {
        this.Set('CompanyID', value);
    }

    /**
    * * Field Name: Company
    * * Display Name: Company
    * * SQL Data Type: nvarchar(50)
    */
    get Company(): string | null {
        return this.Get('Company');
    }
}


/**
 * User Notification Settings - strongly typed entity sub-class
 * * Schema: FeedbackPlatform
 * * Base Table: UserNotificationSetting
 * * Base View: vwUserNotificationSettings
 * * @description User-specific notification preferences for each notification type.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'User Notification Settings')
export class FeedbackPlatformUserNotificationSettingEntity extends BaseEntity<FeedbackPlatformUserNotificationSettingEntityType> {
    /**
    * Loads the User Notification Settings record from the database
    * @param ID: string - primary key value to load the User Notification Settings record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof FeedbackPlatformUserNotificationSettingEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: UserID
    * * Display Name: User ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Users (vwUsers.ID)
    */
    get UserID(): string {
        return this.Get('UserID');
    }
    set UserID(value: string) {
        this.Set('UserID', value);
    }

    /**
    * * Field Name: NotificationTypeID
    * * Display Name: Notification Type ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Notification Types (vwNotificationTypes.ID)
    */
    get NotificationTypeID(): string {
        return this.Get('NotificationTypeID');
    }
    set NotificationTypeID(value: string) {
        this.Set('NotificationTypeID', value);
    }

    /**
    * * Field Name: IsEnabled
    * * Display Name: Is Enabled
    * * SQL Data Type: bit
    * * Default Value: 1
    * * Description: Whether this notification type is enabled for the user.
    */
    get IsEnabled(): boolean {
        return this.Get('IsEnabled');
    }
    set IsEnabled(value: boolean) {
        this.Set('IsEnabled', value);
    }

    /**
    * * Field Name: EmailEnabled
    * * Display Name: Email Enabled
    * * SQL Data Type: bit
    * * Default Value: 1
    * * Description: Whether to send email notifications for this type.
    */
    get EmailEnabled(): boolean {
        return this.Get('EmailEnabled');
    }
    set EmailEnabled(value: boolean) {
        this.Set('EmailEnabled', value);
    }

    /**
    * * Field Name: InAppEnabled
    * * Display Name: In App Enabled
    * * SQL Data Type: bit
    * * Default Value: 1
    * * Description: Whether to send in-app notifications for this type.
    */
    get InAppEnabled(): boolean {
        return this.Get('InAppEnabled');
    }
    set InAppEnabled(value: boolean) {
        this.Set('InAppEnabled', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: User
    * * Display Name: User
    * * SQL Data Type: nvarchar(100)
    */
    get User(): string {
        return this.Get('User');
    }

    /**
    * * Field Name: NotificationType
    * * Display Name: Notification Type
    * * SQL Data Type: nvarchar(100)
    */
    get NotificationType(): string {
        return this.Get('NotificationType');
    }
}


/**
 * Work External IDs - strongly typed entity sub-class
 * * Schema: ORCID
 * * Base Table: WorkExternalID
 * * Base View: vwWorkExternalIDs
 * * @description An external identifier for a work (doi/issn/eid/…). Exploded from Work.RawJSON by the enrichment action (a work can have several).
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Work External IDs')
export class ORCIDWorkExternalIDEntity extends BaseEntity<ORCIDWorkExternalIDEntityType> {
    /**
    * Loads the Work External IDs record from the database
    * @param ID: string - primary key value to load the Work External IDs record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof ORCIDWorkExternalIDEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: WorkID
    * * Display Name: Work ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Works (vwWorks.ID)
    */
    get WorkID(): string {
        return this.Get('WorkID');
    }
    set WorkID(value: string) {
        this.Set('WorkID', value);
    }

    /**
    * * Field Name: ExternalIDType
    * * Display Name: External ID Type
    * * SQL Data Type: nvarchar(100)
    */
    get ExternalIDType(): string | null {
        return this.Get('ExternalIDType');
    }
    set ExternalIDType(value: string | null) {
        this.Set('ExternalIDType', value);
    }

    /**
    * * Field Name: ExternalIDValue
    * * Display Name: External ID Value
    * * SQL Data Type: nvarchar(500)
    */
    get ExternalIDValue(): string | null {
        return this.Get('ExternalIDValue');
    }
    set ExternalIDValue(value: string | null) {
        this.Set('ExternalIDValue', value);
    }

    /**
    * * Field Name: ExternalIDNormalizedValue
    * * Display Name: External ID Normalized Value
    * * SQL Data Type: nvarchar(500)
    */
    get ExternalIDNormalizedValue(): string | null {
        return this.Get('ExternalIDNormalizedValue');
    }
    set ExternalIDNormalizedValue(value: string | null) {
        this.Set('ExternalIDNormalizedValue', value);
    }

    /**
    * * Field Name: ExternalIDUrl
    * * Display Name: External ID Url
    * * SQL Data Type: nvarchar(2000)
    */
    get ExternalIDUrl(): string | null {
        return this.Get('ExternalIDUrl');
    }
    set ExternalIDUrl(value: string | null) {
        this.Set('ExternalIDUrl', value);
    }

    /**
    * * Field Name: ExternalIDRelationship
    * * Display Name: External ID Relationship
    * * SQL Data Type: nvarchar(20)
    */
    get ExternalIDRelationship(): string | null {
        return this.Get('ExternalIDRelationship');
    }
    set ExternalIDRelationship(value: string | null) {
        this.Set('ExternalIDRelationship', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Works - strongly typed entity sub-class
 * * Schema: ORCID
 * * Base Table: Work
 * * Base View: vwWorks
 * * @description A work/publication (works section). external-ids → ORCID.WorkExternalID.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Works')
export class ORCIDWorkEntity extends BaseEntity<ORCIDWorkEntityType> {
    /**
    * Loads the Works record from the database
    * @param ID: string - primary key value to load the Works record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof ORCIDWorkEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ORCIDRecordID
    * * Display Name: ORCID Record ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Records (vwRecords.ID)
    */
    get ORCIDRecordID(): string {
        return this.Get('ORCIDRecordID');
    }
    set ORCIDRecordID(value: string) {
        this.Set('ORCIDRecordID', value);
    }

    /**
    * * Field Name: PutCode
    * * Display Name: Put Code
    * * SQL Data Type: bigint
    */
    get PutCode(): number {
        return this.Get('PutCode');
    }
    set PutCode(value: number) {
        this.Set('PutCode', value);
    }

    /**
    * * Field Name: Title
    * * Display Name: Title
    * * SQL Data Type: nvarchar(2000)
    */
    get Title(): string | null {
        return this.Get('Title');
    }
    set Title(value: string | null) {
        this.Set('Title', value);
    }

    /**
    * * Field Name: Subtitle
    * * Display Name: Subtitle
    * * SQL Data Type: nvarchar(2000)
    */
    get Subtitle(): string | null {
        return this.Get('Subtitle');
    }
    set Subtitle(value: string | null) {
        this.Set('Subtitle', value);
    }

    /**
    * * Field Name: TranslatedTitle
    * * Display Name: Translated Title
    * * SQL Data Type: nvarchar(2000)
    */
    get TranslatedTitle(): string | null {
        return this.Get('TranslatedTitle');
    }
    set TranslatedTitle(value: string | null) {
        this.Set('TranslatedTitle', value);
    }

    /**
    * * Field Name: JournalTitle
    * * Display Name: Journal Title
    * * SQL Data Type: nvarchar(1000)
    */
    get JournalTitle(): string | null {
        return this.Get('JournalTitle');
    }
    set JournalTitle(value: string | null) {
        this.Set('JournalTitle', value);
    }

    /**
    * * Field Name: WorkType
    * * Display Name: Work Type
    * * SQL Data Type: nvarchar(100)
    */
    get WorkType(): string | null {
        return this.Get('WorkType');
    }
    set WorkType(value: string | null) {
        this.Set('WorkType', value);
    }

    /**
    * * Field Name: PublicationYear
    * * Display Name: Publication Year
    * * SQL Data Type: smallint
    */
    get PublicationYear(): number | null {
        return this.Get('PublicationYear');
    }
    set PublicationYear(value: number | null) {
        this.Set('PublicationYear', value);
    }

    /**
    * * Field Name: PublicationMonth
    * * Display Name: Publication Month
    * * SQL Data Type: tinyint
    */
    get PublicationMonth(): number | null {
        return this.Get('PublicationMonth');
    }
    set PublicationMonth(value: number | null) {
        this.Set('PublicationMonth', value);
    }

    /**
    * * Field Name: PublicationDay
    * * Display Name: Publication Day
    * * SQL Data Type: tinyint
    */
    get PublicationDay(): number | null {
        return this.Get('PublicationDay');
    }
    set PublicationDay(value: number | null) {
        this.Set('PublicationDay', value);
    }

    /**
    * * Field Name: Url
    * * Display Name: Url
    * * SQL Data Type: nvarchar(2000)
    */
    get Url(): string | null {
        return this.Get('Url');
    }
    set Url(value: string | null) {
        this.Set('Url', value);
    }

    /**
    * * Field Name: Visibility
    * * Display Name: Visibility
    * * SQL Data Type: nvarchar(20)
    */
    get Visibility(): string | null {
        return this.Get('Visibility');
    }
    set Visibility(value: string | null) {
        this.Set('Visibility', value);
    }

    /**
    * * Field Name: SourceName
    * * Display Name: Source Name
    * * SQL Data Type: nvarchar(255)
    */
    get SourceName(): string | null {
        return this.Get('SourceName');
    }
    set SourceName(value: string | null) {
        this.Set('SourceName', value);
    }

    /**
    * * Field Name: SourceOrcid
    * * Display Name: Source Orcid
    * * SQL Data Type: nvarchar(19)
    */
    get SourceOrcid(): string | null {
        return this.Get('SourceOrcid');
    }
    set SourceOrcid(value: string | null) {
        this.Set('SourceOrcid', value);
    }

    /**
    * * Field Name: OrcidCreatedDate
    * * Display Name: Orcid Created Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidCreatedDate(): Date | null {
        return this.Get('OrcidCreatedDate');
    }
    set OrcidCreatedDate(value: Date | null) {
        this.Set('OrcidCreatedDate', value);
    }

    /**
    * * Field Name: OrcidLastModifiedDate
    * * Display Name: Orcid Last Modified Date
    * * SQL Data Type: datetimeoffset
    */
    get OrcidLastModifiedDate(): Date | null {
        return this.Get('OrcidLastModifiedDate');
    }
    set OrcidLastModifiedDate(value: Date | null) {
        this.Set('OrcidLastModifiedDate', value);
    }

    /**
    * * Field Name: RawJSON
    * * Display Name: Raw JSON
    * * SQL Data Type: nvarchar(MAX)
    */
    get RawJSON(): string | null {
        return this.Get('RawJSON');
    }
    set RawJSON(value: string | null) {
        this.Set('RawJSON', value);
    }

    /**
    * * Field Name: __mj_integration_SyncStatus
    * * Display Name: Mj Integration Sync Status
    * * SQL Data Type: nvarchar(50)
    * * Default Value: Active
    */
    get __mj_integration_SyncStatus(): string {
        return this.Get('__mj_integration_SyncStatus');
    }
    set __mj_integration_SyncStatus(value: string) {
        this.Set('__mj_integration_SyncStatus', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedAt
    * * Display Name: Mj Integration Last Synced At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastSyncedAt(): Date | null {
        return this.Get('__mj_integration_LastSyncedAt');
    }
    set __mj_integration_LastSyncedAt(value: Date | null) {
        this.Set('__mj_integration_LastSyncedAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastSyncedSnapshot
    * * Display Name: Mj Integration Last Synced Snapshot
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_LastSyncedSnapshot(): string | null {
        return this.Get('__mj_integration_LastSyncedSnapshot');
    }
    set __mj_integration_LastSyncedSnapshot(value: string | null) {
        this.Set('__mj_integration_LastSyncedSnapshot', value);
    }

    /**
    * * Field Name: __mj_integration_SyncMessage
    * * Display Name: Mj Integration Sync Message
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_SyncMessage(): string | null {
        return this.Get('__mj_integration_SyncMessage');
    }
    set __mj_integration_SyncMessage(value: string | null) {
        this.Set('__mj_integration_SyncMessage', value);
    }

    /**
    * * Field Name: __mj_integration_ContentHash
    * * Display Name: Mj Integration Content Hash
    * * SQL Data Type: nvarchar(64)
    */
    get __mj_integration_ContentHash(): string | null {
        return this.Get('__mj_integration_ContentHash');
    }
    set __mj_integration_ContentHash(value: string | null) {
        this.Set('__mj_integration_ContentHash', value);
    }

    /**
    * * Field Name: __mj_integration_CustomOverflow
    * * Display Name: Mj Integration Custom Overflow
    * * SQL Data Type: nvarchar(MAX)
    */
    get __mj_integration_CustomOverflow(): string | null {
        return this.Get('__mj_integration_CustomOverflow');
    }
    set __mj_integration_CustomOverflow(value: string | null) {
        this.Set('__mj_integration_CustomOverflow', value);
    }

    /**
    * * Field Name: __mj_integration_ExternalVersion
    * * Display Name: Mj Integration External Version
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_ExternalVersion(): string | null {
        return this.Get('__mj_integration_ExternalVersion');
    }
    set __mj_integration_ExternalVersion(value: string | null) {
        this.Set('__mj_integration_ExternalVersion', value);
    }

    /**
    * * Field Name: __mj_integration_LastSeenModifiedValue
    * * Display Name: Mj Integration Last Seen Modified Value
    * * SQL Data Type: nvarchar(255)
    */
    get __mj_integration_LastSeenModifiedValue(): string | null {
        return this.Get('__mj_integration_LastSeenModifiedValue');
    }
    set __mj_integration_LastSeenModifiedValue(value: string | null) {
        this.Set('__mj_integration_LastSeenModifiedValue', value);
    }

    /**
    * * Field Name: __mj_integration_LastReconciledAt
    * * Display Name: Mj Integration Last Reconciled At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_LastReconciledAt(): Date | null {
        return this.Get('__mj_integration_LastReconciledAt');
    }
    set __mj_integration_LastReconciledAt(value: Date | null) {
        this.Set('__mj_integration_LastReconciledAt', value);
    }

    /**
    * * Field Name: __mj_integration_LastWriterDirection
    * * Display Name: Mj Integration Last Writer Direction
    * * SQL Data Type: nvarchar(10)
    */
    get __mj_integration_LastWriterDirection(): string | null {
        return this.Get('__mj_integration_LastWriterDirection');
    }
    set __mj_integration_LastWriterDirection(value: string | null) {
        this.Set('__mj_integration_LastWriterDirection', value);
    }

    /**
    * * Field Name: __mj_integration_IsTombstoned
    * * Display Name: Mj Integration Is Tombstoned
    * * SQL Data Type: bit
    * * Default Value: 0
    */
    get __mj_integration_IsTombstoned(): boolean {
        return this.Get('__mj_integration_IsTombstoned');
    }
    set __mj_integration_IsTombstoned(value: boolean) {
        this.Set('__mj_integration_IsTombstoned', value);
    }

    /**
    * * Field Name: __mj_integration_DeletedDetectedAt
    * * Display Name: Mj Integration Deleted Detected At
    * * SQL Data Type: datetimeoffset
    */
    get __mj_integration_DeletedDetectedAt(): Date | null {
        return this.Get('__mj_integration_DeletedDetectedAt');
    }
    set __mj_integration_DeletedDetectedAt(value: Date | null) {
        this.Set('__mj_integration_DeletedDetectedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}
